import React, { useState, useEffect, useCallback, useRef } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────────────────────
// Login vérifié par l'API du 87 (POST /seg/login contre seg_passwords.json).
// Aucun mot de passe dans ce fichier : il finit en clair dans main.*.js.
// Cette liste ne sert que de repli si /seg/users ne répond pas.
const SEG_USERS_FALLBACK = ["Alain Perrenoud","Nelson","Sebastien","Sanford Pereira",
  "Daniel Teixeira","David Hakim","Abdellah Khadi"];
const API = "/api";

// Session courante (mémoire seulement — un rechargement de page redemande le login,
// comme avant). Le jeton est signé par l'API et valable 12 h.
let AUTH = { token:null, session:null };
const authHeaders = () => AUTH.token ? { "Authorization": "Bearer " + AUTH.token } : {};
// Événement d'audit : ne bloque jamais l'interface, échoue en silence.
const auditEvent = (event, extra = {}) => {
  if (!AUTH.token) return;
  try {
    fetch(`${API}/seg/event`, {
      method:"POST", keepalive:true,
      headers:{ "Content-Type":"application/json", ...authHeaders() },
      body: JSON.stringify({ event, session:AUTH.session, ...extra }),
    }).catch(()=>{});
  } catch (e) { /* page en cours de fermeture */ }
};

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — palette réduite, hiérarchie nette
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  // Surfaces — Graphite & Ochre palette
  bg:        "#f8f4ef",   // --paper (warm off-white)
  bg2:       "#f1ebe4",   // --paper-2
  sidebar:   "#1d1916",   // --graphite
  card:      "#ffffff",
  cardHover: "#f8f4ef",

  // Borders / shadows
  line:   "#dbd6d0",   // --rule
  ring:   "#dbd6d0",
  sh1:    "0 1px 2px rgba(29,25,22,0.04), 0 1px 1px rgba(29,25,22,0.02)",
  sh2:    "0 4px 12px rgba(29,25,22,0.06), 0 1px 2px rgba(29,25,22,0.03)",
  sh3:    "0 12px 32px rgba(29,25,22,0.10), 0 2px 8px rgba(29,25,22,0.05)",

  // Type scale
  ink:    "#191511",   // --ink
  sub:    "#4b4641",   // --ink-2
  hint:   "#7e7974",   // --ink-3
  ghost:  "#a8a39d",   // --graphite-ink-2

  // Brand — ochre (accent only, max 2x per screen)
  brand:  "#c2722e",   // --brand (ocre lumineux)
  brand2: "#954d08",   // --brand-2 (ochre for text on paper)

  // Chrome — graphite (topbar, headers, hero)
  graphite:    "#1d1916",
  graphite2:   "#2b2824",
  graphiteInk: "#edeae6",
  graphiteInk2:"#a8a39d",

  // Green ramp (legacy compat, now graphite-tinted)
  g900:   "#2b2824",   // graphite2
  g700:   "#1d1916",   // graphite
  g500:   "#af642a",   // brand ochre
  g100:   "#f1ebe4",   // paper-2
  g50:    "#f8f4ef",   // paper

  // Semantic (status — consistent across all screens)
  ok:     "#16a34a",   // --ok (vert vif)
  okBright:"#22c55e",  // vert lumineux (sous budget)
  warn:   "#f59e0b",   // --warn (ambre vif)
  err:    "#dc2626",   // --bad (rouge vif)
  info:   "#0ea5e9",   // --info (cyan vif)
  violet: "#7c3aed",   // --violet vif

  // Restaurant identity overlay
  lv:     "#0095d9",   // --lake-view (bleu vif)
  fd:     "#e11d48",   // --fine-dining (carmin vif, distinct du rouge erreur)
  fi:     "#8b5cf6",   // --food-inspired (violet vif)
  sk:     "#ea7317",   // --soul-kitchen (orange vif)
  spt:    "#7e7974",   // support/other

  // Chart
  grid:   "#f1ebe4",
  axis:   "#dbd6d0",
  po:     "#0080b1",   // lake-view blue for PO
  dry:    "#cd9130",   // warn/ochre for DRY
};


// Backward compat alias
const C = {
  lv:T.lv,fd:T.fd,fi:T.fi,sk:T.sk,spt:T.spt,
  gold:T.brand,bg:T.bg,surf:T.card,bdr:T.line,
  t1:T.ink,t2:T.sub,t3:T.hint,grn:T.graphite,grn2:T.graphite2,
  hdr:T.graphite,
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const fmtK = n => {
  const a = Math.abs(n);
  if (a >= 100000) return "CHF "+(n/1000).toFixed(0)+"k";
  if (a >= 10000)  return "CHF "+(n/1000).toFixed(1)+"k";
  if (a >= 1000)   return "CHF "+(n/1000).toFixed(2)+"k";
  return "CHF "+Math.round(n);
};
const fmtN = n => n ? n.toLocaleString("fr-CH",{minimumFractionDigits:0,maximumFractionDigits:0}) : "0";

// Export XLS générique (table HTML → .xls, ouvre dans Excel, sans dépendance)
// headers: string[] ; rows: (string|number)[][] ; pre: lignes de résumé optionnelles [{label,val}]
// `dec` (optionnel) : nombre de décimales à conserver pour les valeurs
// numériques. Sans lui, les nombres sont arrondis à l'entier — ce qui convient
// aux montants en CHF mais détruit les prix unitaires (1.15 devenait 1).
// `opt` (optionnel) : mise en forme d'un rapport destiné à être envoyé —
//   title, subtitle : bandeau d'en-tête sur toute la largeur
//   accent          : couleur des titres de colonnes (défaut bleu marine)
//   zebra           : alterne le fond des lignes pour la lisibilité
//   groupCol        : indice de colonne ; une valeur vide y déclenche une
//                     ligne « détail » plus discrète (sous-lignes fournisseur)
const dlXls = (filename, headers, rows, pre, dec, opt) => {
  const o = opt || {};
  const accent = o.accent || "#1f3b5b";
  const esc = v => String(v==null?"":v).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const num = (v, st) => (typeof v==="number")
    ? `<td style="text-align:right;${st}">${dec==null ? Math.round(v) : v.toFixed(dec)}</td>`
    : `<td style="${st}">${esc(v)}</td>`;
  const NC = headers.length;
  let body = "";

  if (o.title) {
    body += `<tr><td colspan="${NC}" style="background:${accent};color:#fff;`
          + `font-size:15pt;font-weight:bold;padding:10px">${esc(o.title)}</td></tr>`;
    if (o.subtitle) body += `<tr><td colspan="${NC}" style="background:${accent};`
          + `color:#dbe6f2;font-size:9pt;padding:2px 10px 8px">${esc(o.subtitle)}</td></tr>`;
    body += `<tr>${headers.map(()=>'<td style="border:none"></td>').join("")}</tr>`;
  }

  (pre||[]).forEach(p => {
    body += `<tr><td style="background:#f4f6f9;border:none"><b>${esc(p.label)}</b></td>`
          + (p.val!=null
              ? `<td colspan="${Math.max(1,NC-1)}" style="background:#f4f6f9;border:none">${esc(p.val)}</td>`
              : `<td colspan="${NC-1}" style="background:#f4f6f9;border:none"></td>`)
          + `</tr>`;
  });
  if (pre && pre.length) body += `<tr>${headers.map(()=>'<td style="border:none"></td>').join("")}</tr>`;

  body += `<tr>${headers.map(h=>`<th style="background:${accent};color:#fff;`
        + `text-align:left;font-size:9pt;padding:6px 5px">${esc(h)}</th>`).join("")}</tr>`;

  rows.forEach((r, i) => {
    const detail = (o.groupCol != null) && (r[o.groupCol] === "" || r[o.groupCol] == null);
    let st = "";
    if (detail)            st = "color:#5a6472;font-style:italic;background:#fafbfc;";
    else if (o.zebra && i % 2) st = "background:#f7f9fb;";
    body += `<tr>${r.map(c => num(c, st)).join("")}</tr>`;
  });

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"><style>td,th{font-family:Calibri,Arial,sans-serif;font-size:10pt;vertical-align:top}</style></head><body><table border="1" cellspacing="0" style="border-color:#dfe4ea">${body}</table></body></html>`;
  const blob = new Blob(["\ufeff", html], {type:"application/vnd.ms-excel;charset=utf-8"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename.endsWith(".xls")?filename:filename+".xls";
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────────────────────
// DATA CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const MONTHLY = [
  {label:"January", lv:74000,fd:10613,fi:6210, sk:12677,spt:12800,total:116300},
  {label:"February", lv:97700,fd:18159,fi:10435,sk:27606,spt:23800,total:177700},
  {label:"March",    lv:81700,fd:12356,fi:10286,sk:16317,spt:22600,total:153100},
];
// Q2 budgets = _Q2_TOT (couverts planning Q2 NET) × _REFS (CHF/couvert référence)
// LV: 31020 × 5.00 = 155'100 (2585 cov/sem × 12 sem W0-W11) · SK: 5400 × 12 = 64'800 · FI: 1800 × 20 = 36'000 · FD: 600 × 17 = 10'200
// SK schedule corrigé: 5 chef services × 60 cov + 180 cov mise en place vendredi = 480 cov/sem
// FD ref = 17 (Axel 15 + Jeremy Pastry Lab 2 pour pâtisseries) — incl. Pastry Lab dans CHF
// Tous alignés sur même schéma NET — budget et projected sur la même base → comparaison honnête
// Budgets restos Q2 (validés Alain 10.06): LV 155.1K, FI 35.2K (176 cov×20), FD 9K (600 cov×15), SK 57.6K (4800 cov×12)
const Q2_BUDGET_A = {total:256900,lv:155100,fd:9000,fi:35200,sk:57600};
// Budget toutes places (resto + non-resto, toutes dépenses) — référence du chiffre BRUT du bandeau
const Q2_BUDGET_ALL = 364000;
// days_elapsed dynamique depuis le 1er avril
// Convention: 1er avril = jour 1, 1er mai = jour 31, 30 juin = jour 91
const _Q2_START = new Date(2026,3,1); // 1er avril 2026
const _TODAY    = new Date();
const _DAYS_Q2  = Math.max(1, Math.floor((_TODAY - _Q2_START)/(1000*60*60*24)) + 1);

const Q2_FALLBACK = {
  april_committed:75039,april_po:45577,april_dry:29462,
  april_committed_resto:52058,po_resto:35672,dry_resto:16386,
  hors_resto:{total:118041,po:62142,dry:55899,places:[
    {place:"Application Kitchen",po:18511,dry:7431,total:25942},
    {place:"F&B Service",po:741,dry:22845,total:23586},
    {place:"Leeman Kitchen",po:14173,dry:5523,total:19696},
    {place:"Pastry Production",po:15025,dry:444,total:15469},
    {place:"Chocolate Lab",po:1643,dry:7144,total:8787},
    {place:"Irma Kitchen",po:4852,dry:1857,total:6709},
    {place:"Mane Kitchen",po:1728,dry:2457,total:4185},
    {place:"Baking Lab",po:1611,dry:2311,total:3922},
    {place:"The Bakery",po:326,dry:2890,total:3216},
    {place:"Mosimann Kitchen",po:640,dry:2166,total:2806},
    {place:"Pastry Lab",po:1775,dry:5253,total:7028},
    {place:"Production Kitchen",po:1415,dry:565,total:1980},
    {place:"Demo Kitchen",po:1067,dry:173,total:1240},
  ]},
  q2_budget_a:256900,q2_budget_all:364000,
  april_forecast:75039,days_elapsed:_DAYS_Q2,daily_rate_chf:Math.round(75039/_DAYS_Q2),
  q2_pace_projected:302000,
  note:`Q2 jour ${_DAYS_Q2}/84 — BRUT toutes places CHF 75k (dont restaurants CHF 52k)`,
  by_entity:{
    lv:{committed:41957,po:25710,dry:13407,transfer:2840,q2_budget_a:155100,q2_pace:222479},
    fd:{committed:-1265,po:1411, dry:164,  transfer:-2840,q2_budget_a:9000,   q2_pace:9119},
    fi:{committed:6344, po:5536, dry:808,  q2_budget_a:35200,  q2_pace:36086},
    sk:{committed:5022, po:3015, dry:2007, q2_budget_a:57600,  q2_pace:28568},
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// BASE COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function Card({children,style={}}) {
  return (
    <div style={{background:T.card,borderRadius:16,border:`1px solid ${T.line}`,
      boxShadow:T.sh2,padding:"24px",...style}}>
      {children}
    </div>
  );
}

function Label({children,style={}}) {
  return (
    <div style={{fontSize:11,fontWeight:600,color:T.hint,textTransform:"uppercase",
      letterSpacing:".08em",marginBottom:6,...style}}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{height:1,background:T.line,margin:"20px 0"}}/>;
}

function Chip({label,color=T.ok,light}) {
  const bg = light||color+"14";
  return (
    <span style={{display:"inline-flex",alignItems:"center",padding:"3px 10px",
      borderRadius:20,fontSize:10.5,fontWeight:700,color,background:bg,
      border:`1px solid ${color}28`}}>
      {label}
    </span>
  );
}

function Bar({value,max,color=T.g700,height=6,radius=3}) {
  const w = max>0 ? Math.min((value/max)*100,100) : 0;
  return (
    <div style={{height,background:T.grid,borderRadius:radius,overflow:"hidden"}}>
      <div style={{width:w+"%",height:"100%",background:color,borderRadius:radius,transition:"width .5s"}}/>
    </div>
  );
}

// Backward compat
function PaceBar({label,committed,pace,budget,color}) {
  const pctC=Math.min((committed/budget)*100,100);
  const over=pace>budget;
  return (
    <div style={{marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
        <span style={{fontSize:13,fontWeight:600,color:T.ink}}>{label}</span>
        <span style={{fontSize:11,fontWeight:700,color:over?T.err:T.ok}}>{over?"⚠ AU-DESSUS":"✓ OK"}</span>
      </div>
      <div style={{position:"relative",height:16,background:T.grid,borderRadius:8,overflow:"hidden"}}>
        <div style={{position:"absolute",left:0,top:0,height:"100%",
          width:Math.min((pace/budget)*100,100)+"%",
          background:over?"rgba(220,38,38,0.12)":"rgba(22,163,74,0.1)"}}/>
        <div style={{position:"absolute",left:0,top:0,height:"100%",
          width:pctC+"%",background:color||T.g700,borderRadius:8,transition:"width .5s"}}/>
      </div>
      <div style={{display:"flex",gap:16,marginTop:4,fontSize:11,color:T.hint}}>
        <span>Spent: <b style={{color:color||T.g700}}>{fmtK(committed)}</b></span>
        <span>Rate: <b style={{color:over?T.err:T.ok}}>{fmtK(pace)}</b></span>
        <span>Budget: <b style={{color:T.ink}}>{fmtK(budget)}</b></span>
      </div>
    </div>
  );
}

function BarChart({data,colorKey,maxVal,height=120,showLabel=true}) {
  if(!data||data.length===0) return null;
  const max=maxVal||Math.max(...data.map(d=>d.value),1);
  return (
    <div style={{display:"flex",alignItems:"flex-end",gap:5,height:height+24,paddingTop:8}}>
      {data.map((d,i)=>{
        const pct=max>0?(d.value/max)*100:0;
        const col=d.color||C[colorKey]||T.g700;
        return (
          <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
            {d.value>0&&<span style={{fontSize:8.5,color:T.hint,fontWeight:600}}>
              {d.value>=1000?(d.value/1000).toFixed(0)+"k":Math.round(d.value)}
            </span>}
            <div style={{width:"100%",height:(pct/100)*(height-20),background:d.value===0?T.grid:col,
              borderRadius:"4px 4px 0 0",minHeight:d.value>0?3:2}}/>
            {showLabel&&<span style={{fontSize:8.5,color:T.hint,textAlign:"center"}}>{d.label}</span>}
          </div>
        );
      })}
    </div>
  );
}

function StackedBar({row}) {
  const total=row.lv+row.fd+row.fi+row.sk+row.spt;
  const MAX=177700;
  return (
    <div style={{marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
        <span style={{fontSize:12,fontWeight:500,color:T.sub}}>{row.label}</span>
        <span style={{fontSize:12,fontWeight:700,color:T.ink}}>{fmtK(total)}</span>
      </div>
      <div style={{display:"flex",height:20,borderRadius:6,overflow:"hidden",background:T.grid}}>
        {[{v:row.lv,c:T.lv},{v:row.sk,c:T.sk},{v:row.fd,c:T.fd},{v:row.fi,c:T.fi}].map((s,i)=>(
          <div key={i} style={{width:(s.v/MAX*100)+"%",background:s.c,minWidth:s.v>5000?"4px":0}}/>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUMMARY TAB — design épuré, 1 page
// ─────────────────────────────────────────────────────────────────────────────
function SummaryTab() {
  // KPI hero
  const hero = [
    {label:"Q1 ACTUAL CERTIFIED",   val:"CHF 465k",  delta:"+69% vs budget",   c:T.err,   ok:false},
    {label:"Q1 APPROVED BUDGET", val:"CHF 275k",  delta:"insufficient — CHF 91k gap from day 1",c:T.hint, ok:null},
    {label:`Q2 START (${_DAYS_Q2} days)`,val:"CHF 75k",   delta:`Rate CHF ${Math.round(75039/_DAYS_Q2).toLocaleString()}/d`,c:T.warn,  ok:null},
    {label:"Q2 PROJECTION",      val:"~CHF 248k",  delta:"By actual cover · budget CHF 364k (all places)",c:T.ok, ok:true},
  ];

  const restaurants = [
    {key:"lv",name:"Lake View",    cov:3700, ref:5.38,  q1:276000,chf_cov:5.14, ok:true},
    {key:"sk",name:"Soul Kitchen", cov:250,  ref:12.00, q1:57600, chf_cov:17.82,ok:false},
    {key:"fi",name:"Food Inspired",cov:160,  ref:20.00, q1:26931, chf_cov:25.65,ok:false},
    {key:"fd",name:"Fine Dining",  cov:50,   ref:15.00, q1:38900, chf_cov:24.48,ok:false},
  ];

  const decisions = [
    {n:1, status:"board", label:"Approuver budget Q2 de CHF 364k (toutes places)",
     note:"Budget toutes places CHF 364k — dont restaurants CHF 256.9k (LV 155.1 / SK 57.6 / FI 35.2 / FD 9)."},
    {n:2, status:"board", label:"Valider CHF 465k comme chiffre Q1 officiel",
     note:"Certified PO+DRY method without double-counting. Supersedes all prior references."},
    {n:3, status:"it",    label:"Deploy x_studio_place on DRY before Q2 W1",
     note:"Single Odoo field. Definitively solves LV vs SK allocation."},
    {n:4, status:"ops",   label:"FD: protein cap CHF 8/cover",
     note:"CHF 24.48/cover Q1 vs reference CHF 15. Alerte hebdo CHF 1'200/service."},
  ];

  const statusColors = {board:T.g700, it:T.sk, ops:T.warn};
  const statusLabels = {board:"Board", it:"IT", ops:"Operations"};

  return (
    <div>
      {/* ── HERO ─────────────────────────────────────── */}
      <div style={{
        background:"linear-gradient(135deg,#1d1916 0%,#2b2824 60%,#1d1916 100%)",
        borderRadius:20,padding:"32px 36px",marginBottom:24,color:"#fff",
        position:"relative",overflow:"hidden",
      }}>
        {/* subtle texture */}
        <div style={{position:"absolute",top:0,right:0,width:400,height:400,
          background:"radial-gradient(circle at 80% 20%,rgba(175,100,42,0.06),transparent 60%)",
          pointerEvents:"none"}}/>

        <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",
          letterSpacing:".2em",fontWeight:600,marginBottom:20}}>
          Culinary Arts Academy · Food Cost Q1 certified + Q2 start
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:24}}>
          {hero.map((k,i)=>(
            <div key={i} style={{
              background:"rgba(255,255,255,0.06)",
              borderRadius:14,padding:"20px",
              border:"1px solid rgba(255,255,255,0.08)",
            }}>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",
                letterSpacing:".1em",fontWeight:600,marginBottom:10}}>{k.label}</div>
              <div style={{fontSize:36,fontWeight:800,color:k.c,lineHeight:1,
                letterSpacing:"-.02em",marginBottom:8}}>{k.val}</div>
              <div style={{fontSize:11.5,color:"rgba(255,255,255,0.45)",lineHeight:1.4}}>{k.delta}</div>
            </div>
          ))}
        </div>

        {/* 3 insights */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
          {[
            {icon:"⚠",label:"Structural gap",    v:"CHF 91k",  s:"Budget < theoretical need before first order",c:"rgba(239,68,68,0.2)",bc:"rgba(239,68,68,0.4)"},
            {icon:"↗",label:"Execution gap",      v:"CHF 99k",  s:"FD +63% · FI +28% vs reference CHF/couvert",     c:"rgba(249,115,22,0.15)",bc:"rgba(249,115,22,0.35)"},
            {icon:"↘",label:"Signal Q2 positif",  v:"−10%",     s:"CHF 4'690/j vs CHF 5'200/j Q1 — tendance bonne", c:"rgba(55,128,75,0.12)",bc:"rgba(55,128,75,0.25)"},
          ].map((b,i)=>(
            <div key={i} style={{background:b.c,borderRadius:10,padding:"14px 16px",
              border:"1px solid "+b.bc}}>
              <div style={{fontSize:18,marginBottom:4}}>{b.icon}</div>
              <div style={{fontSize:9.5,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",
                letterSpacing:".08em",marginBottom:4}}>{b.label}</div>
              <div style={{fontSize:22,fontWeight:800,color:"#fff",marginBottom:4}}>{b.v}</div>
              <div style={{fontSize:10.5,color:"rgba(255,255,255,0.5)",lineHeight:1.4}}>{b.s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 3 colonnes ───────────────────────────────── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1.1fr 0.9fr",gap:20}}>

        {/* Col 1 : restaurants */}
        <div>
          <Card>
            <Label>Performance by restaurant · Q1</Label>
            <div style={{fontSize:20,fontWeight:800,color:T.ink,letterSpacing:"-.01em",marginBottom:20}}>
              CHF 465k total
            </div>

            {restaurants.map((r,i)=>{
              const delta = ((r.chf_cov - r.ref)/r.ref*100).toFixed(1);
              const over  = r.chf_cov > r.ref;
              return (
                <div key={r.key} style={{marginBottom:i<restaurants.length-1?18:0,
                  paddingBottom:i<restaurants.length-1?18:0,
                  borderBottom:i<restaurants.length-1?`1px solid ${T.line}`:"none"}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"flex-start",marginBottom:8}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:T.ink,marginBottom:2}}>
                        {r.name}
                      </div>
                      <div style={{fontSize:10.5,color:T.hint}}>
                        {r.cov.toLocaleString()} covers/wk
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:16,fontWeight:800,color:T[r.key]}}>{fmtK(r.q1)}</div>
                      <div style={{fontSize:10.5,fontWeight:700,color:over?T.err:T.ok,marginTop:2}}>
                        CHF {r.chf_cov} {over?`▲+${delta}%`:`▼${delta}%`} vs ref {r.ref}
                      </div>
                    </div>
                  </div>
                  <Bar value={r.q1} max={276000} color={T[r.key]} height={5}/>
                </div>
              );
            })}

            <Divider/>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {[["LV",T.lv],["SK",T.sk],["FD",T.fd],["FI",T.fi]].map(([l,c])=>(
                <span key={l} style={{display:"flex",alignItems:"center",gap:5,
                  fontSize:10.5,color:T.hint}}>
                  <span style={{width:8,height:8,borderRadius:2,
                    background:c,display:"inline-block"}}/>
                  {l}
                </span>
              ))}
            </div>
          </Card>

          {/* CHF/coverert comparison */}
          <Card style={{marginTop:16}}>
            <Label>CHF / cover · Q1 actual vs reference</Label>
            {restaurants.map((r,i)=>{
              const over = r.chf_cov > r.ref;
              const maxV = 28;
              return (
                <div key={r.key} style={{marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    marginBottom:5,fontSize:12}}>
                    <span style={{fontWeight:500,color:T.sub}}>{r.name}</span>
                    <span style={{fontWeight:700,color:over?T.err:T.ok}}>
                      CHF {r.chf_cov}
                      <span style={{color:T.hint,fontWeight:400,marginLeft:6,fontSize:10}}>
                        ref {r.ref}
                      </span>
                    </span>
                  </div>
                  <div style={{position:"relative",height:8,background:T.grid,borderRadius:4}}>
                    <div style={{position:"absolute",left:0,top:0,height:"100%",
                      width:(r.ref/maxV*100)+"%",background:"#e2e8f0",borderRadius:4}}/>
                    <div style={{position:"absolute",left:0,top:0,height:"100%",
                      width:(r.chf_cov/maxV*100)+"%",
                      background:over?T.err:T[r.key],
                      borderRadius:4,opacity:0.85}}/>
                  </div>
                </div>
              );
            })}
          </Card>
        </div>

        {/* Col 2 : spend + trend */}
        <div>
          <Card>
            <Label>Tendance mensuelle Q1</Label>
            <div style={{fontSize:20,fontWeight:800,color:T.ink,marginBottom:4}}>
              Jan → Mar 2026
            </div>
            <div style={{fontSize:12,color:T.hint,marginBottom:20}}>
              Feb peak CHF 177k — Fine Dining alone CHF 18k
            </div>

            {MONTHLY.map((m,i)=>(
              <div key={i} style={{marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <span style={{fontSize:13,fontWeight:600,color:T.ink}}>{m.label}</span>
                  <span style={{fontSize:14,fontWeight:800,color:T.ink}}>{fmtK(m.total)}</span>
                </div>
                <div style={{display:"flex",height:28,borderRadius:8,overflow:"hidden",background:T.grid,gap:1}}>
                  {[
                    {v:m.lv,c:T.lv,n:"LV"},
                    {v:m.sk,c:T.sk,n:"SK"},
                    {v:m.fd,c:T.fd,n:"FD"},
                    {v:m.fi,c:T.fi,n:"FI"},
                  ].map((s,j)=>{
                    const w=(s.v/177700*100).toFixed(1)+"%";
                    return (
                      <div key={j} title={`${s.n}: ${fmtK(s.v)}`}
                        style={{width:w,background:s.c,
                          display:"flex",alignItems:"center",justifyContent:"center",
                          transition:"width .4s",minWidth:s.v>8000?"30px":0}}>
                        {s.v>12000&&(
                          <span style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.9)",
                            whiteSpace:"nowrap"}}>{fmtK(s.v)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{display:"flex",gap:10,marginTop:5,flexWrap:"wrap"}}>
                  {[{v:m.lv,c:T.lv,n:"Lake View"},{v:m.sk,c:T.sk,n:"Soul K."},{v:m.fd,c:T.fd,n:"Fine D."},{v:m.fi,c:T.fi,n:"Food I."}].map((s,j)=>(
                    <span key={j} style={{fontSize:10,color:T.hint,display:"flex",alignItems:"center",gap:4}}>
                      <span style={{width:6,height:6,borderRadius:2,background:s.c,display:"inline-block"}}/>
                      {s.n}: <b style={{color:s.c,marginLeft:2}}>{fmtK(s.v)}</b>
                    </span>
                  ))}
                </div>
              </div>
            ))}

            <Divider/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[
                {l:"Q1 Total", v:"CHF 465k", c:T.err},
                {l:"Budget",   v:"CHF 275k", c:T.hint},
                {l:"Overspend",v:"+CHF 190k",c:T.err},
                {l:"En %",    v:"+69.1%",    c:T.err},
              ].map((k,i)=>(
                <div key={i} style={{padding:"10px 12px",background:T.bg,
                  borderRadius:10,border:`1px solid ${T.line}`}}>
                  <div style={{fontSize:9.5,color:T.hint,textTransform:"uppercase",
                    letterSpacing:".07em",marginBottom:4}}>{k.l}</div>
                  <div style={{fontSize:18,fontWeight:800,color:k.c}}>{k.v}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Col 3 : decisions + top suppliers */}
        <div>
          <Card>
            <Label>4 board decisions required</Label>
            <div style={{fontSize:20,fontWeight:800,color:T.ink,marginBottom:20}}>
              Actions Q2 prioritaires
            </div>

            {decisions.map((d,i)=>{
              const sc = statusColors[d.status];
              return (
                <div key={i} style={{
                  display:"flex",gap:12,
                  padding:"14px",borderRadius:12,
                  marginBottom:10,
                  background:`${sc}08`,
                  border:`1px solid ${sc}20`,
                }}>
                  <div style={{
                    width:28,height:28,borderRadius:8,flexShrink:0,
                    background:sc,color:"#fff",
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:13,fontWeight:800,
                  }}>{d.n}</div>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                      <span style={{fontSize:12,fontWeight:700,color:T.ink}}>{d.label}</span>
                    </div>
                    <div style={{fontSize:10.5,color:T.hint,lineHeight:1.5}}>{d.note}</div>
                    <div style={{marginTop:6}}>
                      <Chip label={statusLabels[d.status]} color={sc}/>
                    </div>
                  </div>
                </div>
              );
            })}
          </Card>

          <Card style={{marginTop:16}}>
            <Label>Top suppliers · Q2 start</Label>
            {[
              {n:"Saviva Food",    v:25054,c:T.po},
              {n:"Léguriviera",    v:13451,c:T.lv},
              {n:"A. Walker AG",   v:12687,c:T.fd},
              {n:"Chaudron",       v:8850, c:T.warn},
              {n:"Pistor AG",      v:6476, c:T.sk},
            ].map((f,i)=>(
              <div key={i} style={{marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  marginBottom:4,fontSize:12}}>
                  <span style={{fontWeight:500,color:T.sub}}>{f.n}</span>
                  <span style={{fontWeight:700,color:T.ink}}>
                    CHF {(f.v/1000).toFixed(0)}k
                  </span>
                </div>
                <Bar value={f.v} max={25054} color={f.c} height={5}/>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WEEKLY REPORTS TAB — Live data from /dashboard/weekly_reports
// ─────────────────────────────────────────────────────────────────────────────
function WeeklyReportsTab({quarter=2}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(quarter>=3?"q"+quarter:"q2");
  const [ragFilter, setRagFilter] = useState("all");
  const [expanded, setExpanded] = useState(new Set());
  const [lang, setLang] = useState("fr");
  const fr = lang==="fr"; const L=(f,e)=>fr?f:e;

  const BDG_TOL = 1.10; // 10% tolerance over reference budget

  useEffect(() => {
    setLoading(true);
    fetch(API + "/dashboard/weekly_reports?quarter=" + quarter)
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setData(d);
        setLoading(false);
      })
      .catch(e => { setError(String(e.message || e)); setLoading(false); });
  }, [quarter]);

  if (loading) return <div style={{padding:40,textAlign:"center",color:T.hint,fontSize:13}}>Loading weekly data from Odoo…</div>;
  if (error)   return <div style={{padding:40,color:T.err,fontSize:13}}>API error: {error}</div>;
  if (!data)   return null;

  const REF = data.refs;
  const FULL_COV = data.full_covers;

  // ─── Enrich each week with computed RAG and totals ───────────────────────
  const today = data.today || new Date().toISOString().slice(0,10);
  const weeks = data.weeks.map(w => {
    const closed = new Set(w.closed || []);
    const ragOf = (k) => {
      if (closed.has(k)) return "x";
      const cov = w.covers[k] || 0;
      if (!cov) return "x";
      const ref = REF[k];
      const total = w.streams[k].total;
      const chfCov = total / cov;
      const refTotal = cov * ref;
      const budgetVar = refTotal ? (total - refTotal*BDG_TOL) / (refTotal*BDG_TOL) : 0;
      const covVar = (chfCov - ref) / ref;
      const worse = Math.max(budgetVar, covVar);
      if (worse <= 0.05) return "g";
      if (worse <= 0.15) return "a";
      return "r";
    };
    const streamRag = {};
    ["lv","fd","fi","sk"].forEach(k => streamRag[k] = ragOf(k));
    const premT = w.streams.fd.total + w.streams.fi.total + w.streams.sk.total;
    const restoSpend = premT + w.streams.lv.total;             // 4 restaurants
    const total = restoSpend;                                  // total = restaurants only (ok eliminated)
    const totalCov = (w.covers.lv||0) + (w.covers.fd||0) + (w.covers.fi||0) + (w.covers.sk||0);
    const refSpend = (w.covers.lv||0)*REF.lv + (w.covers.fd||0)*REF.fd + (w.covers.fi||0)*REF.fi + (w.covers.sk||0)*REF.sk;
    const budget = refSpend * BDG_TOL;

    // Days elapsed in this week (past=full, current=partial, future=0)
    let daysElapsed = 0;
    if (today > w.end)        daysElapsed = w.days_in_week;
    else if (today >= w.start) {
      const t = new Date(today);
      const s = new Date(w.start);
      daysElapsed = Math.min(w.days_in_week, Math.floor((t - s) / 86400000) + 1);
    }
    // Budget pro-rated to elapsed days for fair vs-spend comparison
    const budgetToDate = budget * (daysElapsed / w.days_in_week);

    const varPct = budget ? (restoSpend - budget) / budget : 0;
    // CHF/cov NOT computed at week level (only per-restaurant in drill-down)
    const rags = ["lv","fd","fi","sk"].map(k => streamRag[k]).filter(r => r !== "x");
    const worstRag = rags.includes("r") ? "r" : rags.includes("a") ? "a" : rags.length ? "g" : "x";
    const weightedRag = !budget ? "x" : varPct <= 0.05 ? "g" : varPct <= 0.15 ? "a" : "r";
    const allPremClosed = closed.has("fd") && closed.has("fi") && closed.has("sk");
    return { ...w, streamRag, premT, total, totalCov, budget, budgetToDate, daysElapsed, varPct, worstRag, weightedRag, allPremClosed };
  });

  const curQ = quarter >= 3 ? "q" + quarter : "q2";
  const QL = curQ.toUpperCase();
  const QSTART = {3:"6 juillet", 4:"1er octobre"}[quarter] || "1er avril";
  const filtered = (tab === "all" ? weeks : weeks.filter(w => w.q === tab))
    .filter(w => ragFilter === "all" || w.weightedRag === ragFilter);
  // Hide future weeks (no spend yet, just noise)
  const visibleWeeks = filtered.filter(w => w.start <= today);
  const hiddenFutureCount = filtered.length - visibleWeeks.length;

  const totalSpend        = filtered.reduce((s,w) => s + w.total, 0);
  const totalBudget       = filtered.reduce((s,w) => s + w.budget, 0);          // budget Q2 complet
  const totalBudgetToDate = filtered.reduce((s,w) => s + (w.budgetToDate||0), 0); // budget jusqu'à aujourd'hui
  const variance          = totalBudgetToDate ? (totalSpend - totalBudgetToDate) / totalBudgetToDate : 0;
  const counts = visibleWeeks.reduce((c,w) => { c[w.weightedRag] = (c[w.weightedRag]||0)+1; return c; }, {g:0,a:0,r:0,x:0});
  const currentWeek = weeks.find(w => w.current) || filtered[filtered.length-1];

  const fmtPct = n => (n>=0?"+":"") + (n*100).toFixed(0) + "%";
  const ragHex = r => r==="g"?T.ok:r==="a"?T.warn:r==="r"?T.err:T.hint;
  const ragLabel = r => r==="g"?"On track":r==="a"?"At risk":r==="r"?"Over":"Closed";

  const toggle = (id) => setExpanded(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  // ─── Sparkline data : Q2 weeks only (since Apr 1) ────────────────────────
  const q2Weeks = weeks.filter(w => w.q === curQ);
  const sparkData = (streamKey) => q2Weeks.map(w => ({
    label: w.w,                       // W0, W1, W2, W3, W4 (Q2 implicit)
    fullLabel: `${w.q.toUpperCase()} ${w.w} · ${w.label}`,
    value: streamKey === "total" ? w.total
         : streamKey === "dry"   ? (w.streams.lv.dry + w.streams.fd.dry + w.streams.fi.dry + w.streams.sk.dry)
         : w.streams[streamKey]?.total || 0,
    current: w.current,
    partial: w.partial,
    q: w.q,
  }));

  // Cumulative totals since Apr 1 (Q2)
  const cumQ2 = (streamKey) => sparkData(streamKey).reduce((s,d) => s + d.value, 0);
  // Per-stream weekly reference budget (full-open week)
  const FULL_COV_API = data.full_covers || {lv:2585, sk:480, fd:50, fi:160};
  const weeklyRef = {
    total: FULL_COV_API.lv*REF.lv + FULL_COV_API.sk*REF.sk + FULL_COV_API.fd*REF.fd + FULL_COV_API.fi*REF.fi,
    lv:    FULL_COV_API.lv*REF.lv,
    fd:    FULL_COV_API.fd*REF.fd,
    fi:    FULL_COV_API.fi*REF.fi,
    sk:    FULL_COV_API.sk*REF.sk,
    dry:   null,  // pas de référence DRY explicite
  };

  return (
    <div>
      {/* FR/EN toggle */}
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10}}>
        <div style={{display:"inline-flex",border:`1px solid ${T.line}`,borderRadius:8,overflow:"hidden"}} className="no-print">
          {["fr","en"].map(lg=>(<button key={lg} onClick={()=>setLang(lg)} style={{border:"none",cursor:"pointer",padding:"5px 11px",fontSize:11.5,fontWeight:700,background:lang===lg?T.ink:T.card,color:lang===lg?"#fff":T.sub}}>{lg.toUpperCase()}</button>))}
        </div>
      </div>
      {/* ── Top sparklines : evolution per stream ───────────────────────────── */}
      {/* Section title for cards */}
      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",margin:"0 4px 12px"}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:T.ink,letterSpacing:"-.01em"}}>
            {L(`${QL} consommation — jusqu'à`,`${QL} consumption — through`)} {currentWeek ? currentWeek.w : "—"}
          </div>
          <div style={{fontSize:11,color:T.hint,marginTop:2}}>
            {L("Cumul depuis le","Cumulative since")} {QSTART} · {currentWeek?.label || ""}{currentWeek?.partial ? L(" · en cours"," · in progress") : ""}
          </div>
        </div>
        <div style={{fontSize:10.5,color:T.hint,fontStyle:"italic"}}>
          {L("ligne pointillée = budget hebdo réf · barre surlignée = semaine en cours","dotted line = weekly reference budget · highlighted bar = current week")}
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8,marginBottom:18}}>
        <Sparkline title="Total food"   data={sparkData("total")} cumulative={cumQ2("total")} weeklyRef={weeklyRef.total}
          color={T.graphite} accent={T.brand} icon={<IconChef/>}      bgTint="#f9f7f3" ringColor="#e6dfd2"/>
        <Sparkline title="DRY · stock"  data={sparkData("dry")}   cumulative={cumQ2("dry")}   weeklyRef={weeklyRef.dry}
          color={T.warn} accent={T.warn} icon={<IconBox/>}            bgTint="#fbf3e3" ringColor="#f0e3c4"/>
        <Sparkline title="Lake View"    data={sparkData("lv")}    cumulative={cumQ2("lv")}    weeklyRef={weeklyRef.lv}
          color={T.lv} accent={T.lv}     icon={<IconWave/>}           bgTint="#eaf3f8" ringColor="#cee2ec"/>
        <Sparkline title="Fine Dining"  data={sparkData("fd")}    cumulative={cumQ2("fd")}    weeklyRef={weeklyRef.fd}
          color={T.fd} accent={T.fd}     icon={<IconUtensils/>}       bgTint="#fcefee" ringColor="#f1d6d2"/>
        <Sparkline title="Food Inspired" data={sparkData("fi")}   cumulative={cumQ2("fi")}    weeklyRef={weeklyRef.fi}
          color={T.fi} accent={T.fi}     icon={<IconLeaf/>}           bgTint="#f4f1fa" ringColor="#dcd5f0"/>
        <Sparkline title="Soul Kitchen" data={sparkData("sk")}    cumulative={cumQ2("sk")}    weeklyRef={weeklyRef.sk}
          color={T.sk} accent={T.sk}     icon={<IconBowl/>}           bgTint="#fbefe7" ringColor="#f0d8c5"/>
      </div>

      {/* ══ COCKPIT COO : ① Alertes · ② Heatmap CHF/cov · ③ Burn Q2 ══════════ */}
      {(() => {
        const NAME = {lv:"Lake View",fd:"Fine Dining",fi:"Food Inspired",sk:"Soul Kitchen"};
        const KEYS = ["lv","fd","fi","sk"];
        const q2all  = weeks.filter(w => w.q === curQ);
        const q2done = q2all.filter(w => w.start <= today);

        // ── ① Alertes auto ──
        const alerts = [];
        KEYS.forEach(k => {
          const serie = q2done.filter(w => !(w.closed||[]).includes(k) && (w.covers[k]||0) > 0)
            .map(w => ({w:w.w, cc:w.streams[k].total/w.covers[k], partial:w.partial}));
          if (!serie.length) return;
          let streak = 0;
          for (let i = serie.length-1; i >= 0; i--) { if (serie[i].cc > REF[k]) streak++; else break; }
          const last = serie[serie.length-1];
          if (streak >= 2)
            alerts.push({sev: streak >= 4 ? "r" : "a",
              txt: fr ? <><b>{NAME[k]}</b> au-dessus de la réf {REF[k]} depuis <b>{streak} semaines</b> — actuel {last.cc.toFixed(2)}/couv{last.partial?" (sem. en cours)":""}</> : <><b>{NAME[k]}</b> above ref {REF[k]} for <b>{streak} weeks</b> — current {last.cc.toFixed(2)}/cover{last.partial?" (current wk)":""}</>});
          else if (streak === 0 && serie.length >= 2 && serie[serie.length-2].cc > REF[k])
            alerts.push({sev:"g",
              txt: fr ? <><b>{NAME[k]}</b> repassé ✓ sous la réf {REF[k]} cette semaine ({last.cc.toFixed(2)}/couv)</> : <><b>{NAME[k]}</b> back ✓ under ref {REF[k]} this week ({last.cc.toFixed(2)}/cover)</>});
        });
        const compl = q2done.filter(w => !w.partial && w.total > 0);
        if (compl.length >= 4) {
          const sorted = compl.map(w=>w.total).sort((a,b)=>a-b);
          const med = sorted[Math.floor(sorted.length/2)];
          const lastC = compl[compl.length-1];
          if (lastC.total > med*1.2)
            alerts.push({sev:"a", txt: <><b>{lastC.w}</b> en pic : {fmtK(lastC.total)} vs médiane {fmtK(med)} (+{(((lastC.total/med)-1)*100).toFixed(0)}%)</>});
        }
        if (q2done.some(w => w.streams.lv.transfer))
          alerts.push({sev:"i", txt: fr ? <>⇄ Transfert <b>FD → LV</b> actif (40% PO Protéines+Légumes · W3-W10) — déjà reflété dans les chiffres par resto</> : <>⇄ <b>FD → LV</b> transfer active (40% FD PO Proteins+Veg · W3-W10) — already reflected per restaurant</>});
        const spentQ2  = q2done.reduce((s,w)=>s+w.total,0);
        const dueQ2    = q2done.reduce((s,w)=>s+(w.budgetToDate||0),0);
        if (dueQ2 > 0) {
          const p = spentQ2/dueQ2;
          alerts.push({sev: p>1.05?"r":p>0.95?"a":"g",
            txt: fr ? <>Rythme {QL} : <b>{fmtK(spentQ2)}</b> dépensés vs {fmtK(dueQ2)} de budget à date (<b>{(p*100).toFixed(0)}%</b>)</> : <>{QL} pace: <b>{fmtK(spentQ2)}</b> spent vs {fmtK(dueQ2)} budget to date (<b>{(p*100).toFixed(0)}%</b>)</>});
        }
        const sevC = {r:T.err,a:T.warn,g:T.ok,i:T.brand};
        const sevBg = {r:"#fdf2f0",a:"#fffbeb",g:"#f3f8f3",i:"#f6f2ec"};
        const order = {r:0,a:1,i:2,g:3};
        alerts.sort((x,y)=>order[x.sev]-order[y.sev]);

        // ── ② Heatmap CHF/cov ──
        const hmWeeks = q2done;

        // ── ③ Burn chart ──
        const W=760,H=158,PL=8,PR=118,PT=14,PB=22;
        const n = q2all.length;
        const xi = i => PL + (i/(Math.max(n-1,1)))*(W-PL-PR);
        let acc=0; const cumB = q2all.map(w => (acc += (w.budget||0)));
        const totB = acc;
        let accS=0; const cumS = q2done.map(w => (accS += w.total));
        const lastCi = compl.length ? q2all.findIndex(w=>w.w===compl[compl.length-1].w) : Math.max(q2done.length-1,0);
        const anchor = compl.length ? compl.reduce((s,w)=>s+w.total,0) : spentQ2;
        const avgRate = compl.length ? compl.slice(-4).reduce((s,w)=>s+w.total,0)/Math.min(4,compl.length) : 0;
        const projEnd = anchor + avgRate * Math.max(0,(n-1)-lastCi);
        const maxY = Math.max(totB, projEnd, spentQ2) * 1.06 || 1;
        const yi = v => PT + (1 - v/maxY)*(H-PT-PB);
        const pB = cumB.map((v,i)=>`${xi(i)},${yi(v)}`).join(" ");
        const pS = cumS.map((v,i)=>`${xi(i)},${yi(v)}`).join(" ");
        const projOver = projEnd > totB;

        return (
        <div style={{display:"grid",gridTemplateColumns:"5fr 7fr",gap:14,marginBottom:18,alignItems:"stretch"}}>
          {/* ① Alertes */}
          <Card style={{padding:"16px 18px"}}>
            <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:700,color:T.ink}}>{L("À surveiller","To watch")} · {currentWeek?`${currentWeek.w}`:""}</div>
              <div style={{fontSize:10,color:T.hint}}>{L("généré automatiquement","auto-generated")}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {(alerts.length?alerts:[{sev:"g",txt:L("Rien à signaler — tous les restos dans leurs références","Nothing to flag — all restaurants within their references")}]).map((a,i)=>(
                <div key={i} style={{display:"flex",gap:9,alignItems:"flex-start",padding:"8px 10px",
                  borderRadius:8,background:sevBg[a.sev],border:`1px solid ${sevC[a.sev]}22`,fontSize:11.5,color:T.sub,lineHeight:1.45}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:sevC[a.sev],flexShrink:0,marginTop:4}}/>
                  <span>{a.txt}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* ② + ③ empilés */}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* ② Heatmap */}
            <Card style={{padding:"14px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:700,color:T.ink}}>{L("CHF/couvert vs référence","CHF/cover vs reference")} · heatmap {QL}</div>
                <div style={{fontSize:10,color:T.hint}}>{L("vert ≤ réf · ambre ≤ +15% · rouge","green ≤ ref · amber ≤ +15% · red")} &gt; +15%</div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:`88px repeat(${hmWeeks.length},1fr)`,gap:3,fontSize:10.5}}>
                <div/>
                {hmWeeks.map(w=>(
                  <div key={w.w} style={{textAlign:"center",fontWeight:w.current?800:600,
                    color:w.current?T.brand:T.hint,fontSize:9.5}}>{w.w}{w.current?" •":""}</div>
                ))}
                {KEYS.map(k=>(
                  <React.Fragment key={k}>
                    <div style={{fontWeight:700,color:T.ink,fontSize:10.5,display:"flex",alignItems:"center"}}>
                      {NAME[k]}<span style={{color:T.hint,fontWeight:500,marginLeft:4}}>·{REF[k]}</span>
                    </div>
                    {hmWeeks.map(w=>{
                      const closedK=(w.closed||[]).includes(k)||!(w.covers[k]||0);
                      const cc = closedK?null:w.streams[k].total/w.covers[k];
                      const rag = w.streamRag[k];
                      const bg = closedK?"#f1f0ed":rag==="g"?"rgba(34,197,94,.16)":rag==="a"?"rgba(245,158,11,.18)":"rgba(220,38,38,.14)";
                      const fg = closedK?T.hint:ragHex(rag);
                      return (
                        <div key={w.w} style={{textAlign:"center",padding:"5px 0",borderRadius:5,background:bg,
                          color:fg,fontWeight:700,fontVariantNumeric:"tabular-nums",
                          outline:w.current?`1.5px solid ${T.brand}55`:"none"}}>
                          {closedK?"—":cc>=100?cc.toFixed(0):cc.toFixed(1)}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </Card>

            {/* ③ Burn */}
            <Card style={{padding:"14px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
                <div style={{fontSize:13,fontWeight:700,color:T.ink}}>Burn {QL} · {L("cumul vs budget","cumulative vs budget")}</div>
                <div style={{fontSize:10,color:T.hint}}>{L("budget hebdo dynamique (fermetures & semaines partielles incluses)","dynamic weekly budget (closures & partial weeks included)")}</div>
              </div>
              <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block"}}>
                <polyline points={pB} fill="none" stroke={T.hint} strokeWidth="1.6" strokeDasharray="5 4"/>
                <polyline points={pS} fill="none" stroke={T.brand} strokeWidth="2.4"/>
                {compl.length>0 && (
                  <line x1={xi(lastCi)} y1={yi(anchor)} x2={xi(n-1)} y2={yi(projEnd)}
                    stroke={projOver?T.err:T.ok} strokeWidth="1.8" strokeDasharray="2.5 3.5"/>
                )}
                {q2all.map((w,i)=>(
                  <text key={w.w} x={xi(i)} y={H-7} fontSize="8.5" fill={w.current?T.brand:T.hint}
                    textAnchor="middle" fontWeight={w.current?800:500}>{w.w}</text>
                ))}
                <circle cx={xi(cumS.length-1)} cy={yi(spentQ2)} r="3.4" fill={T.brand}/>
                <text x={xi(n-1)+6} y={yi(totB)+3} fontSize="9.5" fill={T.hint} fontWeight="700">Budget {fmtK(totB)}</text>
                <text x={xi(n-1)+6} y={yi(projEnd)+(Math.abs(yi(projEnd)-yi(totB))<11?(yi(projEnd)<=yi(totB)?-8:13):3)}
                  fontSize="9.5" fill={projOver?T.err:T.ok} fontWeight="800">Proj {fmtK(projEnd)}</text>
                <text x={xi(cumS.length-1)-6} y={yi(spentQ2)-7} fontSize="9.5" fill={T.brand} fontWeight="800" textAnchor="end">{fmtK(spentQ2)}</text>
              </svg>
            </Card>
          </div>
        </div>
        );
      })()}

      {/* ── Weekly budget breakdown : barres par semaine vs ref hebdo ─────── */}
      {(() => {
        const wkList = (tab === "all" ? weeks : weeks.filter(w => w.q === tab)).filter(w => w.start <= today);
        const maxVal = Math.max(...wkList.map(w => Math.max(w.total||0, w.budget||0)), 1);
        return (
          <div style={{
            background:T.card, border:`1px solid ${T.line}`, borderRadius:12,
            padding:"16px 18px", marginBottom:18, boxShadow:T.sh1,
          }}>
            <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:14}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:T.ink,letterSpacing:"-.01em"}}>
                  Weekly budget · spend vs reference
                </div>
                <div style={{fontSize:10.5,color:T.hint,marginTop:2}}>
                  {L('Budget hebdo dynamique = Σ couverts × réf par resto (fermetures/semaines partielles incluses) · % semaine en cours = vs prorata jours écoulés','Dynamic weekly budget = Σ covers × ref per restaurant (closures/partial weeks included) · current-week % = vs elapsed-days proration')}
                </div>
              </div>
              <div style={{fontSize:10,color:T.hint,fontStyle:"italic"}}>
                ▮ spend · ┊ weekly budget · current week shaded
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {wkList.map(w => {
                const spendPct  = (w.total / maxVal) * 100;
                const budgetPct = (w.budget / maxVal) * 100;
                const overBudget = w.budget > 0 && w.total > w.budget;
                const overPct = overBudget ? Math.min(100, ((w.total - w.budget) / maxVal) * 100) : 0;
                const underPct = !overBudget ? spendPct : (w.budget / maxVal) * 100;
                const ratioBase = w.current && w.budgetToDate > 0 ? w.budgetToDate : w.budget;
                const ratio = ratioBase > 0 ? (w.total / ratioBase) : 0;
                const barColor = ratio === 0 ? T.hint : ratio < 1 ? T.okBright : ratio < 1.15 ? T.warn : T.err;
                const isCurrent = w.current;
                const isFuture  = w.start > today;
                return (
                  <div key={w.q+w.w} style={{
                    display:"flex", alignItems:"center", gap:10, fontSize:11,
                    padding:"4px 6px", borderRadius:6,
                    background: isCurrent ? "rgba(175,100,42,0.06)" : "transparent",
                  }}>
                    {/* Week label */}
                    <div style={{flex:"0 0 88px",fontWeight:600,color:T.ink,fontSize:11}}>
                      <span style={{color:isFuture?T.hint:T.ink}}>{w.q.toUpperCase()} {w.w}</span>
                      {isCurrent && <span style={{marginLeft:5,fontSize:9,color:T.brand,fontWeight:700,letterSpacing:".05em"}}>NOW</span>}
                    </div>
                    {/* Bar container */}
                    <div style={{flex:"1 1 auto",position:"relative",height:18,background:"#eef0f3",borderRadius:4,overflow:"hidden",minWidth:0}}>
                      {/* Spend bar */}
                      <div style={{
                        position:"absolute",top:0,left:0,height:"100%",
                        width:`${Math.min(spendPct,100)}%`,
                        background:`linear-gradient(90deg, ${barColor}b8, ${barColor})`,
                        boxShadow:`inset 0 1px 0 rgba(255,255,255,.35), 0 0 8px ${barColor}45`,
                        borderRadius:"4px 3px 3px 4px",
                        opacity: isFuture ? 0.2 : 1,
                        transition:"width .3s",
                      }}/>
                      {/* Budget reference line (vertical dashed) */}
                      {w.budget > 0 && (
                        <div style={{
                          position:"absolute",top:-2,bottom:-2,
                          left:`${budgetPct}%`,
                          width:0,
                          borderLeft:`2px dashed ${T.ink}`,
                          opacity:0.55,
                        }} title={`Budget ${fmtK(w.budget)}`}/>
                      )}
                    </div>
                    {/* Numeric labels */}
                    <div style={{flex:"0 0 180px",textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:11}}>
                      <span style={{fontWeight:700,color:isFuture?T.hint:T.ink}}>{fmtK(w.total).replace("CHF ","")}</span>
                      <span style={{color:T.hint}}> / {fmtK(w.budget).replace("CHF ","")}</span>
                      {w.budget > 0 && !isFuture && (
                        <span style={{marginLeft:8, fontWeight:800, fontSize:10.5,
                          color: ratio < 1 ? T.okBright : ratio < 1.15 ? T.warn : T.err,
                          textShadow: ratio < 1 ? "0 0 8px rgba(34,197,94,0.35)" : "none"}}
                          title={w.current?L("vs budget proratisé aux jours écoulés","vs budget prorated to elapsed days"):L("vs budget semaine","vs weekly budget")}>
                          {ratio >= 1 ? "+" : ""}{((ratio-1)*100).toFixed(0)}%{w.current?"ᵖ":""}
                        </span>
                      )}
                    </div>
                    {/* Closed/partial badge */}
                    <div style={{flex:"0 0 100px",fontSize:9,color:T.hint,letterSpacing:".05em",textTransform:"uppercase",fontWeight:600}}>
                      {w.closed?.length === 3 ? "Premium closed" :
                       w.days_in_week < 7 ? `${w.days_in_week}-day week` :
                       isCurrent ? `day ${w.daysElapsed}/${w.days_in_week}` :
                       isFuture ? "future" : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {quarter >= 4 && (data.fallback_weeks||[]).length > 0 && (
        <div style={{marginBottom:12,padding:"9px 14px",borderRadius:9,background:"#fff7e8",border:"1px solid #f3cf8a",fontSize:12,color:"#8a5a00"}}>
          <b>{L("Référence Q2 appliquée","Q2 reference applied")}</b> · {(data.fallback_weeks||[]).join(", ")} — {L(
            "semaines pas encore saisies dans « Restaurants Planning » : budget hebdo = semaine-type Q2 (LV 2 585 · SK 480 · FI 176 · FD 60 couverts). Une semaine saisie dans le planning remplace la référence.",
            "weeks not yet entered in “Restaurants Planning”: weekly budget = Q2 standard week (LV 2,585 · SK 480 · FI 176 · FD 60 covers). An entered week replaces the reference.")}
        </div>
      )}

      {/* ── Filter bar : Q1/Q2/All + RAG ────────────────────────────────────── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:12}}>
        <div style={{display:"flex",gap:6,padding:4,background:T.bg2,border:`1px solid ${T.line}`,borderRadius:10}}>
          {(quarter>=3
            ? [[curQ,`${QL} · ${weeks.filter(w=>w.q===curQ).length} weeks`], ["all","All"]]
            : [["q1",`Q1 · ${weeks.filter(w=>w.q==="q1").length} weeks`],
               ["q2",`Q2 · ${weeks.filter(w=>w.q==="q2").length} weeks`],
               ["all","All"]]).map(([k,l]) => (
            <button key={k} onClick={()=>setTab(k)}
              style={{padding:"7px 16px",fontSize:12,fontWeight:tab===k?700:500,
                background:tab===k?T.card:"transparent", color:tab===k?T.ink:T.sub,
                border:tab===k?`1px solid ${T.line}`:"1px solid transparent",
                borderRadius:7,cursor:"pointer",
                boxShadow:tab===k?T.sh1:"none"}}>{l}</button>
          ))}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,fontSize:11,color:T.hint}}>
          <span style={{textTransform:"uppercase",letterSpacing:".08em",fontWeight:600}}>RAG filter</span>
          {[["all","All",T.ink],["g","● Green",T.ok],["a","● Amber",T.warn],["r","● Red",T.err]].map(([k,l,c]) => (
            <button key={k} onClick={()=>setRagFilter(k)}
              style={{padding:"4px 11px",fontSize:11,fontWeight:600,
                background:ragFilter===k?T.card:"transparent",
                color:ragFilter===k?c:T.sub,
                border:`1px solid ${ragFilter===k?T.line:"transparent"}`,
                borderRadius:14,cursor:"pointer"}}>{l}</button>
          ))}
        </div>
      </div>

      {/* ── KPI cards ──────────────────────────────────────────────────────── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:18}}>
        <Card style={{padding:"16px 20px"}}>
          <Label>Total spend</Label>
          <div style={{fontSize:24,fontWeight:800,color:T.ink,letterSpacing:"-.02em"}}>{fmtK(totalSpend)}</div>
          <div style={{fontSize:11,color:T.hint,marginTop:3}}>{filtered.length} weeks · live Odoo</div>
        </Card>
        <Card style={{padding:"16px 20px"}}>
          <Label>vs Budget</Label>
          <div style={{fontSize:24,fontWeight:800,letterSpacing:"-.02em",
            color: variance > 0.05 ? T.err : variance < -0.02 ? T.ok : T.ink}}>{fmtPct(variance)}</div>
          <div style={{fontSize:11,color:T.hint,marginTop:3}}>spent vs {fmtK(totalBudgetToDate)} due to-date · full {fmtK(totalBudget)} ref over {filtered.length} weeks</div>
        </Card>
        <Card style={{padding:"16px 20px"}}>
          <Label>Current week</Label>
          <div style={{fontSize:24,fontWeight:800,color:T.ink,letterSpacing:"-.02em"}}>
            {currentWeek ? `${currentWeek.q.toUpperCase()} ${currentWeek.w}` : "—"}
          </div>
          <div style={{fontSize:11,color:T.hint,marginTop:3}}>
            {currentWeek ? currentWeek.label : ""}{currentWeek?.partial?" · in progress":""}
          </div>
        </Card>
        <Card style={{padding:"16px 20px"}}>
          <Label>Status mix</Label>
          <div style={{display:"flex",gap:14,alignItems:"baseline"}}>
            <span style={{fontSize:24,fontWeight:800,color:T.ok,letterSpacing:"-.02em"}}>{counts.g}</span>
            <span style={{fontSize:24,fontWeight:800,color:T.warn,letterSpacing:"-.02em"}}>{counts.a}</span>
            <span style={{fontSize:24,fontWeight:800,color:T.err,letterSpacing:"-.02em"}}>{counts.r}</span>
          </div>
          <div style={{fontSize:11,color:T.hint,marginTop:3}}>Green / Amber / Red weeks</div>
        </Card>
      </div>

      {/* ── Weekly table ───────────────────────────────────────────────────── */}
      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",padding:"12px 14px",
          background:"#eef1f5",borderRadius:"6px 6px 0 0",borderBottom:`1px solid #d8dde3`,
          fontSize:9.5,color:T.hint,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700,padding:"8px 8px"}}>
          <div style={{flex:"0 0 110px"}}>Week</div>
          <div style={{flex:"0 0 60px",textAlign:"center"}}>RAG</div>
          <div style={{flex:"1 1 auto",minWidth:0}}>Streams · LV / FD / FI / SK</div>
          <div style={{flex:"0 0 110px",textAlign:"right"}}>Total</div>
          <div style={{flex:"0 0 150px",textAlign:"right"}}>Spend / Budget</div>
          <div style={{flex:"0 0 22px"}}></div>
        </div>

        {visibleWeeks.map((w) => {
          const id = w.q + w.w;
          const isOpen = expanded.has(id);
          // Breakdown across all 4 streams (LV+FD+FI+SK)
          const totVal = w.total || 1;
          const lvPct = w.streams.lv.total / totVal * 100;
          const fdPct = w.streams.fd.total / totVal * 100;
          const fiPct = w.streams.fi.total / totVal * 100;
          const skPct = w.streams.sk.total / totVal * 100;
          const closed = new Set(w.closed || []);

          return (
            <div key={id}>
              <div onClick={()=>toggle(id)} style={{
                display:"flex",alignItems:"center",padding:"11px 14px",
                borderBottom:`1px solid ${T.line}`,cursor:"pointer",
                background: isOpen ? "#fafbfc" : (w.current ? "rgba(175,100,42,0.06)" : T.card),
                transition:"background .15s",
              }}>
                {/* Week */}
                <div style={{flex:"0 0 110px"}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.ink,display:"flex",alignItems:"center",gap:6}}>
                    {w.w}
                    {w.current && <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:T.brand}}/>}
                  </div>
                  <div style={{fontSize:10,color:T.hint,marginTop:2}}>{w.label}</div>
                  {w.allPremClosed && <div style={{fontSize:9,color:T.warn,marginTop:2,letterSpacing:".05em",textTransform:"uppercase",fontWeight:700}}>Premium closed</div>}
                  {w.current && <div style={{fontSize:9,color:T.brand,marginTop:2,letterSpacing:".05em",textTransform:"uppercase",fontWeight:700}}>In progress</div>}
                </div>

                {/* Dual RAG — same size, centered, with subtle ring on worst-case */}
                <div style={{flex:"0 0 60px",display:"flex",gap:8,alignItems:"center",justifyContent:"center"}}>
                  <div title={fr?`Budget semaine: ${ragLabel(w.weightedRag)} — dépense vs budget hebdo`:`Weekly budget: ${ragLabel(w.weightedRag)} — spend vs weekly budget`}
                    style={{width:12,height:12,borderRadius:"50%",
                      background:`radial-gradient(circle at 35% 30%, #ffffffaa, ${ragHex(w.weightedRag)} 60%)`,
                      boxShadow:`0 0 0 2.5px ${ragHex(w.weightedRag)}30, 0 0 9px ${ragHex(w.weightedRag)}70`}}/>
                  <div title={`Restaurants (worst-case): ${ragLabel(w.worstRag)} — voir heatmap`}
                    style={{width:9,height:9,borderRadius:"50%",
                      background:`radial-gradient(circle at 35% 30%, #ffffff88, ${ragHex(w.worstRag)} 60%)`,
                      boxShadow:`0 0 6px ${ragHex(w.worstRag)}50`,opacity:0.75}}/>
                </div>

                {/* 4-stream breakdown : LV + FD + FI + SK */}
                <div style={{flex:"1 1 auto",minWidth:0,paddingRight:14}}>
                  <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap",fontSize:10.5}}>
                    <span style={{color:T.lv,fontWeight:700}}>LV {fmtK(w.streams.lv.total).replace("CHF ","")}</span>
                    <span style={{color:T.fd,fontWeight:700}}>FD {closed.has("fd")?"—":fmtK(w.streams.fd.total).replace("CHF ","")}</span>
                    <span style={{color:T.fi,fontWeight:700}}>FI {closed.has("fi")?"—":fmtK(w.streams.fi.total).replace("CHF ","")}</span>
                    <span style={{color:T.sk,fontWeight:700}}>SK {closed.has("sk")?"—":fmtK(w.streams.sk.total).replace("CHF ","")}</span>
                  </div>
                  <div style={{display:"flex",height:5,borderRadius:3,overflow:"hidden",marginTop:6,background:"#eef0f3"}}>
                    <div style={{width:`${lvPct}%`,background:T.lv}} title={`LV ${lvPct.toFixed(0)}%`}/>
                    <div style={{width:`${fdPct}%`,background:T.fd}} title={`FD ${fdPct.toFixed(0)}%`}/>
                    <div style={{width:`${fiPct}%`,background:T.fi}} title={`FI ${fiPct.toFixed(0)}%`}/>
                    <div style={{width:`${skPct}%`,background:T.sk}} title={`SK ${skPct.toFixed(0)}%`}/>
                  </div>
                </div>
                <div style={{flex:"0 0 110px",textAlign:"right",fontSize:13,fontWeight:800,color:T.ink,fontVariantNumeric:"tabular-nums"}}>{fmtK(w.total)}</div>
                <div style={{flex:"0 0 150px",textAlign:"right",fontVariantNumeric:"tabular-nums"}}>
                  {w.budget ? (
                    <>
                      <div style={{fontSize:12,fontWeight:700,color:T.ink}}>
                        {fmtK(w.total).replace("CHF ","")} <span style={{color:T.hint,fontWeight:500}}>/ {fmtK(w.budget).replace("CHF ","")}</span>
                      </div>
                      <div style={{fontSize:10,fontWeight:700,marginTop:1,
                        color: w.varPct > 0.05 ? T.err : w.varPct < -0.02 ? T.ok : T.sub}}>
                        {w.varPct > 0 ? "+" : ""}{fmtPct(w.varPct)}
                      </div>
                    </>
                  ) : (
                    <span style={{fontSize:12,color:T.hint}}>—</span>
                  )}
                </div>
                <div style={{flex:"0 0 22px",textAlign:"center",color:T.hint,fontSize:10}}>{isOpen?"▼":"▶"}</div>
              </div>

              {isOpen && <WRDetail w={w} REF={REF} fmtPct={fmtPct} ragHex={ragHex} ragLabel={ragLabel} L={L} quarter={quarter}/>}
            </div>
          );
        })}
        {hiddenFutureCount > 0 && (
          <div style={{padding:"10px 14px",fontSize:11,color:T.hint,textAlign:"center",
            background:"#fafbfc",borderTop:`1px dashed ${T.line}`,letterSpacing:".05em"}}>
            + {hiddenFutureCount} future week{hiddenFutureCount>1?"s":""} not yet started
          </div>
        )}
      </Card>

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      <div style={{display:"flex",gap:20,flexWrap:"wrap",fontSize:11,color:T.hint,padding:"16px 4px 4px"}}>
        <span><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:T.ok,marginRight:6}}/>Green: ≤ +5% vs budget &amp; CHF/cover within reference</span>
        <span><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:T.warn,marginRight:6}}/>Amber: +5–15% over</span>
        <span><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:T.err,marginRight:6}}/>Red: &gt; +15% over</span>
        <span><span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:T.hint,marginRight:6}}/>Closed / N-A</span>
      </div>
      <div style={{fontSize:11,color:T.hint,padding:"4px"}}>
        Two RAG per week: <b style={{color:T.sub}}>worst-case</b> (any restaurant red → week red) and <b style={{color:T.sub}}>budget-weighted</b> (total spend vs sum of restaurant budgets).
        CHF/cover shown <b>per restaurant only</b> (not at week or premium level). Attribution: LV = liste blanche chefs Production Kitchen + Roland (mer) + Simonin (jeu) + Pastry Production ⅓ · FD = place Fine Dining Restaurant seule · Pastry Lab/Jeremy → hors restaurants · ⇄ transfert FD→LV 40% PO Prot+Lég (W3-W10) · Webstar & achats de stock économat exclus.
        References: LV {REF.lv} · SK {REF.sk} · FD {REF.fd} · FI {REF.fi} CHF/cover.
      </div>
    </div>
  );
}

// ─── Inline SVG icons for streams ────────────────────────────────────────────
const IconWave = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 8c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2"/>
    <path d="M2 14c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2"/>
  </svg>
);
const IconUtensils = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2v7a3 3 0 0 0 3 3v9"/><path d="M9 2v7a3 3 0 0 1-3 3"/><path d="M9 2v20"/>
    <path d="M21 15V2a4 4 0 0 0-4 4v6a2 2 0 0 0 2 2h2v6"/>
  </svg>
);
const IconLeaf = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19.2 2.96a1 1 0 0 1 1.8.66 21 21 0 0 1-3.4 11.7c-2.6 4-5.3 5.7-9.6 5.6"/>
    <path d="M2 21c0-3 1.85-5.36 5.08-6"/>
  </svg>
);
const IconBowl = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12h20"/>
    <path d="M21 12c0 4-3.5 8-9 8s-9-4-9-8"/>
    <path d="M11 4c1.5-1 2 1 1 2.5s-1 1.5-1 3"/>
    <path d="M16 4c1.5-1 2 1 1 2.5s-1 1.5-1 3"/>
  </svg>
);
const IconChef = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"/>
    <line x1="6" y1="17" x2="18" y2="17"/>
  </svg>
);
const IconBox = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27,6.96 12,12.01 20.73,6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const IconStarSm = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// Sparkline — mini SVG chart per stream above the table
// ─────────────────────────────────────────────────────────────────────────────
function Sparkline({title, data, color, accent, icon, bgTint, ringColor, cumulative, weeklyRef}) {
  const W = 200, H = 60, PAD_T = 6, PAD_B = 14, PAD_X = 6;
  const values = data.map(d => d.value);
  const refLine = weeklyRef || 0;
  const max = Math.max(...values, refLine || 0, 1);
  const stepX = data.length > 1 ? (W - 2*PAD_X) / (data.length) : 0;
  const yAt = v => H - PAD_B - (v / max) * (H - PAD_T - PAD_B);
  const refY = refLine ? yAt(refLine) : null;
  const barW = stepX * 0.65;

  return (
    <div style={{
      background: bgTint || T.card,
      border: `1px solid ${ringColor || T.line}`,
      borderRadius: 10,
      padding: "10px 12px",
      boxShadow: T.sh1,
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{position:"absolute",top:6,right:8,opacity:0.16,color:color,pointerEvents:"none"}}>
        {icon}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",
          width:20,height:20,borderRadius:5,background:color+"22",color:color}}>
          {icon}
        </div>
        <div style={{fontSize:9.5,color:T.hint,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700}}>{title}</div>
      </div>
      <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:6}}>
        <span style={{fontSize:17,fontWeight:800,color:T.ink,letterSpacing:"-.01em"}}>{fmtK(cumulative || 0)}</span>
        {refLine ? (
          <span style={{fontSize:9.5,color:T.hint}}>
            / wk ref {fmtK(refLine).replace("CHF ","")}
          </span>
        ) : null}
      </div>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{display:"block"}}>
        {/* Reference line */}
        {refY !== null && (
          <>
            <line x1={PAD_X} y1={refY} x2={W-PAD_X} y2={refY} stroke={accent} strokeWidth="1" strokeDasharray="3,2" opacity="0.5"/>
          </>
        )}
        {/* Bars per week */}
        {data.map((d,i) => {
          const x = PAD_X + i*stepX + (stepX - barW)/2;
          const y = yAt(d.value);
          const h = (H - PAD_B) - y;
          const isCurr = d.current;
          const fill = isCurr ? accent : color;
          const op = d.partial && !isCurr ? 0.35 : (isCurr ? 1 : 0.7);
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={Math.max(h, 1)} fill={fill} opacity={op} rx="1.5">
                <title>{d.fullLabel}: CHF {Math.round(d.value).toLocaleString()}{d.partial?" (partial)":""}</title>
              </rect>
              {/* X-axis label */}
              <text x={x + barW/2} y={H-2} textAnchor="middle" fontSize="8" fill={isCurr?accent:T.hint} fontWeight={isCurr?"700":"500"}>
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WeeklyReportsTab — Expanded detail row
// ─────────────────────────────────────────────────────────────────────────────
function WRDetail({w, REF, fmtPct, ragHex, ragLabel, L, quarter}) {
  const Row = ({label, sub, po, dry, total, cov, ref, rag, indent=0, bold=false, icon=null, tint=null, accent=null}) => {
    const chfCov = cov ? total/cov : 0;
    const showCov = !!(cov && ref);  // CHF/cov only for individual restaurants (cov AND ref present)
    return (
      <div style={{display:"flex",alignItems:"center",padding:"8px 8px",fontSize:12,
        borderBottom:`1px solid ${T.line}`,
        background: tint || "transparent",
        borderLeft: accent ? `3px solid ${accent}` : "3px solid transparent",
      }}>
        <div style={{flex:"0 0 200px",paddingLeft:indent,display:"flex",alignItems:"center",gap:8,minWidth:0}}>
          {icon && <span style={{display:"inline-flex",color:accent||T.sub,flexShrink:0}}>{icon}</span>}
          <div style={{minWidth:0}}>
            <div style={{color: bold ? T.ink : T.sub, fontWeight: bold ? 700 : 500}}>{label}</div>
            {sub && <div style={{fontSize:10,color:T.hint,marginTop:1,lineHeight:1.4}}>{sub}</div>}
          </div>
        </div>
        <div style={{flex:"0 0 70px",textAlign:"right",color:T.sub,fontVariantNumeric:"tabular-nums"}}>{po?fmtK(po).replace("CHF ",""):"—"}</div>
        <div style={{flex:"0 0 70px",textAlign:"right",color:T.sub,fontVariantNumeric:"tabular-nums"}}>{dry?fmtK(dry).replace("CHF ",""):"—"}</div>
        <div style={{flex:"0 0 88px",textAlign:"right",color:T.ink,fontWeight:700,fontVariantNumeric:"tabular-nums"}}>{fmtK(total)}</div>
        <div style={{flex:"0 0 60px",textAlign:"right",color:T.hint,fontSize:11,fontVariantNumeric:"tabular-nums"}}>{cov||"—"}</div>
        <div style={{flex:"0 0 110px",textAlign:"right",fontSize:11,fontVariantNumeric:"tabular-nums",fontWeight:600,
          color: rag==="r"?T.err:rag==="a"?T.warn:rag==="g"?T.ok:T.hint}}>
          {showCov ? `${chfCov.toFixed(2)} / ${ref}` : "—"}
        </div>
        <div style={{flex:"0 0 24px",textAlign:"center"}}>
          {showCov && <span style={{display:"inline-block",width:9,height:9,borderRadius:"50%",background:ragHex(rag)}}/>}
        </div>
      </div>
    );
  };

  const closed = new Set(w.closed || []);
  const premPo  = w.streams.fd.po + w.streams.fi.po + w.streams.sk.po;
  const premDry = w.streams.fd.dry + w.streams.fi.dry + w.streams.sk.dry;
  const premCov = (w.covers.fd||0)+(w.covers.fi||0)+(w.covers.sk||0);
  const premRag = w.streamRag.fd==="r"||w.streamRag.fi==="r"||w.streamRag.sk==="r" ? "r"
                : w.streamRag.fd==="a"||w.streamRag.fi==="a"||w.streamRag.sk==="a" ? "a"
                : premCov ? "g" : "x";

  return (
    <div style={{padding:"16px 22px 20px 130px",background:"#f7f9fb",borderBottom:`1px solid ${T.line}`}}>
      <div style={{display:"flex",alignItems:"center",padding:"6px 0",fontSize:9.5,color:T.hint,
        textTransform:"uppercase",letterSpacing:".08em",fontWeight:700,borderBottom:`1px solid ${T.line}`,marginBottom:2}}>
        <div style={{flex:"0 0 200px"}}>Stream / restaurant</div>
        <div style={{flex:"0 0 70px",textAlign:"right"}}>PO</div>
        <div style={{flex:"0 0 70px",textAlign:"right"}}>DRY</div>
        <div style={{flex:"0 0 88px",textAlign:"right"}}>Total</div>
        <div style={{flex:"0 0 60px",textAlign:"right"}}>Covers</div>
        <div style={{flex:"0 0 110px",textAlign:"right"}}>CHF/cov · ref</div>
        <div style={{flex:"0 0 24px"}}></div>
      </div>

      {/* Pas de ligne "Premium restaurants" — les 4 restos s'alignent à gauche */}
      <Row label="Lake View"
           sub={<>
             {L("Production Kitchen, 7j/7","Production Kitchen, 7d/7")} — Sanford, Emerling, Nishizima, Cavalli, Chebel, Borges, Ben Sekkou<br/>
             ↗ Roland — Application ({L("mercredi","Wed")}) · Pierre Simonin — Leeman ({L("jeudi","Thu")})<br/>
             {L("Pastry Production (« dairy fridge ») comptée à 1/3 · Hiestand exclu · pâtisseries Baking Lab/Demo/Mosimann hors LV","Pastry Production (dairy fridge) counted at 1/3 · Hiestand excluded · Baking Lab/Demo/Mosimann pastries excl. LV")}
             {w.streams.lv.transfer?<><br/>⇄ {L("transfert reçu de FD","transfer from FD")}: +{fmtK(w.streams.lv.transfer)} (40% PO Prot+Lég)</>:null}
           </>}
           po={w.streams.lv.po} dry={w.streams.lv.dry} total={w.streams.lv.total} cov={w.covers.lv||0} ref={REF.lv} rag={w.streamRag.lv}
           icon={<IconWave/>} accent={T.lv} tint="rgba(0,128,177,0.05)"/>
      <Row label="Fine Dining"
           sub={closed.has("fd")?L("fermé","closed"):<>Axel Klaes — restaurant · {quarter>=3?(w.covers.fd||0):60} {L("couv./sem","covers/wk")}{w.streams.fd.transfer?<><br/>⇄ {L("transfert vers LV","transfer to LV")}: {fmtK(Math.abs(w.streams.fd.transfer))} (40% PO Prot+Lég)</>:null}</>}
           po={w.streams.fd.po} dry={w.streams.fd.dry} total={w.streams.fd.total} cov={w.covers.fd||0} ref={REF.fd} rag={w.streamRag.fd}
           icon={<IconUtensils/>} accent={T.fd} tint="rgba(178,56,47,0.05)"/>
      <Row label="Food Inspired"
           sub={closed.has("fi")?L("fermé","closed"):<>David Alvarez — restaurant · {quarter>=3?(w.covers.fi||0):176} {L("couv./sem","covers/wk")}</>}
           po={w.streams.fi.po} dry={w.streams.fi.dry} total={w.streams.fi.total} cov={w.covers.fi||0} ref={REF.fi} rag={w.streamRag.fi}
           icon={<IconLeaf/>} accent={T.fi} tint="rgba(113,97,183,0.05)"/>
      <Row label="Soul Kitchen"
           sub={closed.has("sk")?L("fermé","closed"):<>Rando + Sara + Pierre · {quarter===3?L("service Mer+Jeu","Wed+Thu service"):quarter>=4?L("planning","planning"):L("5 services chef + mise en place Ven","5 chef services + Fri mise en place")} · {quarter>=3?(w.covers.sk||0):480} {L("couv./sem","covers/wk")}</>}
           po={w.streams.sk.po} dry={w.streams.sk.dry} total={w.streams.sk.total} cov={w.covers.sk||0} ref={REF.sk} rag={w.streamRag.sk}
           icon={<IconBowl/>} accent={T.sk} tint="rgba(186,78,36,0.06)"/>

      {/* Top products + N-1 comparison */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,marginTop:18}}>
        <Card style={{padding:"14px 18px",boxShadow:"none",background:T.card}}>
          <Label>Top 3 products this week</Label>
          {(w.top || []).length > 0 ? (w.top || []).map((p,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",
              padding:"6px 0",borderBottom:i<2?`1px solid ${T.line}`:"none",fontSize:12}}>
              <span style={{color:T.sub,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginRight:8}}>
                <span style={{color:T.hint,marginRight:8,fontWeight:700}}>{i+1}</span>{p.n}
              </span>
              <span style={{color:T.ink,fontWeight:700,fontVariantNumeric:"tabular-nums",flexShrink:0}}>{fmtK(p.v)}</span>
            </div>
          )) : (
            <div style={{fontSize:11,color:T.hint,fontStyle:"italic",padding:"6px 0"}}>No products this week</div>
          )}
        </Card>
        <Card style={{padding:"14px 18px",boxShadow:"none",background:T.card}}>
          <Label>vs same week prior period</Label>
          {w.n1 ? (
            <>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.sub,padding:"5px 0",borderBottom:`1px solid ${T.line}`}}>
                <span>Q1 {w.n1.w}</span>
                <span style={{color:T.ink,fontWeight:600}}>{fmtK(w.n1.total)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.sub,padding:"5px 0",borderBottom:`1px solid ${T.line}`}}>
                <span>This week ({w.w})</span>
                <span style={{color:T.ink,fontWeight:600}}>{fmtK(w.total)}{w.partial?" *":""}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"8px 0 0"}}>
                <span style={{color:T.hint,fontWeight:600}}>Δ vs Q1</span>
                <span style={{fontWeight:800,color:w.n1.delta_pct===null?T.hint:w.n1.delta_pct>0?T.err:T.ok}}>
                  {w.n1.delta_pct===null ? "in progress" : (w.n1.delta_pct>0?"+":"")+w.n1.delta_pct.toFixed(1)+"%"}
                </span>
              </div>
              {w.partial && <div style={{fontSize:10,color:T.hint,marginTop:4,fontStyle:"italic"}}>* partial — week in progress</div>}
            </>
          ) : (
            <div style={{fontSize:11,color:T.hint,fontStyle:"italic",padding:"6px 0"}}>No comparable week (Q1 baseline)</div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Q2 LIVE TAB
// ─────────────────────────────────────────────────────────────────────────────
function Q2Tab({quarter=2}) {
  const [tracker, setTracker] = useState(null);
  const [tcLang,  setTcLang]  = useState("fr");   // toggle FR/EN local pour la card teaching/catering
  const [weekly,  setWeekly]  = useState(null);
  const [wrpt,    setWrpt]    = useState(null);   // /weekly_reports — source de vérité unique pour le chart
  const [cats,    setCats]    = useState(null);
  const [compare, setCompare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);
  const _qp = `?quarter=${quarter}`;
  const QL = quarter===3 ? "Q3" : "Q2";
  const QPREV = quarter===3 ? "Q2" : "Q1";
  const QSTART_LABEL = quarter===3 ? "6 juillet" : "1ᵉʳ avril";

  const load = useCallback(async()=>{
    setLoading(true); setErr(null);
    try {
      const [r1,r2,r3,r4,r5] = await Promise.all([
        fetch(API+"/dashboard/q2_tracker"+_qp),
        fetch(API+"/dashboard/q2_weekly"+_qp),
        fetch(API+"/dashboard/q2_categories"+_qp),
        fetch(API+"/dashboard/q1q2_weekly_compare"+_qp),
        fetch(API+"/dashboard/weekly_reports"+_qp),
      ]);
      const [j1,j2,j3,j4,j5] = await Promise.all([r1.json(),r2.json(),r3.json(),r4.json(),r5.json()]);
      const d = (j1.april_committed||0)===0 ? Q2_FALLBACK : j1;
      // Q2 : on force les jours calculés client (l'API peut renvoyer une valeur périmée).
      // Q3 : on fait confiance à l'API (days_elapsed calculé depuis le 6 juillet).
      if (quarter !== 3) d.days_elapsed = _DAYS_Q2;
      setTracker(d);
      if (!j2.error) setWeekly(j2);
      if (!j3.error) setCats(j3.by_entity||null);
      if (!j4.error&&j4.weeks) setCompare(j4.weeks);
      if (!j5.error&&j5.weeks) setWrpt(j5);
    } catch(e) {
      setTracker(Q2_FALLBACK);
      setErr("API offline — certified data au 16 avr");
    }
    setLoading(false);
  },[_qp,quarter]);
  useEffect(()=>{load();},[load]);

  if (loading) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",
      height:320,color:T.hint,fontSize:13,gap:10}}>
      <span style={{width:18,height:18,borderRadius:"50%",
        border:"2px solid "+T.g500,borderTopColor:"transparent",
        display:"inline-block",animation:"spin 0.8s linear infinite"}}/>
      Loading Odoo data…
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const data = tracker||Q2_FALLBACK;
  // Schedule-aware daily cover map — midi+soir agrégés par jour
  // DOW: Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6
  const _be   = data.by_entity||{};
  const _days = data.days_elapsed||1;
  const _DAY_COV = {
    lv: { 0:400, 1:510, 2:350, 3:375, 4:375, 5:175, 6:400 },  // = 2585 cov/sem (réel midi+soir: Lun510 Mar350 Mer375 Jeu375 Ven175 Sam400 Dim400)
    fi: { 0:0,   1:0,   2:44,  3:44,  4:44,  5:44,  6:0   },  // = 176 cov/sem (44 × mar→ven)
    // SK = 5 chef services × 60 cov (3x Rando + 1x Sara + 1x Pierre Mon→Fri)
    // + Vendredi soir : 180 cov mise en place chez LV (budget SK @ 12 CHF/cov)
    // Total SK = 480 cov/sem (300 chef + 180 mise en place)
    sk: { 0:0,   1:60,  2:60,  3:60,  4:60,  5:240, 6:0   },
    fd: { 0:0,   1:0,   2:0,   3:30,  4:30,  5:0,   6:0   },  // = 60 cov/sem (30 mer + 30 jeu)
  };
  const _START = { lv:1, fi:13, sk:13, fd:13 };  // FD aligné budget: 10 sem × 50 cov = 500 (démarre W2)
  const _Q2_END = 84;  // 12 semaines W0-W11 (aligné api.py q2_days)
  const _sumCov = (k, from, to) => {
    let t = 0;
    for (let d = from; d <= to; d++) t += _DAY_COV[k][(d+2)%7] || 0;
    return t;
  };
  const _REFS  = {lv:5.00, fi:20.0, sk:12.0, fd:15.0};  // LV 5 · FI 20 · SK 12 · FD 15 (Axel) — validés 10.06
  const _Q2_TOT= {
    lv: _sumCov("lv", 1, _Q2_END),    // 31020 (2585 cov/sem × 12 sem W0-W11)
    fi: _sumCov("fi", 13, _Q2_END),   // 1800
    sk: _sumCov("sk", 13, _Q2_END),   // 5400 (480 cov/sem × 11 sem + extras)
    fd: _sumCov("fd", 13, _Q2_END),   // 550
  };
  let projByCov = 0;
  // Q3 : couverts lus depuis l'API (modèle jour-par-jour) · Q2 : modèle client historique
  const _covTot  = (k)     => (quarter===3 && data.covers_total)   ? (data.covers_total[k]||0)   : _Q2_TOT[k];
  const _covDone = (k, dd) => (quarter===3 && data.covers_to_date) ? (data.covers_to_date[k]||0) : (dd >= _START[k] ? _sumCov(k, _START[k], dd) : 0);
  ["lv","fi","sk","fd"].forEach(k=>{
    const tot = _be[k]?.committed||0;
    const covDone = _covDone(k, _days);
    // Taux actual si données disponibles — pas de substitution par reference
    const chfCov = (covDone>0&&tot>0) ? tot/covDone : _REFS[k];
    projByCov += chfCov * _covTot(k);
  });
  projByCov = Math.round(projByCov);
  const projResto = projByCov > 0 ? projByCov : (data.april_committed_resto||0);
  const B_RESTO   = Q2_BUDGET_A.total;                                        // somme budgets restos
  const B_ALL     = (typeof Q2_BUDGET_ALL!=="undefined"?Q2_BUDGET_ALL:364000); // budget toutes places
  const committedResto = data.april_committed_resto||0;
  const pctResto  = Math.min((committedResto/B_RESTO)*100,100);
  const overResto = projResto > B_RESTO;
  // Entête = BRUT toutes places vs budget 364K  (alias pace/B/pct/over)
  const pace = data.q2_pace_projected||0;
  const B    = B_ALL;
  const pct  = Math.min(((data.april_committed||0)/B)*100,100);
  const over = pace > B;

  const ENTS = [
    {key:"lv",label:"Lake View",    color:T.lv, budget:Q2_BUDGET_A.lv, ref:5.00,  cov:2585},
    {key:"fi",label:"Food Inspired",color:T.fi, budget:Q2_BUDGET_A.fi, ref:20.00, cov:176},
    {key:"sk",label:"Soul Kitchen", color:T.sk, budget:Q2_BUDGET_A.sk, ref:12.00, cov:480},
    {key:"fd",label:"Fine Dining",  color:T.fd, budget:Q2_BUDGET_A.fd, ref:15.00, cov:60},
  ];

  const weeks = weekly?.weeks||[];
  const hasW  = weeks.length>0 && weeks.some(w=>w.total>0);

  // Spend by chef: total Q2 global de l'API (somme complète) — l'ancien cumul des
  // top-8 hebdo tronquait les chefs hors top 8 chaque semaine
  const chefMap={};
  if (weekly?.top_chefs_q2?.length) {
    weekly.top_chefs_q2.forEach(({chef,chf})=>{chefMap[chef]=chf;});
  } else {
    weeks.forEach(w=>{(w.top_chefs||[]).forEach(({chef,chf})=>{chefMap[chef]=(chefMap[chef]||0)+chf;});});
  }
  const topChefs = Object.entries(chefMap).sort((a,b)=>b[1]-a[1]).slice(0,8);

  // ── Teaching vs Catering (card + export, David/CEO) ──
  const tc = data.teaching_catering || {};
  const tcFr = tcLang==="fr";
  const exportTcXls = () => {
    const H = tcFr ? ["Kitchen (enseignement)","Dépense HT (CHF)"] : ["Kitchen (teaching)","Spend excl. VAT (CHF)"];
    const rows = (tc.teaching_places||[]).map(p=>[p.place, Math.round(p.total||0)]);
    const pre = [
      {label:(tcFr?"Q2 — brut food (CHF)":"Q2 — gross food (CHF)"), val:Math.round(tc.gross||0)},
      {label:(tcFr?"Enseignement / teaching":"Teaching"), val:`${Math.round(tc.teaching||0)} (${tc.teaching_pct||0}%)`},
      {label:(tcFr?"Restauration / catering":"Catering / restaurants"), val:`${Math.round(tc.catering||0)} (${tc.catering_pct||0}%)`},
    ];
    dlXls("teaching_vs_catering_q2", H, rows, pre);
  };

  return (
    <div>
      {err&&(
        <div style={{background:T.warn+"18",border:"1px solid "+T.warn+"30",borderRadius:10,
          padding:"9px 14px",marginBottom:16,fontSize:11.5,color:"#92400e"}}>
          ⚠ {err}
        </div>
      )}

      {/* ── Teaching vs Catering (David / CEO) ── */}
      {tc.gross!=null && (
      <Card style={{marginBottom:20,padding:"22px 26px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap",marginBottom:14}}>
          <div>
            <div style={{fontSize:15,fontWeight:800,color:T.ink}}>{tcFr?"Enseignement vs Restauration · Teaching vs Catering":"Teaching vs Catering"}</div>
            <div style={{fontSize:12,color:T.hint,marginTop:2}}>{tcFr?`Brut food ${QL} ${fmtK(tc.gross)} · réparti par usage`:`${QL} gross food ${fmtK(tc.gross)} · split by use`}</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <div style={{display:"inline-flex",border:`1px solid ${T.line}`,borderRadius:8,overflow:"hidden"}}>
              {["fr","en"].map(lg=>(
                <button key={lg} onClick={()=>setTcLang(lg)} style={{border:"none",cursor:"pointer",padding:"6px 11px",fontSize:11.5,fontWeight:700,background:tcLang===lg?T.ink:T.card,color:tcLang===lg?"#fff":T.sub}}>{lg.toUpperCase()}</button>
              ))}
            </div>
            <button onClick={exportTcXls} className="no-print" style={{border:`1px solid ${T.line}`,background:T.card,color:T.sub,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>⬇ Export XLS</button>
          </div>
        </div>
        {(()=>{ const te=tc.teaching||0, ca=tc.catering||0, t=(te+ca)||1; return (
        <>
          <div style={{display:"flex",height:28,borderRadius:8,overflow:"hidden",border:`1px solid ${T.line}`,marginBottom:10}}>
            <div style={{width:`${100*te/t}%`,background:T.info,minWidth:te>0?36:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700}}>{tc.teaching_pct}%</div>
            <div style={{width:`${100*ca/t}%`,background:T.lv,minWidth:ca>0?36:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700}}>{tc.catering_pct}%</div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:14}}>
            <div><span style={{display:"inline-block",width:8,height:8,borderRadius:8,background:T.info,marginRight:6}}/><b style={{color:T.ink}}>{tcFr?"Enseignement (teaching)":"Teaching"}</b> <span style={{color:T.hint}}>· {fmtK(te)} · {tc.teaching_pct}%</span></div>
            <div><span style={{display:"inline-block",width:8,height:8,borderRadius:8,background:T.lv,marginRight:6}}/><b style={{color:T.ink}}>{tcFr?"Restauration (catering)":"Catering"}</b> <span style={{color:T.hint}}>· {fmtK(ca)} · {tc.catering_pct}%</span></div>
          </div>
        </>
        );})()}
        <div style={{fontSize:11.5,color:T.hint,lineHeight:1.55,marginBottom:12}}>{tcFr?tc.note_fr:tc.note_en}</div>
        {(tc.teaching_places||[]).length>0 && (
        <div>
          <div style={{fontSize:10.5,letterSpacing:".05em",textTransform:"uppercase",color:T.hint,fontWeight:800,marginBottom:8}}>{tcFr?"Enseignement — par kitchen":"Teaching — by kitchen"}</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {(tc.teaching_places||[]).slice(0,14).map((p,i)=>(
              <span key={i} style={{fontSize:12,padding:"3px 9px",borderRadius:7,background:T.info+"14",color:T.ink,border:`1px solid ${T.info+"38"}`}}>{p.place} · {fmtK(p.total)}</span>
            ))}
          </div>
        </div>
        )}
      </Card>
      )}

      {/* ── BIG NUMBER HEADER ─────────────────────────── */}
      <Card style={{marginBottom:20,padding:"28px 32px"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1.8fr auto",gap:32,alignItems:"center"}}>

          {/* Left: big number */}
          <div>
            <Label style={{marginBottom:10}}>{QL} jour {data.days_elapsed}/84 · Certified</Label>
            <div style={{fontSize:56,fontWeight:800,color:T.ink,lineHeight:1,
              letterSpacing:"-.04em",marginBottom:10}}>
              {fmtK(data.april_committed)}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5}}>
                <span style={{width:10,height:10,borderRadius:3,
                  background:T.po,display:"inline-block",flexShrink:0}}/>
                <span style={{color:T.hint}}>PO Orders</span>
                <b style={{color:T.ink,marginLeft:"auto"}}>{fmtK(data.april_po||0)}</b>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5}}>
                <span style={{width:10,height:10,borderRadius:3,
                  background:T.dry,display:"inline-block",flexShrink:0}}/>
                <span style={{color:T.hint}}>DRY Stock</span>
                <b style={{color:T.ink,marginLeft:"auto"}}>{fmtK(data.april_dry||0)}</b>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,
                paddingTop:5,borderTop:`1px solid ${T.line}`}}>
                <span style={{color:T.hint}}>Daily rate</span>
                <b style={{color:T.ink,marginLeft:"auto"}}>{fmtK(data.daily_rate_chf)}</b>
              </div>
            </div>
          </div>

          {/* Center: progress */}
          <div>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"baseline",marginBottom:10}}>
              <span style={{fontSize:13,fontWeight:600,color:T.ink}}>
                {QL} progress · Budget
              </span>
              <span style={{fontSize:22,fontWeight:800,color:T.ink}}>
                {pct.toFixed(1)}%
              </span>
            </div>
            <div style={{position:"relative",height:20,background:T.grid,
              borderRadius:10,overflow:"hidden",marginBottom:10}}>
              <div style={{position:"absolute",left:0,top:0,height:"100%",
                width:Math.min((pace/B)*100,100)+"%",
                background:over?"rgba(220,38,38,0.12)":"rgba(22,163,74,0.08)"}}/>
              <div style={{position:"absolute",left:0,top:0,height:"100%",
                width:pct+"%",
                background:T.graphite,
                borderRadius:10,transition:"width .6s"}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
              {[
                {l:`${QL} spent · all`,  v:fmtK(data.april_committed), c:T.g700},
                {l:`Projected ${QL} (real)`, v:fmtK(pace),                 c:over?T.warn:T.ok},
                {l:"Budget · all places",  v:fmtK(B),                  c:T.ink},
                {l:`Remaining ${QL}`,   v:fmtK(Math.max(B-data.april_committed,0)),c:T.hint},
              ].map((k,i)=>(
                <div key={i} style={{padding:"8px 10px",background:T.bg,
                  borderRadius:9,border:`1px solid ${T.line}`}}>
                  <div style={{fontSize:9,color:T.hint,textTransform:"uppercase",
                    letterSpacing:".07em",marginBottom:3}}>{k.l}</div>
                  <div style={{fontSize:13,fontWeight:700,color:k.c}}>{k.v}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:10.5,color:T.hint,marginTop:6,
              padding:"6px 10px",background:T.bg,borderRadius:8,
              border:`1px solid ${T.line}`}}>
              ⓘ Projection by actual CHF/cover × remaining Q2 covers (W0-W11, real covers).
              À J+{data.days_elapsed}, this figure is indicative — early-quarter DRY stocking may inflate the rate.
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8,
              padding:"8px 12px",borderRadius:9,
              background:overResto?"#fffbeb":"#f8f4ef",
              border:`1px solid ${overResto?"#fde68a":"#bbf7d0"}`,fontSize:11.5,flexWrap:"wrap"}}>
              <b style={{color:T.ink}}>Restaurants · LV+SK+FD+FI</b>
              <span style={{color:T.hint}}>dépensé</span>
              <b style={{color:T.ink}}>{fmtK(committedResto)}</b>
              <span style={{color:T.hint}}>/ budget {fmtK(B_RESTO)}</span>
              <span style={{marginLeft:"auto",fontWeight:700,color:overResto?T.warn:T.ok}}>
                proj {fmtK(projResto)} · {overResto?"⚠ over":"✓ within"} ({pctResto.toFixed(0)}%)
              </span>
            </div>
          </div>

          {/* Right: badge */}
          <div style={{textAlign:"center"}}>
            <div style={{
              padding:"10px 20px",borderRadius:12,
              fontSize:12,fontWeight:700,
              color:over?T.warn:T.ok,
              background:over?"#fffbeb":"#f8f4ef",
              border:`1px solid ${over?"#fde68a":"#bbf7d0"}`,
              marginBottom:8,whiteSpace:"nowrap",
            }}>
              {over?"⚠ HIGH PACE":"✓ WITHIN BUDGET"}
            </div>
            <div style={{fontSize:10.5,color:T.hint}}>
              Projection {(pace/1000).toFixed(0)}k vs budget {(B/1000).toFixed(0)}k
            </div>
          </div>
        </div>
      </Card>

      {/* ── HORS RESTAURANTS · Enseignement & Events ──── */}
      {(()=>{
        const hr = data.hors_resto || {};
        const places = hr.places || [];
        if (!places.length) return null;
        const hrTot = hr.total || places.reduce((s,p)=>s+p.total,0);
        const maxP  = Math.max(...places.map(p=>p.total), 1);
        const shown = places.filter(p=>p.total>=500);
        const rest  = places.filter(p=>p.total<500).reduce((s,p)=>s+p.total,0);
        const TAGS = {
          "Application Kitchen":"enseignement","Leeman Kitchen":"enseignement",
          "Irma Kitchen":"enseignement","Mane Kitchen":"enseignement",
          "Mosimann Kitchen":"enseignement","Demo Kitchen":"démos",
          "Pastry Production":"⅔ hors LV (dairy fridge)","Chocolate Lab":"enseignement",
          "Baking Lab":"enseignement","The Bakery":"enseignement",
          "F&B Service":"service & events","Special Event":"events",
          "Pastry Lab":"pâtisserie (hors FD)",
          "Production Kitchen":"part non-LV","Economat":"stock divers",
        };
        return (
        <Card style={{marginBottom:20}}>
          <div style={{display:"flex",alignItems:"baseline",gap:12,marginBottom:4,flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:15,fontWeight:800,color:T.ink}}>Hors restaurants · Enseignement & Events</div>
              <div style={{fontSize:11,color:T.hint,marginTop:2}}>
                Dépenses réelles des places académiques (cours, démos, events) — hors LV / SK / FD / FI ·
                achats de stock économat exclus (comptés en DRY à la sortie)
              </div>
            </div>
            <div style={{marginLeft:"auto",textAlign:"right"}}>
              <div style={{fontSize:22,fontWeight:800,color:T.ink}}>{fmtK(hrTot)}</div>
              <div style={{fontSize:10.5,color:T.hint}}>
                PO {fmtK(hr.po||0)} · DRY {fmtK(hr.dry||0)} · {((hrTot/(data.april_committed||1))*100).toFixed(0)}% du brut {QL}
              </div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 28px",marginTop:10}}>
            {shown.map(p=>(
              <div key={p.place} style={{display:"flex",alignItems:"center",gap:8,padding:"3px 0"}}>
                <div style={{width:148,fontSize:11.5,color:T.ink,fontWeight:600,flexShrink:0,
                  whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.place}</div>
                <div style={{flex:1,height:9,borderRadius:5,background:"#f1ece4",overflow:"hidden",display:"flex"}}>
                  <div style={{width:`${(p.po/maxP)*100}%`,background:T.po}}/>
                  <div style={{width:`${(p.dry/maxP)*100}%`,background:T.dry}}/>
                </div>
                <div style={{width:54,textAlign:"right",fontSize:11.5,fontWeight:700,color:T.ink,flexShrink:0}}>
                  {fmtK(p.total)}</div>
                <div style={{width:108,fontSize:9.5,color:T.hint,flexShrink:0,
                  whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {TAGS[p.place]||""}</div>
              </div>
            ))}
          </div>
          {rest>0 && <div style={{fontSize:10.5,color:T.hint,marginTop:6}}>
            + {fmtK(rest)} répartis sur les petites places (&lt; CHF 500 chacune)</div>}
          <div style={{display:"flex",gap:14,fontSize:10,color:T.hint,marginTop:8,alignItems:"center"}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
              <span style={{width:9,height:9,borderRadius:2,background:T.po,display:"inline-block"}}/>PO fresh</span>
            <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
              <span style={{width:9,height:9,borderRadius:2,background:T.dry,display:"inline-block"}}/>DRY stock</span>
            <span style={{marginLeft:"auto"}}>Brut {fmtK(data.april_committed||0)} = Restaurants {fmtK(data.april_committed_resto||0)} + Hors restaurants {fmtK(hrTot)}</span>
          </div>
        </Card>);
      })()}

      {/* ── RESTAURANT CARDS ──────────────────────────── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20,alignItems:"stretch"}}>
        {ENTS.map(e=>{
          const be    = (data.by_entity||{})[e.key]||{};
          const tot   = be.committed||0;
          const po    = be.po||0;
          const dry   = be.dry||0;
          const eng   = be.engaged;  // engagé semaine courante - PAS de fallback (sinon on mélange cumulatif et hebdo)
          const pctB  = Math.min((tot/e.budget)*100,100);
          const days  = data.days_elapsed||1;
          // Couverts schedule-aware — somme des couverts journaliers réels
          // LV: compte depuis day 1 (Q2 start)    FI/SK/FD: depuis day 13 (W2 start)
          const cov = _covDone(e.key, days);
          // Couverts SEMAINE COURANTE pour la métrique engaged
          // LV: lun→dim (7j/7)  | SK/FI/FD: lun→ven (fermés sam/dim)
          const _jsDow = new Date().getDay(); // Sun=0, Mon=1, ..., Fri=5, Sat=6
          const _isLV = e.key === "lv";
          const _endDow = _isLV ? 0 : 5;  // 0=dimanche pour LV, 5=vendredi pour autres
          const _daysToEnd = _isLV
            ? (_jsDow === 0 ? 0 : 7 - _jsDow)            // si dim, on est au dernier jour
            : (_jsDow >= 1 && _jsDow <= 5 ? 5 - _jsDow   // lun→ven : jusqu'à vendredi
               : _jsDow === 6 ? -1 : -2);                // sam/dim : avant cutoff vendredi (pas de jours futurs)
          const _daysSinceMon = _jsDow === 0 ? 6 : _jsDow - 1;
          const _dayMon = days - _daysSinceMon;          // day index du lundi de la semaine courante
          const _dayEnd = Math.min(days + Math.max(_daysToEnd, 0), _Q2_END);
          const _monClamped = Math.max(_dayMon, _START[e.key]);
          const covWeek = _dayEnd >= _monClamped ? _sumCov(e.key, _monClamped, _dayEnd) : 0;
          // Label jours servis selon planning réel (depuis _DAY_COV)
          // _DAY_COV index: 0=dim, 1=lun, 2=mar, 3=mer, 4=jeu, 5=ven, 6=sam
          const _DOW_NAMES = ["dim","lun","mar","mer","jeu","ven","sam"];
          const _servedDows = [];
          for (let idx = 0; idx < 7; idx++) {
            if ((_DAY_COV[e.key][idx]||0) > 0) {
              _servedDows.push({idx:idx, name:_DOW_NAMES[idx]});
            }
          }
          // Trier en ordre semaine lun→dim pour affichage
          const _weekOrder = [1,2,3,4,5,6,0]; // lun→dim
          _servedDows.sort((a,b)=>_weekOrder.indexOf(a.idx)-_weekOrder.indexOf(b.idx));
          const _weekLabel = _servedDows.length > 0
            ? (_servedDows.length === 1
               ? _servedDows[0].name
               : `${_servedDows[0].name}→${_servedDows[_servedDows.length-1].name}`)
            : "lun→ven";
          const chfC  = cov>0&&tot>0 ? (tot/cov).toFixed(2) : "—";
          const chfE  = (covWeek>0 && typeof eng === "number" && eng>0) ? (eng/covWeek).toFixed(2) : "—";
          const overC = parseFloat(chfC)>e.ref;
          const over  = overC && parseFloat(chfC)>0;
          // Projection par couvert (pas par jour !) — covers Q2 totaux par restaurant
          const ep = cov>0&&tot>0 ? Math.round(parseFloat(chfC)*_covTot(e.key)) : Math.round(e.ref*_covTot(e.key));
          // Trop tôt pour SK/FI/FD : stocking ouverture W2 gonfle le CHF/cover
          const earlyOpen = e.key!=="lv" && days<=19;
          const catD  = cats&&cats[e.key]?.categories||[];

          return (
            <Card key={e.key} style={{padding:"22px 24px",
              borderTop:`3px solid ${e.color}`,
              display:"flex",flexDirection:"column"}}>

              {/* Header */}
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"flex-start",marginBottom:16}}>
                <div>
                  <div style={{fontSize:17,fontWeight:800,color:T.ink,
                    letterSpacing:"-.01em",marginBottom:3}}>{e.label}</div>
                  <div style={{fontSize:11.5,color:T.hint}}>
                    {e.cov.toLocaleString()} covers/wk · ref CHF {e.ref}/cover
                  </div>
                </div>
                <Chip label={cov>0&&tot>0
                    ? (over?`⚠ Moy. ${QL} ${chfC} > ${e.ref.toFixed(2)}`:`✓ Moy. ${QL} ${chfC} ≤ ${e.ref.toFixed(2)}`)
                    : "— pas encore de données"}
                  color={over?T.err:T.ok}
                  light={over?"#fdf2f0":"#f8f4ef"}/>
              </div>

              {/* Big number */}
              <div style={{fontSize:40,fontWeight:800,color:e.color,
                letterSpacing:"-.03em",lineHeight:1,marginBottom:4}}>
                {fmtK(tot)}
              </div>
              <div style={{fontSize:12,color:T.hint,marginBottom:(_be[e.key]?.transfer?6:20)}}>
                vs {QL} budget {fmtK(e.budget)} · projected {fmtK(ep)}
              </div>
              {!!_be[e.key]?.transfer && (
                <div style={{fontSize:10.5,fontWeight:600,marginBottom:14,
                  color:_be[e.key].transfer>0?T.ok:T.warn}}>
                  {_be[e.key].transfer>0?"⇄ incl. +":"⇄ incl. −"}{fmtK(Math.abs(_be[e.key].transfer))} transfert FD → LV · 40% PO Protéines+Légumes · W3-W10
                </div>
              )}

              {/* PO / DRY split */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14,
                minHeight:160,alignItems:"stretch"}}>
                {/* PO */}
                <div style={{padding:"14px",background:"#f8f4ef",
                  borderRadius:10,border:`1px solid ${T.line}`,
                  display:"flex",flexDirection:"column"}}>
                  <div style={{fontSize:10,color:T.ink,textTransform:"uppercase",
                    letterSpacing:".08em",fontWeight:700,marginBottom:6}}>PO · Fresh</div>
                  <div style={{fontSize:22,fontWeight:800,color:T.ink,marginBottom:catD.length?10:0}}>
                    {fmtK(po)}
                  </div>
                  {catD.length>0 ? (
                    <div style={{borderTop:`1px solid ${T.line}`,paddingTop:8}}>
                      {[
                        {key:"PROTEINS",label:"Proteins"},
                        {key:"VEGETABLES_FRUITS",label:"Vegetables & Fruits"},
                        {key:"DAIRY",label:"Dairy"},
                        {key:"SPECIAL ITEM",label:"Special items"},
                      ].map((def,ci)=>{
                        const cat = catD.find(c=>c.key===def.key)||{key:def.key,label:def.label,chf:0,pct:0};
                        const CC={PROTEINS:{b:T.fd,t:T.fd},VEGETABLES_FRUITS:{b:T.lv,t:T.lv},
                          DAIRY:{b:"#3b82f6",t:"#1d4ed8"},"SPECIAL ITEM":{b:T.sk,t:T.sk}};
                        const col=CC[cat.key]||{b:T.hint,t:T.hint};
                        return (
                          <div key={ci} style={{marginBottom:6}}>
                            <div style={{display:"flex",justifyContent:"space-between",
                              fontSize:10.5,marginBottom:3}}>
                              <span style={{color:col.t,fontWeight:600}}>{cat.label}</span>
                              <span style={{color:col.t,fontWeight:700}}>
                                {fmtK(cat.chf)}<span style={{color:T.hint,fontWeight:400}}> {cat.pct}%</span>
                              </span>
                            </div>
                            <Bar value={cat.pct} max={100} color={col.b} height={4}/>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{fontSize:10.5,color:T.hint,marginTop:"auto"}}>
                      {tot>0?(po/tot*100).toFixed(0):0}% of total
                    </div>
                  )}
                </div>

                {/* DRY */}
                <div style={{padding:"14px",background:"#fffbeb",
                  borderRadius:10,border:`1px solid #fde68a`,
                  display:"flex",flexDirection:"column"}}>
                  <div style={{fontSize:10,color:"#92400e",textTransform:"uppercase",
                    letterSpacing:".08em",fontWeight:700,marginBottom:6}}>DRY · Stock</div>
                  <div style={{fontSize:22,fontWeight:800,color:"#78350f",marginBottom:6}}>
                    {fmtK(dry)}
                  </div>
                  <div style={{fontSize:10.5,color:T.warn,marginTop:"auto"}}>
                    {tot>0?(dry/tot*100).toFixed(0):0}% of total
                  </div>
                </div>
              </div>

              {/* Horizontal stacked bar PO+DRY */}
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",height:24,borderRadius:8,
                  overflow:"hidden",background:T.grid}}>
                  <div style={{width:tot>0?(po/tot*100)+"%":"0%",
                    background:T.po,display:"flex",alignItems:"center",
                    justifyContent:"center",transition:"width .5s",
                    minWidth:po>0?"40px":"0"}}>
                    {po>0&&tot>0&&(po/tot)>.12&&(
                      <span style={{fontSize:10,fontWeight:700,
                        color:"#1e3a5f",whiteSpace:"nowrap"}}>{fmtK(po)}</span>
                    )}
                  </div>
                  <div style={{width:tot>0?(dry/tot*100)+"%":"0%",
                    background:T.dry,display:"flex",alignItems:"center",
                    justifyContent:"center",transition:"width .5s",
                    minWidth:dry>0?"40px":"0"}}>
                    {dry>0&&tot>0&&(dry/tot)>.12&&(
                      <span style={{fontSize:10,fontWeight:700,
                        color:"#78350f",whiteSpace:"nowrap"}}>{fmtK(dry)}</span>
                    )}
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",
                  marginTop:5,fontSize:10.5}}>
                  <span style={{color:"#3b82f6",fontWeight:600}}>
                    PO {tot>0?Math.round(po/tot*100):0}%
                  </span>
                  <span style={{fontWeight:800,color:e.color,fontSize:12}}>
                    {fmtK(tot)}
                  </span>
                  <span style={{color:T.warn,fontWeight:600}}>
                    DRY {tot>0?Math.round(dry/tot*100):0}%
                  </span>
                </div>
              </div>

              {/* CHF/coverert */}
              <div style={{
                padding:"11px 14px",borderRadius:10,
                background:earlyOpen?"#fffbeb":overC&&parseFloat(chfC)>0?"#fdf2f0":T.bg,
                border:`1px solid ${earlyOpen?"#fde68a":overC&&parseFloat(chfC)>0?"#e8a09a":T.line}`,
                marginBottom:12,
              }}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:earlyOpen?6:0}}>
                  <div>
                    <Label style={{marginBottom:3}}>Moyenne {QL} · {cov} couverts depuis {QSTART_LABEL}</Label>
                    <div style={{fontSize:22,fontWeight:800,
                      color:earlyOpen?T.warn:overC&&parseFloat(chfC)>0?T.err:T.ok}}>
                      {cov>0?`CHF ${chfC}`:"—"}
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <Label style={{marginBottom:3}}>Référence</Label>
                    <div style={{fontSize:20,fontWeight:700,color:T.hint}}>
                      CHF {e.ref}
                    </div>
                  </div>
                </div>

                {/* Ligne 2 — Engagé semaine courante (incl. quotes + drafts) */}
                {covWeek>0 && chfE!=="—" && typeof eng === "number" && (
                  <div style={{
                    marginTop:10,paddingTop:10,
                    borderTop:`1px dashed ${T.line}`,
                    display:"flex",justifyContent:"space-between",alignItems:"center"
                  }}>
                    <div>
                      <Label style={{marginBottom:3,fontSize:9.5}}>
                        Semaine courante · {covWeek} couverts ({_weekLabel})
                      </Label>
                      <div style={{fontSize:16,fontWeight:700,
                        color:parseFloat(chfE)>e.ref?T.warn:T.ok}}>
                        CHF {chfE}
                        <span style={{fontSize:11,fontWeight:500,color:T.hint,marginLeft:8}}>
                          (engagé incl. quotes + drafts)
                        </span>
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <Label style={{marginBottom:3,fontSize:9.5}}>Total semaine</Label>
                      <div style={{fontSize:13,fontWeight:600,color:T.ink}}>
                        {fmtK(eng)}
                      </div>
                    </div>
                  </div>
                )}
                {earlyOpen&&(
                  <div style={{marginTop:8,padding:"14px 16px",borderRadius:12,
                    background:"linear-gradient(135deg,#fef2f2,#fff1f2)",
                    border:"2px solid #e8a09a",position:"relative"}}>
                    {/* Danger icon */}
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                      <svg width="38" height="34" viewBox="0 0 38 34" style={{flexShrink:0}}>
                        <path d="M19 1L1 33h36L19 1z" fill="#FACC15" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round"/>
                        <circle cx="19" cy="20" r="3" fill="none" stroke="#1a1a1a" strokeWidth="2"/>
                        <ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(45,19,20)"/>
                        <ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(-45,19,20)"/>
                      </svg>
                      <div style={{fontSize:14,fontWeight:800,color:"#b2382f",lineHeight:1.2}}>
                        OVERSPEND ALERT
                      </div>
                    </div>
                    {/* Projected vs budget - big numbers */}
                    <div style={{display:"flex",gap:12,marginBottom:8}}>
                      <div style={{flex:1,padding:"10px 12px",background:"#fff",borderRadius:8,
                        border:"1px solid #fca5a5"}}>
                        <div style={{fontSize:10,fontWeight:600,color:"#8a2520",textTransform:"uppercase",
                          letterSpacing:".08em",marginBottom:4}}>Projected {QL}</div>
                        <div style={{fontSize:22,fontWeight:900,color:"#b2382f"}}>
                          CHF {(ep/1000).toFixed(1)}k
                        </div>
                      </div>
                      <div style={{flex:1,padding:"10px 12px",background:"#fff",borderRadius:8,
                        border:"1px solid #d1d5db"}}>
                        <div style={{fontSize:10,fontWeight:600,color:"#6b7280",textTransform:"uppercase",
                          letterSpacing:".08em",marginBottom:4}}>Budget {QL}</div>
                        <div style={{fontSize:22,fontWeight:900,color:"#374151"}}>
                          CHF {(e.budget/1000).toFixed(1)}k
                        </div>
                      </div>
                    </div>
                    <div style={{fontSize:13,fontWeight:800,color:"#b2382f",textAlign:"center",
                      padding:"6px 10px",background:"#fce0dc",borderRadius:6}}>
                      {ep>e.budget ? `+${Math.round((ep/e.budget-1)*100)}% · +CHF ${((ep-e.budget)/1000).toFixed(1)}k overspend` : "Within budget"}
                    </div>
                    <div style={{fontSize:10,color:"#92400e",marginTop:8,textAlign:"center"}}>
                      Opened W2 — initial stocking included · representative data from W3
                    </div>
                  </div>
                )}
              </div>

              {/* ── Breakdown hebdo W0 → semaine courante ────── */}
              {(() => {
                // Mapping W0/W1/W2/... vers intervalle de jours Q2 (day 1 = 1er avril 2026 mercredi)
                // W0 = 1-5 (mer-dim 1-5 avr), W1 = 6-12, W2 = 13-19, W3 = 20-26, ...
                const weekRanges = [
                  {id:"W0", start:1,  end:5},
                  {id:"W1", start:6,  end:12},
                  {id:"W2", start:13, end:19},
                  {id:"W3", start:20, end:26},
                  {id:"W4", start:27, end:33},
                  {id:"W5", start:34, end:40},
                  {id:"W6", start:41, end:47},
                  {id:"W7", start:48, end:54},
                  {id:"W8", start:55, end:61},
                  {id:"W9", start:62, end:68},
                  {id:"W10",start:69, end:75},
                  {id:"W11",start:76, end:82},
                  {id:"W12",start:83, end:89},
                  {id:"W13",start:90, end:91},
                ];
                // Garder uniquement W0 → semaine courante (inclus)
                const currentWeekIdx = weekRanges.findIndex(w => days >= w.start && days <= w.end);
                if (currentWeekIdx < 0) return null;
                const wksToShow = weekRanges.slice(0, currentWeekIdx+1);

                // Pour chaque semaine, lire CHF + couverts DIRECTEMENT depuis /weekly_reports
                // SOURCE DE VÉRITÉ UNIQUE — mêmes chiffres que le tab Weekly Reports
                // (q2_weekly avait des écarts subtils sur les bornes datetime CUTOFF)
                const wrptWeeks = (wrpt?.weeks || []).filter(w => w.q === "q2");
                const rows = wksToShow.map(wr => {
                  const wkObj  = wrptWeeks.find(w => w.w === wr.id);
                  const stream = wkObj?.streams?.[e.key];
                  const chf    = stream?.total || 0;          // PO + DRY confirmés
                  const cv     = wkObj?.covers?.[e.key] || 0; // full-week covers
                  const chfCov = cv > 0 ? chf/cv : 0;
                  const isCurrent = wr.id === wksToShow[wksToShow.length-1].id;
                  return {label:wr.id, chf, cv, chfCov, isCurrent};
                });
                const maxChf = Math.max(...rows.map(r=>r.chf), 1);
                const chartH = 54;
                return (
                  <div style={{marginTop:14,padding:"10px 12px 12px",borderRadius:10,
                    background:"#fafaf7",border:`1px solid ${T.line}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <Label style={{margin:0}}>Évolution hebdo · W0 → aujourd'hui</Label>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:10,fontWeight:800,color:"#fff",background:T.ink,
                          padding:"2px 7px",borderRadius:3,letterSpacing:".02em"}}>
                          ref CHF {e.ref.toFixed(2)}
                        </span>
                        <span style={{fontSize:9.5,color:T.hint}}>
                          barre = CHF · chiffre = CHF/cov
                        </span>
                      </div>
                    </div>
                    {/* Chart */}
                    <div style={{display:"flex",alignItems:"flex-end",gap:6,height:chartH+38,
                      paddingTop:16,paddingBottom:4,position:"relative"}}>
                      {rows.map((r,i) => {
                        const h = r.chf > 0 ? Math.max((r.chf/maxChf)*chartH, 3) : 2;
                        const overRef = r.chfCov > e.ref && r.chfCov > 0;
                        // Couleur barre = strictement basée sur CHF/cov vs ref
                        // Vert si ≤ ref, rouge si > ref, gris si pas de service
                        const barCol = r.cv === 0 ? T.grid
                          : overRef ? T.err
                          : T.ok;
                        return (
                          <div key={i} style={{flex:1,display:"flex",flexDirection:"column",
                            alignItems:"center",minWidth:0}}>
                            {/* Label CHF/cov au-dessus */}
                            <div style={{fontSize:11,fontWeight:800,
                              color:r.cv===0?T.hint:overRef?T.err:T.ok,
                              marginBottom:3,whiteSpace:"nowrap"}}>
                              {r.cv > 0 ? r.chfCov.toFixed(2) : "—"}
                            </div>
                            {/* Barre */}
                            <div title={`${r.label}: CHF ${r.chf.toFixed(0)} · ${r.cv} couv · ${r.cv>0?r.chfCov.toFixed(2)+' CHF/cov':'pas de service'}`}
                              style={{width:"100%",height:h,background:barCol,
                                borderRadius:"3px 3px 0 0",transition:"height .5s",
                                outline:r.isCurrent && r.cv>0 ? `2px solid ${T.ink}` : "none",
                                outlineOffset:r.isCurrent && r.cv>0 ? "-1px" : "0"}}/>
                            {/* Label semaine */}
                            <div style={{fontSize:9.5,fontWeight:r.isCurrent?800:600,
                              color:r.isCurrent?T.ink:T.hint,marginTop:4}}>
                              {r.label}{r.isCurrent ? " ●" : ""}
                            </div>
                            {/* CHF total en mini */}
                            <div style={{fontSize:8.5,color:T.hint,opacity:.8}}>
                              {r.chf > 0 ? fmtK(r.chf) : "—"}
                            </div>
                          </div>
                        );
                      })}
                      {/* Ligne ref CHF/cov — fine, en haut, ne masque rien */}
                      {rows.some(r=>r.chfCov>0) && (() => {
                        const maxCovVal = Math.max(...rows.map(r=>r.chfCov), e.ref);
                        const refY = (e.ref/maxCovVal)*chartH;
                        return (
                          <div style={{position:"absolute",left:0,right:0,
                            bottom:32+refY,borderTop:`1.5px dashed ${T.ink}`,
                            opacity:.55,pointerEvents:"none",zIndex:1}}/>
                        );
                      })()}
                    </div>
                    {/* Légende — alignée sur le code des couleurs */}
                    <div style={{display:"flex",gap:14,fontSize:9.5,color:T.hint,marginTop:2,flexWrap:"wrap"}}>
                      <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                        <span style={{width:10,height:8,borderRadius:2,background:T.ok,display:"inline-block"}}/>
                        sous référence
                      </span>
                      <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                        <span style={{width:10,height:8,borderRadius:2,background:T.err,display:"inline-block"}}/>
                        au-dessus de référence
                      </span>
                      <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                        <span style={{width:10,height:0,borderTop:`1.5px dashed ${T.ink}`,display:"inline-block"}}/>
                        ligne référence
                      </span>
                      <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                        <span style={{width:10,height:8,borderRadius:2,border:`2px solid ${T.ink}`,display:"inline-block"}}/>
                        semaine courante
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* ── PACING HEBDOMADAIRE ── */}
              {(()=>{
                const WT = e.key==="lv" ? 12 : 10;      // total Q2 weeks for this entity
                const FW = e.key==="lv" ? 0 : 2;         // first week (W0 for LV, W2 premiums)
                const FED = e.key==="lv" ? 5 : 19;       // end-day of first full week
                const wDone  = days>=FED ? 1+Math.floor((days-FED)/7) : 0;
                const wDoneC = Math.min(wDone, WT);
                const wRem   = Math.max(0, WT - wDoneC);
                const theoW  = e.budget / WT;            // budget théo / semaine
                const remB   = Math.max(e.budget - tot, 0);
                const uniW   = wRem>0 ? remB/wRem : 0;
                const pStart = uniW*0.70, pEnd = uniW*1.30;
                const expectedSoFar = theoW * wDoneC;
                const flagged = tot > expectedSoFar && wDoneC>0;
                const flagWeek = flagged ? `W${FW + wDoneC - 1}` : null;
                const deficitPct = theoW>0 ? ((uniW/theoW-1)*100) : 0;
                // Chart scale
                const chartH = 46;
                const maxY = Math.max(tot, theoW*1.1, uniW, pEnd)*1.05 || 1;
                const toH = (v) => Math.max((v/maxY)*chartH, v>0?2:0);
                const theoY = (theoW/maxY)*chartH;
                const nBars = Math.min(wRem, 9);  // max 9 projection bars
                const futureLabels = [];
                for (let i=0;i<nBars;i++) futureLabels.push(`W${FW+wDoneC+i}`);
                return (
                  <div style={{marginTop:14,padding:"10px 12px 12px",borderRadius:10,
                    background:"#fafaf7",border:`1px solid ${T.line}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <Label style={{margin:0}}>Pacing Q2 · {WT} semaines</Label>
                      <span style={{fontSize:9.5,fontWeight:700,
                        color:flagged?T.err:T.ok,
                        background:flagged?"#fdf2f0":"#f0fdf4",
                        padding:"2px 7px",borderRadius:10,border:`1px solid ${flagged?"#e8a09a":"#bbf7d0"}`}}>
                        {flagged?`⚠ Flagged ${flagWeek}`:"✓ On track"}
                      </span>
                    </div>
                    {/* Chart */}
                    <div style={{position:"relative",height:chartH+14,marginBottom:8}}>
                      {/* theo dashed line */}
                      {theoW>0 && (
                        <div style={{position:"absolute",bottom:14+theoY-1,left:0,right:0,
                          borderTop:`1px dashed ${T.hint}`,zIndex:3,pointerEvents:"none"}}>
                          <span style={{fontSize:8,color:T.hint,position:"absolute",
                            right:0,top:-7,background:"#fafaf7",padding:"0 3px",fontWeight:600}}>
                            theo {(theoW/1000).toFixed(1)}k
                          </span>
                        </div>
                      )}
                      {/* Bars row */}
                      <div style={{display:"flex",alignItems:"flex-end",gap:2,height:chartH,
                        position:"absolute",bottom:14,left:0,right:0}}>
                        {/* Bar 1: actual to date */}
                        <div style={{flex:1.2,display:"flex",flexDirection:"column",
                          alignItems:"center",justifyContent:"flex-end",height:"100%"}}>
                          <div style={{fontSize:8.5,fontWeight:700,
                            color:flagged?T.err:T.ok,marginBottom:1,lineHeight:1}}>
                            {(tot/1000).toFixed(1)}
                          </div>
                          <div style={{width:"100%",height:toH(tot),
                            background:flagged?T.err:T.ok,borderRadius:"3px 3px 0 0"}}/>
                        </div>
                        {/* Separator */}
                        <div style={{width:1,height:"100%",background:T.line,margin:"0 2px"}}/>
                        {/* Projection bars */}
                        {futureLabels.map((_,i)=>{
                          const pH = nBars>1
                            ? pStart + (pEnd-pStart)*(i/(nBars-1))
                            : uniW;
                          return (
                            <div key={i} style={{flex:1,position:"relative",height:"100%"}}>
                              {/* uniform bar (grey) */}
                              <div style={{position:"absolute",bottom:0,left:0,right:0,
                                height:toH(uniW),background:"#cbd5e1",borderRadius:"2px 2px 0 0"}}/>
                              {/* progressive top marker (orange) */}
                              <div style={{position:"absolute",bottom:toH(pH)-1.5,
                                left:-1,right:-1,height:2.5,background:T.warn,
                                borderRadius:1,zIndex:2}}/>
                            </div>
                          );
                        })}
                      </div>
                      {/* Labels row */}
                      <div style={{display:"flex",gap:2,position:"absolute",bottom:0,left:0,right:0,height:12}}>
                        <div style={{flex:1.2,textAlign:"center",fontSize:8,color:T.hint,fontWeight:700}}>
                          À date
                        </div>
                        <div style={{width:1,margin:"0 2px"}}/>
                        {futureLabels.map((l,i)=>(
                          <div key={i} style={{flex:1,textAlign:"center",fontSize:8,
                            color:T.hint,fontWeight:600}}>{l}</div>
                        ))}
                      </div>
                    </div>
                    {/* 2-col summary */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                      <div style={{padding:"6px 8px",background:"#fff",borderRadius:6,
                        border:`1px solid #cbd5e1`}}>
                        <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
                          <span style={{width:8,height:8,background:"#cbd5e1",borderRadius:2}}/>
                          <span style={{fontSize:8.5,color:T.hint,textTransform:"uppercase",
                            letterSpacing:".08em",fontWeight:700}}>Uniforme</span>
                        </div>
                        <div style={{fontSize:13,fontWeight:800,color:T.ink,lineHeight:1.2}}>
                          CHF {(uniW/1000).toFixed(2)}k/sem
                        </div>
                        <div style={{fontSize:9.5,color:deficitPct<0?T.err:T.ok,fontWeight:600}}>
                          {deficitPct>=0?"+":""}{deficitPct.toFixed(0)}% vs theo
                        </div>
                      </div>
                      <div style={{padding:"6px 8px",background:"#fff",borderRadius:6,
                        border:`1px solid ${T.warn}`}}>
                        <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
                          <span style={{width:8,height:2.5,background:T.warn,borderRadius:1}}/>
                          <span style={{fontSize:8.5,color:T.hint,textTransform:"uppercase",
                            letterSpacing:".08em",fontWeight:700}}>Progressif</span>
                        </div>
                        <div style={{fontSize:13,fontWeight:800,color:T.ink,lineHeight:1.2}}>
                          {(pStart/1000).toFixed(2)}→{(pEnd/1000).toFixed(2)}k
                        </div>
                        <div style={{fontSize:9.5,color:T.hint}}>
                          serré → relâché
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Budget bar */}
              <div style={{marginTop:"auto",paddingTop:12}}>
              <Bar value={tot} max={e.budget} color={over?T.err:e.color} height={6}/>
              <div style={{fontSize:10.5,color:T.hint,marginTop:4}}>
                {pctB.toFixed(1)}% of Q2 budget used
              </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* ── CHART HEBDOMADAIRE W0-W4 : Q1 vs Q2 PO+DRY ── */}
      {hasW&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>

          {/* Chart gauche : Q2 PO+DRY per week */}
          <Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
              <div>
                <Label>Total spend per week {QL}</Label>
                <div style={{fontSize:16,fontWeight:700,color:T.ink}}>PO + DRY · CHF</div>
              </div>
              <div style={{display:"flex",gap:12}}>
                {[[T.po,"PO"],[T.dry,"DRY"]].map(([c,l])=>(
                  <span key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.hint}}>
                    <span style={{width:10,height:10,borderRadius:3,background:c,display:"inline-block"}}/>
                    {l}
                  </span>
                ))}
              </div>
            </div>
            {(()=>{
              const maxV=Math.max(...weeks.map(w=>w.total),1);
              const H=160;
              return (
                <div>
                  <div style={{position:"relative",height:H,marginBottom:10}}>
                    {[.25,.5,.75,1].map(p=>(
                      <div key={p} style={{position:"absolute",bottom:p*H,left:0,right:0,
                        zIndex:0,pointerEvents:"none",borderTop:`1px solid ${T.grid}`}}>
                        <span style={{fontSize:8.5,color:T.ghost,position:"absolute",
                          left:-2,transform:"translateY(-50%)"}}>{((maxV*p)/1000).toFixed(0)}k</span>
                      </div>
                    ))}
                    <div style={{position:"relative",zIndex:1,display:"flex",
                      alignItems:"flex-end",height:"100%",gap:8,paddingLeft:26}}>
                      {weeks.map((w,i)=>{
                        const tot=w.total||0,po=w.po||0,dry=w.dry||0;
                        const bH=tot>0?Math.max((tot/maxV)*H,4):2;
                        const pH=tot>0?(po/tot)*bH:0;
                        const dH=tot>0?(dry/tot)*bH:0;
                        return (
                          <div key={i} style={{flex:1,display:"flex",flexDirection:"column",
                            alignItems:"center",justifyContent:"flex-end",height:"100%"}}>
                            {tot>0&&<div style={{fontSize:10,fontWeight:700,color:T.ink,marginBottom:4}}>{(tot/1000).toFixed(0)}k</div>}
                            <div style={{width:"100%",height:bH,display:"flex",
                              flexDirection:"column",borderRadius:"6px 6px 0 0",overflow:"hidden"}}>
                              <div style={{width:"100%",height:dH,background:T.dry,flexShrink:0}}/>
                              <div style={{width:"100%",height:pH,background:T.po, flexShrink:0}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,paddingLeft:26}}>
                    {weeks.map((w,i)=>(
                      <div key={i} style={{flex:1,textAlign:"center",fontSize:10.5,fontWeight:600,color:T.hint}}>{w.week}</div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </Card>

          {/* Chart droit : Q1 vs Q2 horizontal */}
          <Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
              <div>
                <Label>Comparaison {QPREV} vs {QL}</Label>
                <div style={{fontSize:16,fontWeight:700,color:T.ink}}>Semaines W0 → {weeks.length>0?weeks[weeks.length-1].week:"…"}</div>
              </div>
              <div style={{display:"flex",gap:12}}>
                <span style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.hint}}>
                  <span style={{width:14,height:8,borderRadius:2,background:"#94a3b8",display:"inline-block"}}/>{QPREV}
                </span>
                <span style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.hint}}>
                  <span style={{width:14,height:8,borderRadius:2,background:T.g700,display:"inline-block"}}/>{QL}
                </span>
              </div>
            </div>
            {(()=>{
              // Toutes les semaines Q2 écoulées (alignées sur le chart de gauche) — fini W0→W4 figé
              const q2vis=weeks.filter(w=>(w.total||0)>0);
              const q1map={};
              (compare||[]).forEach(w=>{ q1map[w.week]=w.q1; });
              const maxV=Math.max(
                ...q2vis.map(w=>w.total||0),
                ...Object.values(q1map).map(d=>d?.total||0),
                1
              );
              return (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  {q2vis.map((w,i)=>{
                    const q1=q1map[w.week];
                    const q1v=q1?.total||0;
                    const q2v=w.total||0;
                    const q1w=(q1v/maxV*100).toFixed(1)+"%";
                    const q2w=(q2v/maxV*100).toFixed(1)+"%";
                    return (
                      <div key={i}>
                        <div style={{fontSize:11,fontWeight:700,color:T.sub,marginBottom:6}}>{w.week}</div>
                        {/* Q1 */}
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:5}}>
                          <span style={{fontSize:10,color:T.hint,width:18,textAlign:"right",flexShrink:0}}>{QPREV}</span>
                          <div style={{flex:1,height:16,background:T.grid,borderRadius:4,overflow:"hidden"}}>
                            <div style={{width:q1w,height:"100%",background:"#94a3b8",borderRadius:4,transition:"width .5s"}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:600,color:T.hint,width:32,flexShrink:0}}>
                            {q1v>0?Math.round(q1v/1000)+"k":"—"}
                          </span>
                        </div>
                        {/* Q2 */}
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <span style={{fontSize:10,color:T.sub,fontWeight:600,width:18,textAlign:"right",flexShrink:0}}>{QL}</span>
                          <div style={{flex:1,height:16,background:T.grid,borderRadius:4,overflow:"hidden"}}>
                            <div style={{width:q2w,height:"100%",background:T.g700,borderRadius:4,transition:"width .5s"}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:700,color:T.ink,width:32,flexShrink:0}}>
                            {q2v>0?Math.round(q2v/1000)+"k":"—"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </Card>
        </div>
      )}

      {/* ── CHEFS ─────────────────────────────────────── */}
      {topChefs.length>0&&(
        <Card>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",marginBottom:20}}>
            <div>
              <Label>Spend by chef — Q2</Label>
              <div style={{fontSize:16,fontWeight:700,color:T.ink}}>PO + DRY depuis le 1er avril</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 32px"}}>
            {topChefs.map(([chef,chf],i)=>{
              const maxV=topChefs[0][1];
              const pal=[T.g700,"#3b82f6",T.sk,T.fi,"#14b8a6",T.warn,"#ec4899",T.hint];
              const col=pal[i%pal.length];
              return (
                <div key={chef} style={{padding:"8px 0",
                  borderBottom:i<topChefs.length-2?`1px solid ${T.line}`:"none"}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    marginBottom:5,fontSize:12.5}}>
                    <span style={{fontWeight:500,color:T.sub}}>{chef}</span>
                    <b style={{color:T.ink}}>{fmtK(chf)}</b>
                  </div>
                  <Bar value={chf} max={maxV} color={col} height={6}/>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Note */}
      <div style={{padding:"10px 16px",background:T.bg,border:`1px solid ${T.line}`,
        borderRadius:10,fontSize:11,color:T.hint,marginTop:14}}>
        <b style={{color:T.sub}}>Method:</b> Confirmed PO (excl. economat stock buys Saviva/Pistor/Bevanar/Moga/NovaDelta + Webstar) + DRY. Covers/wk : LV 2'585 · SK 480 · FI 176 · FD 60. Achats de stock économat (place vide / fournisseurs stock) exclus du PO, comptés en DRY à la sortie.
      </div>
    </div>
  );
}


// ─── PREMIUM RESTAURANTS TAB ──────────────────────────────────────────────

// ─── Contexte trimestriel partagé (Premium Restaurants + Lake View) ─────────
// Même source que Q4 Live : /dashboard/q2_tracker (dépense à date, budgets,
// couverts) + /planning (couverts jour par jour). Les cartes mensuelles
// viennent de /dashboard/premium_monthly_detail, filtrées sur le trimestre.
const QR_MONTHS = {1:[1,2,3], 2:[4,5,6], 3:[7,8,9], 4:[10,11,12]};
const QR_CUR = Math.floor(new Date().getMonth() / 3) + 1;
const QR_REF = {lv:5, fd:15, fi:20, sk:12};

function useQuarterCtx(q) {
  const [trk, setTrk] = useState(null);
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    let alive = true; setTrk(null); setPlan(null);
    if (q >= 2 && q <= 4) {
      fetch(`${API}/dashboard/q2_tracker?quarter=${q}`).then(r=>r.json())
        .then(d => { if (alive && d && !d.error) setTrk(d); }).catch(()=>{});
    }
    if (q >= 3) {
      fetch(`${API}/planning?quarter=${q}`).then(r=>r.json())
        .then(d => { if (alive && d && d.weeks) setPlan(d); }).catch(()=>{});
    }
    return () => { alive = false; };
  }, [q]);
  return { trk, plan };
}

function QuarterPicker({ q, setQ }) {
  const opts = [4,3,2,1].filter(n => n <= QR_CUR).map(n => ({v:n, l:`Q${n} · ${n===QR_CUR?"Live":"Closed"}`}));
  opts.push({v:0, l:"Année"});
  return (
    <div style={{display:"inline-flex",gap:4,padding:4,background:T.bg2||T.bg,border:`1px solid ${T.line}`,borderRadius:10}}>
      {opts.map(o => (
        <button key={o.v} onClick={()=>setQ(o.v)}
          style={{padding:"6px 13px",fontSize:12,fontWeight:q===o.v?700:500,cursor:"pointer",
            background:q===o.v?T.card:"transparent",color:q===o.v?T.ink:T.sub,
            border:q===o.v?`1px solid ${T.line}`:"1px solid transparent",borderRadius:7}}>{o.l}</button>
      ))}
    </div>
  );
}

// chiffres d'un restaurant pour le trimestre (tracker = même calcul que Q4 Live)
function qrStats(trk, key) {
  if (!trk) return null;
  const e = (trk.by_entity||{})[key] || {};
  const cov = (trk.covers_total||{})[key];
  const ctd = (trk.covers_to_date||{})[key];
  const budget = e.q2_budget_a || 0;
  const covTot = cov != null ? cov : (QR_REF[key] ? Math.round(budget / QR_REF[key]) : 0);
  const spent = e.committed || 0;
  const covTd = ctd != null ? ctd : (trk.closed ? covTot : null);
  return { spent, po:e.po||0, dry:e.dry||0, budget, pace:e.q2_pace||0, covTot, covTd,
           chfCov: covTd ? spent / covTd : null,
           weeks: Math.max(1, Math.round((trk.quarter_days || 84) / 7)),
           closed: !!trk.closed, days: trk.days_elapsed, qDays: trk.quarter_days || 84 };
}


function PremiumTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(QR_CUR);
  const { trk } = useQuarterCtx(q);

  useEffect(() => {
    fetch(API + "/dashboard/premium_monthly_detail")
      .then(r => r.json())
      .then(d => { setData(d.data || null); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const RESTAURANTS = [
    {key:"fd", label:"Fine Dining", icon:"🍽", color:T.fd, chef:"Axel Klaes", ref:15},
    {key:"fi", label:"Food Inspired", icon:"🌿", color:T.fi, chef:"David Alvarez", ref:20},
    {key:"sk", label:"Soul Kitchen", icon:"🍲", color:T.sk, chef:"Sara, Rando et Pierre", ref:12},
  ];

  const CAT_COLORS = {
    PROTEINS:{b:T.fd,t:T.fd}, VEGETABLES_FRUITS:{b:T.lv,t:T.lv},
    DAIRY:{b:"#3b82f6",t:"#1d4ed8"}, "SPECIAL ITEM":{b:T.sk,t:T.sk},
  };

  if (loading) return (
    <div style={{textAlign:"center",padding:60,color:T.hint}}>
      <div style={{width:24,height:24,border:"3px solid "+T.line,borderTopColor:"transparent",
        borderRadius:"50%",display:"inline-block",animation:"spin 0.8s linear infinite"}}/>
      <div style={{marginTop:10}}>Loading Odoo data…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!data) return (
    <div style={{textAlign:"center",padding:60,color:T.hint}}>
      Unable to load data. Check API connection.
    </div>
  );

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:16}}>
        <div style={{fontSize:12,color:T.hint2||T.hint,maxWidth:620}}>
          Live Odoo data — confirmed PO + DRY (internal transfers) by restaurant and month.
          Categories based on <b>product.category</b> Odoo. Top 3 = highest value products ordered.
          {q>0 && <> Budget & couverts {`Q${q}`} = Restaurants Planning (même calcul que {`Q${q}`} Live).</>}
        </div>
        <QuarterPicker q={q} setQ={setQ}/>
      </div>

      {RESTAURANTS.map(r => {
        const months = (data[r.key] || []).filter(m => !q || (QR_MONTHS[q]||[]).includes(m.month));
        const st = q ? qrStats(trk, r.key) : null;
        const totalAll = months.reduce((s,m) => s + m.total, 0);
        const totalPO = months.reduce((s,m) => s + m.po, 0);
        const totalDRY = months.reduce((s,m) => s + m.dry, 0);
        const totalTR  = months.reduce((s,m) => s + (m.transfer||0), 0);

        return (
          <Card key={r.key} style={{marginBottom:20,borderLeft:`4px solid ${r.color}`,padding:"20px 24px"}}>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <div style={{fontSize:18,fontWeight:800,color:T.ink,marginBottom:3}}>
                  {r.icon} {r.label}
                </div>
                <div style={{fontSize:11.5,color:T.hint}}>
                  👨‍🍳 {r.chef} · ref CHF {r.ref}/cover
                  {st && <> · {fmtN(st.covTot)} couv. {`Q${q}`} ({fmtN(Math.round(st.covTot/st.weeks))}/sem moy.)</>}
                </div>
                {st && (
                  <div style={{display:"flex",gap:16,flexWrap:"wrap",marginTop:8,fontSize:12}}>
                    <span style={{color:T.sub}}>Budget {`Q${q}`} <b style={{color:T.ink}}>{fmtK(st.budget)}</b></span>
                    <span style={{color:T.sub}}>{st.closed?"Dépensé":"Dépensé à date"} <b style={{color:T.ink}}>{fmtK(st.spent)}</b>
                      {st.budget>0 && <span style={{color:T.hint}}> · {Math.round(100*st.spent/st.budget)} %</span>}</span>
                    <span style={{color:T.sub}}>CHF/couvert <b style={{color:st.chfCov==null?T.hint:(st.chfCov<=r.ref?T.ok:T.err)}}>
                      {st.chfCov==null?"—":st.chfCov.toFixed(2)}</b><span style={{color:T.hint}}> / {r.ref}</span>
                      {!st.closed && st.covTd===0 && <span style={{color:T.hint}}> (pas encore de service — mise en place)</span>}</span>
                  </div>
                )}
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:28,fontWeight:900,color:r.color,lineHeight:1}}>
                  {fmtK(totalAll)}
                </div>
                <div style={{fontSize:10.5,color:T.hint,marginTop:2}}>
                  PO {fmtK(totalPO)} + DRY {fmtK(totalDRY)}
                  {totalTR!==0 && <span style={{color:totalTR>0?T.ok:T.warn,fontWeight:700}}> ⇄ {totalTR>0?"+":"−"}{fmtK(Math.abs(totalTR))} FD→LV</span>}
                </div>
              </div>
            </div>

            {/* Monthly grid — défilement horizontal au-delà de la largeur (vue Année) */}
            {months.length===0 && <div style={{fontSize:12,color:T.hint,padding:"8px 0"}}>Aucune dépense sur la période.</div>}
            <div style={{overflowX:"auto",paddingBottom:4}}>
            <div style={{display:"grid",gridTemplateColumns:`repeat(${q ? 3 : months.length},minmax(210px,1fr))`,gap:12,minWidth:"min-content"}}>
              {months.map(mo => {
                const maxCat = mo.categories.length > 0
                  ? Math.max(...mo.categories.map(c => c.chf)) : 1;
                return (
                  <div key={mo.month} style={{background:T.bg,borderRadius:12,
                    border:`1px solid ${T.line}`,padding:"14px",
                    display:"flex",flexDirection:"column"}}>

                    {/* Month header */}
                    <div style={{display:"flex",justifyContent:"space-between",
                      alignItems:"baseline",marginBottom:10}}>
                      <span style={{fontSize:13,fontWeight:700,color:T.ink}}>{mo.label}</span>
                      <span style={{fontSize:18,fontWeight:900,color:r.color}}>{fmtK(mo.total)}</span>
                    </div>

                    {/* PO / DRY mini split */}
                    {(()=>{const base=(mo.po+mo.dry)||1; return (
                    <div style={{display:"flex",height:6,borderRadius:3,overflow:"hidden",
                      background:T.grid,marginBottom:8}}>
                      <div style={{width:(mo.po/base*100)+"%",
                        background:T.po,borderRadius:"3px 0 0 3px"}}/>
                      <div style={{width:(mo.dry/base*100)+"%",
                        background:T.dry,borderRadius:"0 3px 3px 0"}}/>
                    </div>);})()}
                    <div style={{display:"flex",justifyContent:"space-between",
                      fontSize:9.5,color:T.hint,marginBottom:mo.transfer?4:12}}>
                      <span>PO {fmtK(mo.po)}</span>
                      <span>DRY {fmtK(mo.dry)}</span>
                    </div>
                    {!!mo.transfer && (
                      <div style={{fontSize:9.5,fontWeight:700,marginBottom:12,
                        color:mo.transfer>0?T.ok:T.warn}}>
                        ⇄ transfert FD→LV {mo.transfer>0?"+":"−"}{fmtK(Math.abs(mo.transfer))}
                      </div>
                    )}

                    {/* Categories */}
                    <div style={{marginBottom:12,minHeight:90}}>
                      <div style={{fontSize:9,fontWeight:700,color:T.hint,textTransform:"uppercase",
                        letterSpacing:".08em",marginBottom:6}}>PO Categories</div>
                      {[
                        {key:"PROTEINS",label:"Proteins"},
                        {key:"VEGETABLES_FRUITS",label:"Vegetables & Fruits"},
                        {key:"DAIRY",label:"Dairy"},
                        {key:"SPECIAL ITEM",label:"Special items"},
                      ].map(def => {
                        const cat = mo.categories.find(c=>c.key===def.key)||{chf:0,pct:0};
                        const col = CAT_COLORS[def.key]||{b:T.hint,t:T.hint};
                        return (
                          <div key={def.key} style={{marginBottom:4}}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:10}}>
                              <span style={{color:col.t,fontWeight:600}}>{def.label}</span>
                              <span style={{color:col.t,fontWeight:700}}>
                                {cat.chf>0?fmtK(cat.chf):"—"}
                              </span>
                            </div>
                            <div style={{height:3,background:T.grid,borderRadius:2,marginTop:2}}>
                              <div style={{height:"100%",width:(cat.pct||0)+"%",
                                background:col.b,borderRadius:2,maxWidth:"100%"}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Top 3 products */}
                    <div style={{marginTop:"auto"}}>
                      <div style={{fontSize:9,fontWeight:700,color:T.hint,textTransform:"uppercase",
                        letterSpacing:".08em",marginBottom:6}}>Top 3 products</div>
                      {mo.top_products.length > 0 ? mo.top_products.map((p,i) => {
                        const shortName = p.name.replace(/\[.*?\]\s*/,"").substring(0,30);
                        return (
                          <div key={i} style={{display:"flex",justifyContent:"space-between",
                            alignItems:"center",padding:"4px 0",
                            borderBottom:i<2?`1px solid ${T.line}`:"none",fontSize:10}}>
                            <div style={{display:"flex",alignItems:"center",gap:5,flex:1,minWidth:0}}>
                              <span style={{color:r.color,fontWeight:800,fontSize:11}}>{i+1}</span>
                              <span style={{color:T.ink,whiteSpace:"nowrap",overflow:"hidden",
                                textOverflow:"ellipsis"}}>{shortName}</span>
                            </div>
                            <span style={{fontWeight:700,color:r.color,whiteSpace:"nowrap",
                              marginLeft:6}}>CHF {p.chf}</span>
                          </div>
                        );
                      }) : (
                        <div style={{fontSize:10,color:T.hint}}>No data</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}


// ─── TERMS & KITCHENS TAB ─────────────────────────────────────────────────
// ─── Cost per student — by academic term (Q2) ───────────────────────────────
function TermCostPerStudentCard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    fetch(API + "/dashboard/term_cost_per_student?year=" + year)
      .then(r => r.json())
      .then(d => { setData(d && !d.error ? d : null); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const PO_COL  = "#639922";
  const DRY_COL = "#BA7517";

  if (loading) {
    return <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:40,textAlign:"center",color:C.t3,marginBottom:16}}>Loading cost-per-student data from Odoo...</div>;
  }
  if (!data || !data.terms || data.terms.length === 0) {
    return <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:40,textAlign:"center",color:C.t3,marginBottom:16}}>No cost-per-student data for this period.</div>;
  }

  const cohorts = data.terms.filter(t => t.students > 0).sort((a,b) => b.cost_per_student - a.cost_per_student);
  const others  = data.terms.filter(t => t.students === 0 && t.direct > 0);
  const maxCps  = cohorts.length ? cohorts[0].cost_per_student : 1;

  const KPI = ({label, value, sub, color}) => (
    <div style={{flex:1,background:"rgba(90,138,90,0.06)",border:"1px solid "+C.bdr,borderRadius:10,padding:"12px 14px"}}>
      <div style={{fontSize:11.5,color:C.t2}}>{label}</div>
      <div style={{fontSize:22,fontWeight:800,marginTop:2,color:color||C.t1}}>{value}</div>
      {sub && <div style={{fontSize:11,color:C.t3,marginTop:2}}>{sub}</div>}
    </div>
  );

  return (
    <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px",marginBottom:16}}>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:6}}>Cost per student — by academic term · {data.period}</div>
      <div style={{fontSize:12,color:C.t2,marginBottom:16}}>Coût chargé d'une classe = dépense directe (PO + DRY) + sa part de la consommation commune Lake View, répartie au prorata des effectifs.</div>

      <div style={{display:"flex",gap:12,marginBottom:18}}>
        <KPI label="Élèves Q2 (total)" value={data.total_students} sub="toutes cohortes"/>
        <KPI label="Commun Lake View" value={fmtK(data.lv_common.total)} sub={"PO "+fmtK(data.lv_common.po)+" · DRY "+fmtK(data.lv_common.dry)}/>
        <KPI label="Commun / élève" value={"CHF "+data.lv_common.per_student} sub="ajouté à chaque cohorte" color={DRY_COL}/>
      </div>

      <div style={{display:"flex",gap:18,marginBottom:14,fontSize:12,color:C.t2}}>
        <span><span style={{display:"inline-block",width:11,height:11,borderRadius:2,background:PO_COL,marginRight:6}}/>PO · frais</span>
        <span><span style={{display:"inline-block",width:11,height:11,borderRadius:2,background:DRY_COL,marginRight:6}}/>DRY · épicerie (stock)</span>
      </div>

      {cohorts.map((t,i) => {
        const barW   = Math.max((t.cost_per_student/maxCps)*100, 2);
        const poPct  = t.class_cost > 0 ? (t.po/t.class_cost)*100 : 0;
        const dryPct = t.class_cost > 0 ? (t.dry/t.class_cost)*100 : 0;
        const comPct = Math.max(100 - poPct - dryPct, 0);
        return (
          <div key={i} style={{marginBottom:15}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
              <span style={{fontSize:13,fontWeight:600,color:C.t1}}>{t.term} <span style={{color:C.t3,fontWeight:400}}>· {t.students} élèves</span></span>
              <span style={{fontSize:12,color:C.t2}}>
                <b style={{color:C.t1,fontSize:13}}>CHF {t.cost_per_student}/élève</b>
                &nbsp;·&nbsp; classe {fmtK(t.class_cost)}
                &nbsp;·&nbsp; direct {fmtK(t.direct)} + commun {fmtK(t.common_share)}
              </span>
            </div>
            <div style={{height:18,background:"#f0f4f0",borderRadius:4,overflow:"hidden"}}>
              <div style={{width:barW+"%",height:"100%",display:"flex",borderRadius:4,overflow:"hidden"}}>
                <div style={{width:poPct+"%",background:PO_COL}}/>
                <div style={{width:dryPct+"%",background:DRY_COL}}/>
                <div style={{width:comPct+"%",background:DRY_COL,opacity:0.4}} title="part commune Lake View"/>
              </div>
            </div>
          </div>
        );
      })}

      {others.length > 0 && (
        <div style={{marginTop:18,paddingTop:14,borderTop:"1px dashed "+C.bdr}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:C.t3,marginBottom:8}}>Hors cohorte (pas de coût/élève)</div>
          {others.map((t,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.t2,padding:"3px 0"}}>
              <span>{t.term}</span>
              <span>{fmtK(t.direct)} <span style={{color:C.t3}}>· PO {fmtK(t.po)} · DRY {fmtK(t.dry)}</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TermsTab() {
  const [view, setView] = useState("terms");
  const [terms, setTerms] = useState(null);
  const [kitchens, setKitchens] = useState(null);
  const [loading, setLoading] = useState(true);
  const month = new Date().getMonth() + 1;
  const year = new Date().getFullYear();
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  useEffect(() => {
    Promise.all([
      fetch(API + "/dashboard/term_analysis?month=" + month + "&year=" + year).then(r => r.json()),
      fetch(API + "/dashboard/kitchen_detail?month=" + month + "&year=" + year).then(r => r.json()),
    ]).then(([t, k]) => {
      setTerms(Array.isArray(t) ? t : null);
      setKitchens(Array.isArray(k) ? k : null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const TERM_COLORS = ["#0080b1","#ba4e24","#7161b7","#387849","#af642a","#cd9130","#0e7297","#6958ad","#b2382f","#7e7974","#4b4641","#a8a39d"];
  const MAX_TERM = terms && terms.length > 0 ? terms[0].total_chf : 1;
  const MAX_KIT  = kitchens && kitchens.length > 0 ? kitchens[0].total_chf : 1;

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:20}}>
        {[["terms","Academic Terms"],["kitchens","Kitchens & Workshops"]].map(([v,l]) => (
          <button key={v} onClick={() => setView(v)}
            style={{padding:"8px 20px",borderRadius:8,border:"1px solid "+(view===v?C.grn:C.bdr),background:view===v?C.grn:"transparent",color:view===v?"#fff":C.t3,fontSize:12.5,fontWeight:600,cursor:"pointer"}}>
            {v==="terms"?"📚 ":"🍳 "}{l}
          </button>
        ))}
      </div>

      {view === "terms" && (
        <div>
          <div style={{padding:"11px 14px",background:"rgba(90,138,90,0.07)",border:"1px solid rgba(90,138,90,0.25)",borderRadius:8,marginBottom:16,fontSize:12,color:C.t2}}>
            Academic term spend for <b>{MONTHS[month-1]}</b> — parsed from the Term field in each purchase order.
            <b> Unspecified</b> = purchases outside a specific academic programme (events, stock, support).
          </div>
          {loading ? (
            <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"40px",textAlign:"center",color:C.t3}}>Loading term data from Odoo...</div>
          ) : !terms || terms.length === 0 ? (
            <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"40px",textAlign:"center",color:C.t3}}>No term data for {MONTHS[month-1]}</div>
          ) : (
            <div>
              <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px",marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:16}}>Spend by academic term — {MONTHS[month-1]}</div>
                {terms.slice(0,12).map((t,i) => (
                  <div key={i} style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:12,fontWeight:500,color:C.t1}}>{t.term}</span>
                      <span style={{fontSize:12,fontWeight:800,color:TERM_COLORS[i%TERM_COLORS.length]}}>{fmtK(t.total_chf)} <span style={{color:C.t3,fontWeight:400}}>· {t.nb_orders} orders · {t.nb_chefs} chefs</span></span>
                    </div>
                    <div style={{height:16,background:"#f0f4f0",borderRadius:4,position:"relative",overflow:"hidden"}}>
                      <div style={{position:"absolute",left:0,top:0,height:"100%",width:(t.total_chf/MAX_TERM*100)+"%",background:TERM_COLORS[i%TERM_COLORS.length],borderRadius:4,opacity:0.85}}/>
                      <div style={{position:"absolute",left:6,top:2,fontSize:10,fontWeight:700,color:"#fff"}}>{fmtK(t.total_chf)}</div>
                    </div>
                  </div>
                ))}
              </div>

              <TermCostPerStudentCard />

              <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px"}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:16}}>Term detail — entity breakdown</div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:C.hdr}}>
                        {["Academic Term","Total CHF","Lake View","Fine Dining","Food Insp.","Soul Kitchen","Orders","Chefs"].map(h => (
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:9.5,letterSpacing:".08em",color:"#fff",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {terms.map((t,i) => (
                        <tr key={i} style={{background:i%2===0?"rgba(255,255,255,0.8)":"rgba(212,238,212,0.3)",borderBottom:"1px solid "+C.bdr}}>
                          <td style={{padding:"8px 12px",fontWeight:500,color:C.t1}}>{t.term}</td>
                          <td style={{padding:"8px 12px",fontWeight:800,color:TERM_COLORS[i%TERM_COLORS.length]}}>{fmtK(t.total_chf)}</td>
                          <td style={{padding:"8px 12px",color:C.lv}}>{t.lv > 100 ? fmtK(t.lv) : "—"}</td>
                          <td style={{padding:"8px 12px",color:C.fd}}>{t.fd > 100 ? fmtK(t.fd) : "—"}</td>
                          <td style={{padding:"8px 12px",color:C.fi}}>{t.fi > 100 ? fmtK(t.fi) : "—"}</td>
                          <td style={{padding:"8px 12px",color:C.sk}}>{t.sk > 100 ? fmtK(t.sk) : "—"}</td>
                          <td style={{padding:"8px 12px",color:C.t3}}>{t.nb_orders}</td>
                          <td style={{padding:"8px 12px",color:C.t3,fontSize:10.5}}>{(t.chefs||[]).slice(0,2).join(", ")}{(t.chefs||[]).length>2?" +"+(t.chefs.length-2):""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {view === "kitchens" && (
        <div>
          <div style={{padding:"11px 14px",background:"rgba(245,158,11,0.07)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:8,marginBottom:16,fontSize:12,color:C.t2}}>
            Kitchen spend for <b>{MONTHS[month-1]}</b> with entity attribution.
            Mixed kitchens (Leeman, Pauli, Application, Irma) serve both LV and SK — splits are estimated from the allocation matrix until x_studio_place is enforced.
          </div>
          {loading ? (
            <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"40px",textAlign:"center",color:C.t3}}>Loading kitchen data from Odoo...</div>
          ) : !kitchens || kitchens.length === 0 ? (
            <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"40px",textAlign:"center",color:C.t3}}>No kitchen data for {MONTHS[month-1]}</div>
          ) : (
            <div>
              <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px",marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:16}}>Kitchen spend — {MONTHS[month-1]} (stacked by entity)</div>
                {kitchens.slice(0,12).map((k,i) => (
                  <div key={i} style={{marginBottom:12,background:k.is_mixed?"rgba(245,158,11,0.04)":"transparent",borderRadius:6,padding:k.is_mixed?"6px 8px":"0"}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:12,fontWeight:500,color:C.t1}}>
                        {k.is_mixed && <span style={{fontSize:9,color:"#f59e0b",fontWeight:700,marginRight:6}}>⚠ MIXED</span>}
                        {k.kitchen}
                      </span>
                      <span style={{fontSize:12,fontWeight:800,color:C.t1}}>{fmtK(k.total_chf)}</span>
                    </div>
                    <div style={{display:"flex",height:14,borderRadius:4,overflow:"hidden",background:"#f0f4f0"}}>
                      {[["lv",k.lv],["fd",k.fd],["fi",k.fi],["sk",k.sk],["spt",k.spt]].map(([ent,v],j) => (
                        v > 10 ? <div key={j} title={ent.toUpperCase()+": "+fmtK(v)} style={{width:(v/k.total_chf*100)+"%",background:C[ent],minWidth:"1px"}}/> : null
                      ))}
                    </div>
                    <div style={{display:"flex",gap:8,marginTop:3,flexWrap:"wrap"}}>
                      {[["lv","LV"],["fd","FD"],["fi","FI"],["sk","SK"]].filter(([ent])=>k[ent]>100).map(([ent,l],j)=>(
                        <span key={j} style={{fontSize:10,color:C[ent]}}>{l}: {fmtK(k[ent])}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px"}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:16}}>Kitchen detail — mixed workshops flagged</div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{background:C.hdr}}>
                      {["Kitchen","Total CHF","Dominant","Attribution","Orders","Chefs"].map(h => (
                        <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:9.5,letterSpacing:".08em",color:"#fff",fontWeight:700}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kitchens.map((k,i) => (
                      <tr key={i} style={{background:k.is_mixed?"rgba(245,158,11,0.05)":i%2===0?"rgba(255,255,255,0.8)":"rgba(212,238,212,0.3)",borderBottom:"1px solid "+C.bdr}}>
                        <td style={{padding:"8px 12px",fontWeight:500,color:C.t1}}>{k.kitchen}</td>
                        <td style={{padding:"8px 12px",fontWeight:800,color:C.t1}}>{fmtK(k.total_chf)}</td>
                        <td style={{padding:"8px 12px"}}>
                          <span style={{padding:"2px 8px",borderRadius:10,fontSize:10,fontWeight:700,color:C[(k.dominant_entity||"").toLowerCase()]||C.spt,background:(C[(k.dominant_entity||"").toLowerCase()]||C.spt)+"18"}}>
                            {k.dominant_entity||"—"}
                          </span>
                        </td>
                        <td style={{padding:"8px 12px"}}>
                          {k.is_mixed
                            ? <span style={{fontSize:10,fontWeight:700,color:"#f59e0b"}}>⚠ MIXED — Odoo tag needed</span>
                            : <span style={{fontSize:10,fontWeight:700,color:C.lv}}>✓ Certain</span>
                          }
                        </td>
                        <td style={{padding:"8px 12px",color:C.t3}}>{k.nb_orders}</td>
                        <td style={{padding:"8px 12px",color:C.t3,fontSize:10.5}}>{(k.chefs||[]).slice(0,2).join(", ")}{(k.chefs||[]).length>2?" +"+(k.chefs.length-2):""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── LAKE VIEW TAB ────────────────────────────────────────────────────────
function LakeViewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(QR_CUR);
  const { trk, plan } = useQuarterCtx(q);

  useEffect(() => {
    fetch(API + "/dashboard/premium_monthly_detail")
      .then(r => r.json())
      .then(d => { setData(d.data?.lv || null); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const CAT_COLORS = {
    PROTEINS:{b:T.fd,t:T.fd}, VEGETABLES_FRUITS:{b:T.lv,t:T.lv},
    DAIRY:{b:"#3b82f6",t:"#1d4ed8"}, "SPECIAL ITEM":{b:T.sk,t:T.sk},
  };

  const allMonths = data || [];
  const months = allMonths.filter(m => !q || (QR_MONTHS[q]||[]).includes(m.month));
  const QK = q ? `Q${q}` : "";
  const qPrev = q > 1 ? q - 1 : 0;
  const sumQ = (n) => allMonths.filter(m => (QR_MONTHS[n]||[]).includes(m.month)).reduce((s,m)=>s+m.total,0);
  const st = q ? qrStats(trk, "lv") : null;
  // couverts planifiés jour par jour de la semaine en cours (ou W2 si trimestre clos)
  const DAYS_FR = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];
  const planWeek = (() => {
    if (!plan || !plan.week_start) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    let wid = plan.closed ? "W2" : null;
    if (!wid) Object.entries(plan.week_start).forEach(([w,s]) => {
      const d0 = new Date(s+"T00:00:00"); if (today >= d0 && today < new Date(d0.getTime()+7*864e5)) wid = w; });
    wid = wid || "W1";
    const wk = (plan.weeks||{})[wid] || {};
    const cv = (r,s,d) => ((((wk[r]||{})[s]||{}).covers)||[])[d] || 0;
    const rows = DAYS_FR.map((lbl,d) => ({ lbl, lvM:cv("lv","midi",d), lvS:cv("lv","soir",d),
      prem: ["fd","fi","sk"].reduce((a,r)=>a+cv(r,"midi",d)+cv(r,"soir",d),0) }));
    return { wid, start: plan.week_start[wid], rows };
  })();
  const totalAll = months.reduce((s,m) => s + m.total, 0);
  const totalPO = months.reduce((s,m) => s + m.po, 0);
  const totalDRY = months.reduce((s,m) => s + m.dry, 0);
  const totalTR  = months.reduce((s,m) => s + (m.transfer||0), 0);
  const mLabels  = months.length ? `${months[0].label}–${months[months.length-1].label}` : "—";

  if (loading) return (
    <div style={{textAlign:"center",padding:60,color:T.hint}}>
      <div style={{width:24,height:24,border:"3px solid "+T.line,borderTopColor:"transparent",
        borderRadius:"50%",display:"inline-block",animation:"spin 0.8s linear infinite"}}/>
      <div style={{marginTop:10}}>Loading Odoo data…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div>
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
        <QuarterPicker q={q} setQ={setQ}/>
      </div>
      {/* KPI Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:14,marginBottom:20}}>
        {(q ? [
          {l:`${QK} · ${st&&st.closed?"DÉPENSÉ":"DÉPENSÉ À DATE"}`, v:st?fmtK(st.spent):"…",
           s:st?`PO ${fmtK(st.po)} + DRY ${fmtK(st.dry)}${st.closed?"":` · jour ${st.days}/${st.qDays}`}`:"chargement…", c:T.lv},
          {l:qPrev?`Q${qPrev} → ${QK}${st&&st.closed?"":" À DATE"}`:"", v:qPrev?`${fmtK(sumQ(qPrev))} → ${fmtK(sumQ(q))}`:"—",
           s:"cartes mensuelles · même périmètre d'attribution", c:T.lv},
          {l:"CHF / COUVERT", v:st&&st.chfCov!=null?`CHF ${st.chfCov.toFixed(2)}`:"—",
           s:st?`réf CHF 5.00 · ${fmtN(st.covTd||0)} couverts ${st.closed?"servis":"servis à date"}`:"réf CHF 5.00",
           c:st&&st.chfCov!=null?(st.chfCov<=5?T.ok:T.err):T.hint},
          {l:`BUDGET ${QK}`, v:st?fmtK(st.budget):"…",
           s:st?`${fmtN(st.covTot)} couverts ${q>=3?"planifiés":"(semaine-type)"} × 5 · ${fmtN(Math.round(st.covTot/st.weeks))}/sem moy.`:"", c:T.warn},
        ] : [
          {l:`TOTAL ${mLabels.toUpperCase()}`, v:fmtK(totalAll),
           s:`PO ${fmtK(totalPO)} + DRY ${fmtK(totalDRY)}${totalTR? ` ⇄ +${fmtK(totalTR)} FD→LV`:""}`, c:T.lv},
          ...[1,2,3,4].filter(n=>n<=QR_CUR).slice(-3).map(n=>({l:`Q${n}`, v:fmtK(sumQ(n)), s:n===QR_CUR?"à date":"clos", c:n===QR_CUR?T.warn:T.hint})),
        ]).map((k,i) => (
          <div key={i} style={{padding:"16px 18px",background:T.card,border:`1px solid ${T.line}`,
            borderRadius:12,borderLeft:`3px solid ${k.c}`}}>
            <div style={{fontSize:9.5,color:T.hint,textTransform:"uppercase",
              letterSpacing:".08em",marginBottom:6}}>{k.l}</div>
            <div style={{fontSize:20,fontWeight:800,color:k.c}}>{k.v}</div>
            <div style={{fontSize:10.5,color:T.hint,marginTop:3}}>{k.s}</div>
          </div>
        ))}
      </div>

      {/* Monthly grid */}
      <Card style={{marginBottom:20,borderLeft:`4px solid ${T.lv}`,padding:"20px 24px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:T.ink}}>🏨 Lake View — Monthly detail</div>
            <div style={{fontSize:11.5,color:T.hint}}>
              {st ? `${fmtN(st.covTot)} couverts ${QK} · ` : ""}ref CHF 5.00/cover · Odoo PO + DRY data{q ? ` · ${QK}` : " · année"}
            </div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:28,fontWeight:900,color:T.lv,lineHeight:1}}>{fmtK(totalAll)}</div>
            <div style={{fontSize:10.5,color:T.hint,marginTop:2}}>
              PO {fmtK(totalPO)} + DRY {fmtK(totalDRY)}
              {totalTR!==0 && <span style={{color:T.ok,fontWeight:700}}> ⇄ +{fmtK(totalTR)} FD→LV</span>}
            </div>
          </div>
        </div>

        <div style={{overflowX:"auto",paddingBottom:4}}>
        <div style={{display:"grid",gridTemplateColumns:`repeat(${q ? 3 : months.length},minmax(210px,1fr))`,gap:12,minWidth:"min-content"}}>
          {months.map(mo => (
            <div key={mo.month} style={{background:T.bg,borderRadius:12,
              border:`1px solid ${T.line}`,padding:"14px",
              display:"flex",flexDirection:"column"}}>

              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"baseline",marginBottom:10}}>
                <span style={{fontSize:13,fontWeight:700,color:T.ink}}>{mo.label}</span>
                <span style={{fontSize:18,fontWeight:900,color:T.lv}}>{fmtK(mo.total)}</span>
              </div>

              {(()=>{const base=(mo.po+mo.dry)||1; return (
              <div style={{display:"flex",height:6,borderRadius:3,overflow:"hidden",
                background:T.grid,marginBottom:8}}>
                <div style={{width:(mo.po/base*100)+"%",
                  background:T.po,borderRadius:"3px 0 0 3px"}}/>
                <div style={{width:(mo.dry/base*100)+"%",
                  background:T.dry,borderRadius:"0 3px 3px 0"}}/>
              </div>);})()}
              <div style={{display:"flex",justifyContent:"space-between",
                fontSize:9.5,color:T.hint,marginBottom:mo.transfer?4:12}}>
                <span>PO {fmtK(mo.po)}</span>
                <span>DRY {fmtK(mo.dry)}</span>
              </div>
              {!!mo.transfer && (
                <div style={{fontSize:9.5,fontWeight:700,marginBottom:12,color:T.ok}}>
                  ⇄ transfert reçu de FD +{fmtK(mo.transfer)}
                </div>
              )}

              <div style={{marginBottom:12,minHeight:90}}>
                <div style={{fontSize:9,fontWeight:700,color:T.hint,textTransform:"uppercase",
                  letterSpacing:".08em",marginBottom:6}}>PO Categories</div>
                {[
                  {key:"PROTEINS",label:"Proteins"},
                  {key:"VEGETABLES_FRUITS",label:"Vegetables & Fruits"},
                  {key:"DAIRY",label:"Dairy"},
                  {key:"SPECIAL ITEM",label:"Special items"},
                ].map(def => {
                  const cat = mo.categories.find(c=>c.key===def.key)||{chf:0,pct:0};
                  const col = CAT_COLORS[def.key]||{b:T.hint,t:T.hint};
                  return (
                    <div key={def.key} style={{marginBottom:4}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10}}>
                        <span style={{color:col.t,fontWeight:600}}>{def.label}</span>
                        <span style={{color:col.t,fontWeight:700}}>
                          {cat.chf>0?fmtK(cat.chf):"—"}
                        </span>
                      </div>
                      <div style={{height:3,background:T.grid,borderRadius:2,marginTop:2}}>
                        <div style={{height:"100%",width:(cat.pct||0)+"%",
                          background:col.b,borderRadius:2,maxWidth:"100%"}}/>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{marginTop:"auto"}}>
                <div style={{fontSize:9,fontWeight:700,color:T.hint,textTransform:"uppercase",
                  letterSpacing:".08em",marginBottom:6}}>Top 3 products</div>
                {mo.top_products.length > 0 ? mo.top_products.map((p,i) => {
                  const shortName = p.name.replace(/\[.*?\]\s*/,"").substring(0,30);
                  return (
                    <div key={i} style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",padding:"4px 0",
                      borderBottom:i<2?`1px solid ${T.line}`:"none",fontSize:10}}>
                      <div style={{display:"flex",alignItems:"center",gap:5,flex:1,minWidth:0}}>
                        <span style={{color:T.lv,fontWeight:800,fontSize:11}}>{i+1}</span>
                        <span style={{color:T.ink,whiteSpace:"nowrap",overflow:"hidden",
                          textOverflow:"ellipsis"}}>{shortName}</span>
                      </div>
                      <span style={{fontWeight:700,color:T.lv,whiteSpace:"nowrap",
                        marginLeft:6}}>CHF {p.chf}</span>
                    </div>
                  );
                }) : (
                  <div style={{fontSize:10,color:T.hint}}>No data</div>
                )}
              </div>
            </div>
          ))}
        </div>
        </div>
      </Card>

      {/* Couverts planifiés — semaine en cours (Restaurants Planning) */}
      {planWeek && (
      <Card style={{padding:"20px 24px"}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:T.hint,marginBottom:8}}>
          Couverts planifiés — {QK} {planWeek.wid} · semaine du {planWeek.start.slice(8,10)}.{planWeek.start.slice(5,7)}
        </div>
        <div style={{padding:"9px 13px",background:T.bg,border:`1px solid ${T.line}`,borderRadius:6,marginBottom:14,fontSize:12,color:T.hint}}>
          Source : <b>Restaurants Planning</b> (saisi par le F&B). Lake View midi + soir, premiums = Fine Dining + Food Inspired + Soul Kitchen.
          {" "}Total semaine : LV <b>{fmtN(planWeek.rows.reduce((a,r)=>a+r.lvM+r.lvS,0))}</b> · Premium <b>{fmtN(planWeek.rows.reduce((a,r)=>a+r.prem,0))}</b>.
        </div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{background:T.g700}}>
              {["Jour","LV midi","LV soir","LV total","Premium","Total"].map(h => (
                <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:9.5,letterSpacing:".09em",color:"#fff",fontWeight:700}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {planWeek.rows.map((r,i) => (
              <tr key={i} style={{background:i%2===0?T.card:T.bg,borderBottom:`1px solid ${T.line}`}}>
                <td style={{padding:"10px 14px",fontWeight:500,color:T.ink}}>{r.lbl}</td>
                <td style={{padding:"10px 14px",color:T.sub}}>{r.lvM||"—"}</td>
                <td style={{padding:"10px 14px",color:T.sub}}>{r.lvS||"—"}</td>
                <td style={{padding:"10px 14px",color:T.lv,fontWeight:800,fontSize:15}}>{r.lvM+r.lvS}</td>
                <td style={{padding:"10px 14px",color:T.fi,fontWeight:700}}>{r.prem||"—"}</td>
                <td style={{padding:"10px 14px",fontWeight:700,color:T.ink}}>{r.lvM+r.lvS+r.prem}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      )}
    </div>
  );
}


// ─── ECONOMAT TAB ─────────────────────────────────────────────────────────

function ChefSupplierTable({ data }) {
  const [expanded, setExpanded] = useState({});
  const toggle = function(chef){ setExpanded(function(p){ var n=Object.assign({},p); n[chef]=!p[chef]; return n; }); };
  const SUPP_COLORS = {
    "Saviva Food Services":"#37804b","Pistor AG":"#8b5cf6",
    "Moga Sàrl":"#f97316","NovaDelta Suisse":"#64748b","Bevanar & Dorga SA":"#06b6d4",
  };
  var cleanData = {};
  Object.entries(data).forEach(function(entry){
    var chef=entry[0], d=entry[1];
    var foodSupp = {};
    Object.entries(d.by_supplier||{}).forEach(function(se){
      if(se[0].toUpperCase()!=="WEBSTAR") foodSupp[se[0]]=se[1];
    });
    var foodTotal = Object.values(foodSupp).reduce(function(a,v){return a+v;},0);
    if(foodTotal>=1) cleanData[chef]={total:foodTotal, by_supplier:foodSupp};
  });
  var sortedChefs = Object.entries(cleanData).sort(function(a,b){return b[1].total-a[1].total;});
  var grandTotal = sortedChefs.reduce(function(a,e){return a+e[1].total;},0);

  if(!sortedChefs.length) return (
    <div style={{color:C.t3,padding:"20px",textAlign:"center",fontSize:12}}>
      No chef data — check endpoint chef_supplier_breakdown on server 87
    </div>
  );

  return (
    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
      <thead>
        <tr style={{background:C.hdr}}>
          {["Chef","Main supplier","Total CHF","% of total","Detail"].map(function(h){
            return <th key={h} style={{padding:"9px 12px",textAlign:h==="Total CHF"||h==="% of total"?"right":"left",fontSize:9.5,color:"#fff",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>;
          })}
        </tr>
      </thead>
      <tbody>
        {sortedChefs.map(function(entry,i){
          var chef=entry[0], d=entry[1];
          var topSupp = Object.entries(d.by_supplier)[0];
          var isOpen = expanded[chef];
          return [
            <tr key={chef} onClick={function(){toggle(chef);}}
              style={{background:isOpen?"rgba(90,138,90,0.08)":i%2===0?"rgba(255,255,255,0.8)":"rgba(212,238,212,0.3)",borderBottom:"1px solid "+C.bdr,cursor:"pointer"}}>
              <td style={{padding:"10px 12px",fontWeight:700,color:C.t1}}>{chef}</td>
              <td style={{padding:"10px 12px"}}>
                {topSupp && <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
                  <span style={{width:8,height:8,borderRadius:2,background:SUPP_COLORS[topSupp[0]]||C.spt,display:"inline-block"}}/>
                  <span style={{color:C.t2,fontSize:12}}>{topSupp[0]}</span>
                </span>}
              </td>
              <td style={{padding:"10px 12px",textAlign:"right",fontWeight:800,color:T.ok}}>
                CHF {d.total.toLocaleString("fr-CH",{maximumFractionDigits:0})}
              </td>
              <td style={{padding:"10px 12px",textAlign:"right",color:C.t3}}>
                {grandTotal>0?(d.total/grandTotal*100).toFixed(1):0}%
              </td>
              <td style={{padding:"10px 12px",textAlign:"center",color:C.t3,fontSize:13}}>{isOpen?"▲":"▼"}</td>
            </tr>,
            isOpen && Object.entries(d.by_supplier).map(function(se,j){
              var supp=se[0], amt=se[1];
              return (
                <tr key={chef+supp} style={{background:"rgba(90,138,90,0.04)",borderBottom:"1px dashed "+C.bdr}}>
                  <td style={{padding:"7px 12px 7px 28px",color:C.t3,fontSize:11}}>↳</td>
                  <td style={{padding:"7px 12px",color:C.t2,fontSize:11}}>
                    <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
                      <span style={{width:6,height:6,borderRadius:2,background:SUPP_COLORS[supp]||C.spt,display:"inline-block"}}/>
                      {supp}
                    </span>
                  </td>
                  <td style={{padding:"7px 12px",textAlign:"right",fontWeight:600,color:SUPP_COLORS[supp]||C.t2,fontSize:11}}>
                    CHF {amt.toLocaleString("fr-CH",{maximumFractionDigits:0})}
                  </td>
                  <td style={{padding:"7px 12px",textAlign:"right",color:C.t3,fontSize:11}}>
                    {d.total>0?(amt/d.total*100).toFixed(1):0}%
                  </td>
                  <td/>
                </tr>
              );
            })
          ];
        })}
        <tr style={{background:"rgba(90,138,90,0.12)",borderTop:"2px solid "+C.grn}}>
          <td style={{padding:"10px 12px",fontWeight:800,color:T.ok}}>TOTAL DRY food</td>
          <td/>
          <td style={{padding:"10px 12px",textAlign:"right",fontWeight:800,color:T.ok}}>
            CHF {grandTotal.toLocaleString("fr-CH",{maximumFractionDigits:0})}
          </td>
          <td style={{padding:"10px 12px",textAlign:"right",color:C.t3}}>100%</td>
          <td/>
        </tr>
      </tbody>
    </table>
  );
}

function EconomatTab() {
  const [monitor, setMonitor] = useState(null);
  const [chefData, setChefData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selMonth, setSelMonth] = useState(new Date().getMonth() + 1);
  const selYear = new Date().getFullYear();
  const MONTHS_LBL = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  useEffect(() => {
    setLoading(true);
    setChefData({});
    Promise.all([
      fetch(API + "/dashboard/economat_detail?month=" + selMonth + "&year=" + selYear).then(r=>r.json()),
      fetch(API + "/dashboard/chef_supplier_breakdown?month=" + selMonth + "&year=" + selYear).then(r=>r.json()).catch(()=>({chefs:{}})),
    ]).then(([mon, chef]) => {
      setMonitor(mon);
      setChefData((chef && chef.chefs) ? chef.chefs : {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selMonth, selYear]);

  const alertLevel = monitor?.alert || "ok";
  const alertColor = alertLevel==="danger"?C.fd:alertLevel==="warning"?"#f59e0b":C.lv;
  const SUPPLIER_COLORS = [C.fd, C.fi, C.sk];

  // ── Calculs dérivés hors JSX (évite conflits minifieur) ─────────────────
  const FOOD_BUDGET = 40000;   // budget mensuel économat (Alain, 07/10/2026 : 40k, avant 20k)
  const today = new Date();
  const isCurrentMonth = selMonth === today.getMonth()+1 && selYear === today.getFullYear();
  const daysInMonth = new Date(selYear, selMonth, 0).getDate();
  const daysElapsed = isCurrentMonth ? today.getDate() : daysInMonth;
  const daysLeft = daysInMonth - daysElapsed;
  const SUPP_PAL = ["#37804b","#7161b7","#ba4e24","#7e7974","#0e7297","#cd9130","#a8a39d"];

  const foodEntries = monitor && monitor.by_supplier
    ? Object.entries(monitor.by_supplier).filter(function(e){ return e[0].toUpperCase() !== "WEBSTAR"; })
    : [];
  const foodTotal = foodEntries.reduce(function(a,e){ return a+e[1]; }, 0);

  // ── PROJECTION INTELLIGENTE (Apr) ────────────────────────────────────────
  const byWeek = (monitor && monitor.by_week) || [];
  const completedWeeks = byWeek.filter(function(w){
    var wkIdx = typeof w.week === "number" ? w.week : parseInt(String(w.week).replace("W",""),10);
    return !isNaN(wkIdx) && (wkIdx * 7 + 7) < daysElapsed;
  });
  var projMethod = "linear";
  var totalProjected;
  if (completedWeeks.length >= 1 && isCurrentMonth) {
    var avgWeek = completedWeeks.reduce(function(a,w){return a+(w.total||0);},0) / completedWeeks.length;
    totalProjected = Math.round(avgWeek * (daysInMonth / 7));
    projMethod = "weekly";
  } else {
    totalProjected = daysElapsed>0 ? Math.round(foodTotal/daysElapsed*daysInMonth) : foodTotal;
  }
  // Garde-fou : si la projection hebdo est < dépensé actuel (cas anormal),
  // on bascule en linéaire plutôt que d'afficher un chiffre absurde.
  if (totalProjected < foodTotal) {
    totalProjected = Math.round(foodTotal * (daysInMonth / Math.max(daysElapsed,1)));
    projMethod = "linear-safeguard";
  }
  const globalAlert = foodTotal > FOOD_BUDGET || totalProjected > FOOD_BUDGET;
  const alreadyOver = foodTotal > FOOD_BUDGET;

  // Pacing par fournisseur — projection cohérente avec méthode globale
  const suppPacing = foodEntries.map(function(e,i){
    var sName=e[0], spent=e[1];
    var dailyRate = daysElapsed>0 ? spent/daysElapsed : 0;
    var share = foodTotal>0 ? spent/foodTotal : 0;
    // Utiliser la même méthode que le global : part du projeté total
    var projected = Math.round(totalProjected * share);
    var allocBudget = FOOD_BUDGET*share;
    var willOverrun = projected > allocBudget*1.1;
    return {name:sName, spent:spent, dailyRate:dailyRate, projected:projected, allocBudget:allocBudget, willOverrun:willOverrun, color:SUPP_PAL[i]||"#94a3b8"};
  });

  const dryTotal = Object.values(chefData).reduce(function(acc,d){
    return acc + Object.entries(d.by_supplier||{})
      .filter(function(e){ return e[0].toUpperCase() !== "WEBSTAR"; })
      .reduce(function(a,e){ return a+e[1]; }, 0);
  }, 0);
  const dryPct = Math.min(dryTotal/FOOD_BUDGET*100, 100);
  const dryOver = dryTotal > FOOD_BUDGET;
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center"}}>
        <span style={{fontSize:12,color:C.t3,fontWeight:600}}>Mois :</span>
        {Array.from({length: today.getMonth()+1}, (_,i)=>i+1).slice(-6).map(mn => (
          <button key={mn} onClick={()=>setSelMonth(mn)}
            style={{padding:"5px 14px",borderRadius:8,border:"1px solid "+(selMonth===mn?C.grn:C.bdr),
              background:selMonth===mn?C.grn:"#fff",color:selMonth===mn?"#fff":C.t2,
              fontSize:12,fontWeight:selMonth===mn?700:500,cursor:"pointer"}}>
            {MONTHS_LBL[mn-1]}{mn===today.getMonth()+1?" ●":""}
          </button>
        ))}
      </div>

      <div style={{padding:"10px 14px",background:"rgba(255,255,255,0.93)",border:"1px solid rgba(148,163,184,0.3)",borderRadius:8,marginBottom:16,fontSize:12,color:C.t2}}>
        <b>ECONOMAT — stock central :</b> réassorts DRY chez Saviva, Pistor, Moga, NovaDelta… passés par
        {" "}<b>Sanford Pereira</b> et <b>Daniel Teixeira</b> (PO sans Place). Ces achats sont exclus des pages Live
        (sinon double comptage) : la consommation y est comptée à la sortie du stock, via les transferts internes des chefs (PO_5_).
        {monitor && monitor.by_buyer && Object.keys(monitor.by_buyer).length>0 && (
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
            <span style={{fontSize:11,color:C.t3}}>Acheteurs {MONTHS_LBL[selMonth-1]} :</span>
            {Object.entries(monitor.by_buyer).map(([b,v]) => (
              <span key={b} style={{fontSize:11,padding:"2px 9px",borderRadius:10,background:"#f8f4ef",border:"1px solid "+C.bdr,color:C.t1}}>
                {b} · <b>{fmtK(v)}</b></span>
            ))}
          </div>
        )}
      </div>

      {/* How to read the Economat numbers */}
      {monitor && !monitor.error && foodTotal > 0 && (
        <Card style={{marginBottom:16,padding:"18px 22px"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#1a1a1a",marginBottom:12}}>How to read these numbers</div>
          <div style={{fontSize:12.5,color:"#374151",lineHeight:1.7,marginBottom:14}}>
            <b style={{color:"#f97316"}}>Economat purchases</b> (CHF {(foodTotal/1000).toFixed(1)}k) = what the Economat buyers (Sanford, Daniel) order from Saviva, Pistor, Moga, etc. for the central Economat stock.
            <br/>
            <b style={{color:dryOver?"#b2382f":"#37804b"}}>Chefs DRY consumed</b> (CHF {(dryTotal/1000).toFixed(1)}k) = what chefs actually withdraw from that stock via internal transfers (PO_5_).
            <br/>
            <b>Ratio {dryTotal>0?(foodTotal/dryTotal*100).toFixed(0):"-"}%</b> = the Economat purchased CHF {(foodTotal/1000).toFixed(1)}k and chefs consumed CHF {(dryTotal/1000).toFixed(1)}k.
            {foodTotal < dryTotal
              ? <span> The difference of <b style={{color:"#3b82f6"}}>CHF {((dryTotal-foodTotal)/1000).toFixed(1)}k</b> comes from existing stock built up in previous months.</span>
              : <span> The Economat is buying more than consumed — stock is growing.</span>
            }
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
            {[
              {range:"> 100%", label:"Stock growing",     desc:"Economat buys more than consumed", color:"#f97316", bg:"#fff7ed"},
              {range:"~ 100%", label:"Perfect balance",   desc:"No stock variation",             color:"#37804b", bg:"#f8f4ef"},
              {range:"70-90%", label:"Healthy",           desc:"Chefs draw from existing stock", color:"#37804b", bg:"#f8f4ef"},
              {range:"< 60%",  label:"Stock depletion",   desc:"Stock emptying dangerously",     color:"#b2382f", bg:"#fdf2f0"},
            ].map((r,i) => {
              const currentRatio = dryTotal > 0 ? foodTotal/dryTotal*100 : 0;
              const isActive = (i===0 && currentRatio>100) || (i===1 && currentRatio>=95 && currentRatio<=105)
                || (i===2 && currentRatio>=60 && currentRatio<95) || (i===3 && currentRatio<60);
              return (
                <div key={i} style={{padding:"10px 12px",borderRadius:8,
                  background:isActive?r.bg:"#f8fafc",
                  border:isActive?`2px solid ${r.color}`:`1px solid #e5e7eb`,
                  opacity:isActive?1:0.6}}>
                  <div style={{fontSize:14,fontWeight:800,color:r.color}}>{r.range}</div>
                  <div style={{fontSize:11,fontWeight:700,color:"#1a1a1a",marginTop:2}}>{r.label}</div>
                  <div style={{fontSize:10,color:"#64748b",marginTop:2}}>{r.desc}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {monitor && !monitor.error && (
        <div style={{padding:"16px 20px",background:"#fff",border:"1px solid "+alertColor+"50",borderRadius:10,marginBottom:20}}>
          <div style={{display:"flex",gap:24,alignItems:"center",flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:9.5,color:C.t3,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Achats économat livrés · HT · hors Webstar ({MONTHS_LBL[selMonth-1]})</div>
              <div style={{fontSize:26,fontWeight:800,color:alertColor}}>{fmtK(foodTotal)}</div>
            </div>
            <div style={{fontSize:18,color:C.t3}}>vs</div>
            <div>
              <div style={{fontSize:9.5,color:C.t3,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Chefs DRY consumed</div>
              <div style={{fontSize:26,fontWeight:800,color:C.lv}}>{fmtK(dryTotal)}</div>
            </div>
            <div>
              <div style={{fontSize:9.5,color:C.t3,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Ratio</div>
              <div style={{fontSize:26,fontWeight:800,color:alertColor}}>{dryTotal>0?(foodTotal/dryTotal*100).toFixed(1):"-"}%</div>
            </div>
            <div>
              <div style={{fontSize:9.5,color:C.t3,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Monthly budget</div>
              <div style={{fontSize:26,fontWeight:800,color:C.t3}}>CHF {FOOD_BUDGET/1000}k</div>
            </div>
            <div style={{marginLeft:"auto",textAlign:"right"}}>
              {alertLevel==="danger" ? (
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",
                  background:"linear-gradient(135deg,#fef2f2,#fff1f2)",border:"2px solid #e8a09a",borderRadius:12}}>
                  <svg width="32" height="28" viewBox="0 0 38 34" style={{flexShrink:0}}>
                    <path d="M19 1L1 33h36L19 1z" fill="#FACC15" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round"/>
                    <circle cx="19" cy="20" r="3" fill="none" stroke="#1a1a1a" strokeWidth="2"/>
                    <ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(45,19,20)"/>
                    <ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(-45,19,20)"/>
                  </svg>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:"#b2382f"}}>OVER BUDGET</div>
                    <div style={{fontSize:10,color:"#8a2520"}}>
                      +CHF {((foodTotal-FOOD_BUDGET)/1000).toFixed(1)}k over CHF {FOOD_BUDGET/1000}k
                    </div>
                  </div>
                </div>
              ) : (
                <span style={{padding:"4px 14px",borderRadius:10,fontSize:11,fontWeight:700,color:alertColor,background:alertColor+"18",border:"1px solid "+alertColor+"40"}}>
                  {alertLevel==="warning"?"MONITOR":"OK"}
                </span>
              )}
            </div>
          </div>
          <div style={{marginTop:14,position:"relative",height:20,background:"#f0f4f0",borderRadius:6,overflow:"visible"}}>
            <div style={{width:Math.min(foodTotal/FOOD_BUDGET*100,100)+"%",height:"100%",
              background:"#37804b",borderRadius:globalAlert?"6px 0 0 6px":6,transition:"width .5s"}}/>
            {globalAlert && <div style={{position:"absolute",left:Math.min(100, FOOD_BUDGET/foodTotal*100)+"%",top:0,height:"100%",
              width:(100-Math.min(100, FOOD_BUDGET/foodTotal*100))+"%",
              background:"#3b82f6",borderRadius:"0 6px 6px 0"}}/>}
            <div style={{position:"absolute",left:Math.min(foodTotal>FOOD_BUDGET?(FOOD_BUDGET/(foodTotal*1.1))*100:100, 100)+"%",
              top:-4,width:2,height:28,background:"#1a1a1a",borderRadius:1,transform:"translateX(-1px)"}}/>
            <div style={{position:"absolute",left:Math.min(foodTotal>FOOD_BUDGET?(FOOD_BUDGET/(foodTotal*1.1))*100:100, 100)+"%",
              top:-18,transform:"translateX(-50%)",
              fontSize:9,fontWeight:700,color:"#1a1a1a",whiteSpace:"nowrap"}}>
              Budget CHF {FOOD_BUDGET/1000}k
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:8,fontSize:11,color:"#1a1a1a"}}>
            <span style={{fontWeight:700}}>Livré HT : CHF {(foodTotal/1000).toFixed(1)}k
              {(monitor.pending_total||0) > 0 && <span style={{fontWeight:500,color:C.t3}}> · + CHF {(monitor.pending_total/1000).toFixed(1)}k commandés, à livrer ({(monitor.pending_orders||[]).map(p=>p.name).join(", ")})</span>}
            </span>
            {globalAlert ? (
              <span>Overspend since <b style={{color:"#b2382f"}}>~{(() => {
                const rate = foodTotal / daysElapsed;
                const dayOver = Math.ceil(FOOD_BUDGET / rate);
                const d = new Date(selYear, selMonth-1, dayOver);
                return d.toLocaleDateString('en-GB',{day:'numeric',month:'short'});
              })()}</b> · <b style={{color:"#3b82f6"}}>+CHF {((foodTotal-FOOD_BUDGET)/1000).toFixed(1)}k over</b></span>
            ) : (
              <span>Budget: CHF {FOOD_BUDGET/1000}k</span>
            )}
          </div>
        </div>
      )}

      {monitor && !monitor.error && (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20}}>

              <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3}}>
                    Fournisseurs économat {MONTHS_LBL[selMonth-1]} — Webstar exclu
                  </div>
                  {globalAlert && (
                    <div style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",
                      background:"linear-gradient(135deg,#fef2f2,#fff1f2)",border:"2px solid #e8a09a",borderRadius:10}}>
                      <svg width="22" height="19" viewBox="0 0 38 34" style={{flexShrink:0}}>
                        <path d="M19 1L1 33h36L19 1z" fill="#FACC15" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round"/>
                        <circle cx="19" cy="20" r="3" fill="none" stroke="#1a1a1a" strokeWidth="2"/>
                        <ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(45,19,20)"/>
                        <ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(-45,19,20)"/>
                      </svg>
                      <span style={{fontSize:10,fontWeight:700,color:"#b2382f"}}>PROJECTED OVERSPEND</span>
                    </div>
                  )}
                </div>
                <div style={{marginBottom:14}}>
                  <span style={{fontSize:26,fontWeight:800,color:globalAlert?C.fd:C.grn}}>CHF {(foodTotal/1000).toFixed(1)}k</span>
                  <span style={{fontSize:12,color:C.t3,marginLeft:8}}>
                    total food · budget {FOOD_BUDGET/1000}k · projected <b style={{color:globalAlert?C.fd:C.grn}}>CHF {(totalProjected/1000).toFixed(1)}k</b>
                    <span style={{marginLeft:6,padding:"1px 6px",fontSize:9.5,fontWeight:600,
                      color:C.t3,background:"#f8f4ef",border:`1px solid ${C.bdr}`,borderRadius:4}}
                      title={projMethod==="weekly" ? "Projection = moyenne des semaines complètes × (30/7). Évite le biais du stocking ponctuel en milieu/fin de mois." : "Projection linéaire: CHF dépensé ÷ jours écoulés × jours du mois."}>
                      {projMethod==="weekly" ? "moyenne hebdo" : "linéaire"}
                    </span>
                  </span>
                </div>
                {foodEntries.map(function(e,i){
                  var s=e[0], v=e[1];
                  var pct = foodTotal>0 ? v/foodTotal*100 : 0;
                  return (
                    <div key={i} style={{marginBottom:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:12,fontWeight:500,color:C.t1}}>{s}</span>
                        <span style={{fontSize:12,fontWeight:700,color:SUPP_PAL[i]||C.spt}}>
                          CHF {(v/1000).toFixed(1)}k <span style={{color:C.t3,fontWeight:400}}>({pct.toFixed(1)}%)</span>
                        </span>
                      </div>
                      <div style={{height:9,background:"#f0f4f0",borderRadius:4}}>
                        <div style={{width:pct+"%",height:"100%",background:SUPP_PAL[i]||C.spt,borderRadius:4}}/>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px",borderLeft:"4px solid "+C.lv}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:4}}>
                  Chef DRY consumed — via internal transfers PO_5_ (Webstar exclu)
                </div>
                <div style={{display:"flex",alignItems:"baseline",gap:12,marginBottom:4}}>
                  <span style={{fontSize:32,fontWeight:800,color:C.lv}}>CHF {(dryTotal/1000).toFixed(1)}k</span>
                  <span style={{fontSize:13,color:C.t3}}>consommé par les chefs</span>
                  <span style={{marginLeft:"auto",padding:"3px 12px",borderRadius:10,fontSize:11,fontWeight:700,
                    color:C.t3,background:"rgba(126,121,116,0.1)",border:"1px solid "+C.t3+"40"}}>
                    INFORMATIF · pas un budget
                  </span>
                </div>
                <div style={{marginTop:10,padding:"10px 14px",background:"#f8f4ef",borderRadius:8,
                  fontSize:11.5,color:C.t2,lineHeight:1.6}}>
                  <b>Comment lire :</b> ce montant représente la <b>sortie de stock central vers les cuisines</b>,
                  valorisée aux prix fournisseurs. Ce n'est pas un achat — c'est une consommation.
                  {foodTotal>0 && dryTotal>foodTotal && (
                    <span> Les chefs sortent <b style={{color:C.lv}}>CHF {((dryTotal-foodTotal)/1000).toFixed(1)}k de plus</b> que
                    l'économat n'achète ce mois → ils puisent dans le stock constitué précédemment (ratio sain).</span>
                  )}
                  {foodTotal>0 && dryTotal<=foodTotal && (
                    <span> L'économat reconstitue le stock (achats &gt; consommation).</span>
                  )}
                </div>
                <div style={{display:"none"}}>
                  <div style={{position:"relative",height:20,background:"#f0f4f0",borderRadius:6,overflow:"visible",marginBottom:6}}>
                  <div style={{width:Math.min(dryTotal/FOOD_BUDGET*100,100)+"%",height:"100%",
                    background:"#37804b",borderRadius:dryOver?"6px 0 0 6px":6,transition:"width .5s"}}/>
                  {dryOver && <div style={{position:"absolute",left:Math.min(100, FOOD_BUDGET/dryTotal*100)+"%",top:0,height:"100%",
                    width:(100-Math.min(100, FOOD_BUDGET/dryTotal*100))+"%",
                    background:"#3b82f6",borderRadius:"0 6px 6px 0"}}/>}
                  <div style={{position:"absolute",left:Math.min(dryOver?(FOOD_BUDGET/(dryTotal*1.1))*100:100, 100)+"%",
                    top:-4,width:2,height:28,background:"#1a1a1a",borderRadius:1,transform:"translateX(-1px)"}}/>
                  <div style={{position:"absolute",left:Math.min(dryOver?(FOOD_BUDGET/(dryTotal*1.1))*100:100, 100)+"%",
                    top:-18,transform:"translateX(-50%)",
                    fontSize:9,fontWeight:700,color:"#1a1a1a",whiteSpace:"nowrap"}}>
                    Budget CHF {FOOD_BUDGET/1000}k
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#1a1a1a"}}>
                  {dryOver ? (
                    <>
                      <span style={{fontWeight:700}}>Total: CHF {(dryTotal/1000).toFixed(1)}k · <span style={{color:"#3b82f6"}}>+CHF {((dryTotal-FOOD_BUDGET)/1000).toFixed(1)}k over</span></span>
                      <span>Overspend since <b style={{color:"#b2382f"}}>~{(() => {
                        const rate = dryTotal / daysElapsed;
                        const dayOver = Math.ceil(FOOD_BUDGET / rate);
                        const d = new Date(selYear, selMonth-1, dayOver);
                        return d.toLocaleDateString('en-GB',{day:'numeric',month:'short'});
                      })()}</b></span>
                    </>
                  ) : (
                    <span style={{fontWeight:700}}>Remaining: CHF {((FOOD_BUDGET-dryTotal)/1000).toFixed(1)}k — {daysLeft} days left</span>
                  )}
                </div>
                </div>

                {/* Ratio explanation */}
                {foodTotal > 0 && (
                  <div style={{marginTop:14,padding:"12px 16px",background:T.bg,borderRadius:10,
                    border:`1px solid ${T.line}`}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#1a1a1a",textTransform:"uppercase",
                      letterSpacing:".08em",marginBottom:8}}>Ratio analysis</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:10}}>
                      <div style={{textAlign:"center",padding:"8px",background:"#fff",borderRadius:8,
                        border:`1px solid ${T.line}`}}>
                        <div style={{fontSize:9,color:"#64748b",marginBottom:2}}>Economat purchased</div>
                        <div style={{fontSize:16,fontWeight:800,color:"#f97316"}}>CHF {(foodTotal/1000).toFixed(1)}k</div>
                      </div>
                      <div style={{textAlign:"center",padding:"8px",background:"#fff",borderRadius:8,
                        border:`1px solid ${T.line}`}}>
                        <div style={{fontSize:9,color:"#64748b",marginBottom:2}}>Chefs consumed</div>
                        <div style={{fontSize:16,fontWeight:800,color:"#37804b"}}>CHF {(dryTotal/1000).toFixed(1)}k</div>
                      </div>
                      <div style={{textAlign:"center",padding:"8px",background:"#fff",borderRadius:8,
                        border:`1px solid ${T.line}`}}>
                        <div style={{fontSize:9,color:"#64748b",marginBottom:2}}>Ratio</div>
                        <div style={{fontSize:16,fontWeight:800,
                          color:foodTotal/dryTotal>1?"#ba4e24":foodTotal/dryTotal>0.6?"#37804b":"#b2382f"}}>
                          {(foodTotal/dryTotal*100).toFixed(0)}%
                        </div>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:"#374151",lineHeight:1.5}}>
                      {foodTotal > dryTotal ? (
                        <span>The Economat is buying <b style={{color:"#f97316"}}>more</b> than chefs consume — stock is growing by <b>CHF {((foodTotal-dryTotal)/1000).toFixed(1)}k</b> this month.</span>
                      ) : (
                        <span>Chefs consume <b style={{color:"#3b82f6"}}>CHF {((dryTotal-foodTotal)/1000).toFixed(1)}k more</b> than the Economat purchased — they are drawing from <b>existing stock</b> built up in previous months.</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{background:C.surf,border:"1px solid "+(globalAlert?C.fd:C.bdr),borderRadius:12,padding:"22px 26px",marginBottom:20}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:14}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:3}}>
                    Budget {MONTHS_LBL[selMonth-1]} {FOOD_BUDGET/1000}k · Projection vs actuel
                  </div>
                  <div style={{fontSize:11.5,color:C.t2}}>
                    Jour {daysElapsed}/{daysInMonth} · {daysLeft} jours restants · méthode : {projMethod==="weekly"?"moyenne des semaines complètes":"rythme journalier"}
                  </div>
                </div>
                {globalAlert && (
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",
                    background:"linear-gradient(135deg,#fef2f2,#fff1f2)",border:"2px solid #e8a09a",borderRadius:10}}>
                    <svg width="22" height="19" viewBox="0 0 38 34" style={{flexShrink:0}}>
                      <path d="M19 1L1 33h36L19 1z" fill="#FACC15" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round"/>
                      <circle cx="19" cy="20" r="3" fill="none" stroke="#1a1a1a" strokeWidth="2"/>
                      <ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(45,19,20)"/>
                      <ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(-45,19,20)"/>
                    </svg>
                    <div>
                      <div style={{fontSize:11,fontWeight:800,color:"#b2382f"}}>
                        {alreadyOver ? "DÉJÀ DÉPASSÉ" : "PROJECTION > BUDGET"}
                      </div>
                      <div style={{fontSize:10,color:"#8a2520",fontWeight:600}}>
                        {alreadyOver
                          ? `+${((foodTotal-FOOD_BUDGET)/1000).toFixed(1)}k actuel · +${((totalProjected-FOOD_BUDGET)/1000).toFixed(1)}k fin du mois`
                          : `+${((totalProjected-FOOD_BUDGET)/1000).toFixed(1)}k fin du mois (${Math.round((totalProjected/FOOD_BUDGET-1)*100)}%)`
                        }
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Grande barre de progression à 3 niveaux ────────────── */}
              {(() => {
                const maxScale = Math.max(totalProjected, FOOD_BUDGET, foodTotal) * 1.05;
                const pctSpent  = (foodTotal / maxScale) * 100;
                const pctBudget = (FOOD_BUDGET / maxScale) * 100;
                const pctProj   = (totalProjected / maxScale) * 100;
                // La barre elle-même : couleur verte si dans budget, rouge si déjà dépassé
                const spentColor = alreadyOver ? C.fd : C.grn;
                // Zone de dépassement projeté (au-dessus du budget)
                const overProjPct = totalProjected > FOOD_BUDGET
                  ? ((totalProjected - Math.max(foodTotal, FOOD_BUDGET)) / maxScale) * 100
                  : 0;
                const pctOver = alreadyOver ? ((foodTotal - FOOD_BUDGET) / maxScale) * 100 : 0;

                return (
                  <div>
                    {/* Labels haut */}
                    <div style={{position:"relative",height:20,marginBottom:2}}>
                      {/* Label dépensé */}
                      <div style={{position:"absolute",left:`calc(${pctSpent}% - 4px)`,
                        transform:"translateX(-100%)",fontSize:11,fontWeight:800,color:spentColor,whiteSpace:"nowrap"}}>
                        Dépensé {(foodTotal/1000).toFixed(1)}k
                      </div>
                      {/* Label projected à droite si différent */}
                      {totalProjected > foodTotal && (
                        <div style={{position:"absolute",left:`${pctProj}%`,
                          transform:"translateX(-100%)",fontSize:11,fontWeight:800,
                          color:totalProjected>FOOD_BUDGET?C.fd:C.grn,whiteSpace:"nowrap"}}>
                          Projeté {(totalProjected/1000).toFixed(1)}k
                        </div>
                      )}
                    </div>

                    {/* Barre */}
                    <div style={{position:"relative",height:36,background:"#f0f4f0",
                      borderRadius:8,overflow:"visible",border:"1px solid "+C.bdr}}>
                      {/* Partie dépensée (verte OU rouge si déjà dépassé) */}
                      <div style={{position:"absolute",left:0,top:0,bottom:0,
                        width:(alreadyOver ? pctBudget : pctSpent)+"%",
                        background:alreadyOver ? C.grn : spentColor,
                        borderRadius:"8px 0 0 8px",transition:"width .5s"}}/>
                      {/* Si déjà dépassé : segment rouge au-dessus du budget */}
                      {alreadyOver && (
                        <div style={{position:"absolute",left:pctBudget+"%",top:0,bottom:0,
                          width:pctOver+"%",background:C.fd,transition:"width .5s"}}/>
                      )}
                      {/* Projection future (zone hachurée entre dépensé/dépassé et projected) */}
                      {totalProjected > foodTotal && (
                        <div style={{position:"absolute",
                          left:(alreadyOver?(pctBudget+pctOver):pctSpent)+"%",top:0,bottom:0,
                          width:(pctProj - (alreadyOver?(pctBudget+pctOver):pctSpent))+"%",
                          background:totalProjected>FOOD_BUDGET
                            ? "repeating-linear-gradient(45deg,#fca5a5,#fca5a5 6px,#fef2f2 6px,#fef2f2 12px)"
                            : "repeating-linear-gradient(45deg,#86efac,#86efac 6px,#f0fdf4 6px,#f0fdf4 12px)",
                          transition:"width .5s",
                          borderRadius:pctProj>=99?"0 8px 8px 0":"0"}}/>
                      )}
                      {/* Ligne Budget verticale */}
                      <div style={{position:"absolute",left:pctBudget+"%",top:-6,bottom:-6,
                        width:3,background:"#1a1a1a",borderRadius:2,zIndex:3}}/>
                      <div style={{position:"absolute",left:pctBudget+"%",bottom:-22,
                        transform:"translateX(-50%)",fontSize:10,fontWeight:800,color:"#1a1a1a",whiteSpace:"nowrap"}}>
                        Budget {(FOOD_BUDGET/1000).toFixed(0)}k
                      </div>
                    </div>

                    {/* Légende */}
                    <div style={{display:"flex",gap:20,marginTop:32,fontSize:11,flexWrap:"wrap"}}>
                      <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
                        <span style={{width:14,height:10,borderRadius:2,background:alreadyOver?C.grn:C.grn,display:"inline-block"}}/>
                        <span style={{color:C.t2}}>Dans le budget · {Math.min(foodTotal,FOOD_BUDGET).toLocaleString("fr-CH")} CHF</span>
                      </span>
                      {alreadyOver && (
                        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
                          <span style={{width:14,height:10,borderRadius:2,background:C.fd,display:"inline-block"}}/>
                          <span style={{color:C.t2}}>Dépassement actuel · +{(foodTotal-FOOD_BUDGET).toLocaleString("fr-CH")} CHF</span>
                        </span>
                      )}
                      {totalProjected > foodTotal && (
                        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
                          <span style={{width:14,height:10,borderRadius:2,
                            background:totalProjected>FOOD_BUDGET
                              ? "repeating-linear-gradient(45deg,#fca5a5,#fca5a5 3px,#fef2f2 3px,#fef2f2 6px)"
                              : "repeating-linear-gradient(45deg,#86efac,#86efac 3px,#f0fdf4 3px,#f0fdf4 6px)",
                            display:"inline-block"}}/>
                          <span style={{color:C.t2}}>
                            Projection fin {MONTHS_LBL[selMonth-1]} · +{(totalProjected-foodTotal).toLocaleString("fr-CH")} CHF à venir
                          </span>
                        </span>
                      )}
                      <span style={{marginLeft:"auto",fontWeight:700,color:C.t1}}>
                        Projection vs budget : <b style={{color:totalProjected>FOOD_BUDGET?C.fd:C.grn}}>
                          {totalProjected>FOOD_BUDGET?"+":""}{(((totalProjected/FOOD_BUDGET)-1)*100).toFixed(1)}%
                        </b>
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* ── Table fournisseurs ─────────────────────────────────── */}
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5,marginTop:22}}>
                <thead>
                  <tr style={{background:C.hdr}}>
                    {["Fournisseur","Dépensé","Rythme","Projeté fin mois","Statut"].map(function(h){
                      return <th key={h} style={{padding:"8px 12px",textAlign:h==="Fournisseur"?"left":"right",fontSize:9.5,color:"#fff",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {suppPacing.map(function(s,i){
                    // Rythme affiché = même logique que total (hebdo si méthode weekly)
                    var rateDisplay = projMethod==="weekly"
                      ? (s.projected / (daysInMonth/7)).toFixed(0) + "/sem"
                      : s.dailyRate.toFixed(0) + "/j";
                    return (
                      <tr key={i} style={{background:s.willOverrun?"rgba(239,68,68,0.06)":i%2===0?"rgba(255,255,255,0.8)":"rgba(212,238,212,0.3)",borderBottom:"1px solid "+C.bdr}}>
                        <td style={{padding:"9px 12px"}}>
                          <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
                            <span style={{width:8,height:8,borderRadius:2,background:s.color,display:"inline-block"}}/>
                            <span style={{fontWeight:600,color:C.t1}}>{s.name}</span>
                          </span>
                        </td>
                        <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:s.color}}>CHF {(s.spent/1000).toFixed(1)}k</td>
                        <td style={{padding:"9px 12px",textAlign:"right",color:C.t3}}>CHF {rateDisplay}</td>
                        <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:s.willOverrun?C.fd:C.t1}}>CHF {(s.projected/1000).toFixed(1)}k</td>
                        <td style={{padding:"9px 12px",textAlign:"right"}}>
                          {s.willOverrun
                            ? <span style={{padding:"2px 10px",borderRadius:8,fontSize:10,fontWeight:700,color:"#fff",background:C.fd}}>⚠ ALERT</span>
                            : <span style={{padding:"2px 10px",borderRadius:8,fontSize:10,fontWeight:600,color:C.grn,background:"rgba(175,100,42,0.06)",border:"1px solid rgba(55,128,75,0.2)"}}>✓ OK</span>}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{background:"rgba(90,138,90,0.1)",borderTop:"2px solid "+C.grn}}>
                    <td style={{padding:"9px 12px",fontWeight:800,color:globalAlert?C.fd:C.grn}}>TOTAL FOOD</td>
                    <td style={{padding:"9px 12px",textAlign:"right",fontWeight:800,color:globalAlert?C.fd:C.grn}}>CHF {(foodTotal/1000).toFixed(1)}k</td>
                    <td style={{padding:"9px 12px",textAlign:"right",color:C.t3}}>
                      {projMethod==="weekly"
                        ? "CHF "+Math.round(totalProjected/(daysInMonth/7))+"/sem"
                        : "CHF "+(daysElapsed>0?(foodTotal/daysElapsed).toFixed(0):0)+"/j"}
                    </td>
                    <td style={{padding:"9px 12px",textAlign:"right",fontWeight:800,color:globalAlert?C.fd:C.grn}}>CHF {(totalProjected/1000).toFixed(1)}k</td>
                    <td style={{padding:"9px 12px",textAlign:"right"}}>
                      {globalAlert
                        ? <span style={{padding:"2px 10px",borderRadius:8,fontSize:10,fontWeight:700,color:"#fff",background:C.fd,display:"inline-flex",alignItems:"center",gap:4}}>
                            <svg width="14" height="12" viewBox="0 0 38 34"><path d="M19 1L1 33h36L19 1z" fill="#FACC15" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round"/><circle cx="19" cy="20" r="3" fill="none" stroke="#1a1a1a" strokeWidth="2"/><ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(45,19,20)"/><ellipse cx="19" cy="20" rx="9" ry="4" fill="none" stroke="#1a1a1a" strokeWidth="1.8" transform="rotate(-45,19,20)"/></svg>
                            OVERSPEND
                          </span>
                        : <span style={{padding:"2px 10px",borderRadius:8,fontSize:10,fontWeight:700,color:"#fff",background:T.ok}}>✓ WITHIN BUDGET</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
      )}

      {/* Weekly spend by supplier chart */}
      {monitor && monitor.by_week && monitor.by_week.length > 0 && (
        <Card style={{marginBottom:20,padding:"20px 24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:"#1a1a1a"}}>
              Weekly spend by supplier — {MONTHS_LBL[selMonth-1]} (excl. Webstar)
            </div>
            <div style={{fontSize:11,color:"#1a1a1a",fontWeight:600}}>
              Budget: CHF {FOOD_BUDGET/1000}k/month · CHF {FOOD_BUDGET/4000}k/week
            </div>
          </div>
          {(() => {
            const weeks = monitor.by_week;
            const maxW = Math.max(...weeks.map(w => w.total), FOOD_BUDGET/4);
            const allSupp = [...new Set(weeks.flatMap(w => Object.keys(w.suppliers)))];
            let cumul = 0;
            return (
              <div>
                <div style={{display:"flex",gap:8,alignItems:"flex-end",height:180,marginBottom:8}}>
                  {weeks.map((w,wi) => {
                    cumul += w.total;
                    const overBudgetNow = cumul > FOOD_BUDGET;
                    return (
                      <div key={wi} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        <div style={{fontSize:10,fontWeight:700,color:"#1a1a1a"}}>{fmtK(w.total)}</div>
                        <div style={{width:"100%",height:Math.max((w.total/maxW)*140, 4),
                          borderRadius:"4px 4px 0 0",overflow:"hidden",display:"flex",flexDirection:"column-reverse"}}>
                          {allSupp.map((s,si) => {
                            const val = w.suppliers[s] || 0;
                            if (val <= 0) return null;
                            return (
                              <div key={si} title={s+": CHF "+Math.round(val)}
                                style={{width:"100%",height:(val/w.total)*100+"%",
                                  background:SUPP_PAL[si%SUPP_PAL.length],minHeight:val>0?2:0}}/>
                            );
                          })}
                        </div>
                        <div style={{fontSize:10,color:"#1a1a1a",fontWeight:500}}>{w.label}</div>
                        <div style={{fontSize:9,color:overBudgetNow?"#b2382f":"#37804b",fontWeight:700}}>
                          Σ {fmtK(cumul)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Budget line reference */}
                <div style={{borderTop:"2px dashed #1a1a1a",marginTop:4,paddingTop:6,
                  display:"flex",justifyContent:"space-between",fontSize:10,color:"#1a1a1a"}}>
                  <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                    {allSupp.map((s,si) => (
                      <span key={si} style={{display:"inline-flex",alignItems:"center",gap:4}}>
                        <span style={{width:8,height:8,borderRadius:2,background:SUPP_PAL[si%SUPP_PAL.length],display:"inline-block"}}/>
                        <span style={{fontWeight:500}}>{s}</span>
                      </span>
                    ))}
                  </div>
                  <span style={{fontWeight:700}}>Total: CHF {(foodTotal/1000).toFixed(1)}k</span>
                </div>
              </div>
            );
          })()}
        </Card>
      )}

      <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px",marginBottom:20}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:16}}>
          DRY transfers by chef — subtotals by supplier (Webstar excluded)
          <span style={{fontSize:10,fontWeight:400,color:C.t3,marginLeft:8}}>(▼ click to expand)</span>
        </div>
        <ChefSupplierTable data={chefData}/>
      </div>

      <div style={{background:C.surf,border:"1px solid "+C.bdr,borderRadius:12,padding:"20px 24px"}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:C.t3,marginBottom:16}}>Most repeated food products — current month (Webstar excluded)</div>
        {loading ? (
          <div style={{color:C.t3,padding:"20px",textAlign:"center"}}>Loading...</div>
        ) : !monitor ? (
          <div style={{color:C.t3,padding:"20px",textAlign:"center"}}>No data</div>
        ) : (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:C.hdr}}>
                {["#","Product","Supplier","Unit price","Times","Total qty","Total CHF"].map(h => (
                  <th key={h} style={{padding:"9px 14px",textAlign:h==="Product"?"left":"center",fontSize:9.5,color:"#fff",fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(monitor?.top_repeated_products||[]).filter(p => (p.supplier||"").toUpperCase() !== "WEBSTAR").map((p,i) => (
                <tr key={i} style={{background:i%2===0?"rgba(255,255,255,0.8)":"rgba(212,238,212,0.3)",borderBottom:"1px solid "+C.bdr}}>
                  <td style={{padding:"10px 14px",textAlign:"center",color:C.t3,fontWeight:600}}>{i+1}</td>
                  <td style={{padding:"10px 14px",color:C.t1,fontWeight:500,fontSize:11.5}}>{p.name}</td>
                  <td style={{padding:"10px 14px",color:C.t3,fontSize:11}}>{p.supplier||"—"}</td>
                  <td style={{padding:"10px 14px",textAlign:"center",color:C.t3,fontWeight:600}}>CHF {p.price_unit||"—"}</td>
                  <td style={{padding:"10px 14px",textAlign:"center"}}>
                    <span style={{display:"inline-block",padding:"2px 10px",background:p.count>=5?"rgba(239,68,68,0.1)":"rgba(212,238,212,0.5)",color:p.count>=5?C.fd:C.grn,borderRadius:10,fontSize:12,fontWeight:700}}>{p.count}x</span>
                  </td>
                  <td style={{padding:"10px 14px",textAlign:"center",color:C.t3,fontWeight:600}}>{p.qty||"—"}</td>
                  <td style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:C.t1}}>{fmtK(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}



// ─── SIMULATOR TAB ────────────────────────────────────────────────────────
const SIM_DEFAULTS = {
  lv: { label:"Lake View",     color:"#37804b", covers:369, price:5.00,  services_week:7, weeks:12 },  // 369×7 ≈ 2585/sem · 12 sem W0-W11
  fd: { label:"Fine Dining",   color:"#b2382f", covers:25,  price:15.00, services_week:2, weeks:13 },  // 25×2 = 50/sem (Mer+Jeu midi)
  fi: { label:"Food Inspired", color:"#f97316", covers:40,  price:20.00, services_week:4, weeks:13 },  // 40×4 = 160/sem (Mar→Ven)
  sk: { label:"Soul Kitchen",  color:"#8b5cf6", covers:80,  price:12.00, services_week:6, weeks:13 },  // 80×6 = 480/sem (5 chef×60 + 1 mise en place×180 Ven)
};
const Q2_BUDGET_DEFAULT = 155100;

function SimSlider({ label, min, max, step, value, onChange, color, unit }) {
  return (
    <div style={{marginBottom:10}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
        <span style={{fontSize:11,color:"#64748b"}}>{label}</span>
        <span style={{fontSize:12,fontWeight:700,color:color||"#1e2010"}}>{value}{unit||""}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e=>onChange(Number(e.target.value))}
        style={{width:"100%",accentColor:color||"#5a8a5a",cursor:"pointer"}}/>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#94a3b8"}}>
        <span>{min}{unit||""}</span><span>{max}{unit||""}</span>
      </div>
    </div>
  );
}

function SimulatorTab() {
  const [params, setParams] = useState(
    Object.fromEntries(Object.entries(SIM_DEFAULTS).map(([k,v])=>[k,{covers:v.covers,price:v.price,services_week:v.services_week}]))
  );

  const set = (entity, field, val) => setParams(p=>({...p,[entity]:{...p[entity],[field]:val}}));

  const calc = (entity) => {
    const p = params[entity];
    const d = SIM_DEFAULTS[entity];
    const per_service = p.covers * p.price;
    const per_week    = per_service * p.services_week;
    const per_month   = per_week * (d.weeks/3);
    const q2_total    = per_service * p.services_week * d.weeks;
    return { per_service, per_week, per_month, q2_total };
  };

  const totals = Object.keys(SIM_DEFAULTS).reduce((acc,k)=>{
    const c = calc(k);
    return {
      per_service: acc.per_service + c.per_service,
      per_week:    acc.per_week    + c.per_week,
      per_month:   acc.per_month   + c.per_month,
      q2_total:    acc.q2_total    + c.q2_total,
    };
  }, {per_service:0,per_week:0,per_month:0,q2_total:0});

  const [budget, setBudget] = useState(Q2_BUDGET_DEFAULT);
  const over = totals.q2_total > budget;
  const alertColor = over ? "#b2382f" : "#37804b";

  const fmt = n => "CHF " + Math.round(n).toLocaleString("fr-CH");

  return (
    <div>
      <div style={{background:"#fff",border:"1px solid #c8bfb0",borderRadius:12,padding:"16px 24px",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".1em",whiteSpace:"nowrap"}}>Q2 Budget target</div>
          <input type="range" min={100000} max={500000} step={20000} value={budget}
            onChange={e=>setBudget(Number(e.target.value))}
            style={{flex:1,minWidth:200,accentColor:"#5a8a5a",cursor:"pointer"}}/>
          <div style={{fontSize:20,fontWeight:800,color:"#5a8a5a",whiteSpace:"nowrap"}}>CHF {budget.toLocaleString("fr-CH")}</div>
          <button onClick={()=>setBudget(Q2_BUDGET_DEFAULT)}
            style={{padding:"4px 12px",borderRadius:6,border:"1px solid #c8bfb0",background:"transparent",fontSize:11,color:"#64748b",cursor:"pointer"}}>
            Reset
          </button>
        </div>
        <div style={{display:"flex",gap:12,marginTop:8,fontSize:10.5,color:"#94a3b8"}}>
          {[100000,120000,140000,160000,180000,200000,220000,240000,260000,280000,300000,320000,340000,360000,380000,400000].map(v=>(
            <span key={v} onClick={()=>setBudget(v)}
              style={{cursor:"pointer",color:budget===v?"#5a8a5a":"#94a3b8",fontWeight:budget===v?700:400,textDecoration:"underline"}}>
              CHF {(v/1000).toFixed(0)}k
            </span>
          ))}
        </div>
      </div>
      <div style={{padding:"14px 18px",background:"#fff",border:"1px solid "+alertColor+"50",borderRadius:10,marginBottom:20,display:"flex",alignItems:"center",gap:24,flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:9.5,color:"#64748b",textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Q2 Projected total — all restaurants</div>
          <div style={{fontSize:36,fontWeight:800,color:alertColor,lineHeight:1}}>{fmt(totals.q2_total)}</div>
          <div style={{fontSize:12,color:"#64748b",marginTop:4}}>
            Q2 Budget: <b>CHF 180 000</b> · Gap: <b style={{color:alertColor}}>{over?"+":""}{fmt(totals.q2_total-budget)}</b>
          </div>
        </div>
        <div style={{flex:1,minWidth:200}}>
          <div style={{height:14,background:"#f0f4f0",borderRadius:6,overflow:"hidden",marginBottom:6}}>
            <div style={{width:Math.min(totals.q2_total/budget*100,100)+"%",height:"100%",background:alertColor,borderRadius:6,transition:"width .3s"}}/>
          </div>
          <div style={{display:"flex",gap:20,fontSize:11,color:"#64748b"}}>
            <span>Per service: <b style={{color:"#1e2010"}}>{fmt(totals.per_service)}</b></span>
            <span>Per week: <b style={{color:"#1e2010"}}>{fmt(totals.per_week)}</b></span>
            <span>Per month: <b style={{color:"#1e2010"}}>{fmt(totals.per_month)}</b></span>
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          <span style={{padding:"4px 14px",borderRadius:10,fontSize:11,fontWeight:700,color:alertColor,background:alertColor+"18",border:"1px solid "+alertColor+"40"}}>
            {over?"OVER BUDGET":"WITHIN BUDGET"} ({(totals.q2_total/budget*100).toFixed(1)}%)
          </span>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        {Object.entries(SIM_DEFAULTS).map(([key,def])=>{
          const p = params[key];
          const c = calc(key);
          const over_entity = c.q2_total > (budget * (def.covers / Object.values(SIM_DEFAULTS).reduce((a,d)=>a+d.covers,0)));
          return (
            <div key={key} style={{background:"#fff",border:"1px solid #c8bfb0",borderRadius:12,padding:"20px 24px",borderLeft:"4px solid "+def.color}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <div style={{fontSize:14,fontWeight:800,color:def.color}}>{def.label}</div>
                <div style={{fontSize:10.5,color:"#64748b"}}>{p.services_week} svc/week · {def.weeks} weeks Q2</div>
              </div>

              <SimSlider label="Covers per service" min={10} max={400} step={5} value={p.covers}
                onChange={v=>set(key,"covers",v)} color={def.color} />
              <SimSlider label="CHF per cover" min={3} max={40} step={0.5} value={p.price}
                onChange={v=>set(key,"price",v)} color={def.color} unit=" CHF"/>
              <SimSlider label="Services per week" min={1} max={14} step={1} value={p.services_week}
                onChange={v=>set(key,"services_week",v)} color={def.color} unit=" svc"/>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginTop:14,paddingTop:12,borderTop:"1px solid #e2e8f0"}}>
                {[
                  {l:"Per service", v:c.per_service},
                  {l:"Per week",    v:c.per_week},
                  {l:"Per month",   v:c.per_month},
                  {l:"Q2 Total",    v:c.q2_total, bold:true},
                ].map((item,i)=>(
                  <div key={i} style={{textAlign:"center",padding:"8px 4px",background:i===3?"rgba(0,0,0,0.03)":"transparent",borderRadius:6}}>
                    <div style={{fontSize:9,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".07em",marginBottom:3}}>{item.l}</div>
                    <div style={{fontSize:item.bold?15:13,fontWeight:item.bold?800:600,color:item.bold?def.color:"#1e2010"}}>{fmt(item.v)}</div>
                  </div>
                ))}
              </div>

              <div style={{marginTop:10,height:6,background:"#f0f4f0",borderRadius:3}}>
                <div style={{width:Math.min(c.q2_total/budget*100,100)+"%",height:"100%",background:def.color,borderRadius:3,transition:"width .3s"}}/>
              </div>
              <div style={{fontSize:10,color:"#94a3b8",marginTop:3,textAlign:"right"}}>
                {(c.q2_total/budget*100).toFixed(1)}% of Q2 budget
              </div>
            </div>
          );
        })}
      </div>

      <div style={{background:"#fff",border:"1px solid #c8bfb0",borderRadius:12,padding:"20px 24px",marginTop:20}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",textTransform:"uppercase",color:"#64748b",marginBottom:16}}>Q2 Summary — all restaurants</div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{background:"linear-gradient(90deg,#5a8a5a,#7aaa7a)"}}>
              {["Restaurant","Covers/svc","Svc/week","CHF/cover","Per service","Per week","Per month","Q2 Total","% of budget"].map(h=>(
                <th key={h} style={{padding:"9px 12px",textAlign:h==="Restaurant"?"left":"right",fontSize:9.5,color:"#fff",fontWeight:700}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(SIM_DEFAULTS).map(([key,def],i)=>{
              const p = params[key];
              const c = calc(key);
              return (
                <tr key={key} style={{background:i%2===0?"rgba(255,255,255,0.8)":"rgba(212,238,212,0.3)",borderBottom:"1px solid #c8bfb0"}}>
                  <td style={{padding:"9px 12px",fontWeight:700,color:def.color}}>{def.label}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",color:"#1e2010"}}>{p.covers}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",color:"#1e2010"}}>{p.services_week}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",color:def.color,fontWeight:600}}>CHF {p.price}</td>
                  <td style={{padding:"9px 12px",textAlign:"right"}}>{fmt(c.per_service)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right"}}>{fmt(c.per_week)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right"}}>{fmt(c.per_month)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontWeight:800,color:def.color}}>{fmt(c.q2_total)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",color:"#64748b"}}>{(c.q2_total/budget*100).toFixed(1)}%</td>
                </tr>
              );
            })}
            <tr style={{background:"rgba(90,138,90,0.1)",borderTop:"2px solid #5a8a5a"}}>
              <td style={{padding:"9px 12px",fontWeight:800,color:"#5a8a5a"}}>TOTAL</td>
              <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700}}>
                {Object.keys(SIM_DEFAULTS).reduce((a,k)=>a+params[k].covers,0)}
              </td>
              <td style={{padding:"9px 12px"}}/>
              <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700}}>{fmt(totals.per_service)}</td>
              <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700}}>{fmt(totals.per_week)}</td>
              <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700}}>{fmt(totals.per_month)}</td>
              <td style={{padding:"9px 12px",textAlign:"right",fontWeight:800,color:alertColor,fontSize:15}}>{fmt(totals.q2_total)}</td>
              <td style={{padding:"9px 12px",textAlign:"right",fontWeight:800,color:alertColor}}>{(totals.q2_total/budget*100).toFixed(1)}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// APP — nouveau layout avec sidebar
// ════════════════════════════════════════════════════════════════════════════
// ─── PLANNING TAB ─────────────────────────────────────────────────────────
// ─── PLANNING TAB ─────────────────────────────────────────────────────────
// Source unique des couverts, PAR TRIMESTRE (07/10/2026). Le F&B manager saisit
// chefs + couverts, clique "Appliquer" → PUT /planning?quarter=N. Le trimestre en
// cours est choisi par l'API ; un trimestre clos (Q3) s'affiche en lecture seule,
// ce qui protège Q3 Live et le Weekly Report Q3.
function PlanningTab() {
  const DAYS  = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
  const WEEKS = ["W0","W1","W2","W3","W4","W5","W6","W7","W8","W9","W10","W11"];
  const RESTS = {
    lv:{label:"Lake View",    color:C.lv},
    sk:{label:"Soul Kitchen", color:C.sk},
    fi:{label:"Food Inspired",color:C.fi},
    fd:{label:"Fine Dining",  color:C.fd},
  };
  // FD = déjeuner uniquement (Axel). Les autres = midi + soir.
  const SERVICES = { lv:["midi","soir"], sk:["midi","soir"], fi:["midi","soir"], fd:["midi"] };
  const SVC_LABEL = { midi:"Midi", soir:"Soir" };
  const R = {
    ii:{ bg:"#ddeeff", color:"#0a3d6b", dot:"#378ADD", label:"Chef instructeur — même location" },
    ie:{ bg:"#d4f0e4", color:"#0a4a30", dot:"#1D9E75", label:"Chef instructeur — location externe ↗" },
    pr:{ bg:"#eae6fc", color:"#2d237a", dot:"#7c72d8", label:"Chef de production" },
  };

  const [planning, setPlanning] = useState(null);
  const [quarter, setQuarter] = useState(null);       // null = trimestre en cours (choisi par l'API)
  const [week,  setWeek]  = useState(null);
  const [rest,  setRest]  = useState("lv");
  const [dirty, setDirty] = useState(false);
  const [saving,setSaving]= useState(false);
  const [saved, setSaved] = useState(false);
  const [err,   setErr]   = useState("");
  const [modal, setModal] = useState(null);           // {svc, day, idx} — idx:-1 = ajout
  const [form,  setForm]  = useState({name:"",loc:"",role:"ii"});

  // ── Chargement depuis le backend ──────────────────────────────────────────
  // semaine contenant aujourd'hui (sinon W0 avant le trimestre, W11 après)
  function currentWeek(ws){
    if(!ws) return "W0";
    const today = new Date(); today.setHours(0,0,0,0);
    let cur = null;
    WEEKS.forEach(w => {
      const s = new Date((ws[w]||"") + "T00:00:00");
      const e = new Date(s.getTime() + 7*864e5);
      if(today >= s && today < e) cur = w;
    });
    if(cur) return cur;
    return today < new Date(ws.W0 + "T00:00:00") ? "W0" : "W11";
  }

  const skipLoad = useRef(false);                     // évite le double chargement au 1er rendu
  useEffect(() => {
    if(skipLoad.current){ skipLoad.current = false; return; }
    setErr("");
    fetch(API + "/planning" + (quarter ? "?quarter=" + quarter : ""))
      .then(r => r.json())
      .then(d => {
        setPlanning(d); setDirty(false); setSaved(false);
        if(!quarter && d.quarter_num){ skipLoad.current = true; setQuarter(d.quarter_num); }
        setWeek(d.closed ? "W11" : currentWeek(d.week_start));
      })
      .catch(e => setErr("Chargement impossible : " + e));
  }, [quarter]);

  function switchQuarter(q){
    if(q === quarter) return;
    if(dirty && !window.confirm("Des modifications ne sont pas appliquées. Changer de trimestre quand même ?")) return;
    setPlanning(null); setQuarter(q);
  }

  function mutate(fn) {
    if(planning && planning.closed) return;           // trimestre clos = lecture seule
    setPlanning(p => {
      const next = JSON.parse(JSON.stringify(p));
      fn(next);
      return next;
    });
    setDirty(true); setSaved(false);
  }

  function svcObj(p, wk, r, s) {
    p.weeks[wk]      = p.weeks[wk]      || {};
    p.weeks[wk][r]   = p.weeks[wk][r]   || {};
    p.weeks[wk][r][s]= p.weeks[wk][r][s]|| {covers:[0,0,0,0,0,0,0], chefs:[[],[],[],[],[],[],[]]};
    return p.weeks[wk][r][s];
  }

  function setCover(svc, day, val) {
    const n = Math.max(0, parseInt(val,10) || 0);
    mutate(p => { svcObj(p, week, rest, svc).covers[day] = n; });
  }

  // ── Chefs (modal) ─────────────────────────────────────────────────────────
  function openAdd(svc, day){ if(planning.closed) return; setForm({name:"",loc:"",role:"ii"}); setModal({svc,day,idx:-1}); }
  function openEdit(svc, day, idx){
    if(planning.closed) return;
    const c = planning.weeks[week][rest][svc].chefs[day][idx];
    setForm({name:c.name||"", loc:c.loc||"", role:c.role||"ii"}); setModal({svc,day,idx});
  }
  function saveModal(){
    if(!form.name.trim()){ setModal(null); return; }
    mutate(p => {
      const chefs = svcObj(p, week, rest, modal.svc).chefs[modal.day];
      const chip = {name:form.name.trim(), loc:form.loc.trim(), role:form.role};
      if(modal.idx===-1) chefs.push(chip); else chefs[modal.idx]=chip;
    });
    setModal(null);
  }
  function deleteChip(){
    mutate(p => { svcObj(p, week, rest, modal.svc).chefs[modal.day].splice(modal.idx,1); });
    setModal(null);
  }

  // ── Actions semaine ───────────────────────────────────────────────────────
  function copyPrev(){
    const i = WEEKS.indexOf(week);
    if(i<=0) return;
    mutate(p => { p.weeks[week] = JSON.parse(JSON.stringify(p.weeks[WEEKS[i-1]] || {})); });
  }
  // Reprend la semaine-type du trimestre précédent (sa W2) dans la semaine affichée
  async function importPrevType(){
    const pq = (planning.quarters||[]).filter(q => q.num < planning.quarter_num).slice(-1)[0];
    if(!pq) return;
    try{
      const d = await fetch(API + "/planning?quarter=" + pq.num).then(r => r.json());
      const src = (d.weeks||{}).W2;
      if(!src) throw new Error("semaine W2 introuvable dans " + pq.key);
      mutate(p => { p.weeks[week] = JSON.parse(JSON.stringify(src)); });
    }catch(e){ setErr("Import impossible : " + e.message); }
  }
  function clearCovers(){
    mutate(p => {
      const wk = p.weeks[week] || {};
      Object.keys(wk).forEach(r => Object.keys(wk[r]).forEach(s => { wk[r][s].covers=[0,0,0,0,0,0,0]; }));
    });
  }

  // ── APPLY → PUT ───────────────────────────────────────────────────────────
  async function apply(){
    setSaving(true); setErr("");
    try{
      const res = await fetch(API + "/planning?quarter=" + planning.quarter_num, {
        method:"PUT", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ weeks:planning.weeks, prices:planning.prices, updated_by:"F&B Manager" }),
      });
      const out = await res.json();
      if(!out.ok) throw new Error(out.detail || "PUT refusé");
      setDirty(false); setSaved(true);
      setPlanning(p => ({...p, updated_at: out.updated_at}));
      setTimeout(()=>setSaved(false), 3500);
    }catch(e){ setErr("Échec de la sauvegarde : " + e.message); }
    finally{ setSaving(false); }
  }

  // ── Rendu ─────────────────────────────────────────────────────────────────
  if(err && !planning) return <div style={{padding:24,color:"#b2382f",fontSize:13}}>⚠ {err}</div>;
  if(!planning || !week) return <div style={{padding:40,textAlign:"center",color:C.t3,fontSize:13}}>Chargement du planning…</div>;

  const QK      = planning.quarter || "Q3";
  const closed  = !!planning.closed;
  const wsOf    = (w) => (planning.week_start||{})[w];
  const ddmm    = (iso) => iso ? iso.slice(8,10) + "." + iso.slice(5,7) : "";
  const curWk   = closed ? null : currentWeek(planning.week_start);
  const prevQ   = (planning.quarters||[]).filter(q => q.num < planning.quarter_num).slice(-1)[0];
  const rc      = RESTS[rest].color;
  const services= SERVICES[rest];
  const prices  = planning.prices || {lv:5,fi:20,fd:15,sk:12};
  const wk      = planning.weeks[week] || {};

  const cov = (r,s,d) => ((((wk[r]||{})[s]||{}).covers)||[])[d] || 0;
  const rowTotal = (s) => DAYS.reduce((a,_,d)=>a+cov(rest,s,d),0);
  const dayTotal = (d) => services.reduce((a,s)=>a+cov(rest,s,d),0);
  const weekTotal= () => services.reduce((a,s)=>a+rowTotal(s),0);
  const chefsAt  = (s,d) => (((wk[rest]||{})[s]||{}).chefs||[])[d] || [];

  const TH = {padding:"9px 8px",textAlign:"center",fontSize:11,color:"#fff",fontWeight:700};
  const totalCA = weekTotal() * (prices[rest]||0);

  return (
    <div style={{fontFamily:"DM Sans,Helvetica Neue,sans-serif"}}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:16,flexWrap:"wrap"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,flexWrap:"wrap"}}>
            <span style={{fontSize:13,fontWeight:700,color:C.t1}}>Planning hebdomadaire — {QK} 2026</span>
            <span style={{fontSize:10.5,color:C.t3}}>{ddmm(planning.start)} → {ddmm(planning.end)}</span>
            <div style={{display:"flex",gap:4,marginLeft:4}}>
              {(planning.quarters||[]).map(q => {
                const on = q.num === planning.quarter_num;
                const col = q.closed ? "#4C7BD9" : "#E08A00";
                return (
                  <button key={q.num} onClick={()=>switchQuarter(q.num)}
                    style={{padding:"3px 10px",borderRadius:20,border:"1px solid "+(on?col:C.bdr),
                      background:on?col:"transparent",color:on?"#fff":C.t3,fontSize:10.5,fontWeight:700,
                      letterSpacing:".06em",cursor:"pointer"}}>
                    {q.key} · {q.closed ? "CLOSED" : "LIVE"}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{fontSize:10.5,color:C.t3}}>
            {closed
              ? <span>Trimestre <b>clos</b> — lecture seule. Ces couverts restent la source de {QK} Live et du Weekly Report {QK}.</span>
              : <span>Saisir les <b>couverts</b> par service et le <b>nom des chefs</b> · Appliquer enregistre le planning {QK}</span>}
            {planning.updated_at && <span style={{marginLeft:8,color:C.t3,opacity:.7}}>· dernière sauvegarde {String(planning.updated_at).replace("T"," ").slice(0,16)}</span>}
          </div>
        </div>
        {!closed && <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {dirty && <span style={{fontSize:11,color:"#b06a00",fontWeight:600}}>● Modifications non appliquées</span>}
          {saved && <span style={{fontSize:11,color:"#1D9E75",fontWeight:700}}>✓ Appliqué</span>}
          {prevQ && (
          <button onClick={importPrevType} disabled={saving} title={"Copie la W2 de "+prevQ.key+" (couverts + chefs) dans "+week}
            style={{padding:"7px 14px",borderRadius:8,border:"1px solid "+C.bdr,background:C.surf,color:C.t1,fontSize:12,cursor:"pointer"}}>
            Semaine-type {prevQ.key}
          </button>
          )}
          <button onClick={copyPrev} disabled={saving || week==="W0"}
            style={{padding:"7px 14px",borderRadius:8,border:"1px solid "+C.bdr,background:C.surf,color:week==="W0"?C.t3:C.t1,fontSize:12,cursor:week==="W0"?"default":"pointer"}}>
            Copier sem. précédente
          </button>
          <button onClick={clearCovers} disabled={saving}
            style={{padding:"7px 14px",borderRadius:8,border:"1px solid "+C.bdr,background:C.surf,color:C.t3,fontSize:12,cursor:"pointer"}}>
            Vider couverts
          </button>
          <button onClick={apply} disabled={saving || !dirty}
            style={{padding:"8px 22px",borderRadius:9,border:"none",fontSize:13,fontWeight:800,letterSpacing:".02em",
              cursor:(saving||!dirty)?"default":"pointer",color:"#fff",
              background:(saving||!dirty)?"#b9c7b9":"linear-gradient(135deg,#37804b,#2d7a4a)",
              boxShadow:(saving||!dirty)?"none":"0 2px 10px rgba(45,122,74,.35)",transition:"all .15s"}}>
            {saving ? "Application…" : "✓ Appliquer"}
          </button>
        </div>}
      </div>

      {err && <div style={{marginBottom:10,padding:"8px 12px",borderRadius:8,background:"#fdf2f0",border:"1px solid #fca5a5",color:"#b2382f",fontSize:12}}>⚠ {err}</div>}

      {/* ── Week selector ──────────────────────────────────────── */}
      <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
        {WEEKS.map(w => (
          <button key={w} onClick={()=>setWeek(w)} title={wsOf(w) ? "Semaine du "+ddmm(wsOf(w)) : w}
            style={{padding:"4px 11px",borderRadius:14,border:"1px solid "+(week===w?C.grn:(curWk===w?"#E08A00":C.bdr)),
              background:week===w?C.grn:"transparent",color:week===w?"#fff":C.t3,
              fontSize:12,fontWeight:week===w?700:400,cursor:"pointer",lineHeight:1.15,
              display:"flex",flexDirection:"column",alignItems:"center",minWidth:46}}>
            <span>{w}{curWk===w && <span style={{color:week===w?"#fff":"#E08A00"}}> ●</span>}</span>
            <span style={{fontSize:9,opacity:.75}}>{ddmm(wsOf(w))}</span>
          </button>
        ))}
      </div>

      {/* ── Restaurant tabs (avec total couverts) ──────────────── */}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        {Object.entries(RESTS).map(([key,r]) => {
          const t = SERVICES[key].reduce((a,s)=>a+DAYS.reduce((b,_,d)=>b+cov(key,s,d),0),0);
          return (
            <button key={key} onClick={()=>setRest(key)}
              style={{padding:"7px 16px",borderRadius:8,border:"2px solid "+(rest===key?r.color:C.bdr),
                background:rest===key?r.color+"18":"transparent",color:rest===key?r.color:C.t3,
                fontSize:13,fontWeight:rest===key?700:500,cursor:"pointer",display:"flex",gap:8,alignItems:"center"}}>
              {r.label}
              <span style={{fontSize:11,fontWeight:700,padding:"1px 7px",borderRadius:10,
                background:rest===key?r.color:"#e8ebe8",color:rest===key?"#fff":C.t3}}>{fmtN(t)}</span>
            </button>
          );
        })}
      </div>

      {/* ── KPI bar (couverts / prix / CA) ─────────────────────── */}
      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        {[
          {k:"Couverts "+week, v:fmtN(weekTotal())},
          {k:"CHF / couvert",  v:"CHF "+(prices[rest]||0)},
          {k:"CA estimé "+week, v:fmtN(totalCA)+" CHF"},
        ].map((x,i)=>(
          <div key={i} style={{flex:"1 1 150px",background:rc+"0e",border:"1px solid "+rc+"33",borderRadius:10,padding:"10px 14px"}}>
            <div style={{fontSize:9.5,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:C.t3}}>{x.k}</div>
            <div style={{fontSize:19,fontWeight:800,color:rc,marginTop:2}}>{x.v}</div>
          </div>
        ))}
      </div>

      {/* ── Legend ─────────────────────────────────────────────── */}
      <div style={{display:"flex",gap:16,marginBottom:10,flexWrap:"wrap"}}>
        {Object.entries(R).map(([k,r]) => (
          <span key={k} style={{display:"flex",alignItems:"center",gap:5,fontSize:10.5,color:r.color}}>
            <span style={{width:10,height:10,borderRadius:"50%",background:r.dot,display:"inline-block"}}/>{r.label}
          </span>
        ))}
      </div>

      {/* ── Grille ─────────────────────────────────────────────── */}
      <div style={{overflowX:"auto",borderRadius:10,border:"1px solid "+C.bdr}}>
        <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed",minWidth:820}}>
          <thead>
            <tr style={{background:"linear-gradient(90deg,#5a8a5a,#7aaa7a)"}}>
              <th style={{...TH,textAlign:"left",padding:"9px 14px",width:"9%"}}>Service</th>
              {DAYS.map(d => <th key={d} style={{...TH,width:"11%"}}>{d}</th>)}
              <th style={{...TH,width:"10%",background:"rgba(0,0,0,.12)"}}>Total</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s, ri) => (
              <tr key={s} style={{borderBottom:"1px solid "+C.bdr,background:ri%2===0?"#fff":"rgba(212,238,212,0.15)"}}>
                <td style={{padding:"8px 14px",fontWeight:700,fontSize:12,color:C.t1,verticalAlign:"top",borderRight:"1px solid "+C.bdr}}>
                  {SVC_LABEL[s]}
                </td>
                {DAYS.map((d,di) => {
                  const chips = chefsAt(s,di);
                  const c = cov(rest,s,di);
                  return (
                    <td key={di} style={{padding:"6px 6px",verticalAlign:"top",borderRight:"1px solid "+C.bdr+"50"}}>
                      {/* Couverts */}
                      <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:5}}>
                        <input type="number" min="0" value={c===0?"":c}
                          placeholder="0" readOnly={closed}
                          onChange={e=>setCover(s,di,e.target.value)}
                          style={{width:"100%",padding:"4px 6px",borderRadius:6,textAlign:"center",
                            border:"1px solid "+(c>0?rc+"66":C.bdr),background:c>0?rc+"10":"#fafbfa",
                            color:c>0?rc:C.t3,fontSize:13,fontWeight:700,boxSizing:"border-box",
                            MozAppearance:"textfield"}}/>
                        <span style={{fontSize:8.5,color:C.t3,whiteSpace:"nowrap"}}>cvt</span>
                      </div>
                      {/* Chefs */}
                      {chips.map((chip,idx)=>{
                        const role = R[chip.role] || R.ii;
                        return (
                          <div key={idx} onClick={()=>openEdit(s,di,idx)}
                            style={{background:role.bg,borderLeft:"3px solid "+role.dot,borderRadius:5,
                              padding:"3px 6px",marginBottom:3,cursor:closed?"default":"pointer",fontSize:10.5}}
                            onMouseEnter={e=>e.currentTarget.style.opacity=".75"}
                            onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                            <div style={{fontWeight:700,color:role.color,lineHeight:1.2}}>{chip.name}</div>
                            {chip.loc && <div style={{fontSize:9,color:role.color,opacity:.75,lineHeight:1.2}}>{chip.loc}</div>}
                          </div>
                        );
                      })}
                      {!closed && <div onClick={()=>openAdd(s,di)}
                        style={{fontSize:15,color:"#bbb",textAlign:"center",cursor:"pointer",lineHeight:"16px",transition:"color .15s"}}
                        onMouseEnter={e=>e.currentTarget.style.color=C.grn}
                        onMouseLeave={e=>e.currentTarget.style.color="#bbb"}>+ chef</div>}
                    </td>
                  );
                })}
                {/* Total ligne */}
                <td style={{padding:"6px 8px",textAlign:"center",verticalAlign:"top",background:rc+"08",fontWeight:800,fontSize:15,color:rc}}>
                  {fmtN(rowTotal(s))}
                </td>
              </tr>
            ))}
            {/* Total / jour */}
            <tr style={{background:"#2f3b2f"}}>
              <td style={{padding:"9px 14px",fontWeight:700,fontSize:11,color:"#dfe8df",textTransform:"uppercase",letterSpacing:".06em"}}>Total / jour</td>
              {DAYS.map((d,di)=>(
                <td key={di} style={{padding:"9px 6px",textAlign:"center",fontWeight:800,fontSize:14,color:"#fff"}}>{fmtN(dayTotal(di))}</td>
              ))}
              <td style={{padding:"9px 8px",textAlign:"center",fontWeight:900,fontSize:16,color:"#fff",background:"rgba(255,255,255,.08)"}}>{fmtN(weekTotal())}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{marginTop:10,padding:"9px 14px",background:"#f8faf8",borderRadius:8,border:"1px solid "+C.bdr,fontSize:11,color:C.t3,lineHeight:1.6}}>
        {!closed && <>Cliquer un chef pour le modifier · <b>+ chef</b> pour ajouter · saisir les couverts directement dans les cases. </>}
        Le total par jour agrège midi + soir. « Appliquer » écrit dans la source de couverts du {QK}
        {QK==="Q3" ? " (Q3 Live, Weekly Report Q3)." : " — un fichier par trimestre, le planning Q3 n'est jamais modifié."}
      </div>

      {/* ── Modal chef ─────────────────────────────────────────── */}
      {modal && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.45)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}} onClick={()=>setModal(null)}>
          <div style={{background:"#fff",borderRadius:12,padding:"24px 28px",width:320,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:14,fontWeight:700,color:C.t1,marginBottom:16}}>
              {modal.idx===-1?"Ajouter un chef":"Modifier le chef"}
            </div>
            {[["Nom","name"],["Location / Cuisine","loc"]].map(([label,field])=>(
              <div key={field} style={{marginBottom:12}}>
                <div style={{fontSize:10,color:C.t3,textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>{label}</div>
                <input value={form[field]} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))}
                  style={{width:"100%",padding:"8px 10px",borderRadius:7,border:"1px solid "+C.bdr,fontSize:13,boxSizing:"border-box",color:C.t1}}/>
              </div>
            ))}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,color:C.t3,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6}}>Rôle</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                {Object.entries(R).map(([k,r])=>(
                  <button key={k} onClick={()=>setForm(f=>({...f,role:k}))}
                    style={{padding:"7px 6px",borderRadius:7,border:"2px solid "+(form.role===k?r.dot:C.bdr),
                      background:form.role===k?r.bg:"#fff",color:r.color,fontSize:10,cursor:"pointer",
                      textAlign:"center",fontWeight:form.role===k?700:400}}>
                    {k==="ii"?"Interne":k==="ie"?"Externe ↗":"Prod."}
                  </button>
                ))}
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              {modal.idx!==-1 && (
                <button onClick={deleteChip}
                  style={{flex:1,padding:"9px",borderRadius:8,border:"1px solid #fca5a5",background:"#fdf2f0",color:"#b2382f",fontSize:13,cursor:"pointer",fontWeight:600}}>
                  Supprimer
                </button>
              )}
              <button onClick={saveModal}
                style={{flex:2,padding:"9px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#5a8a5a,#2d7a4a)",color:"#fff",fontSize:13,cursor:"pointer",fontWeight:700}}>
                {modal.idx===-1?"Ajouter":"Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APP — top bar layout
// ─────────────────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
//  📦  INVENTORY — MOIS COURANT (total par location · lignes au clic)
//      Live : poll 30 s + refresh au retour d'onglet, sans cache HTTP
// ═══════════════════════════════════════════════════════════════════
const MONTHS_FULL = ["January","February","March","April","May","June",
  "July","August","September","October","November","December"];

const INV_LOC_COLORS = [
  "#0080b1","#ba4e24","#7161b7","#37804b","#af642a",
  "#cd9130","#0e7297","#6958ad","#b2382f","#4b4641",
  "#7e7974","#a8a39d","#387849","#954d08","#1d1916",
];

// jamais de cache : un chef qui soumet doit apparaître au tick suivant
const NOCACHE = {cache:"no-store", headers:{"Cache-Control":"no-cache", "Pragma":"no-cache"}};
const INV_POLL_MS = 30000;

// Remonte l'erreur RÉELLE (404 endpoint absent, 500 Odoo, API arrêtée) au lieu
// de laisser le composant afficher un "aucune donnée" trompeur.
const invFetch = (url) => fetch(url, NOCACHE)
  .then(r => {
    if (!r.ok) {
      const route = url.split("?")[0].replace(API, "");
      throw new Error(`HTTP ${r.status} ${r.statusText || ""} sur ${route}`.trim());
    }
    return r.json();
  })
  .then(d => {
    if (d && d.detail) {
      throw new Error("API 87 : " + (typeof d.detail === "string"
        ? d.detail : JSON.stringify(d.detail)));
    }
    return d;
  });

function InventoryValuationTab() {
  const [year]                = useState(2026);
  const [month, setMonth]     = useState(() => { const d = new Date(); return d.getMonth() || 12; });   // ← dernier mois clos par défaut
  const [countedOnly, setCountedOnly] = useState(true);
  const [cmpMonth, setCmpMonth] = useState(0);   // 0 = pas de comparaison
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr]         = useState("");
  const [lastSync, setLastSync] = useState(null);
  const [live, setLive]       = useState(true);
  const [open, setOpen]       = useState(null);       // location dépliée
  const [lines, setLines]     = useState({});         // cache lignes produits
  const [sortKey, setSortKey] = useState("value");    // value | count | name
  const [q, setQ]             = useState("");         // filtre produit
  const [hidePending, setHidePending] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lang, setLang] = useState("en");        // FR / EN (08/10/2026)
  const tr = (fr, en) => lang === "fr" ? fr : en;

  const openRef = useRef(null);
  useEffect(() => { openRef.current = open; }, [open]);

  const loadLines = useCallback((locId, silent) => {
    if (!silent) setLines(p => ({...p,
      [locId]:{loading:true, rows:(p[locId]&&p[locId].rows)||[], err:""}}));
    return invFetch(`${API}/dashboard/inventory_month_lines?year=${year}&month=${month}`
      + `&location_id=${locId}&counted_only=${countedOnly}&_t=${Date.now()}`)
      .then(d => setLines(p => ({...p,
        [locId]:{loading:false, rows:d.products||[], err:""}})))
      .catch(e => setLines(p => ({...p,
        [locId]:{loading:false, rows:(p[locId]&&p[locId].rows)||[], err:String(e)}})));
  }, [year, month, countedOnly]);

  const load = useCallback((silent) => {
    if (silent) setRefreshing(true); else setLoading(true);
    return invFetch(`${API}/dashboard/inventory_month?year=${year}&month=${month}`
      + `&counted_only=${countedOnly}&compare_month=${cmpMonth}&_t=${Date.now()}`)
      .then(d => {
        if (!d || !Array.isArray(d.locations)) {
          throw new Error("Réponse inattendue de l'API (pas de champ 'locations')");
        }
        setData(d); setErr(""); setLastSync(new Date());
      })
      .catch(e => setErr(e && e.message ? e.message : String(e)))
      .then(() => { setLoading(false); setRefreshing(false); });
  }, [year, month, countedOnly, cmpMonth]);

  // chargement initial + changement de mois / périmètre
  useEffect(() => { setOpen(null); setLines({}); setQ(""); load(false); }, [load]);

  // poll 30 s (suspendu si l'onglet est caché) + refresh au retour
  useEffect(() => {
    if (!live) return undefined;
    const tick = () => {
      if (document.hidden) return;
      load(true);
      if (openRef.current) loadLines(openRef.current, true);
    };
    const id = setInterval(tick, INV_POLL_MS);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [live, load, loadLines]);

  const refreshNow = () => { load(true); if (open) loadLines(open, true); };

  const toggle = (loc) => {
    setQ("");
    if (open === loc.id) { setOpen(null); return; }
    setOpen(loc.id);
    if (!lines[loc.id]) loadLines(loc.id, false);
    else loadLines(loc.id, true);          // on réaffiche le cache et on rafraîchit derrière
  };

  if (loading && !data) return (
    <div style={{background:T.card,border:`1px solid ${T.line}`,borderRadius:12,
      padding:40,textAlign:"center",color:T.hint}}>
      Loading {MONTHS_FULL[month-1]} inventory from Odoo…
    </div>
  );
  if (err && !data) return (
    <div style={{background:"rgba(178,56,47,0.06)",border:`1px solid ${T.err}`,
      borderRadius:12,padding:"20px 24px"}}>
      <div style={{color:T.err,fontSize:13,fontWeight:800,marginBottom:6}}>
        ⚠ {tr("L'API du serveur 87 ne répond pas correctement","Server 87 API is not responding properly")}
      </div>
      <div style={{color:T.err,fontSize:12.5,fontFamily:"DM Mono,SF Mono,monospace",
        marginBottom:10}}>{err}</div>
      <div style={{color:T.sub,fontSize:11.5,lineHeight:1.6}}>
        Un <b>404</b> signifie que l'endpoint n'existe pas encore sur le 87 :
        pousser <code>api.py</code> puis <code>sudo systemctl restart foodops-api</code>.
        Un <b>500</b> vient d'Odoo — voir <code>journalctl -u foodops-api -n 40</code>.
      </div>
      <button onClick={refreshNow} className="no-print"
        style={{marginTop:12,padding:"6px 14px",borderRadius:6,
          border:`1px solid ${T.err}`,background:T.card,color:T.err,
          fontSize:11,fontWeight:700,cursor:"pointer"}}>
        ⟳ {tr("Réessayer","Retry")}
      </button>
    </div>
  );
  if (!data || !data.locations || data.locations.length === 0) return (
    <div style={{background:T.card,border:`1px solid ${T.line}`,borderRadius:12,
      padding:40,textAlign:"center",color:T.hint,fontSize:12.5}}>
      {tr("Aucune location comptée en","No location counted in")} {MONTHS_FULL[month-1]} {year}.
      <div style={{fontSize:11,marginTop:6}}>
        {tr("L'API répond bien — c'est le mois qui est vide.","The API is responding — the month is simply empty.")}
      </div>
    </div>
  );

  const label   = data.label || MONTHS_FULL[month-1];
  const allLocs = data.locations;
  const isDone  = l => (l.count || l.lines || 0) > 0 || l.status === "submitted";
  const counted = allLocs.filter(isDone);
  const pending = allLocs.filter(l => !isDone(l));
  const grand   = allLocs.reduce((s,l) => s + (l.value||0), 0);
  const totLines= allLocs.reduce((s,l) => s + (l.count||l.lines||0), 0);
  const totStock= allLocs.reduce((s,l) => s + (l.stock_count||0), 0);
  const cmpLabel= cmpMonth ? MONTHS_FULL[cmpMonth-1] : "";
  const totCmpV = allLocs.reduce((s,l) => s + (l.cmp_value||0), 0);
  const totCmpN = allLocs.reduce((s,l) => s + (l.cmp_count||0), 0);
  // 08/10/2026 — Un mois CLOS est désormais reconstitué à sa fin de mois par l'API
  // (photo historique, mode "snapshot") : on compare deux photos, on ne les
  // additionne plus. Écart = mois principal − mois comparé.
  const totDiff = grand - totCmpV;
  const snapNote = (mode) => mode === "snapshot" ? tr("photo fin de mois","month-end snapshot") : tr("en cours","in progress");
  const totCumN = totLines;

  let shown = hidePending ? counted.slice() : allLocs.slice();
  shown.sort((a,b) => {
    if (sortKey === "name")  return (a.name||"").localeCompare(b.name||"");
    if (sortKey === "count") return (b.count||b.lines||0) - (a.count||a.lines||0);
    return (b.value||0) - (a.value||0);
  });
  const maxVal = Math.max(1, ...shown.map(l => l.value||0));
  const top    = shown.slice().sort((a,b)=>(b.value||0)-(a.value||0))[0];

  const exportCsv = () => {
    const rows = [["Location","Statut","Comptées","En stock","Couverture %",
                   `Valeur ${label} ${year} (CHF)`,
                   ...(cmpMonth?[`Réf. ${cmpLabel}`,`Valeur ${cmpLabel} (CHF)`,
                                 "Écart (CHF)"]:[]),
                   "Part %"]];
    shown.forEach(l => rows.push([
      l.complete_name || l.name,
      isDone(l) ? "Compté" : "En attente",
      l.count||l.lines||0,
      l.stock_count||0,
      l.stock_count ? ((l.count||0)/l.stock_count*100).toFixed(1) : "0.0",
      (l.value||0).toFixed(2),
      ...(cmpMonth?[l.cmp_count||0,(l.cmp_value||0).toFixed(2),
                    ((l.value||0)-(l.cmp_value||0)).toFixed(2)]:[]),
      grand ? ((l.value||0)/grand*100).toFixed(1) : "0.0",
    ]));
    rows.push(["TOTAL","",totLines,totStock,
      totStock?(totLines/totStock*100).toFixed(1):"0.0",grand.toFixed(2),
      ...(cmpMonth?[totCmpN,totCmpV.toFixed(2),totDiff.toFixed(2)]:[]),"100.0"]);
    const csv = rows.map(r => r.map(c =>
      typeof c === "string" && (c.includes(";")||c.includes(",")) ? `"${c}"` : c).join(";")).join("\n");
    const blob = new Blob(["\uFEFF"+csv], {type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `inventory_${label.toLowerCase()}_${year}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Export global du DÉTAIL produit, toutes locations, sur le(s) mois retenu(s).
  // Un seul appel API : l'endpoint fait le travail côté serveur.
  const exportDetailCsv = () => {
    setExporting(true);
    invFetch(`${API}/dashboard/inventory_month_export?year=${year}&month=${month}`
      + `&compare_month=${cmpMonth}&_t=${Date.now()}`)
      .then(d => {
        const rows = d.rows || [];
        if (!rows.length) { setErr("Aucune ligne à exporter pour cette période."); return; }
        const out = [["Location","Mois","Code","Produit","UOM","Qté","PU CHF",
                      "Valeur CHF","Dernier comptage"]];
        rows.forEach(r => out.push([
          r.location, r.month, r.code||"", r.name||"", r.uom||"",
          r.qty, (r.price||0).toFixed(2), (r.value||0).toFixed(2), r.last_count||"",
        ]));
        out.push(["TOTAL","","","","", "", "",
                  (d.totals&&d.totals.value||0).toFixed(2),
                  `${rows.length} lignes`]);
        const csv = out.map(r => r.map(c =>
          typeof c === "string" && (c.includes(";")||c.includes(",")||c.includes("\n"))
            ? `"${c.replace(/"/g,'""')}"` : c).join(";")).join("\n");
        const blob = new Blob(["\uFEFF"+csv], {type:"text/csv;charset=utf-8"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `inventaire_detail_${label.toLowerCase()}`
          + `${cmpMonth?"_"+cmpLabel.toLowerCase():""}_${year}.csv`;
        a.click(); URL.revokeObjectURL(url);
      })
      .catch(e => setErr(e && e.message ? e.message : String(e)))
      .then(() => setExporting(false));
  };

  const exportLocCsv = (loc, rows) => {
    const out = [["Code","Produit","UOM","Qté","PU CHF","Valeur CHF"]];
    rows.forEach(p => out.push([
      p.code||"", p.name||"", p.uom||"",
      (p.qty||0), (p.price||0).toFixed(2), (p.value||0).toFixed(2)]));
    out.push(["","","","","TOTAL", rows.reduce((s,p)=>s+(p.value||0),0).toFixed(2)]);
    const csv = out.map(r => r.map(c =>
      typeof c === "string" && (c.includes(";")||c.includes(",")) ? `"${c}"` : c).join(";")).join("\n");
    const blob = new Blob(["\uFEFF"+csv], {type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventory_${label.toLowerCase()}_${(loc.name||"loc").replace(/\s+/g,"_")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const syncTxt = lastSync
    ? lastSync.toLocaleTimeString("fr-CH",{hour:"2-digit",minute:"2-digit",second:"2-digit"})
    : "—";

  return (
    <div>
      {/* ── Barre de contexte + état live ─────────────────────────── */}
      <div style={{padding:"12px 16px",
        background:"linear-gradient(135deg,rgba(175,100,42,0.08),rgba(14,114,151,0.05))",
        border:`1px solid ${T.line}`,borderRadius:10,marginBottom:18,
        display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{width:10,height:10,borderRadius:"50%",background:T.brand,
              boxShadow:"0 0 0 3px rgba(175,100,42,0.18)"}}/>
            <span style={{fontSize:11.5,fontWeight:700,color:T.brand,letterSpacing:".02em"}}>
              {label.toUpperCase()} {year} · PHYSICAL INVENTORY
            </span>
          </div>
          <span style={{fontSize:11,color:T.hint}}>
            {counted.length}/{allLocs.length} {tr("locations comptées","locations counted")} · {fmtN(totLines)} {tr("lignes","lines")}
          </span>
          {pending.length > 0 && (
            <span style={{fontSize:10.5,fontWeight:700,color:T.warn,
              background:"rgba(205,145,48,0.12)",padding:"3px 9px",borderRadius:5}}>
              ⏳ {pending.length} {tr("en attente","pending")}
            </span>
          )}
          <span style={{fontSize:10.5,color:T.hint,display:"flex",alignItems:"center",gap:5}}>
            <span style={{width:7,height:7,borderRadius:"50%",
              background:live?(refreshing?T.warn:"#37804b"):T.ghost,
              transition:"background .3s"}}/>
            {live ? (refreshing ? "sync…" : `live · ${tr("maj","updated")} ${syncTxt}`) : `${tr("figé","paused")} · ${tr("maj","updated")} ${syncTxt}`}
          </span>
          {err && (
            <span title={err} style={{fontSize:10.5,fontWeight:700,color:T.err,
              background:"rgba(178,56,47,0.08)",padding:"3px 9px",borderRadius:5,
              maxWidth:340,overflow:"hidden",textOverflow:"ellipsis",
              whiteSpace:"nowrap"}}>
              ⚠ {err}
            </span>
          )}
        </div>
        <div className="no-print" style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{display:"flex",gap:0,border:`1px solid ${T.line}`,borderRadius:6,
            overflow:"hidden"}}>
            {[[true,tr("Comptés","Counted")],[false,tr("Stock complet","Full stock")]].map(([v,l])=>(
              <button key={String(v)} onClick={()=>setCountedOnly(v)}
                title={v?tr("Seuls les quants recomptés ce mois-ci","Only items recounted this month"):tr("Tout le stock Odoo courant de la location","All current Odoo stock of the location")}
                style={{padding:"6px 11px",border:"none",
                  background:countedOnly===v?T.graphite:T.card,
                  color:countedOnly===v?"#fff":T.sub,
                  fontSize:10.5,fontWeight:600,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
          <select value={month} onChange={e=>setMonth(Number(e.target.value))}
            style={{padding:"6px 10px",borderRadius:6,border:`1px solid ${T.line}`,
              background:T.card,color:T.ink,fontSize:11,fontWeight:600,cursor:"pointer"}}>
            {MONTHS_FULL.map((m,i)=>(
              <option key={m} value={i+1}>{m} {year}</option>
            ))}
          </select>
          <select value={cmpMonth} onChange={e=>setCmpMonth(Number(e.target.value))}
            title={tr("Comparer avec un second mois","Compare with a second month")}
            style={{padding:"6px 10px",borderRadius:6,
              border:`1px solid ${cmpMonth?T.info:T.line}`,
              background:cmpMonth?"rgba(14,114,151,0.08)":T.card,
              color:cmpMonth?T.info:T.sub,fontSize:11,
              fontWeight:cmpMonth?700:600,cursor:"pointer"}}>
            <option value={0}>{tr("+ comparer…","+ compare…")}</option>
            {MONTHS_FULL.map((mm,i)=>(
              <option key={mm} value={i+1} disabled={i+1===month}>+ {mm}</option>
            ))}
          </select>
          <div style={{display:"flex",border:`1px solid ${T.line}`,borderRadius:6,overflow:"hidden"}}>
            {["fr","en"].map(lg => (
              <button key={lg} onClick={()=>setLang(lg)}
                style={{padding:"6px 10px",border:"none",cursor:"pointer",fontSize:10.5,fontWeight:700,
                  background:lang===lg?T.graphite:T.card,color:lang===lg?"#fff":T.sub}}>{lg.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={refreshNow} title={tr("Recharger depuis Odoo maintenant","Reload from Odoo now")}
            style={{padding:"6px 11px",borderRadius:6,border:`1px solid ${T.line}`,
              background:T.card,color:T.ink,fontSize:11,fontWeight:600,cursor:"pointer"}}>
            ⟳
          </button>
          <button onClick={()=>setLive(v=>!v)}
            title="Rafraîchissement automatique toutes les 30 s"
            style={{padding:"6px 11px",borderRadius:6,
              border:`1px solid ${live?"#37804b":T.line}`,
              background:live?"rgba(55,128,75,0.1)":T.card,
              color:live?"#37804b":T.sub,fontSize:10.5,fontWeight:700,cursor:"pointer"}}>
            {live?"● LIVE":"○ LIVE"}
          </button>
          <button onClick={exportCsv}
            title="Totaux par location (une ligne par location)"
            style={{padding:"6px 14px",borderRadius:6,border:`1px solid ${T.line}`,
              background:T.card,color:T.ink,fontSize:11,fontWeight:600,cursor:"pointer",
              whiteSpace:"nowrap"}}>
            {tr("⬇ CSV totaux","⬇ Totals CSV")}
          </button>
          <button onClick={exportDetailCsv} disabled={exporting}
            title={cmpMonth
              ? `${tr("Détail produit de toutes les locations","Product detail, all locations")}, ${label} + ${cmpLabel}`
              : `${tr("Détail produit de toutes les locations","Product detail, all locations")}, ${label}`}
            style={{padding:"6px 14px",borderRadius:6,
              border:`1px solid ${T.brand}`,
              background:exporting?T.bg2:"rgba(175,100,42,0.1)",
              color:T.brand,fontSize:11,fontWeight:700,
              cursor:exporting?"wait":"pointer",whiteSpace:"nowrap"}}>
            {exporting?"⏳ export…":tr("⬇ CSV détail produits","⬇ Product detail CSV")}
          </button>
        </div>
      </div>

      {/* ── KPI ───────────────────────────────────────────────────── */}
      <div style={{display:"grid",gridTemplateColumns:"1.3fr 1fr 1fr 1fr",gap:12,marginBottom:12}}>
        <div style={{background:T.card,border:`2px solid ${T.brand}`,borderRadius:12,
          padding:"14px 16px",boxShadow:T.sh2,position:"relative"}}>
          <div style={{position:"absolute",top:10,right:12,fontSize:8.5,fontWeight:800,
            letterSpacing:".1em",color:T.brand,background:"rgba(175,100,42,0.1)",
            padding:"2px 7px",borderRadius:4}}>
            {countedOnly?"PHYSICAL":"ODOO LIVE"}
          </div>
          <div style={{fontSize:9.5,fontWeight:700,letterSpacing:".12em",color:T.hint,marginBottom:8}}>
            {tr("VALEUR STOCK","STOCK VALUE")} {label.toUpperCase()} {year}
          </div>
          <div style={{fontSize:26,fontWeight:800,color:T.brand,letterSpacing:"-.02em"}}>
            {fmtK(grand)}
            {cmpMonth ? (
              <span style={{fontSize:15,color:totDiff>=0?"#b06a00":"#37804b",fontWeight:800,marginLeft:8}}>
                {totDiff>=0?"+":"−"}{fmtK(Math.abs(totDiff))} vs {cmpLabel}
              </span>
            ) : null}
          </div>
          <div style={{fontSize:11,color:T.hint,marginTop:4}}>
            {cmpMonth
              ? `${label.toLowerCase()} (${snapNote(data.mode)}) vs ${cmpLabel.toLowerCase()} (${snapNote(data.compare_mode)}) · ${fmtK(totCmpV)}`
              : (countedOnly
                  ? (data.mode==="snapshot" ? tr("inventaire du mois, reconstitué au dernier jour du mois","month's count, rebuilt as of the last day of the month") : tr("somme des inventaires physiques saisis","sum of physical counts entered"))
                  : tr("stock Odoo courant, toutes locations","current Odoo stock, all locations"))}
          </div>
        </div>

        <div style={{background:T.card,border:`1px solid ${T.line}`,borderRadius:12,
          padding:"14px 16px",boxShadow:T.sh1}}>
          <div style={{fontSize:9.5,fontWeight:700,letterSpacing:".12em",color:T.hint,marginBottom:8}}>
            {tr("LOCATIONS COMPTÉES","LOCATIONS COUNTED")}
          </div>
          <div style={{fontSize:22,fontWeight:800,color:T.info,letterSpacing:"-.02em"}}>
            {counted.length}<span style={{fontSize:14,color:T.hint,fontWeight:700}}> / {allLocs.length}</span>
          </div>
          <div style={{marginTop:8,height:5,background:T.bg2,borderRadius:3,overflow:"hidden"}}>
            <div style={{width:`${allLocs.length?counted.length/allLocs.length*100:0}%`,
              height:"100%",background:T.info,transition:"width .4s"}}/>
          </div>
        </div>

        <div style={{background:T.card,border:`1px solid ${T.line}`,borderRadius:12,
          padding:"14px 16px",boxShadow:T.sh1}}>
          <div style={{fontSize:9.5,fontWeight:700,letterSpacing:".12em",color:T.hint,marginBottom:8}}>
            {tr("COUVERTURE","COVERAGE")}
          </div>
          <div style={{fontSize:22,fontWeight:800,letterSpacing:"-.02em",
            color:totStock&&totCumN/totStock>=0.9?"#37804b"
                 :totStock&&totCumN/totStock>=0.5?T.warn:T.err}}>
            {totStock?`${(totCumN/totStock*100).toFixed(0)}%`:"—"}
          </div>
          <div style={{fontSize:11,color:T.hint,marginTop:4}}>
            {fmtN(totCumN)} {tr("comptées","counted")} / {fmtN(totStock)} {tr("en stock","in stock")}
            {cmpMonth?` (${label.toLowerCase()})`:""}
          </div>
        </div>

        <div style={{background:T.card,border:`1px solid ${T.line}`,borderRadius:12,
          padding:"14px 16px",boxShadow:T.sh1}}>
          <div style={{fontSize:9.5,fontWeight:700,letterSpacing:".12em",color:T.hint,marginBottom:8}}>
            {cmpMonth ? `${tr("COMPTÉ","COUNTED")} ${cmpLabel.toUpperCase()}` : "TOP LOCATION"}
          </div>
          {cmpMonth ? (
            <>
              <div style={{fontSize:22,fontWeight:800,color:T.info,letterSpacing:"-.02em"}}>
                {fmtK(totCmpV)}
              </div>
              <div style={{fontSize:11,color:T.hint,marginTop:4}}>
                {fmtN(totCmpN)} {tr("références","items")} · {snapNote(data.compare_mode)}
              </div>
            </>
          ) : (
            <>
              <div style={{fontSize:16,fontWeight:800,color:T.ink,letterSpacing:"-.01em",
                whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                {top ? top.name : "—"}
              </div>
              <div style={{fontSize:11,color:T.brand,marginTop:4,fontWeight:700}}>
                {top ? fmtK(top.value||0) : "—"}
                <span style={{color:T.hint,fontWeight:500}}>
                  {" "}· {grand && top ? ((top.value||0)/grand*100).toFixed(0) : 0}% {tr("du total","of total")}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Note de périmètre ─────────────────────────────────────── */}
      <div style={{padding:"9px 14px",background:"rgba(241,235,228,0.5)",
        border:`1px solid ${T.line}`,borderRadius:8,marginBottom:20,
        fontSize:11,color:T.sub,display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:14}}>ⓘ</span>
        <span>
          {lang==="fr" ? (<>
          <b>Scope :</b> seuls les produits <b>réellement recomptés en {label} {year}</b>{" "}
          sont valorisés. Un mois clos est reconstitué tel qu'il était au dernier jour du mois ;
          le mois en cours est lu en direct. La colonne Couverture indique la part des références
          en stock vérifiée : sous 50 %, la valeur ne couvre qu'une fraction de la location.
          Saisie par les chefs (CAA Chef Order → Odoo), relue toutes les 30 s.
          Seuls les produits suivis en stock apparaissent : les produits frais commandés en PO
          fournisseur sont consommés directement et ne sont pas inventoriés.
          <b> Cliquez sur une location</b> pour voir le détail produit.</>) : (<>
          <b>Scope:</b> only items <b>actually counted in {label} {year}</b> are valued.
          A closed month is rebuilt as it stood on the last day of the month; the current month
          is read live. The Coverage column shows the share of stocked items that were checked:
          below 50%, the value covers only part of the location. Entered by the chefs
          (CAA Chef Order → Odoo), refreshed every 30 s. Only stock-tracked items appear: fresh
          products bought on supplier POs are consumed directly and are not inventoried.
          <b> Click a location</b> to see its product detail.</>)}
        </span>
      </div>

      {/* ── Tableau par location ──────────────────────────────────── */}
      <div style={{background:T.card,border:`1px solid ${T.line}`,borderRadius:12,
        padding:"20px 24px",marginBottom:20,boxShadow:T.sh1}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          marginBottom:16,flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:".14em",
            textTransform:"uppercase",color:T.hint}}>
            {tr("Valeur d'inventaire par location","Inventory value by location")} · {label} {year}
          </div>
          <div className="no-print" style={{display:"flex",gap:4,alignItems:"center"}}>
            <label style={{fontSize:10.5,color:T.sub,display:"flex",alignItems:"center",
              gap:5,marginRight:8,cursor:"pointer"}}>
              <input type="checkbox" checked={hidePending}
                onChange={e=>setHidePending(e.target.checked)}/>
              {tr("masquer les non comptées","hide uncounted")}
            </label>
            {[["value",tr("Valeur","Value")],["count",tr("Lignes","Lines")],["name","A→Z"]].map(([k,l])=>(
              <button key={k} onClick={()=>setSortKey(k)}
                style={{padding:"4px 12px",borderRadius:6,
                  border:`1px solid ${sortKey===k?T.graphite:T.line}`,
                  background:sortKey===k?T.graphite:T.card,
                  color:sortKey===k?"#fff":T.sub,
                  fontSize:11,fontWeight:600,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
        </div>

        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
            <thead>
              <tr style={{background:T.graphite}}>
                {[
                  {h:"Location", align:"left"},
                  {h:tr("Statut","Status"), align:"center"},
                  {h:tr("Comptées","Counted"), sub:`${tr("en","in")} ${label.toLowerCase()}`, align:"right"},
                  {h:tr("Couverture","Coverage"), sub:`${tr("comptées","counted")} ${label.toLowerCase()} / ${tr("en stock","in stock")}`, align:"right"},
                  {h:tr("Valeur CHF","Value CHF"), sub:`${tr("comptée","counted")} ${label.toLowerCase()}`, align:"right", c:T.brand},
                  ...(cmpMonth ? [
                    {h:`${tr("Compté","Counted")} ${cmpLabel.toLowerCase()}`, sub:tr("réf. · CHF","items · CHF"),
                     align:"right", c:T.info},
                    {h:tr("Écart","Change"), sub:`${label.toLowerCase()} − ${cmpLabel.toLowerCase()}`,
                     align:"right", c:"#37804b"},
                  ] : []),
                  {h:tr("Part","Share"), sub:tr("du total","of total"), align:"right"},
                  {h:"", align:"center"},
                ].map((col,i)=>(
                  <th key={i} style={{padding:"10px 12px",textAlign:col.align,
                    color:"#fff",fontWeight:700,whiteSpace:"nowrap",
                    borderLeft:col.c?`3px solid ${col.c}`:"none"}}>
                    <div style={{fontSize:10,letterSpacing:".08em"}}>{col.h}</div>
                    {col.sub&&<div style={{fontSize:8.5,fontWeight:500,opacity:.55,
                      marginTop:2,letterSpacing:".05em",textTransform:"uppercase"}}>{col.sub}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((l,i)=>{
                const col     = INV_LOC_COLORS[i%INV_LOC_COLORS.length];
                const nLines  = l.count || l.lines || 0;
                const stock   = l.stock_count || 0;
                const cov     = stock ? nLines/stock*100 : 0;
                const covCol  = cov >= 90 ? "#37804b" : cov >= 50 ? T.warn : T.err;
                const done    = isDone(l);
                const share   = grand ? (l.value||0)/grand*100 : 0;
                const isOpen  = open === l.id;
                const st      = lines[l.id] || {loading:false, rows:[], err:""};
                const rows    = (st.rows||[]).slice().sort((a,b)=>(b.value||0)-(a.value||0));
                const filtered= q.trim()
                  ? rows.filter(p => ((p.name||"")+" "+(p.code||"")).toLowerCase()
                      .includes(q.trim().toLowerCase()))
                  : rows;
                return (
                  <React.Fragment key={l.id}>
                    <tr onClick={()=>toggle(l)}
                      style={{background:isOpen?"rgba(175,100,42,0.07)":(i%2===0?"#fff":"rgba(241,235,228,0.4)"),
                        borderBottom:`1px solid ${T.line}`,cursor:"pointer",
                        opacity:done?1:0.55}}>
                      <td style={{padding:"9px 12px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{width:9,height:9,borderRadius:"50%",
                            background:col,flexShrink:0}}/>
                          <span style={{fontSize:12.5,fontWeight:600,color:T.ink}}>{l.name}</span>
                        </div>
                      </td>
                      <td style={{padding:"9px 12px",textAlign:"center"}}>
                        <span style={{fontSize:9.5,fontWeight:700,letterSpacing:".05em",
                          padding:"2px 8px",borderRadius:4,
                          color:done?T.brand:T.warn,
                          background:done?"rgba(175,100,42,0.1)":"rgba(205,145,48,0.12)"}}>
                          {done?tr("COMPTÉ","COUNTED"):tr("EN ATTENTE","PENDING")}
                        </span>
                        {l.last_count && (
                          <div style={{fontSize:8.5,color:T.ghost,marginTop:2}}>{l.last_count}</div>
                        )}
                      </td>
                      <td style={{padding:"9px 12px",textAlign:"right",fontSize:12.5,
                        fontWeight:500,color:nLines?T.sub:T.hint,
                        fontVariantNumeric:"tabular-nums"}}>
                        {nLines?fmtN(nLines):"—"}
                      </td>
                      <td style={{padding:"9px 12px",textAlign:"right",
                        fontVariantNumeric:"tabular-nums"}}>
                        {stock>0 ? (
                          <div style={{display:"flex",alignItems:"center",gap:7,
                            justifyContent:"flex-end"}}>
                            <div style={{width:52,height:5,background:T.bg2,
                              borderRadius:3,overflow:"hidden"}}>
                              <div style={{width:`${Math.min(100,cov)}%`,height:"100%",
                                background:covCol,transition:"width .4s"}}/>
                            </div>
                            <span style={{fontSize:11,color:T.hint,minWidth:52,
                              textAlign:"right"}}>{nLines}/{stock}</span>
                            <span style={{fontSize:11.5,fontWeight:800,color:covCol,
                              minWidth:40,textAlign:"right"}}>{cov.toFixed(0)}%</span>
                          </div>
                        ) : <span style={{color:T.hint}}>—</span>}
                      </td>
                      <td style={{padding:"9px 12px",textAlign:"right",fontSize:13,
                        fontWeight:800,color:(l.value||0)>0?T.brand:T.hint,
                        borderLeft:`3px solid ${T.brand}`,
                        fontVariantNumeric:"tabular-nums"}}>
                        {(l.value||0)>0?fmtN(l.value):"—"}
                      </td>
                      {cmpMonth ? (
                        <td style={{padding:"9px 12px",textAlign:"right",
                          borderLeft:`3px solid ${T.info}`,
                          fontVariantNumeric:"tabular-nums"}}>
                          {(l.cmp_count||0)>0 ? (
                            <>
                              <div style={{fontSize:12.5,fontWeight:800,color:T.info}}>
                                {fmtN(l.cmp_value)}
                              </div>
                              <div style={{fontSize:9.5,color:T.hint,marginTop:1}}>
                                {l.cmp_count} {tr("réf.","items")}{l.cmp_last?` · ${l.cmp_last.slice(8,10)}.${l.cmp_last.slice(5,7)}`:""}
                              </div>
                            </>
                          ) : <span style={{color:T.ghost,fontSize:11}}>—</span>}
                        </td>
                      ) : null}
                      {cmpMonth ? (()=>{
                        const a = l.value||0, b = l.cmp_value||0, d = a - b;
                        const pct = b ? d / b * 100 : null;
                        return (
                          <td style={{padding:"9px 12px",textAlign:"right",
                            borderLeft:"3px solid #37804b",
                            fontVariantNumeric:"tabular-nums"}}>
                            {(a>0 || b>0) ? (
                              <>
                                <div style={{fontSize:13,fontWeight:800,color:d>=0?"#b06a00":"#37804b"}}>
                                  {d>=0?"+":"−"}{fmtN(Math.abs(d))}
                                </div>
                                <div style={{fontSize:9.5,color:T.hint,marginTop:1}}>
                                  {pct==null ? tr("nouveau","new") : `${pct>=0?"+":""}${pct.toFixed(0)} %`}
                                </div>
                              </>
                            ) : <span style={{color:T.ghost,fontSize:11}}>—</span>}
                          </td>
                        );
                      })() : null}
                      <td style={{padding:"9px 12px",textAlign:"right",
                        fontVariantNumeric:"tabular-nums"}}>
                        <div style={{display:"flex",alignItems:"center",gap:7,
                          justifyContent:"flex-end"}}>
                          <div style={{width:64,height:5,background:T.bg2,borderRadius:3,
                            overflow:"hidden"}}>
                            <div style={{width:`${Math.min(100,(l.value||0)/maxVal*100)}%`,
                              height:"100%",background:col,transition:"width .4s"}}/>
                          </div>
                          <span style={{fontSize:11.5,color:T.sub,fontWeight:600,
                            minWidth:38,textAlign:"right"}}>
                            {share>=0.05?share.toFixed(1)+"%":"—"}
                          </span>
                        </div>
                      </td>
                      <td style={{padding:"9px 12px",textAlign:"center",color:T.hint,
                        fontSize:11,fontWeight:700}}>
                        {isOpen?"▾":"▸"}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={cmpMonth?9:7} style={{padding:0,background:"rgba(241,235,228,0.35)",
                          borderBottom:`2px solid ${T.line}`}}>
                          <div style={{padding:"14px 18px 18px"}}>
                            <div style={{display:"flex",justifyContent:"space-between",
                              alignItems:"center",gap:10,marginBottom:10,flexWrap:"wrap"}}>
                              <div style={{fontSize:10,fontWeight:700,letterSpacing:".12em",
                                textTransform:"uppercase",color:T.hint}}>
                                {l.name} · {tr("lignes d'inventaire","inventory lines")} {label} {year}
                                {st.loading && <span style={{color:T.warn}}> · sync…</span>}
                              </div>
                              <div className="no-print" style={{display:"flex",gap:8,alignItems:"center"}}>
                                <input value={q} onChange={e=>setQ(e.target.value)}
                                  placeholder={tr("Filtrer produit ou code…","Filter product or code…")}
                                  onClick={e=>e.stopPropagation()}
                                  style={{padding:"5px 10px",borderRadius:6,
                                    border:`1px solid ${T.line}`,fontSize:11,width:200,
                                    background:"#fff",color:T.ink}}/>
                                <button onClick={(e)=>{e.stopPropagation();loadLines(l.id,false);}}
                                  style={{padding:"5px 10px",borderRadius:6,
                                    border:`1px solid ${T.line}`,background:T.card,
                                    color:T.ink,fontSize:10.5,fontWeight:600,cursor:"pointer"}}>
                                  ⟳
                                </button>
                                <button onClick={(e)=>{e.stopPropagation();exportLocCsv(l,rows);}}
                                  disabled={!rows.length}
                                  style={{padding:"5px 12px",borderRadius:6,
                                    border:`1px solid ${T.line}`,background:T.card,
                                    color:T.ink,fontSize:10.5,fontWeight:600,
                                    cursor:rows.length?"pointer":"default",
                                    opacity:rows.length?1:0.4}}>
                                  ⬇ CSV location
                                </button>
                              </div>
                            </div>

                            {st.loading && rows.length===0 && (
                              <div style={{padding:"18px 0",textAlign:"center",
                                color:T.hint,fontSize:12}}>{tr("Chargement des lignes…","Loading lines…")}</div>
                            )}
                            {st.err && (
                              <div style={{padding:"12px",color:T.err,fontSize:12}}>⚠ {st.err}</div>
                            )}
                            {!st.loading && !st.err && rows.length===0 && (
                              <div style={{padding:"18px 0",textAlign:"center",
                                color:T.hint,fontSize:12}}>
                                {tr("Aucune ligne d'inventaire saisie pour cette location en","No inventory line entered for this location in")} {label}.
                              </div>
                            )}
                            {filtered.length>0 && (
                              <div style={{maxHeight:420,overflowY:"auto",
                                border:`1px solid ${T.line}`,borderRadius:8,background:"#fff"}}>
                                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11.5}}>
                                  <thead>
                                    <tr style={{background:T.bg2,position:"sticky",top:0}}>
                                      {[["Code","left"],[tr("Produit","Product"),"left"],["UOM","left"],
                                        [tr("Qté","Qty"),"right"],[tr("PU CHF","Unit CHF"),"right"],[tr("Valeur CHF","Value CHF"),"right"]]
                                        .map(([h,a],hi)=>(
                                        <th key={hi} style={{padding:"7px 10px",textAlign:a,
                                          fontSize:9,letterSpacing:".08em",fontWeight:700,
                                          color:T.hint,textTransform:"uppercase",
                                          borderBottom:`1px solid ${T.line}`,
                                          whiteSpace:"nowrap"}}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filtered.map((p,pi)=>(
                                      <tr key={(p.code||"")+"_"+pi}
                                        style={{borderBottom:`1px solid ${T.line}`,
                                          background:pi%2===0?"#fff":"rgba(241,235,228,0.3)"}}>
                                        <td style={{padding:"6px 10px",fontSize:10,
                                          color:T.ghost,fontFamily:"DM Mono,SF Mono,monospace",
                                          whiteSpace:"nowrap"}}>{p.code||"—"}</td>
                                        <td style={{padding:"6px 10px",color:T.ink,
                                          fontWeight:500}}>{p.name}</td>
                                        <td style={{padding:"6px 10px",color:T.hint,
                                          fontSize:10.5,whiteSpace:"nowrap"}}>{p.uom||""}</td>
                                        <td style={{padding:"6px 10px",textAlign:"right",
                                          fontWeight:700,color:T.info,
                                          fontVariantNumeric:"tabular-nums"}}>
                                          {(p.qty||0).toLocaleString("fr-CH",
                                            {minimumFractionDigits:0,maximumFractionDigits:2})}
                                        </td>
                                        <td style={{padding:"6px 10px",textAlign:"right",
                                          color:T.sub,fontVariantNumeric:"tabular-nums"}}>
                                          {(p.price||0).toFixed(2)}
                                        </td>
                                        <td style={{padding:"6px 10px",textAlign:"right",
                                          fontWeight:700,color:T.brand,
                                          fontVariantNumeric:"tabular-nums"}}>
                                          {(p.value||0).toLocaleString("fr-CH",
                                            {minimumFractionDigits:2,maximumFractionDigits:2})}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr style={{background:T.bg2,position:"sticky",bottom:0}}>
                                      <td colSpan={3} style={{padding:"8px 10px",fontWeight:800,
                                        fontSize:11,color:T.ink,borderTop:`2px solid ${T.graphite}`}}>
                                        {q.trim()?`${filtered.length} / ${rows.length} ${tr("lignes","lines")}`
                                                 :`${rows.length} ${tr("lignes","lines")}`}
                                      </td>
                                      <td colSpan={2} style={{padding:"8px 10px",textAlign:"right",
                                        fontSize:10,color:T.hint,letterSpacing:".08em",
                                        fontWeight:700,borderTop:`2px solid ${T.graphite}`}}>
                                        {q.trim()?tr("SOUS-TOTAL FILTRÉ","FILTERED SUBTOTAL"):tr("TOTAL LOCATION","LOCATION TOTAL")}
                                      </td>
                                      <td style={{padding:"8px 10px",textAlign:"right",
                                        fontWeight:900,fontSize:12.5,color:T.brand,
                                        borderTop:`2px solid ${T.graphite}`,
                                        fontVariantNumeric:"tabular-nums"}}>
                                        {filtered.reduce((s,p)=>s+(p.value||0),0)
                                          .toLocaleString("fr-CH",{minimumFractionDigits:2,
                                            maximumFractionDigits:2})}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              <tr style={{background:T.bg2,borderTop:`2px solid ${T.graphite}`,
                borderBottom:`2px solid ${T.graphite}`}}>
                <td style={{padding:"12px 12px",fontWeight:800,color:T.ink,fontSize:13}}>
                  TOTAL · {shown.length} locations
                </td>
                <td style={{padding:"12px 12px",textAlign:"center",fontSize:10.5,
                  color:T.hint,fontWeight:600}}>
                  {counted.length} {tr("comptées","counted")}
                </td>
                <td style={{padding:"12px 12px",textAlign:"right",fontWeight:800,
                  fontSize:13,color:T.sub,fontVariantNumeric:"tabular-nums"}}>
                  {fmtN(totLines)}
                </td>
                <td style={{padding:"12px 12px",textAlign:"right",fontWeight:800,
                  fontSize:12,color:T.sub,fontVariantNumeric:"tabular-nums"}}>
                  {totStock ? `${fmtN(totLines)}/${fmtN(totStock)} · ${(totLines/totStock*100).toFixed(0)}%` : "—"}
                </td>
                <td style={{padding:"12px 12px",textAlign:"right",fontWeight:900,
                  fontSize:14,color:T.brand,borderLeft:`3px solid ${T.brand}`,
                  fontVariantNumeric:"tabular-nums"}}>
                  {fmtN(grand)}
                </td>
                {cmpMonth ? (
                  <td style={{padding:"12px 12px",textAlign:"right",fontWeight:900,
                    fontSize:13,color:T.info,borderLeft:`3px solid ${T.info}`,
                    fontVariantNumeric:"tabular-nums"}}>
                    {fmtN(totCmpV)}
                    <div style={{fontSize:9.5,color:T.hint,fontWeight:600}}>{fmtN(totCmpN)} {tr("réf.","items")}</div>
                  </td>
                ) : null}
                {cmpMonth ? (
                  <td style={{padding:"12px 12px",textAlign:"right",fontWeight:900,
                    fontSize:14,color:totDiff>=0?"#b06a00":"#37804b",borderLeft:"3px solid #37804b",
                    fontVariantNumeric:"tabular-nums"}}>
                    {totDiff>=0?"+":"−"}{fmtN(Math.abs(totDiff))}
                    <div style={{fontSize:9.5,color:T.hint,fontWeight:600}}>
                      {totCmpV ? `${totDiff>=0?"+":""}${(totDiff/totCmpV*100).toFixed(0)} %` : "—"}
                    </div>
                  </td>
                ) : null}
                <td style={{padding:"12px 12px",textAlign:"right",fontWeight:800,
                  fontSize:12,color:T.sub}}>100%</td>
                <td/>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{fontSize:10,color:T.ghost,textAlign:"center",marginTop:14}}>
        Source : Odoo stock.quant · {tr("inventaire physique","physical inventory")} {label} {year} ·
        {tr("lecture live","live read")} (poll {INV_POLL_MS/1000}s) · {tr("dernière synchro","last sync")} {syncTxt}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WEBSTAR — Produits non-food · v4 (06.08.2026)
// Cockpit de pilotage : bandeau sombre en haut (KPIs + trimestres + écart),
// tout le reste en sections repliables. Palette propre à l'onglet.
// ─────────────────────────────────────────────────────────────────────────────
const W = {
  bg:    "#0A1220",   // fond cockpit
  panel: "#131E33",   // panneau interne
  edge:  "#233350",   // filets
  txt:   "#E8EEF9",   // texte clair
  dim:   "#8296B8",   // texte secondaire
  ghost: "#54688C",
  teal:  "#00E0C6",   // accent principal / Q1
  blue:  "#4C9AFF",   // Q2
  amber: "#FF9F1C",   // Q3 live
  red:   "#FF4D6D",   // écart / erreur
  green: "#3DDC84",   // conforme
  viol:  "#A78BFA",
  pink:  "#F472B6",
  deep:  "#0a7a6c",   // teal lisible sur fond clair
};
const WS_CSV_BTN = {
  border:`1px solid ${W.edge}`, background:"transparent", color:W.dim,
  borderRadius:8, padding:"5px 11px", fontSize:11.5, fontWeight:700,
  cursor:"pointer", whiteSpace:"nowrap",
};
const WS_PALETTE = [W.teal, W.blue, W.viol, W.amber, W.pink, W.green, "#38BDF8", "#FB7185", "#34D399", "#C084FC"];
const WS_MONTHS = {
  fr: ["Janv","Févr","Mars","Avr","Mai","Juin","Juil","Août","Sept","Oct","Nov","Déc"],
  en: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
};
const WS_M_FROM_LABEL = {
  Janv:1,Janvier:1,"Févr":2,Fevr:2,"Février":2,Mars:3,Avr:4,Avril:4,Mai:5,Juin:6,
  Juil:7,Juillet:7,"Août":8,Aout:8,Sept:9,Oct:10,Octobre:10,Nov:11,Novembre:11,"Déc":12,Dec:12,
  Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,
};
const WS_QCOLOR = {Q1:W.teal, Q2:W.blue, Q3:W.amber, Q4:W.viol};
const WS_QKEYS  = ["q1","q2","q3","q4"];
const WS_NUM    = {fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'};
const WS_norm   = s => (s||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"");
const WS_median = arr => { const a=[...arr].sort((x,y)=>x-y); const n=a.length; return n?(n%2?a[(n-1)/2]:(a[n/2-1]+a[n/2])/2):0; };
const WS_HACCP = [
  {fr:"Gants",en:"Gloves",kw:["gant","glove","nitrile","vinyl","latex"]},
  {fr:"Désinfection / nettoyage",en:"Disinfection / cleaning",kw:["desinfect","disinfect","sanit","nettoy","detergent","savon","soap","degraiss","cleaner","bactericide","javel","ecl ","clean"]},
  {fr:"Protection corporelle",en:"Body protection",kw:["charlotte","hairnet","tablier","apron","calot","masque","mask","blouse","couvre","hat","toque","resille","filet pour les cheveux"]},
  {fr:"Papier / essuyage",en:"Paper / wiping",kw:["papier","paper","essuie","wipe","towel","serviette","wiper","cellulo","roll"]},
];
const WS_PACK_KW = ["sac","sachet","sous vide","vacuum","boite","boîte"," box","poche","paquet","carton"," pcs","pieces","pièces"," x "];
const WS_ODOO = "https://caa2.fx1.tech";

const WS_L = {
  fr: {
    tagline:"achats depuis le", htnote:"HT",
    spend:"Dépense totale", orders:"commandes", avgpo:"PO moyenne", perorder:"par commande",
    units:"Unités", distinct:"produits", perstud:"Coût / étudiant", cohort:"cohorte",
    gapKpi:"Écart Odoo ↔ Webstar", gapSub:"période réconciliée",
    csvProd:"CSV produits", csvOrd:"CSV commandes",
    csvH:{period:"Période depuis", total:"Total HT (CHF)", gen:"Généré le",
          code:"Code Odoo", ws:"Article Webstar", name:"Produit", cat:"Catégorie",
          unit:"Unité", qty:"Quantité", spend:"Dépense HT", orders:"Nb commandes",
          avg:"Prix moyen", min:"Prix min", max:"Prix max", refp:"Prix réf. Webstar",
          pack:"Conditionnement", packn:"Nom conditionnement", gap:"Écart réf.",
          po:"PO Odoo", date:"Date", quarter:"Trimestre",
          sub:"Sous-total HT", refsub:"Sous-total réf."},
    quarters:"Trimestres", qClosed:"clos", qLive:"live", unitsShort:"unités", cpsQ:"coût/étud.",
    evo:"Dépenses par mois", evoWeek:"Dépenses par semaine", month:"Mois", week:"Semaine",
    byCat:"Répartition & top produits", catSub:"Par catégorie Odoo · totaux HT",
    catUncat:"produit(s) sans catégorie — à corriger dans Odoo",
    top:"Top 10 produits", topSub:"par dépense (80/20)",
    prods:"Produits commandés", prodsSub:"Quantité, dépense et fréquence",
    sort:"Trier :", sTotal:"Total", sQty:"Quantité", sFreq:"Fréquence",
    cSpend:"Dépense", cQty:"Quantité", ordered:"× commandé",
    poTitle:"Commandes Webstar", poSub:"Toutes les PO par date · clic = détail des lignes",
    ref:"Réf.", date:"Date", lines:"Lignes", poTotal:"Total PO",
    kpiTitle:"KPIs proposés", kpiSub:"Pistes au-delà de la demande initiale",
    priceWarn:(n)=>`${n} produit(s) avec variation de prix > 10 % : `,
    advTitle:"Analyse avancée", advSub:"Dérive de prix · fréquence/valeur · cadence · HACCP · burn rate",
    driftTitle:"Dérive de prix par produit", driftSub:"Écart entre prix min et max payés (même article, PO différentes)",
    noDrift:"Aucune dérive de prix détectée.",
    fvTitle:"Fréquence vs valeur", fvSub:"Articles fréquents à faible valeur = candidats commande-cadre",
    fvX:"Nb de commandes", fvY:"CHF moyen / commande", fvCand:"Candidats commande-cadre",
    cadTitle:"Cadence de réassort & risque de rupture", cadSub:"Délai moyen entre deux commandes d'un même article",
    cadEvery:"tous les ~", cadDays:"j", cadLast:"dernière", cadSince:"il y a", cadRisk:"À surveiller",
    noCad:"Pas assez d'historique (≥2 commandes requises).",
    haccpTitle:"Consommables HACCP", haccpSub:"Familles critiques pour la traçabilité & les audits",
    haccpLast:"dernier achat", haccpNone:"Aucun consommable HACCP identifié.",
    burnTitle:"Burn rate — mois en cours", burnSub:"Projection fin de mois vs budget non-food",
    burnBudget:"Budget mensuel (CHF)", burnSpent:"Dépensé à date", burnProj:"Projection fin de mois",
    burnOver:"dépassement projeté", burnUnder:"sous budget projeté", burnSetBudget:"Saisis un budget mensuel pour la comparaison.",
    statusActive:"Actif", statusSoon:"À venir", statusPartial:"Courbe hebdo",
    recTitle:"Réconciliation Odoo ↔ Webstar", recSub:"Bon de commande Odoo vs valeur marchandise du portail (HT)",
    recPO:"PO Odoo", recWs:"Cmd Webstar", recWsVal:"Webstar", recOdoo:"Odoo", recGap:"Écart",
    recTotal:"Total période", recOk:"conforme", recUnder:"sous-évalué", recOver:"sur-évalué", recMissing:"absent d'Odoo",
    recExtra:"PO Odoo sans commande Webstar référencée", recNone:"Aucune référence Webstar chargée.",
    recFrom:"réf. depuis le", recHint:"Webstar est la source de vérité. Un écart = prix ou quantité à corriger dans la PO Odoo.",
    anomTitle:"Anomalies à corriger", anomSub:"Lignes de PO à corriger dans Odoo",
    anomNone:"Aucune anomalie détectée 🎉",
    anomZero:"prix nul", anomRef:"≠ prix Webstar", anomQty:"quantité élevée",
    anomNoCode:"code manquant", anomPack:"conditionnement à déclarer",
    packCol:"Cond.", packNone:"—", packUnit:"à l'unité",
    anomPO:"PO", anomMotif:"Motif", anomThr:"Seuil qté (CHF)", anomImpact:"Impact",
    anomOdooP:"Prix Odoo", anomRefP:"Prix Webstar",
    anomTotalImpact:"Impact total des corrections", anomBlocking:"à corriger", anomPackN:"conditionnement",
    anomShowPack:"afficher les conditionnements à déclarer",
    anomHint:"Le conditionnement est lu sur la fiche produit Odoo (onglet Inventaire). Une ligne à l'unité dont le prix × le carton retombe sur le prix Webstar est conforme. « Conditionnement à déclarer » = le rapport est un multiple net mais aucun carton n'est renseigné dans Odoo. « — » : article absent de la référence, envoie le PDF Webstar.",
  },
  en: {
    tagline:"purchases since", htnote:"net of VAT",
    spend:"Total spend", orders:"orders", avgpo:"Avg PO", perorder:"per order",
    units:"Units", distinct:"products", perstud:"Cost / student", cohort:"cohort",
    gapKpi:"Odoo ↔ Webstar gap", gapSub:"reconciled period",
    csvProd:"Products CSV", csvOrd:"Orders CSV",
    csvH:{period:"Period from", total:"Total excl. VAT (CHF)", gen:"Generated on",
          code:"Odoo code", ws:"Webstar item", name:"Product", cat:"Category",
          unit:"Unit", qty:"Quantity", spend:"Spend excl. VAT", orders:"Order count",
          avg:"Avg price", min:"Min price", max:"Max price", refp:"Webstar ref. price",
          pack:"Pack size", packn:"Pack name", gap:"Ref. gap",
          po:"Odoo PO", date:"Date", quarter:"Quarter",
          sub:"Subtotal excl. VAT", refsub:"Ref. subtotal"},
    quarters:"Quarters", qClosed:"closed", qLive:"live", unitsShort:"units", cpsQ:"cost/stud.",
    evo:"Spend by month", evoWeek:"Spend by week", month:"Month", week:"Week",
    byCat:"Breakdown & top products", catSub:"By Odoo category · net of VAT",
    catUncat:"product(s) with no category — fix in Odoo",
    top:"Top 10 products", topSub:"by spend (80/20)",
    prods:"Products ordered", prodsSub:"Quantity, spend and frequency",
    sort:"Sort:", sTotal:"Total", sQty:"Quantity", sFreq:"Frequency",
    cSpend:"Spend", cQty:"Quantity", ordered:"× ordered",
    poTitle:"Webstar orders", poSub:"All POs by date · click = line detail",
    ref:"Ref.", date:"Date", lines:"Lines", poTotal:"PO total",
    kpiTitle:"Suggested KPIs", kpiSub:"Ideas beyond the initial request",
    priceWarn:(n)=>`${n} product(s) with price variation > 10%: `,
    advTitle:"Advanced analytics", advSub:"Price drift · frequency/value · cadence · HACCP · burn rate",
    driftTitle:"Per-product price drift", driftSub:"Gap between min and max price paid (same item, different POs)",
    noDrift:"No price drift detected.",
    fvTitle:"Frequency vs value", fvSub:"Frequent, low-value items = standing-order candidates",
    fvX:"Number of orders", fvY:"Avg CHF / order", fvCand:"Standing-order candidates",
    cadTitle:"Reorder cadence & stockout risk", cadSub:"Average gap between two orders of the same item",
    cadEvery:"every ~", cadDays:"d", cadLast:"last", cadSince:"", cadRisk:"Watch",
    noCad:"Not enough history (≥2 orders required).",
    haccpTitle:"HACCP consumables", haccpSub:"Critical families for traceability & audits",
    haccpLast:"last buy", haccpNone:"No HACCP consumable identified.",
    burnTitle:"Burn rate — current month", burnSub:"Month-end projection vs non-food budget",
    burnBudget:"Monthly budget (CHF)", burnSpent:"Spent to date", burnProj:"Month-end projection",
    burnOver:"projected over", burnUnder:"projected under", burnSetBudget:"Enter a monthly budget to compare.",
    statusActive:"Live", statusSoon:"Coming", statusPartial:"Weekly curve",
    recTitle:"Odoo ↔ Webstar reconciliation", recSub:"Odoo purchase order vs portal goods value (net of VAT)",
    recPO:"Odoo PO", recWs:"Webstar order", recWsVal:"Webstar", recOdoo:"Odoo", recGap:"Gap",
    recTotal:"Period total", recOk:"match", recUnder:"under-stated", recOver:"over-stated", recMissing:"missing in Odoo",
    recExtra:"Odoo POs with no referenced Webstar order", recNone:"No Webstar reference loaded.",
    recFrom:"ref. from", recHint:"Webstar is the source of truth. A gap = price or quantity to fix on the Odoo PO.",
    anomTitle:"Anomalies to fix", anomSub:"PO lines to fix in Odoo",
    anomNone:"No anomaly detected 🎉",
    anomZero:"zero price", anomRef:"≠ Webstar price", anomQty:"high quantity",
    anomNoCode:"missing code", anomPack:"pack size to declare",
    packCol:"Pack", packNone:"—", packUnit:"per unit",
    anomPO:"PO", anomMotif:"Reason", anomThr:"Qty threshold (CHF)", anomImpact:"Impact",
    anomOdooP:"Odoo price", anomRefP:"Webstar price",
    anomTotalImpact:"Total impact of corrections", anomBlocking:"to fix", anomPackN:"pack size",
    anomShowPack:"show pack sizes to declare",
    anomHint:"Pack size is read from the Odoo product form (Inventory tab). A per-unit line whose price × case size lands on the Webstar price is a match. \"Pack size to declare\" = the ratio is a clean multiple but no case is set in Odoo. \"—\": item not in the reference, send the Webstar PDF.",
  },
};

const WS_KPI_IDEAS = [
  {fr:{t:"Coût non-food par étudiant / par trimestre", d:"Dépense Webstar ÷ cohorte (199). Pendant du coût food/cover — chiffre le « kit consommables » réel d'un étudiant sur un terme académique."},
   en:{t:"Non-food cost per student / per term", d:"Webstar spend ÷ cohort (199). Counterpart to food cost/cover — quantifies a student's real \"consumables kit\" over an academic term."}, s:"active"},
  {fr:{t:"Réconciliation Odoo ↔ portail Webstar", d:"Valeur marchandise du portail comparée au bon de commande Odoo, PO par PO. Détecte les prix nuls et les saisies erronées avant qu'ils ne polluent le food cost."},
   en:{t:"Odoo ↔ Webstar portal reconciliation", d:"Portal goods value compared to the Odoo purchase order, PO by PO. Catches zero prices and wrong entries before they pollute food cost."}, s:"active"},
  {fr:{t:"Pic d'achat en début de terme (saisonnalité)", d:"Gants, tabliers, charlottes, papier sont achetés par vagues à chaque intake. Une courbe alignée sur les rentrées révèle sur/sous-commandes et permet de pré-commander."},
   en:{t:"Start-of-term purchasing spike (seasonality)", d:"Gloves, aprons, hairnets, paper are bought in waves at each intake. A curve aligned with term starts reveals over/under-ordering and enables pre-ordering."}, s:"partial"},
  {fr:{t:"Dérive de prix par produit (price drift)", d:"Min / max / prix moyen pondéré du même article entre PO. Branché sur prix_caa, détecte une hausse Webstar silencieuse."},
   en:{t:"Per-product price drift", d:"Min / max / weighted average price of the same item across POs. Wired to prix_caa, it catches a silent Webstar increase."}, s:"active"},
  {fr:{t:"Fréquence vs valeur (candidats commande-cadre)", d:"Articles commandés souvent mais en petites quantités = surcoût administratif. Les regrouper réduit le nombre de PO et la charge de saisie."},
   en:{t:"Frequency vs value (standing-order candidates)", d:"Items ordered often but in small quantities = admin overhead. Bundling them cuts the number of POs and data-entry load."}, s:"active"},
  {fr:{t:"Ratio non-food / food", d:"Part du non-food dans l'approvisionnement total. Repère d'efficacité opérationnelle propre à la restauration de collectivité."},
   en:{t:"Non-food / food ratio", d:"Share of non-food in total procurement. An operational-efficiency benchmark specific to institutional catering."}, s:"soon"},
  {fr:{t:"Cadence de réassort & risque de rupture", d:"Jours moyens entre deux commandes d'un même article. Un délai qui s'allonge sur un consommable critique signale un risque en cuisine pédagogique."},
   en:{t:"Reorder cadence & stockout risk", d:"Average days between two orders of the same item. A lengthening gap on a critical consumable flags risk in the teaching kitchens."}, s:"active"},
  {fr:{t:"Suivi consommables HACCP", d:"Désinfectant contact alimentaire, gants, charlottes par cuisine — utile pour la traçabilité HACCP et les audits."},
   en:{t:"HACCP consumables tracking", d:"Food-contact sanitiser, gloves, hairnets per kitchen — useful for HACCP traceability and audits."}, s:"active"},
  {fr:{t:"Burn rate vs budget non-food dédié", d:"Projection fin de mois, même logique que la page Economat — mais sur un budget non-food séparé, ce qui évite le double-comptage."},
   en:{t:"Burn rate vs dedicated non-food budget", d:"Month-end projection, same logic as the Economat page — but on a separate non-food budget, avoiding double-counting."}, s:"active"},
];

// ── primitives de l'onglet (hors composant : pas de remount, pas de perte de focus)
function WSFold({title, sub, badges, color, defaultOpen, children}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const c = color || W.deep;
  return (
    <div style={{border:`1px solid ${open?c+"55":T.line}`, borderRadius:14, background:T.card,
      marginBottom:12, overflow:"hidden"}}>
      <div onClick={()=>setOpen(o=>!o)} role="button" tabIndex={0}
        onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();setOpen(o=>!o);}}}
        style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:"pointer",
          borderLeft:`3px solid ${c}`, background:open?c+"0A":"transparent"}}>
        <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:20,height:20,
          borderRadius:6,background:c+"1F",color:c,fontSize:12,fontWeight:900,flexShrink:0,
          transform:open?"rotate(90deg)":"none",transition:"transform .18s"}}>›</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:800,color:T.ink,letterSpacing:"-.01em"}}>{title}</div>
          {sub && <div style={{fontSize:11.5,color:T.hint,marginTop:1}}>{sub}</div>}
        </div>
        <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end"}}>
          {(badges||[]).map((b,i)=>(
            <span key={i} style={{...WS_NUM,fontSize:12,fontWeight:800,color:b.c||T.sub,
              background:(b.c||T.sub)+"14",border:`1px solid ${(b.c||T.sub)}33`,
              borderRadius:8,padding:"3px 9px",whiteSpace:"nowrap"}}>{b.t}</span>
          ))}
        </div>
      </div>
      {open && <div style={{padding:"4px 16px 18px"}}>{children}</div>}
    </div>
  );
}

function WSTile({label, value, sub, color}) {
  const c = color || W.teal;
  return (
    <div style={{background:W.panel, border:`1px solid ${W.edge}`, borderRadius:13,
      padding:"13px 15px", position:"relative", overflow:"hidden"}}>
      <div style={{position:"absolute",left:0,top:0,bottom:0,width:3,background:c}}/>
      <div style={{fontSize:9.5,fontWeight:800,letterSpacing:".13em",textTransform:"uppercase",color:W.dim}}>{label}</div>
      <div style={{...WS_NUM,fontSize:25,fontWeight:800,color:c,letterSpacing:"-.02em",marginTop:5,lineHeight:1.1}}>{value}</div>
      {sub && <div style={{fontSize:11,color:W.ghost,marginTop:3}}>{sub}</div>}
    </div>
  );
}

function WSSeg({value, opts, onChange, dark}) {
  return (
    <div style={{display:"inline-flex",border:`1px solid ${dark?W.edge:T.line}`,borderRadius:9,overflow:"hidden"}}>
      {opts.map(o=>(
        <button key={o.v} onClick={()=>onChange(o.v)} style={{border:"none",cursor:"pointer",
          padding:"5px 12px",fontSize:11.5,fontWeight:800,
          background:value===o.v?W.teal:(dark?"transparent":T.card),
          color:value===o.v?W.bg:(dark?W.dim:T.sub)}}>{o.l}</button>
      ))}
    </div>
  );
}

function WSSortBtn({active, dir, onClick, children}) {
  return (
    <button onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:5,cursor:"pointer",
      border:`1px solid ${active?W.deep:T.line}`, background:active?W.deep+"14":T.card,
      color:active?W.deep:T.sub, borderRadius:8,padding:"5px 11px",fontSize:12,fontWeight:700}}>
      {children}{active && <span style={{fontSize:10}}>{dir==="asc"?"▲":"▼"}</span>}
    </button>
  );
}

function WSBars({data, height}) {
  const max = Math.max(...data.map(d=>d.value), 1);
  return (
    <div style={{display:"flex",alignItems:"flex-end",gap:6,height,marginTop:8}}>
      {data.map((d,i)=>{
        const h = Math.max((d.value/max)*(height-32), 2);
        // Barre empilee des que l'API fournit la repartition par categorie.
        // column-reverse = premiere categorie en bas, meme ordre que la carte
        // "Repartition & top produits", donc memes couleurs a la meme place.
        const segs = (d.segs && d.segs.length) ? d.segs : null;
        return (
          <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:5,minWidth:0}}>
            <div style={{...WS_NUM,fontSize:9.5,color:W.dim,whiteSpace:"nowrap"}}>{fmtK(d.value)}</div>
            {segs ? (
              <div title={`${d.label} · ${fmtK(d.value)}\n` + segs.map(g=>`${g.name} : ${fmtK(g.value)}`).join("\n")}
                style={{width:"100%",height:h,display:"flex",flexDirection:"column-reverse",
                  borderRadius:"4px 4px 0 0",overflow:"hidden",
                  boxShadow:`0 0 12px ${segs[segs.length-1].color}44`}}>
                {segs.map((g,j)=>(
                  <div key={j} style={{width:"100%",height:`${(g.value/(d.value||1))*100}%`,background:g.color}}/>
                ))}
              </div>
            ) : (
              <div title={`${d.label} · ${fmtK(d.value)}`}
                style={{width:"100%",height:h,background:d.color,
                  borderRadius:"4px 4px 0 0",boxShadow:`0 0 12px ${d.color}44`}}/>
            )}
            <div style={{fontSize:9.5,color:W.ghost,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:"100%"}}>{d.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function WebstarTab() {
  const [data, setData]   = useState(null);
  const [loading, setLoad]= useState(true);
  const [err, setErr]     = useState("");
  // Onglet destine a la direction (Nelson) : anglais par defaut.
  // Le bouton FR reste disponible.
  const [lang, setLang]   = useState("en");
  const [tf, setTf]       = useState("month");
  const [pSort, setPSort] = useState({key:"spend", dir:"desc"});
  const [oSort, setOSort] = useState({key:"date",  dir:"desc"});
  const [openPO, setOpen] = useState(null);
  const [openRec, setOpenRec] = useState(null);     // détail facture ↔ PO (27/10/2026)
  const [budget, setBudget] = useState("");
  const [anomQtyMin, setAnomQtyMin] = useState(1000);
  const [showPack, setShowPack] = useState(false);

  useEffect(() => {
    setLoad(true); setErr("");
    fetch(`${API}/dashboard/webstar_nonfood`)
      .then(r => r.json())
      .then(d => { if (d.error) setErr(d.error); else setData(d); })
      .catch(e => setErr(String(e)))
      .finally(() => setLoad(false));
  }, []);

  const L = WS_L[lang];

  if (loading) return <Card><div style={{color:T.hint,fontSize:13,padding:"8px 0"}}>Chargement / Loading…</div></Card>;
  if (err)     return <Card><div style={{color:T.err,fontSize:13}}>Erreur : {err}</div></Card>;
  if (!data || !data.totals || !data.totals.pos)
    return <Card><div style={{color:T.hint,fontSize:13}}>Aucune commande Webstar / No Webstar orders.</div></Card>;

  // Cohorte : l'API renvoie celle du trimestre couvert par la periode
  // (Q3 = 96 etudiants, pas 199). Ne PAS remettre 199 en dur ici.
  const t = data.totals, Q = data.quarters || {}, STUD = data.students || 199;
  const nameOf = (o) => lang==="en" ? (o.name_en||o.name_fr||o.name||"?") : (o.name_fr||o.name||"?");
  const catOf  = (o) => lang==="en" ? (o.cat_en||o.cat_fr||o.cat||"")     : (o.cat_fr||o.cat||"");
  const catName= (c) => lang==="en" ? (c.name_en||c.name_fr||c.name||"")  : (c.name_fr||c.name||"");
  const colorFor = (catId) => {
    const i = (data.categories||[]).findIndex(c => c.id === catId);
    return WS_PALETTE[(i<0?0:i) % WS_PALETTE.length];
  };

  const togP = (k) => setPSort(s => ({key:k, dir:s.key===k&&s.dir==="desc"?"asc":"desc"}));
  const togO = (k) => setOSort(s => ({key:k, dir:s.key===k&&s.dir==="desc"?"asc":"desc"}));
  const products = [...(data.products||[])].sort((a,b)=>{const d=a[pSort.key]-b[pSort.key];return pSort.dir==="asc"?d:-d;});
  const pos = [...(data.pos||[])].sort((a,b)=>{
    if (oSort.key==="date"){const d=a.date<b.date?-1:a.date>b.date?1:0;return oSort.dir==="asc"?d:-d;}
    const d=a.total-b.total;return oSort.dir==="asc"?d:-d;
  });
  const maxProd = Math.max(...products.map(p=>p.spend), 1);

  // ── trimestres ──
  const qRows = WS_QKEYS.filter(k => Q[k]).map(k => ({
    key:k, code:k.toUpperCase(), d:Q[k], color:WS_QCOLOR[k.toUpperCase()],
    lbl: lang==="en" ? (Q[k].label_en||"") : (Q[k].label_fr||""),
    live: !Q[k].closed,
  }));
  const qMax   = Math.max(...qRows.map(r=>r.d.spend||0), 1);
  const curQ   = (Q.current||"q3").toUpperCase();
  const prevQ  = (Q.previous||"q2").toUpperCase();
  const delta  = Q.delta_pct || 0;
  const curSpendQ  = Q[Q.current]?.spend || 0;
  const prevSpendQ = Q[Q.previous]?.spend || 0;

  const monthLabel = (s) => {
    const idx = s.m || WS_M_FROM_LABEL[s.label] || null;
    return idx ? WS_MONTHS[lang][idx-1] : (s.label||"");
  };
  // ── Export CSV ──────────────────────────────────────────────────────────
  // Separateur ";" et BOM UTF-8 : Excel FR/CH ouvre le fichier en colonnes
  // et affiche les accents correctement sans passer par l'assistant d'import.
  const csvEsc = (v) => {
    const str = (v === null || v === undefined) ? "" : String(v);
    return /[";\r\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
  };
  const csvNum = (v) => (v === null || v === undefined || v === "") ? "" : String(v);
  const csvDownload = (filename, rows) => {
    const body = rows.map(r => r.map(csvEsc).join(";")).join("\r\n");
    const blob = new Blob(["\uFEFF" + body], {type:"text/csv;charset=utf-8;"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  const csvStamp = new Date().toISOString().slice(0,10);
  const csvHead  = () => [
    [L.csvH.period, data.since || ""],
    [L.csvH.total,  csvNum(t.spend)],
    [L.csvH.gen,    new Date().toLocaleString(lang === "en" ? "en-GB" : "fr-CH")],
    [],
  ];

  // Un produit par ligne, dans l'ordre d'affichage du tableau.
  const exportProductsCsv = () => {
    const H = L.csvH;
    const rows = [
      ...csvHead(),
      [H.code, H.ws, H.name, H.cat, H.unit, H.qty, H.spend, H.orders,
       H.avg, H.min, H.max, H.refp, H.pack, H.packn, H.gap],
      ...products.map(p => [
        p.code || "", p.ws_code || "", nameOf(p), catOf(p), p.unit || "",
        csvNum(p.qty), csvNum(p.spend), csvNum(p.orders),
        csvNum(p.avg), csvNum(p.min), csvNum(p.max),
        csvNum(p.ref_price || ""), csvNum(p.pack_qty || ""), p.pack_name || "",
        csvNum(p.ref_gap || ""),
      ]),
    ];
    csvDownload(`non-food_products_${csvStamp}.csv`, rows);
  };

  // Une ligne de PO par ligne : c'est le grain qui permet de recouper
  // avec le portail Webstar commande par commande.
  const exportOrdersCsv = () => {
    const H = L.csvH;
    const rows = [
      ...csvHead(),
      [H.po, H.date, H.quarter, H.code, H.ws, H.name, H.cat, H.unit,
       H.qty, H.avg, H.sub, H.refp, H.refsub, H.pack],
    ];
    (pos || []).forEach(po => (po.lines || []).forEach(l => rows.push([
      po.ref || "", po.date || "", po.q || "",
      l.code || "", l.ws_code || "", nameOf(l),
      catName((data.categories || []).find(c => c.id === l.cat_id) || {}),
      l.unit || "",
      csvNum(l.qty), csvNum(l.price), csvNum(l.subtotal),
      csvNum(l.ref_price || ""), csvNum(l.ref_subtotal || ""),
      csvNum(l.pack_qty || ""),
    ])));
    csvDownload(`non-food_orders_${csvStamp}.csv`, rows);
  };

  // Segments empiles de l'histogramme. L'API renvoie `cats` (somme des lignes
  // par categorie) alors que `spend` est le total HT des PO : on renormalise
  // pour que la pile fasse exactement la hauteur de la barre.
  const segsOf = (s) => {
    const c = s.cats || {};
    const tot = Object.values(c).reduce((a,b)=>a+(b||0), 0);
    if (!tot) return null;
    const out = (data.categories||[])
      .map(cat => ({name:catName(cat), color:colorFor(cat.id), amount:(c[String(cat.id)]||0)}))
      .filter(x => x.amount > 0);
    const known = out.reduce((a,b)=>a+b.amount, 0);
    if (tot - known > 0.5) out.push({name:"—", color:W.ghost, amount:tot-known});
    const k = (s.spend||0) / tot;
    return out.map(x => ({...x, value:x.amount*k}));
  };
  const series = tf==="month"
    ? (data.monthly||[]).map(s=>({label:monthLabel(s), value:s.spend, color:WS_QCOLOR[s.q]||W.teal, segs:segsOf(s)}))
    : (data.weekly||[]).map(s=>({label:s.label, value:s.spend, color:W.teal, segs:segsOf(s)}));

  // ── réconciliation ──
  const rec = data.reconcile || {};
  const recRows = rec.rows || [];
  const recGap = rec.gap || 0;
  const REC_ST = {
    ok:{t:L.recOk, c:T.ok}, under:{t:L.recUnder, c:T.err},
    over:{t:L.recOver, c:T.warn}, missing:{t:L.recMissing, c:T.err},
  };

  // ── anomalies : API + règle client « quantité élevée » ──
  const byLine = {};
  (data.anomalies||[]).forEach(a=>{ byLine[a.line_id] = {...a, reasons:[...(a.reasons||[])]}; });
  (data.pos||[]).forEach(po=>(po.lines||[]).forEach(l=>{
    const hay = WS_norm((l.name_fr||l.name||"")+" "+(l.name_en||""));
    if (WS_PACK_KW.some(k=>hay.includes(k)) && l.qty>=10 && (l.subtotal||0) > anomQtyMin) {
      if (byLine[l.line_id]) byLine[l.line_id].reasons.push("qty");
      else byLine[l.line_id] = {po:po.ref, po_id:po.id, date:po.date, line_id:l.line_id,
        code:l.code, ws_code:l.ws_code, name_fr:l.name_fr, name_en:l.name_en,
        qty:l.qty, price:l.price, subtotal:l.subtotal, ref_price:l.ref_price,
        suggested:0, delta:0, blocking:false, reasons:["qty"]};
    }
  }));
  const allAnom   = Object.values(byLine);
  const isPackOnly= a => a.reasons.includes("packmiss") && !a.blocking;
  const packAnom  = allAnom.filter(isPackOnly);
  const anomalies = (showPack ? allAnom : allAnom.filter(a=>!isPackOnly(a)))
    .sort((a,b)=> Math.abs(b.delta||0)-Math.abs(a.delta||0) || (b.subtotal||0)-(a.subtotal||0));
  const anomShown  = anomalies.slice(0,40);
  const anomImpact = allAnom.reduce((s,a)=>s+(a.blocking?(a.delta||0):0),0);
  const nBlocking  = allAnom.filter(a=>a.blocking).length;
  const RZ = {zero:{t:L.anomZero,c:T.err}, ref:{t:L.anomRef,c:T.warn},
              qty:{t:L.anomQty,c:T.info}, nocode:{t:L.anomNoCode,c:T.hint},
              packmiss:{t:L.anomPack,c:T.violet||W.viol}};
  const poLink = (id) => id ? `${WS_ODOO}/web#id=${id}&model=purchase.order&view_type=form` : null;

  // ── dérive de prix ──
  const drift = [...(data.products||[])].filter(p=>p.variance>0)
    .sort((a,b)=>b.variance-a.variance).slice(0,12);
  const driftMaxPrice = Math.max(...drift.map(p=>p.max), 1);

  // ── fréquence vs valeur ──
  const fv = (data.products||[]).map(p=>({
    name:nameOf(p), x:p.orders, y:p.orders?p.spend/p.orders:0, spend:p.spend, cat_id:p.cat_id }));
  const medX = WS_median(fv.map(p=>p.x)), medY = WS_median(fv.map(p=>p.y));
  const fvCand = fv.filter(p=>p.x>=medX && p.y<=medY && p.x>=2).sort((a,b)=>b.x-a.x).slice(0,8);
  const fvMaxX = Math.max(...fv.map(p=>p.x),1), fvMaxY = Math.max(...fv.map(p=>p.y),1);

  // ── dates par produit ──
  const acc = {};
  (data.pos||[]).forEach(po=>(po.lines||[]).forEach(l=>{
    const k = l.name_fr||l.name||"?";
    if(!acc[k]) acc[k]={dates:[],obj:l};
    acc[k].dates.push(po.date);
  }));
  const today = new Date();
  const D = (s)=>new Date(s+"T00:00:00");

  const cadence = Object.entries(acc).map(([name,v])=>{
    const u = [...new Set(v.dates)].sort();
    if(u.length<2) return null;
    let g=0; for(let i=1;i<u.length;i++) g += (D(u[i])-D(u[i-1]))/864e5;
    const mean = g/(u.length-1);
    const last = u[u.length-1];
    const since = (today - D(last))/864e5;
    return {obj:v.obj, n:u.length, mean:Math.round(mean), last,
            since:Math.round(since), ratio: mean>0?since/mean:0,
            risk: u.length>=3 && since > mean*1.5};
  }).filter(Boolean).sort((a,b)=> b.ratio - a.ratio).slice(0,12);
  const cadRisk = cadence.filter(c=>c.risk).length;

  const lastByName = {};
  Object.entries(acc).forEach(([n,v])=>{ lastByName[n]=[...v.dates].sort().slice(-1)[0]; });
  const haccp = WS_HACCP.map(fam=>{
    let spend=0, qty=0, last=null, count=0;
    (data.products||[]).forEach(p=>{
      const hay = WS_norm((p.name_fr||p.name||"")+" "+(p.name_en||""));
      if(fam.kw.some(k=>hay.includes(k))){
        spend+=p.spend; qty+=p.qty; count++;
        const ld = lastByName[p.name_fr||p.name];
        if(ld && (!last || ld>last)) last=ld;
      }
    });
    return {fr:fam.fr, en:fam.en, spend:Math.round(spend), qty:Math.round(qty), count, last};
  }).filter(f=>f.spend>0).sort((a,b)=>b.spend-a.spend);
  const haccpTotal = haccp.reduce((s,f)=>s+f.spend,0);

  // ── burn rate ──
  const curM = (data.monthly||[]).slice(-1)[0];
  const curMonthNum = curM ? curM.m : (today.getMonth()+1);
  const dim = new Date(2026, curMonthNum, 0).getDate();
  const elapsed = (curMonthNum === today.getMonth()+1) ? today.getDate() : dim;
  const curSpent = curM ? curM.spend : 0;
  const proj = elapsed>0 ? Math.round(curSpent/elapsed*dim) : 0;
  const budgetNum = Number(budget)||0;
  const burnDelta = budgetNum>0 ? proj - budgetNum : 0;

  const Status = ({s}) => {
    const map = {active:{t:L.statusActive,c:T.ok}, partial:{t:L.statusPartial,c:T.info}, soon:{t:L.statusSoon,c:T.hint}};
    const o = map[s]||map.soon;
    return <Chip label={(s==="active"?"✓ ":"")+o.t} color={o.c}/>;
  };

  const nUncat = (data.uncategorized||[]).length;

  return (
    <div>
      {/* ══════════ COCKPIT ══════════ */}
      <div style={{background:`linear-gradient(155deg, ${W.bg} 0%, #0E1830 60%, #101C36 100%)`,
        border:`1px solid ${W.edge}`, borderRadius:18, padding:"18px 20px 20px", marginBottom:16,
        boxShadow:"0 18px 40px -24px rgba(0,0,0,.55)"}}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",gap:11,flexWrap:"wrap"}}>
            <span style={{width:9,height:9,borderRadius:9,background:W.teal,boxShadow:`0 0 12px ${W.teal}`}}/>
            <span style={{fontSize:12.5,fontWeight:900,letterSpacing:".16em",textTransform:"uppercase",color:W.txt}}>Non-food · Webstar</span>
            <span style={{fontSize:11.5,color:W.ghost}}>{L.tagline} {data.since} · {L.htnote}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <button onClick={exportProductsCsv} className="no-print" style={WS_CSV_BTN}>⬇ {L.csvProd}</button>
            <button onClick={exportOrdersCsv}   className="no-print" style={WS_CSV_BTN}>⬇ {L.csvOrd}</button>
            <WSSeg dark value={lang} onChange={setLang} opts={[{v:"fr",l:"FR"},{v:"en",l:"EN"}]}/>
          </div>
        </div>

        <div style={{display:"grid",gap:11,gridTemplateColumns:"repeat(auto-fit,minmax(158px,1fr))",marginBottom:16}}>
          <WSTile label={L.spend}   value={fmtK(t.spend)}      sub={`${t.pos} ${L.orders}`}         color={W.teal}/>
          <WSTile label={L.avgpo}   value={fmtK(t.avg_po)}     sub={L.perorder}                     color={W.blue}/>
          <WSTile label={L.units}   value={fmtN(t.units)}      sub={`${t.distinct} ${L.distinct}`}  color={W.viol}/>
          {Q.delta_same_pct != null
            ? <WSTile label={`${curQ} vs ${prevQ} · ${lang==="fr"?"même période":"same period"}`}
                      value={`${Q.delta_same_pct>=0?"+":""}${Q.delta_same_pct.toFixed(0)} %`}
                      sub={`${lang==="fr"?"J1→J":"D1→D"}${Q.elapsed_days} : ${fmtK(Q.prev_same||0)} → ${fmtK(curSpendQ)}`}
                      color={Q.delta_same_pct>=0?W.amber:W.green}/>
            : <WSTile label={`${curQ} ${L.qLive}`} value={fmtK(curSpendQ)}
                      sub={`${Q[Q.current]?.pos||0} ${L.orders}`} color={W.amber}/>}
          <WSTile label={L.gapKpi}  value={`${recGap>0?"+":""}${fmtK(recGap)}`}
                  sub={(rec.pending_portal||[]).length
                        ? `${lang==="fr"?"juil. rapproché":"Jul reconciled"} · ${rec.pending_portal.length} PO ${lang==="fr"?"à charger depuis le portail":"to load from portal"} (${fmtK(rec.pending_total||0)})`
                        : `${L.gapSub} · ${nBlocking} ${L.anomBlocking}`}
                  color={Math.abs(recGap)<1?W.green:W.red}/>
        </div>

        <div style={{display:"grid",gap:14,gridTemplateColumns:"minmax(0,1fr) minmax(0,1.1fr)"}}>
          <div style={{background:W.panel,border:`1px solid ${W.edge}`,borderRadius:13,padding:"14px 16px"}}>
            <div style={{fontSize:9.5,fontWeight:800,letterSpacing:".13em",textTransform:"uppercase",color:W.dim,marginBottom:12}}>{L.quarters}</div>
            {qRows.map(r=>(
              <div key={r.key} style={{marginBottom:11}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,marginBottom:4,gap:8}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:7,color:W.txt}}>
                    <b>{r.code}</b><span style={{color:W.ghost}}>{r.lbl}</span>
                    <span style={{fontSize:9,fontWeight:800,letterSpacing:".08em",textTransform:"uppercase",
                      padding:"2px 6px",borderRadius:5,
                      background:r.live?W.amber+"22":"transparent",
                      border:`1px solid ${r.live?W.amber+"66":W.edge}`,
                      color:r.live?W.amber:W.ghost}}>{r.live?"● "+L.qLive:L.qClosed}</span>
                  </span>
                  <b style={{...WS_NUM,color:r.color}}>{fmtK(r.d.spend||0)}</b>
                </div>
                <div style={{height:8,background:"#0A1220",borderRadius:5,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${Math.max((r.d.spend||0)/qMax*100,1.5)}%`,
                    background:r.color,borderRadius:5,boxShadow:`0 0 10px ${r.color}66`}}/>
                </div>
                <div style={{...WS_NUM,fontSize:10.5,color:W.ghost,marginTop:3}}>
                  {fmtN(r.d.units||0)} {L.unitsShort} · {r.d.pos||0} {L.orders}
                </div>
              </div>
            ))}
          </div>
          <div style={{background:W.panel,border:`1px solid ${W.edge}`,borderRadius:13,padding:"14px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
              <div style={{fontSize:9.5,fontWeight:800,letterSpacing:".13em",textTransform:"uppercase",color:W.dim}}>
                {tf==="month"?L.evo:L.evoWeek}
              </div>
              <WSSeg dark value={tf} onChange={setTf} opts={[{v:"month",l:L.month},{v:"week",l:L.week}]}/>
            </div>
            <WSBars data={series} height={146}/>
            <div style={{display:"flex",flexWrap:"wrap",gap:"6px 14px",marginTop:10}}>
              {(data.categories||[]).map(c=>(
                <div key={c.id} style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                  <span style={{width:9,height:9,borderRadius:3,background:colorFor(c.id),flex:"0 0 auto"}}/>
                  <span style={{fontSize:10,color:W.dim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {catName(c)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ══════════ SECTIONS REPLIABLES ══════════ */}

      <WSFold defaultOpen color={Math.abs(recGap)<1?W.green:W.red}
        title={L.recTitle} sub={`${L.recSub}${rec.ref_from?` · ${L.recFrom} ${rec.ref_from}`:""}`}
        badges={[
          {t:`Webstar ${fmtK(rec.webstar_total||0)}`, c:W.deep},
          {t:`Odoo ${fmtK(rec.odoo_total||0)}`,       c:T.sub},
          {t:`${recGap>0?"+":""}${(recGap||0).toFixed(2)}`, c:Math.abs(recGap)<1?T.ok:T.err},
        ]}>
        {recRows.length===0 ? <div style={{fontSize:12.5,color:T.hint}}>{L.recNone}</div> : <>
          <div style={{display:"grid",gridTemplateColumns:"92px 1fr 104px 104px 104px 112px",gap:10,padding:"8px 4px",
            borderBottom:`2px solid ${T.line}`,fontSize:10,fontWeight:800,color:T.hint,textTransform:"uppercase",letterSpacing:".06em"}}>
            <span>{L.recPO}</span><span>{L.recWs}</span>
            <span style={{textAlign:"right"}}>{L.recWsVal}</span>
            <span style={{textAlign:"right"}}>{L.recOdoo}</span>
            <span style={{textAlign:"right"}}>{L.recGap}</span>
            <span style={{textAlign:"right"}}>Statut</span>
          </div>
          {recRows.map((r,i)=>{
            const st = REC_ST[r.status]||REC_ST.ok;
            const link = poLink(r.po_id);
            const fill = r.webstar ? Math.min((r.odoo/r.webstar)*100,100) : 0;
            return (
              <div key={i} style={{padding:"10px 4px",borderBottom:`1px solid ${T.line}`}}>
                <div onClick={()=>setOpenRec(openRec===r.po?null:r.po)}
                  style={{display:"grid",gridTemplateColumns:"92px 1fr 104px 104px 104px 112px",gap:10,alignItems:"center",fontSize:12.5,cursor:"pointer"}}>
                  <span style={{fontWeight:800,color:link?W.deep:T.err,whiteSpace:"nowrap"}}>
                    <span style={{color:FR_P.acc,marginRight:5}}>{openRec===r.po?"▾":"▸"}</span>{r.po}
                    {link && <a href={link} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
                      style={{color:W.deep,textDecoration:"none",marginLeft:4}}>↗</a>}
                  </span>
                  <span style={{color:T.sub,fontSize:11.5}}>
                    {(r.ws_orders||[]).map((w,k)=>(
                      <span key={k} style={{marginRight:10,whiteSpace:"nowrap"}}>
                        <b style={{color:T.ink}}>{w.ws}</b> <span style={{color:T.hint}}>{w.date} · {fmtK(w.total)}</span>
                      </span>
                    ))}
                  </span>
                  <span style={{...WS_NUM,textAlign:"right",fontWeight:800,color:T.ink}}>{(r.webstar||0).toFixed(2)}</span>
                  <span style={{...WS_NUM,textAlign:"right",color:T.sub}}>{(r.odoo||0).toFixed(2)}</span>
                  <span style={{...WS_NUM,textAlign:"right",fontWeight:800,color:Math.abs(r.gap)<0.51?T.ok:(r.gap<0?T.err:T.warn)}}>
                    {r.gap>0?"+":""}{(r.gap||0).toFixed(2)}
                  </span>
                  <span style={{textAlign:"right"}}><Chip label={st.t} color={st.c}/></span>
                </div>
                <div style={{height:5,background:T.grid,borderRadius:3,marginTop:7,overflow:"hidden",display:"flex"}}>
                  <div style={{width:fill+"%",background:st.c,borderRadius:3}}/>
                  <div style={{flex:1,background:`repeating-linear-gradient(45deg,${T.err}22,${T.err}22 4px,transparent 4px,transparent 8px)`}}/>
                </div>
                {openRec===r.po && <FoodReconcileDetail po={r.po} L={FR_L[lang]||FR_L.fr} />}
              </div>
            );
          })}
          <div style={{display:"grid",gridTemplateColumns:"92px 1fr 104px 104px 104px 112px",gap:10,padding:"12px 4px",
            borderTop:`2px solid ${T.line}`,alignItems:"center",fontSize:13,fontWeight:800}}>
            <span style={{color:T.hint,textTransform:"uppercase",fontSize:10,letterSpacing:".06em"}}>{L.recTotal}</span>
            <span/>
            <span style={{...WS_NUM,textAlign:"right",color:W.deep}}>{(rec.webstar_total||0).toFixed(2)}</span>
            <span style={{...WS_NUM,textAlign:"right",color:T.ink}}>{(rec.odoo_total||0).toFixed(2)}</span>
            <span style={{...WS_NUM,textAlign:"right",color:Math.abs(recGap)<0.51?T.ok:T.err}}>
              {recGap>0?"+":""}{recGap.toFixed(2)}
            </span>
            <span/>
          </div>
          {(rec.pending_portal||[]).length>0 && (
            <div style={{marginTop:10,padding:"9px 12px",borderRadius:8,background:"#fff7e8",border:"1px solid #f3cf8a",fontSize:11.5,color:"#8a5a00"}}>
              {lang==="fr"
                ? <>Référence portail chargée du <b>{rec.ref_from}</b> au <b>{rec.ref_to}</b>. Les <b>{rec.pending_portal.length} PO</b> postérieures (CHF {(rec.pending_total||0).toFixed(2)}) ne sont pas un écart : leurs commandes my.webstar.ch restent à charger dans <code>webstar_ref.json</code> pour être rapprochées.</>
                : <>Portal reference loaded from <b>{rec.ref_from}</b> to <b>{rec.ref_to}</b>. The <b>{rec.pending_portal.length} later POs</b> (CHF {(rec.pending_total||0).toFixed(2)}) are not a gap: their my.webstar.ch orders still need loading into <code>webstar_ref.json</code>.</>}
            </div>
          )}
          {(rec.unmatched_odoo||[]).length>0 && (
            <div style={{marginTop:14,fontSize:12,color:T.sub}}>
              <div style={{fontSize:10,fontWeight:800,color:T.hint,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>{L.recExtra}</div>
              {rec.unmatched_odoo.map((u,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${T.line}`}}>
                  <span><b style={{color:W.deep}}>{u.po}</b> <span style={{color:T.hint}}>{u.date}</span></span>
                  <span style={{...WS_NUM,fontWeight:700}}>{(u.odoo||0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </>}
        <div style={{fontSize:11,color:T.hint,marginTop:12,fontStyle:"italic"}}>{L.recHint}</div>
      </WSFold>

      <WSFold defaultOpen color={nBlocking?W.red:W.green}
        title={L.anomTitle} sub={L.anomSub}
        badges={[
          {t:`${nBlocking} ${L.anomBlocking}`, c:nBlocking?T.err:T.ok},
          {t:`${packAnom.length} ${L.anomPackN}`, c:T.hint},
          {t:`${anomImpact>0?"+":""}${anomImpact.toFixed(0)} CHF`, c:anomImpact?T.err:T.ok},
        ]}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:12}}>
          <label style={{display:"flex",alignItems:"center",gap:7,fontSize:11.5,color:T.sub,cursor:"pointer"}}>
            <input type="checkbox" checked={showPack} onChange={e=>setShowPack(e.target.checked)}/>
            {L.anomShowPack} ({packAnom.length})
          </label>
          <label style={{display:"flex",alignItems:"center",gap:7,fontSize:11.5,color:T.hint}}>
            {L.anomThr}
            <input type="number" value={anomQtyMin} onChange={e=>setAnomQtyMin(Number(e.target.value)||0)}
              style={{width:88,padding:"5px 9px",border:`1px solid ${T.line}`,borderRadius:8,
                fontSize:13,fontWeight:700,color:T.ink,background:T.card,outline:"none",boxSizing:"border-box"}}/>
          </label>
        </div>
        {anomalies.length===0 ? <div style={{fontSize:13,color:T.ok,fontWeight:600}}>{L.anomNone}</div> : <>
          <div style={{display:"grid",gridTemplateColumns:"88px 76px 1fr 48px 66px 74px 82px 86px 150px",gap:8,padding:"8px 4px",
            borderBottom:`2px solid ${T.line}`,fontSize:10,fontWeight:800,color:T.hint,textTransform:"uppercase",letterSpacing:".05em"}}>
            <span>{L.anomPO}</span><span>{L.date}</span><span>{L.prods}</span>
            <span style={{textAlign:"right"}}>{L.cQty}</span>
            <span style={{textAlign:"right"}}>{L.packCol}</span>
            <span style={{textAlign:"right"}}>{L.anomOdooP}</span>
            <span style={{textAlign:"right"}}>{L.anomRefP}</span>
            <span style={{textAlign:"right"}}>{L.anomImpact}</span>
            <span style={{textAlign:"right"}}>{L.anomMotif}</span>
          </div>
          {anomShown.map((a,i)=>{
            const link = poLink(a.po_id);
            return (
              <div key={i} style={{display:"grid",gridTemplateColumns:"88px 76px 1fr 48px 66px 74px 82px 86px 150px",gap:8,
                padding:"9px 4px",borderBottom:`1px solid ${T.line}`,alignItems:"center",fontSize:12.5,
                opacity:a.blocking?1:0.72}}>
                {link
                  ? <a href={link} target="_blank" rel="noreferrer" style={{fontWeight:800,color:W.deep,textDecoration:"none"}}>{a.po} ↗</a>
                  : <span style={{fontWeight:800,color:W.deep}}>{a.po}</span>}
                <span style={{color:T.hint,fontSize:11.5}}>{a.date}</span>
                <span style={{color:T.sub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {a.code && <span style={{color:T.ghost,marginRight:6,fontSize:11}}>{a.code}</span>}{nameOf(a)}
                </span>
                <span style={{...WS_NUM,textAlign:"right",fontWeight:700,color:a.reasons.includes("qty")?T.warn:T.ink}}>{fmtN(a.qty)}</span>
                <span style={{...WS_NUM,textAlign:"right",fontSize:11.5,color:a.pack_qty>1?W.viol:T.ghost}}
                  title={a.pack_name||""}>{a.pack_qty>1?`×${Math.round(a.pack_qty)}`:L.packNone}</span>
                <span style={{...WS_NUM,textAlign:"right",color:a.price<=0?T.err:T.sub}}>{(a.price||0).toFixed(2)}</span>
                <span style={{...WS_NUM,textAlign:"right",fontWeight:700,color:a.ref_price>0?W.deep:T.ghost}}>
                  {a.ref_price>0 ? a.ref_price.toFixed(2) : "—"}
                </span>
                <span style={{...WS_NUM,textAlign:"right",fontWeight:800,color:a.blocking?((a.delta||0)>0?T.err:T.ok):T.ghost}}>
                  {a.blocking && a.delta ? `${a.delta>0?"+":""}${a.delta.toFixed(2)}` : "—"}
                </span>
                <span style={{display:"flex",gap:4,justifyContent:"flex-end",flexWrap:"wrap"}}>
                  {a.reasons.map(r=>RZ[r] ? <Chip key={r} label={r==="packmiss"&&a.pack?`${RZ[r].t} ×${Math.abs(a.pack)}`:RZ[r].t} color={RZ[r].c}/> : null)}
                </span>
              </div>
            );
          })}
          {anomalies.length>anomShown.length && <div style={{fontSize:11.5,color:T.hint,marginTop:8}}>… +{anomalies.length-anomShown.length}</div>}
          <div style={{marginTop:14,padding:"11px 13px",background:T.bg2,border:`1px solid ${T.line}`,borderRadius:10,
            display:"flex",justifyContent:"space-between",fontSize:12.5,color:T.sub}}>
            <span>{L.anomTotalImpact}</span>
            <b style={{...WS_NUM,color:anomImpact>0?T.err:T.ok}}>{anomImpact>0?"+":""}{anomImpact.toFixed(2)} CHF</b>
          </div>
        </>}
        <div style={{fontSize:11,color:T.hint,marginTop:12,fontStyle:"italic"}}>{L.anomHint}</div>
      </WSFold>

      <WSFold color={W.viol} title={L.byCat} sub={L.catSub}
        badges={(data.categories||[]).slice(0,3).map((c,i)=>({t:`${catName(c)} ${fmtK(c.spend)}`, c:i===0?W.deep:T.sub}))
          .concat(nUncat?[{t:`${nUncat} ⚠`, c:T.warn}]:[])}>
        <div style={{display:"grid",gap:18,gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)"}}>
          <div>
            {(data.categories||[]).map(c=>(
              <div key={c.id} style={{marginBottom:11}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,marginBottom:4}}>
                  <span style={{color:T.sub}}>{catName(c)} <span style={{color:T.ghost}}>· {c.n_products}</span></span>
                  <span><b style={{...WS_NUM,color:T.ink}}>{fmtK(c.spend)}</b> <span style={{color:T.hint}}>{t.spend?((c.spend/t.spend)*100).toFixed(1):0}%</span></span>
                </div>
                <div style={{height:8,background:T.grid,borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${Math.max(c.spend/data.categories[0].spend*100,1)}%`,
                    background:colorFor(c.id),borderRadius:4}}/>
                </div>
              </div>
            ))}
            {nUncat>0 && (
              <div style={{marginTop:14,padding:"10px 12px",background:T.bg2,border:`1px solid ${T.warn}`,borderRadius:10}}>
                <div style={{fontSize:11.5,color:T.warn,fontWeight:700,marginBottom:6}}>⚠ {nUncat} {L.catUncat}</div>
                {data.uncategorized.slice(0,6).map((u,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.sub,padding:"2px 0"}}>
                    <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"72%"}}>
                      {u.code && <span style={{color:T.ghost,marginRight:5}}>{u.code}</span>}{nameOf(u)}
                    </span>
                    <span style={WS_NUM}>{fmtK(u.spend)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:800,color:T.hint,textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>{L.top} · {L.topSub}</div>
            {[...(data.products||[])].slice(0,10).map((p,i)=>(
              <div key={i} style={{marginBottom:9}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,marginBottom:3}}>
                  <span style={{color:T.sub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"72%"}}>
                    <span style={{color:T.ghost,fontWeight:700,marginRight:6}}>{i+1}</span>{nameOf(p)}</span>
                  <b style={{...WS_NUM,color:T.ink}}>{fmtK(p.spend)}</b>
                </div>
                <div style={{height:6,background:T.grid,borderRadius:3,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${Math.max(p.spend/maxProd*100,1)}%`,background:W.deep,borderRadius:3}}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </WSFold>

      <WSFold color={W.blue} title={`${L.prods} (${products.length})`} sub={L.prodsSub}
        badges={[{t:`${fmtN(t.units)} ${L.unitsShort}`, c:T.sub}, {t:fmtK(t.spend), c:W.deep}]}>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,alignItems:"center",marginBottom:14}}>
          <span style={{fontSize:11.5,color:T.hint}}>{L.sort}</span>
          <WSSortBtn active={pSort.key==="spend"}  dir={pSort.dir} onClick={()=>togP("spend")}>{L.sTotal}</WSSortBtn>
          <WSSortBtn active={pSort.key==="qty"}    dir={pSort.dir} onClick={()=>togP("qty")}>{L.sQty}</WSSortBtn>
          <WSSortBtn active={pSort.key==="orders"} dir={pSort.dir} onClick={()=>togP("orders")}>{L.sFreq}</WSSortBtn>
        </div>
        <div style={{display:"grid",gap:12,gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",
          maxHeight:520,overflowY:"auto",paddingRight:4}}>
          {products.map((p,idx)=>(
            <div key={idx} style={{border:`1px solid ${T.line}`,borderRadius:12,padding:"13px 14px",background:T.card}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
                <div style={{fontSize:12.5,fontWeight:700,color:T.ink,lineHeight:1.25}}>{nameOf(p)}</div>
                {p.variance>0.1 && <span title=">10%" style={{color:T.warn,fontSize:14}}>⚠</span>}
              </div>
              <div style={{marginTop:7,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                <Chip label={catOf(p)} color={colorFor(p.cat_id)}/>
                {p.code && <span style={{fontSize:10.5,color:T.ghost}}>{p.code}</span>}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:11}}>
                <div><Label style={{marginBottom:2}}>{L.cSpend}</Label>
                  <div style={{...WS_NUM,fontSize:17,fontWeight:800,color:W.deep}}>{fmtK(p.spend)}</div></div>
                <div style={{textAlign:"right"}}><Label style={{marginBottom:2}}>{L.cQty}</Label>
                  <div style={{...WS_NUM,fontSize:17,fontWeight:800,color:T.ink}}>{fmtN(p.qty)} <span style={{fontSize:11,fontWeight:500,color:T.hint}}>{p.unit}</span></div></div>
              </div>
              <div style={{height:6,background:T.grid,borderRadius:3,marginTop:10,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${Math.max(p.spend/maxProd*100,1)}%`,background:colorFor(p.cat_id),borderRadius:3}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:8,fontSize:11.5,color:T.sub}}>
                <span>↻ {p.orders}{L.ordered}</span>
                <span style={WS_NUM}>~ CHF {p.avg.toFixed(2)} / {p.unit}</span>
              </div>
            </div>
          ))}
        </div>
      </WSFold>

      <WSFold color={W.pink} title={`${L.poTitle} (${pos.length})`} sub={L.poSub}
        badges={[{t:`${L.avgpo} ${fmtK(t.avg_po)}`, c:T.sub}]}>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,alignItems:"center",marginBottom:12}}>
          <span style={{fontSize:11.5,color:T.hint}}>{L.sort}</span>
          <WSSortBtn active={oSort.key==="date"}  dir={oSort.dir} onClick={()=>togO("date")}>{L.date}</WSSortBtn>
          <WSSortBtn active={oSort.key==="total"} dir={oSort.dir} onClick={()=>togO("total")}>{L.poTotal}</WSSortBtn>
        </div>
        <div style={{maxHeight:460,overflowY:"auto",paddingRight:4}}>
          <div style={{display:"grid",gridTemplateColumns:"108px 1fr 54px 64px 108px",gap:10,padding:"8px 4px",
            borderBottom:`2px solid ${T.line}`,fontSize:10,fontWeight:800,color:T.hint,textTransform:"uppercase",letterSpacing:".06em"}}>
            <span>{L.ref}</span><span>{L.date}</span><span style={{textAlign:"right"}}>Q</span>
            <span style={{textAlign:"right"}}>{L.lines}</span><span style={{textAlign:"right"}}>{L.poTotal}</span>
          </div>
          {pos.map(po=>(
            <div key={po.ref}>
              <div onClick={()=>setOpen(openPO===po.ref?null:po.ref)}
                style={{display:"grid",gridTemplateColumns:"108px 1fr 54px 64px 108px",gap:10,padding:"10px 4px",
                  borderBottom:`1px solid ${T.line}`,cursor:"pointer",alignItems:"center"}}>
                <span style={{fontWeight:800,color:W.deep,fontSize:12.5}}>{po.ref}</span>
                <span style={{fontSize:12.5,color:T.sub}}>{po.date}</span>
                <span style={{textAlign:"right",fontSize:10.5,fontWeight:800,color:WS_QCOLOR[po.q]||T.hint}}>{po.q}</span>
                <span style={{textAlign:"right",fontSize:12,color:T.hint}}>{po.n_lines}</span>
                <span style={{...WS_NUM,textAlign:"right",fontWeight:800,color:T.ink}}>{fmtK(po.total)}</span>
              </div>
              {openPO===po.ref && (
                <div style={{padding:"6px 4px 12px 116px",fontSize:12.5,color:T.sub,background:T.bg}}>
                  {po.lines.map((l,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"3px 0",gap:10}}>
                      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {fmtN(l.qty)} {l.unit} · {nameOf(l)}
                        {l.code && <span style={{color:T.ghost,marginLeft:6,fontSize:11}}>{l.code}</span>}
                      </span>
                      <span style={{whiteSpace:"nowrap",...WS_NUM}}>
                        {l.ref_price>0 && Math.abs((l.price_eff||l.price)-l.ref_price)>Math.max(0.02,l.ref_price*0.01) &&
                          <span style={{color:T.warn,marginRight:8,fontSize:11.5}}>WS {l.ref_price.toFixed(2)}</span>}
                        <span style={{color:l.price<=0?T.err:T.sub}}>CHF {l.subtotal.toFixed(2)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </WSFold>

      <WSFold color={W.amber} title={L.advTitle} sub={L.advSub}
        badges={[
          {t:`${drift.length} drift`, c:drift.length?T.warn:T.hint},
          {t:`${cadRisk} ⚠`, c:cadRisk?T.warn:T.hint},
          {t:`HACCP ${fmtK(haccpTotal)}`, c:T.sub},
        ]}>

        <div style={{fontSize:13.5,fontWeight:800,color:T.ink,marginTop:6}}>{L.driftTitle}</div>
        <div style={{fontSize:11.5,color:T.hint,marginBottom:14}}>{L.driftSub}</div>
        {drift.length===0 ? <div style={{fontSize:12.5,color:T.hint}}>{L.noDrift}</div> :
          drift.map((p,i)=>{
            const lo = (p.min/driftMaxPrice)*100, span = ((p.max-p.min)/driftMaxPrice)*100;
            return (
              <div key={i} style={{marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,marginBottom:5}}>
                  <span style={{color:T.sub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"60%"}}>{nameOf(p)}</span>
                  <span style={WS_NUM}><span style={{color:T.hint}}>CHF {p.min.toFixed(2)} → {p.max.toFixed(2)}</span>{" "}
                    <b style={{color:p.variance>0.25?T.err:T.warn}}>+{(p.variance*100).toFixed(0)}%</b></span>
                </div>
                <div style={{position:"relative",height:8,background:T.grid,borderRadius:4}}>
                  <div style={{position:"absolute",left:lo+"%",width:Math.max(span,1)+"%",height:"100%",
                    background:`linear-gradient(90deg, ${W.deep}, ${T.warn})`,borderRadius:4}}/>
                </div>
              </div>
            );
          })}

        <div style={{fontSize:13.5,fontWeight:800,color:T.ink,marginTop:26}}>{L.fvTitle}</div>
        <div style={{fontSize:11.5,color:T.hint,marginBottom:12}}>{L.fvSub}</div>
        <div style={{display:"grid",gap:18,gridTemplateColumns:"minmax(0,1.5fr) minmax(0,1fr)",alignItems:"center"}}>
          <svg viewBox="0 0 600 260" style={{width:"100%",height:"auto"}}>
            {(() => {
              const padL=46,padR=14,padT=12,padB=34, pw=600-padL-padR, ph=260-padT-padB;
              const X=v=>padL+(v/fvMaxX)*pw, Y=v=>padT+ph-(v/fvMaxY)*ph;
              const xMed=X(medX), yMed=Y(medY);
              return <>
                <rect x={xMed} y={yMed} width={padL+pw-xMed} height={padT+ph-yMed} fill={W.deep} opacity="0.10"/>
                <line x1={padL} y1={padT+ph} x2={padL+pw} y2={padT+ph} stroke={T.line}/>
                <line x1={padL} y1={padT} x2={padL} y2={padT+ph} stroke={T.line}/>
                <line x1={xMed} y1={padT} x2={xMed} y2={padT+ph} stroke={T.hint} strokeDasharray="4 4" opacity="0.5"/>
                <line x1={padL} y1={yMed} x2={padL+pw} y2={yMed} stroke={T.hint} strokeDasharray="4 4" opacity="0.5"/>
                {fv.map((p,i)=>{
                  const cand = p.x>=medX && p.y<=medY && p.x>=2;
                  return <circle key={i} cx={X(p.x)} cy={Y(p.y)} r={cand?5:3.5}
                    fill={colorFor(p.cat_id)} stroke={cand?W.deep:"none"} strokeWidth={cand?2:0} opacity={cand?1:0.55}/>;
                })}
                <text x={padL+pw} y={padT+ph+22} fontSize="11" fill={T.hint} textAnchor="end">{L.fvX} →</text>
                <text x={padL-6} y={padT+8} fontSize="11" fill={T.hint} textAnchor="end">↑ {L.fvY}</text>
              </>;
            })()}
          </svg>
          <div>
            <div style={{fontSize:10.5,fontWeight:800,color:W.deep,marginBottom:8,textTransform:"uppercase",letterSpacing:".06em"}}>{L.fvCand}</div>
            {fvCand.length===0 ? <div style={{fontSize:12,color:T.hint}}>—</div> :
              fvCand.map((p,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12.5,padding:"5px 0",borderBottom:`1px solid ${T.line}`}}>
                  <span style={{color:T.sub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"62%"}}>{p.name}</span>
                  <span style={{...WS_NUM,color:T.hint}}>{p.x}× · CHF {Math.round(p.y)}/cmd</span>
                </div>
              ))}
          </div>
        </div>

        <div style={{fontSize:13.5,fontWeight:800,color:T.ink,marginTop:26}}>{L.cadTitle}</div>
        <div style={{fontSize:11.5,color:T.hint,marginBottom:12}}>{L.cadSub}</div>
        {cadence.length===0 ? <div style={{fontSize:12.5,color:T.hint}}>{L.noCad}</div> :
          cadence.map((c,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"1fr auto auto auto",gap:12,alignItems:"center",
              padding:"8px 0",borderBottom:i<cadence.length-1?`1px solid ${T.line}`:"none"}}>
              <span style={{fontSize:12.5,color:T.sub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{nameOf(c.obj)}</span>
              <span style={{...WS_NUM,fontSize:12,color:T.hint,whiteSpace:"nowrap"}}>{L.cadEvery}{c.mean} {L.cadDays}</span>
              <span style={{...WS_NUM,fontSize:12,color:T.hint,whiteSpace:"nowrap"}}>{L.cadLast} {c.last} · {L.cadSince} {c.since} {L.cadDays}</span>
              <span style={{width:74,textAlign:"right"}}>{c.risk && <Chip label={"⚠ "+L.cadRisk} color={T.warn}/>}</span>
            </div>
          ))}

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",flexWrap:"wrap",gap:8,marginTop:26}}>
          <div style={{fontSize:13.5,fontWeight:800,color:T.ink}}>{L.haccpTitle}</div>
          <div style={{fontSize:12.5,color:T.sub}}>Total : <b style={{...WS_NUM,color:W.deep}}>{fmtK(haccpTotal)}</b></div>
        </div>
        <div style={{fontSize:11.5,color:T.hint,marginBottom:12}}>{L.haccpSub}</div>
        {haccp.length===0 ? <div style={{fontSize:12.5,color:T.hint}}>{L.haccpNone}</div> :
          <div style={{display:"grid",gap:12,gridTemplateColumns:"repeat(auto-fill,minmax(210px,1fr))"}}>
            {haccp.map((f,i)=>(
              <div key={i} style={{border:`1px solid ${T.line}`,borderRadius:12,padding:"13px 14px",
                borderTop:`3px solid ${WS_PALETTE[i%WS_PALETTE.length]}`}}>
                <div style={{fontSize:12.5,fontWeight:700,color:T.ink,marginBottom:8}}>{lang==="en"?f.en:f.fr}</div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:800,...WS_NUM}}>
                  <span style={{color:W.deep}}>{fmtK(f.spend)}</span><span style={{color:T.ink}}>{fmtN(f.qty)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.hint,marginTop:6}}>
                  <span>{f.count} {L.distinct}</span><span>{L.haccpLast} : {f.last||"—"}</span>
                </div>
              </div>
            ))}
          </div>}

        <div style={{fontSize:13.5,fontWeight:800,color:T.ink,marginTop:26}}>{L.burnTitle}</div>
        <div style={{fontSize:11.5,color:T.hint,marginBottom:14}}>{L.burnSub} · {WS_MONTHS[lang][curMonthNum-1]} ({elapsed}/{dim} {L.cadDays})</div>
        <div style={{display:"grid",gap:18,gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",alignItems:"end"}}>
          <div>
            <Label>{L.burnSpent}</Label>
            <div style={{...WS_NUM,fontSize:21,fontWeight:800,color:T.ink}}>{fmtK(curSpent)}</div>
          </div>
          <div>
            <Label>{L.burnProj}</Label>
            <div style={{...WS_NUM,fontSize:21,fontWeight:800,color:budgetNum>0?(burnDelta>0?T.err:T.ok):W.deep}}>{fmtK(proj)}</div>
          </div>
          <div>
            <Label>{L.burnBudget}</Label>
            <input type="number" value={budget} onChange={e=>setBudget(e.target.value)} placeholder="20000"
              style={{width:"100%",maxWidth:140,padding:"7px 10px",border:`1px solid ${T.line}`,borderRadius:9,
                fontSize:14,fontWeight:700,color:T.ink,background:T.card,outline:"none",boxSizing:"border-box"}}/>
          </div>
        </div>
        {budgetNum>0 ? (
          <div style={{marginTop:16}}>
            <div style={{position:"relative",height:14,background:T.grid,borderRadius:7,overflow:"hidden"}}>
              <div style={{position:"absolute",left:0,top:0,height:"100%",width:Math.min(curSpent/budgetNum*100,100)+"%",background:W.deep,borderRadius:7}}/>
              <div style={{position:"absolute",left:Math.min(proj/budgetNum*100,100)+"%",top:-3,width:2,height:20,background:burnDelta>0?T.err:T.ok}}/>
            </div>
            <div style={{fontSize:12,color:T.sub,marginTop:8}}>
              {burnDelta>0
                ? <>⚠ <b style={{color:T.err}}>{fmtK(Math.abs(burnDelta))}</b> {L.burnOver}</>
                : <>✓ <b style={{color:T.ok}}>{fmtK(Math.abs(burnDelta))}</b> {L.burnUnder}</>}
            </div>
          </div>
        ) : <div style={{marginTop:12,fontSize:12,color:T.hint}}>{L.burnSetBudget}</div>}
      </WSFold>

      <WSFold color={W.green} title={L.kpiTitle} sub={L.kpiSub}
        badges={[{t:`${WS_KPI_IDEAS.filter(k=>k.s==="active").length} ${L.statusActive.toLowerCase()}`, c:T.ok}]}>
        {WS_KPI_IDEAS.map((k,i)=>(
          <div key={i} style={{display:"flex",gap:11,padding:"11px 0",borderBottom:i<WS_KPI_IDEAS.length-1?`1px solid ${T.line}`:"none"}}>
            <div style={{width:26,height:26,borderRadius:8,background:WS_PALETTE[i%WS_PALETTE.length]+"22",
              color:W.deep,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontWeight:800,fontSize:12}}>{i+1}</div>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <div style={{fontSize:12.5,fontWeight:700,color:T.ink}}>{k[lang].t}</div>
                <Status s={k.s}/>
              </div>
              <div style={{fontSize:12,color:T.sub,marginTop:2}}>{k[lang].d}</div>
            </div>
          </div>
        ))}
      </WSFold>

      {data.flagged && data.flagged.length>0 && (
        <div style={{marginTop:12,fontSize:11.5,color:T.warn}}>
          ⚠ {L.priceWarn(data.flagged.length)}{data.flagged.slice(0,6).map(f=>nameOf(f)).join(", ")}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  SECTION FOOD INVOICES — factures fournisseurs lues depuis prix_caa (242),
//  alimentée par le pipeline OCR. Réutilise les primitives cockpit ci-dessus
//  (W, WSTile, WSBars, WSFold, WSSeg, WS_NUM, WS_CSV_BTN, WS_PALETTE, fmtK,
//  fmtN, Card, Chip, T, API). © FX1 Labs.
// ═══════════════════════════════════════════════════════════════════════════

// =====================================================================
// PROCUREMENT ANALYSIS — bloc KPI direction (section FOOD INVOICES)
// Source : /dashboard/procurement_analysis (factures scannées prix_caa)
// =====================================================================
// Analyse achats — vit dans PROCUREMENT (déplacée depuis FOOD INVOICES le
// 04/09/2026 : elle analyse des commandes, pas des factures, et ralentissait
// le chargement de la page factures). Repliée par défaut.
function ProcurementAnalysis({ L }) {
  const F = { soft:"#eef2f8", soft2:"#f7f9fc", card:"#ffffff", line:"#e3e8f0",
              ink:"#111827", sub:"#4b5563", hint:"#9aa4b2", ghost:"#c3cad6" };
  const W = { deep:"#0a7a6c", blue:"#2563eb", green:"#15803d" };
  const [foldOpen, setFoldOpen] = React.useState(false);
  const [d, setD] = React.useState(null);
  const [busy, setBusy] = React.useState(true);
  const [since, setSince] = React.useState("2026-04-01");
  const [topN, setTopN] = React.useState(15);
  const [open, setOpen] = React.useState(null);

  React.useEffect(() => {
    if (!foldOpen) return;          // pas de calcul tant que c'est replié
    let alive = true;
    setBusy(true);
    fetch(`${API}/dashboard/procurement_analysis?since=${since}&top_n=${topN}`)
      .then(r => r.json())
      .then(j => { if (alive) { setD(j); setBusy(false); } })
      .catch(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [since, topN, foldOpen]);

  const k = (d && d.kpis) || {};
  const tops = (d && d.top_products) || [];
  const multi = (d && d.multi_supplier) || [];
  const months = (d && d.months) || [];
  const nf = (n, dg) => (n == null ? "—" :
    Number(n).toLocaleString("fr-CH", { minimumFractionDigits: dg == null ? 2 : dg,
                                        maximumFractionDigits: dg == null ? 2 : dg }));

  const HEAD = { fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em",
                 textTransform: "uppercase", color: F.hint,
                 padding: "8px 12px", borderBottom: `1px solid ${F.line}` };
  const GT = "1fr 74px 96px 92px 84px 62px 68px 56px";
  const GC = "1fr 190px 74px 96px 92px 86px 70px";

  // renommée : `Card` masquait le composant global du même nom,
  // ce qui empêchait le rendu du conteneur repliable.
  const KpiCard = ({ label, value, sub, color }) => (
    <div style={{ flex: "1 1 150px", minWidth: 150, background: F.card,
                  border: `1px solid ${F.line}`, borderLeft: `3px solid ${color}`,
                  borderRadius: 8, padding: "10px 14px" }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em",
                    textTransform: "uppercase", color: F.hint }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, ...WS_NUM, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 11, color: F.hint }}>{sub}</div>
    </div>
  );

  const xlsUrl = `${API}/dashboard/procurement_analysis.xlsx?since=${since}&top_n=${topN}`;

  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      {/* en-tête repliable — fermé par défaut : le calcul n'est lancé
          que si l'utilisateur ouvre la section */}
      <div onClick={() => setFoldOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                 padding: "15px 18px", borderLeft: `3px solid ${W.deep}`,
                 background: foldOpen ? F.soft2 : F.card }}>
        <span style={{ fontSize: 12, color: F.hint, transition: "transform .15s",
                       transform: foldOpen ? "rotate(90deg)" : "none" }}>▶</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: F.ink }}>{L.paTitle}</div>
          <div style={{ fontSize: 12, color: F.hint, marginTop: 1 }}>{L.paSub}</div>
        </div>
        <span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 800,
                       color: W.deep, background: "rgba(10,122,108,.10)",
                       border: "1px solid rgba(10,122,108,.30)",
                       borderRadius: 20, padding: "3px 10px" }}>
          {busy ? "…" : tops.length}
        </span>
      </div>

      {foldOpen && (<>

      {/* barre de controle */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
                    padding: "10px 12px", borderBottom: `1px solid ${F.line}` }}>
        <span style={{ fontSize: 11, color: F.hint, fontWeight: 700 }}>{L.paPeriod}</span>
        <select value={since} onChange={e => setSince(e.target.value)}
          style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6,
                   border: `1px solid ${F.line}`, background: F.card, color: F.ink }}>
          <option value="2026-04-01">{L.paAll}</option>
          <option value="2026-07-01">Q3 (juil → sept)</option>
          <option value="2026-04-01">Q2 (avr → juin)</option>
        </select>
        <span style={{ fontSize: 11, color: F.hint, fontWeight: 700 }}>{L.paTopN}</span>
        <select value={topN} onChange={e => setTopN(Number(e.target.value))}
          style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6,
                   border: `1px solid ${F.line}`, background: F.card, color: F.ink }}>
          {[10, 15, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <a href={xlsUrl} style={{ fontSize: 12, fontWeight: 700, color: T.ok,
             textDecoration: "none", border: `1px solid ${F.line}`,
             borderRadius: 6, padding: "5px 10px" }}>⬇ {L.paXls}</a>
        <button onClick={() => window.print()}
          style={{ fontSize: 12, fontWeight: 700, color: F.ink, cursor: "pointer",
                   border: `1px solid ${F.line}`, borderRadius: 6,
                   padding: "5px 10px", background: F.card }}>🖨 {L.paPdf}</button>
      </div>

      {busy && <div style={{ padding: 16, color: F.hint, fontSize: 12.5 }}>{L.paLoading}</div>}

      {!busy && d && d.error && (
        <div style={{ padding: 16, color: T.err, fontSize: 12.5 }}>{String(d.error)}</div>
      )}

      {!busy && d && !d.error && (<>
        {/* KPIs */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "12px" }}>
          <KpiCard label={L.paKpiShare} value={`${nf(k.top_share_pct, 1)} %`}
                sub={`${L.paKpiShareSub} · CHF ${nf(k.total_spend, 0)}`} color={W.deep} />
          <KpiCard label={L.paKpiMulti} value={nf(k.n_multi_supplier, 0)}
                sub={L.paKpiMultiSub} color={W.blue} />
          <KpiCard label={L.paKpiSaving} value={`CHF ${nf(k.potential_saving, 0)}`}
                sub={L.paKpiSavingSub} color={k.potential_saving > 0 ? T.warn || T.err : T.ok} />
          <KpiCard label={L.paKpiFreq} value={nf(k.avg_interval_days, 1)}
                sub={L.paKpiFreqSub} color={W.green} />
        </div>

        {/* TOP ARTICLES */}
        <div style={{ fontSize: 12.5, fontWeight: 800, color: F.ink, padding: "6px 12px" }}>
          {L.paTopTitle}
          <span style={{ fontWeight: 500, color: F.hint }}> · {L.paTopSub}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: GT, ...HEAD }}>
          <div>{L.paProduct}</div>
          <div style={{ textAlign: "right" }}>{L.paQty}</div>
          <div style={{ textAlign: "right" }}>{L.paSpend}</div>
          <div style={{ textAlign: "right" }}>{L.paAvg}</div>
          <div style={{ textAlign: "right" }}>{L.paLast}</div>
          <div style={{ textAlign: "right" }}>{L.paDeliv}</div>
          <div style={{ textAlign: "right" }}>{L.paInterval}</div>
          <div style={{ textAlign: "right" }}>{L.paSup}</div>
        </div>
        {tops.map((r, i) => (
          <div key={i}>
            <div onClick={() => setOpen(open === i ? null : i)}
              style={{ display: "grid", gridTemplateColumns: GT, alignItems: "center",
                       fontSize: 12, padding: "8px 12px", cursor: "pointer",
                       color: F.ink, background: i % 2 ? F.soft2 : F.card,
                       borderTop: i ? `1px solid ${F.line}` : "none" }}>
              <div>
                <span style={{ color: F.hint, marginRight: 6 }}>{i + 1}</span>
                <span style={{ fontWeight: 700 }}>{r.product}</span>
                <span style={{ color: F.hint }}> · {r.uom}</span>
              </div>
              <div style={{ textAlign: "right", ...WS_NUM }}>{nf(r.qty, 1)}</div>
              <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700 }}>{nf(r.spend, 0)}</div>
              <div style={{ textAlign: "right", ...WS_NUM }}>{nf(r.avg_price, 2)}</div>
              <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{nf(r.last_price, 2)}</div>
              <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{r.deliveries}</div>
              <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>
                {r.avg_interval_days == null ? "—" : `${r.avg_interval_days}j`}</div>
              <div style={{ textAlign: "right", ...WS_NUM,
                            color: r.n_suppliers > 1 ? W.blue : F.hint,
                            fontWeight: r.n_suppliers > 1 ? 700 : 400 }}>{r.n_suppliers}</div>
            </div>
            {open === i && (
              <div style={{ padding: "8px 12px 12px 30px", background: F.soft2,
                            borderTop: `1px solid ${F.line}` }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em",
                              textTransform: "uppercase", color: F.hint, marginBottom: 4 }}>
                  {L.paCmpTitle}
                </div>
                {r.suppliers.map((sp, j) => {
                  const prices = r.suppliers.filter(x => x.avg_price > 0).map(x => x.avg_price);
                  const best = prices.length ? Math.min(...prices) : 0;
                  const isBest = best && sp.avg_price === best;
                  return (
                    <div key={j} style={{ display: "grid",
                        gridTemplateColumns: "1fr 80px 96px 92px 92px 90px",
                        fontSize: 11.5, padding: "3px 0", color: F.sub }}>
                      <div style={{ fontWeight: isBest ? 700 : 500,
                                    color: isBest ? T.ok : F.sub }}>
                        {isBest ? "★ " : ""}{sp.supplier}</div>
                      <div style={{ textAlign: "right", ...WS_NUM }}>{nf(sp.qty, 1)}</div>
                      <div style={{ textAlign: "right", ...WS_NUM }}>{nf(sp.spend, 0)}</div>
                      <div style={{ textAlign: "right", ...WS_NUM,
                                    fontWeight: 700, color: isBest ? T.ok : F.ink }}>
                        {nf(sp.avg_price, 2)}</div>
                      <div style={{ textAlign: "right", ...WS_NUM, color: F.hint }}>
                        {best && !isBest ? `+${nf(100 * (sp.avg_price - best) / best, 1)}%` : ""}</div>
                      <div style={{ textAlign: "right", ...WS_NUM, color: F.hint }}>{sp.last_date}</div>
                    </div>
                  );
                })}
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em",
                              textTransform: "uppercase", color: F.hint,
                              margin: "10px 0 4px" }}>{L.paDelTitle}</div>
                {r.places.map((pl, j) => (
                  <div key={j} style={{ display: "grid",
                      gridTemplateColumns: "1fr 80px 96px 92px", fontSize: 11.5,
                      padding: "3px 0", color: F.sub }}>
                    <div>{pl.place}</div>
                    <div style={{ textAlign: "right", ...WS_NUM }}>{pl.deliveries}</div>
                    <div style={{ textAlign: "right", ...WS_NUM }}>{nf(pl.qty, 1)}</div>
                    <div style={{ textAlign: "right", ...WS_NUM }}>{nf(pl.spend, 0)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* OPPORTUNITES */}
        <div style={{ fontSize: 12.5, fontWeight: 800, color: F.ink, padding: "14px 12px 6px" }}>
          {L.paCmpTitle}
          <span style={{ fontWeight: 500, color: F.hint }}> · {L.paCmpSub}</span>
        </div>
        {multi.length === 0
          ? <div style={{ color: F.hint, fontSize: 12.5, padding: "6px 12px" }}>{L.paNoMulti}</div>
          : (<>
            <div style={{ display: "grid", gridTemplateColumns: GC, ...HEAD }}>
              <div>{L.paProduct}</div><div>{L.paBest}</div>
              <div style={{ textAlign: "right" }}>{L.paQty}</div>
              <div style={{ textAlign: "right" }}>{L.paSpend}</div>
              <div>{L.paWorst}</div>
              <div style={{ textAlign: "right" }}>{L.paGap}</div>
              <div style={{ textAlign: "right" }}>{L.paSaving}</div>
            </div>
            {multi.map((m, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: GC,
                  alignItems: "center", fontSize: 12, padding: "8px 12px", color: F.ink,
                  background: i % 2 ? F.soft2 : F.card,
                  borderTop: i ? `1px solid ${F.line}` : "none" }}>
                <div><span style={{ fontWeight: 700 }}>{m.product}</span>
                     <span style={{ color: F.hint }}> · {m.uom}</span></div>
                <div style={{ color: T.ok, fontSize: 11.5 }}>
                  {m.best_supplier} <span style={{ ...WS_NUM }}>{nf(m.best_price, 2)}</span></div>
                <div style={{ textAlign: "right", ...WS_NUM }}>{nf(m.qty, 1)}</div>
                <div style={{ textAlign: "right", ...WS_NUM }}>{nf(m.spend, 0)}</div>
                <div style={{ color: F.hint, fontSize: 11.5 }}>
                  {m.worst_supplier} <span style={{ ...WS_NUM }}>{nf(m.worst_price, 2)}</span></div>
                <div style={{ textAlign: "right", ...WS_NUM,
                              color: m.gap_pct > 15 ? T.err : F.sub }}>+{nf(m.gap_pct, 1)}%</div>
                <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700,
                              color: T.ok }}>{nf(m.potential_saving, 0)}</div>
              </div>
            ))}
          </>)}

        {(d.notes || []).length > 0 && (
          <div style={{ padding: "12px", borderTop: `1px solid ${F.line}`,
                        fontSize: 11, color: F.hint, lineHeight: 1.6 }}>
            {d.notes.map((n, i) => <div key={i}>· {n}</div>)}
          </div>
        )}
      </>)}
      </>)}
    </Card>
  );
}

const FOOD_L = {
  fr: {
    tagline: "Factures fournisseurs · depuis", htnote: "totaux HT",
    csvProd: "Produits CSV", csvInv: "Factures CSV",
    all: "Tous fournisseurs",
    spend: "Dépense totale", invoices: "Factures", lines: "Lignes",
    units: "Unités", suppliers: "Fournisseurs", avgInv: "Moyenne / facture",
    perInv: "par facture", perStud: "Coût / étudiant", cohort: "cohorte",
    drift: "Alertes dérive", driftSub: "prix payé hors plage catalogue",
    distinct: "articles", orders: "factures",
    evo: "Dépense par mois", evoWeek: "Dépense par semaine",
    month: "Mois", week: "Semaine",
    bySup: "Dépense par fournisseur",
    supTitle: "Fournisseurs", supSub: "dépense HT · part · factures",
    prodTitle: "Articles — dérive de prix", prodSub: "min / max / moyen par article",
    driftTitle: "Dérive de prix — à contrôler", driftSub2: "prix payé vs prix catalogue courant",
    invTitle: "Factures", invSub: "détail ligne à ligne",
    qLive: "en cours", cpsQ: "coût/étud.", vsPrev: "vs trim. préc.",
    none: "Aucune facture dans prix_caa pour la période.",
    hint: "Alimenté par le pipeline d'ingestion OCR : dépose Drive → 242 → réconciliation au centime → prix_caa → ce dashboard.",
    repTitle: "Rapport achats pour la direction",
    repDesc: "Un fichier Excel prêt à envoyer : pour chaque article, la quantité totale, le prix unitaire actuel, le prix moyen payé, la dépense, la fréquence, les lieux de livraison — et le prix payé à chaque fournisseur lorsque l'article vient de plusieurs sources.",
    repTop: "Top 15 par dépense", repAll: "Toutes les références",
    repScopeAll: "Mai 2026 → aujourd'hui", repScopeMonth: "Un mois :",
    repNote: "Couvre toute la période disponible, quel que soit le mois affiché ci-dessus. Source : factures livrées et réconciliées, pas les bons de commande.",
    truth: "Source de vérité : factures réconciliées au centime. Les PO Odoo sont contrôlées contre la facture — pas l'inverse. Écarts à corriger : Sébastien.",
  },
  en: {
    tagline: "Supplier invoices · since", htnote: "net of VAT",
    csvProd: "Products CSV", csvInv: "Invoices CSV",
    all: "All suppliers",
    spend: "Total spend", invoices: "Invoices", lines: "Lines",
    units: "Units", suppliers: "Suppliers", avgInv: "Avg / invoice",
    perInv: "per invoice", perStud: "Cost / student", cohort: "cohort",
    drift: "Drift alerts", driftSub: "price paid outside catalogue range",
    distinct: "articles", orders: "invoices",
    evo: "Spend by month", evoWeek: "Spend by week",
    month: "Month", week: "Week",
    bySup: "Spend by supplier",
    supTitle: "Suppliers", supSub: "net spend · share · invoices",
    prodTitle: "Articles — price drift", prodSub: "min / max / avg per article",
    driftTitle: "Price drift — to review", driftSub2: "price paid vs current catalogue price",
    invTitle: "Invoices", invSub: "line-by-line detail",
    qLive: "live", cpsQ: "cost/stud.", vsPrev: "vs prev. quarter",
    none: "No invoices in prix_caa for the period.",
    hint: "Fed by the OCR ingestion pipeline: Drive drop → 242 → cent-level reconciliation → prix_caa → this dashboard.",
    repTitle: "Purchasing report for management",
    repDesc: "A ready-to-send Excel file: for each item, total quantity, current unit price, average price paid, spend, frequency, delivery locations — and the price paid to each supplier when the item is sourced from several.",
    repTop: "Top 15 by spend", repAll: "All references",
    repScopeAll: "May 2026 → today", repScopeMonth: "One month:",
    repNote: "Covers the full available period, whatever month is displayed above. Source: delivered and reconciled invoices, not purchase orders.",
    truth: "Source of truth: cent-level reconciled invoices. Odoo POs are checked against the invoice — not the other way around. Gaps to fix: Sébastien.",
  },
};

// ═════════════════════════════════════════════════════════════════
//  BANDEAU SPEND vs BUDGET (Food) — trimestre précédent (clos) + trimestre
//  en cours (live), choisis automatiquement d'après la date du jour.
//  Q2 = budgets mensuels · Q3 et Q4 = grille hebdo de David, 25k / semaine,
//  W0 → W11. Une semaine appartient au mois de son JEUDI (règle ISO) :
//  Q3 W0 = 29.06 → juillet ; Q4 W0 = 28.09 → octobre.
//  Ajouter un trimestre = ajouter une entrée à FB_QUARTERS, rien d'autre.
// ═════════════════════════════════════════════════════════════════
const FB_QUARTERS = [
  { key:"Q2", id:"Q2-2026", months:["2026-04","2026-05","2026-06"],
    budgets:{ "2026-04":120000, "2026-05":120000, "2026-06":124000 } },
  { key:"Q3", id:"Q3-2026", w0:"2026-06-29", weekBudget:25000,
    weeks:{ "2026-07":[0,1,2,3,4], "2026-08":[5,6,7,8], "2026-09":[9,10,11] } },
  // Q4 : W0 = semaine du 28.09 (jeudi 1er octobre), dernière semaine W11 14→20.12.
  // weekBudget = valeur par défaut ; les montants réels se saisissent dans Admin.
  { key:"Q4", id:"Q4-2026", w0:"2026-09-28", weekBudget:25000,
    weeks:{ "2026-10":[0,1,2,3,4], "2026-11":[5,6,7,8], "2026-12":[9,10,11] } },
  // Q1 2027 : W0 = lun. 04.01.2027 (la semaine du 28.12 a son jeudi le 31.12 →
  // décembre). 4 + 4 + 4 : la semaine du 29.03 a son jeudi le 01.04 → avril.
  { key:"Q1", id:"Q1-2027", w0:"2027-01-04", weekBudget:25000,
    weeks:{ "2027-01":[0,1,2,3], "2027-02":[4,5,6,7], "2027-03":[8,9,10,11] } },
];
FB_QUARTERS.forEach(q => {
  if (q.weeks) {
    q.months = Object.keys(q.weeks);
    q.nWeeks = q.months.reduce((a, m) => a + q.weeks[m].length, 0);
  }
});
// Applique les budgets hebdo saisis dans l'onglet Admin (GET /seg/food_budgets).
// Total du mois = Σ des semaines du mois ; semaine non saisie = weekBudget.
const fbApply = (ov = {}) => FB_QUARTERS.map(q0 => {
  const q = { ...q0 };
  if (q.weeks) {
    const sv = ov[q.id] && Array.isArray(ov[q.id].weeks) ? ov[q.id].weeks : null;
    q.wb = Array.from({ length: q.nWeeks }, (_, i) => sv && sv[i] != null ? (+sv[i] || 0) : q.weekBudget);
    q.budgets = {};
    q.months.forEach(m => { q.budgets[m] = q.weeks[m].reduce((a, w) => a + q.wb[w], 0); });
    q.uniform = q.wb.every(v => v === q.wb[0]) ? q.wb[0] : null;
    q.edited = !!sv;
  }
  q.budget = q.months.reduce((a, m) => a + (q.budgets[m] || 0), 0);
  return q;
});
const fbLoad = () =>
  fetch(`${API}/seg/food_budgets`, { headers: authHeaders(), cache: "no-store" })
    .then(r => r.ok ? r.json() : null)
    .then(d => (d && d.quarters) || {})
    .catch(() => ({}));
// Lundi de la semaine w (le 1er du trimestre si W0 démarre avant : Q4 → « 01.10 »)
const fbWeekDate = (q, w) => {
  const qStart = new Date(q.months[0] + "-01T00:00:00");
  const m0 = new Date(new Date(q.w0 + "T00:00:00").getTime() + w*6048e5 + 36e5*3);
  const d = m0 < qStart ? qStart : m0;
  return ("0"+d.getDate()).slice(-2) + "." + ("0"+(d.getMonth()+1)).slice(-2);
};
const fbMonthKey = (d) => d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
const fbCurrentQ = () => {
  const mk = fbMonthKey(new Date());
  const i = FB_QUARTERS.findIndex(q => q.months.includes(mk));
  if (i >= 0) return i;
  return mk > FB_QUARTERS[FB_QUARTERS.length - 1].months[2] ? FB_QUARTERS.length - 1 : 0;
};
const fbMonday = (iso) => {
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};
const FB_MLBL = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
const fbLabel = (mk) => FB_MLBL[(+mk.slice(5, 7)) - 1] || mk;
const fbTitle = (mk) => { const l = fbLabel(mk); return l.charAt(0) + l.slice(1).toLowerCase(); };
const FB_MONO   = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const FB_PLOT   = 220, FB_MAX = 160000;
const fbPx = (v) => (v / FB_MAX) * FB_PLOT;
const fbK  = (v) => v >= 100000 ? Math.round(v/1000) + "k" : (v/1000).toFixed(1).replace(/\.0$/,"") + "k";
const FB_HATCH = "repeating-linear-gradient(135deg, rgba(61,220,132,.22) 0 5px, rgba(61,220,132,.05) 5px 10px)";

function FoodBudgetBanner({ monthly = [], invoices = [], updated = "" }) {
  const [fbOv, setFbOv] = useState({});
  useEffect(() => { fbLoad().then(setFbOv); }, []);
  const FBQ = fbApply(fbOv);
  // dépense par mois depuis l'API (non filtrée par le sélecteur de la page)
  const spend = {};
  monthly.forEach(m => { if (typeof m.spend === "number" && m.spend > 0) spend[m.m] = m.spend; });

  const ci   = fbCurrentQ();
  const cur  = FBQ[ci];
  const prev = ci > 0 ? FBQ[ci - 1] : null;
  const shown = prev ? [prev, cur] : [cur];
  const MONTHS = shown.flatMap(q => q.months);
  const budgetOf = (mk) => { for (const q of shown) if (q.budgets[mk] != null) return q.budgets[mk]; return 0; };
  const weeksOf  = (mk) => { for (const q of shown) if (q.weeks && q.weeks[mk]) return q.weeks[mk]; return null; };

  // dépense par semaine du trimestre EN COURS, calculée sur toutes les factures
  // (et non sur le mois choisi dans la page, sinon la grille se vide)
  const weekSpend = {};
  if (cur.w0) {
    const w0 = new Date(cur.w0 + "T00:00:00");
    invoices.forEach(o => {
      if (!o.date) return;
      // seules les factures DU trimestre : W0 de Q4 démarre le lun. 28.09 mais
      // le 28-30.09 appartient à Q3 → sinon la grille ne totalise pas le bandeau
      if (!cur.months.includes(String(o.date).slice(0, 7))) return;
      const idx = Math.max(0, Math.round((fbMonday(o.date) - w0) / 6048e5));
      if (idx >= 0 && idx < cur.nWeeks) weekSpend[idx] = (weekSpend[idx] || 0) + (o.total || 0);
    });
  }

  const curKey = fbMonthKey(new Date());
  const sumQ   = (q) => q.months.reduce((a, m) => a + (spend[m] || 0), 0);
  const pSpend = prev ? sumQ(prev) : 0, pBud = prev ? prev.budget : 0;
  const pDelta = pBud ? ((pSpend - pBud) / pBud) * 100 : 0;
  const cSpend = sumQ(cur), cBud = cur.budget;
  const cPct   = cBud ? (cSpend / cBud) * 100 : 0;

  const dot = (c, size) => ({ width: size||8, height: size||8, borderRadius: "50%", background: c, boxShadow: "0 0 12px " + c, animation: "fbPulse 2.2s ease-in-out infinite" });
  const legend = (sw, txt) => (
    <div style={{ display:"flex", alignItems:"center", gap:9, fontSize:11, color:"#8296B8" }}>{sw}{txt}</div>
  );

  return (
    <div style={{ position:"relative", overflow:"hidden", borderRadius:18, marginBottom:16,
      background:"radial-gradient(1200px 420px at 78% -20%, rgba(76,154,255,.22), transparent 60%), radial-gradient(760px 380px at 8% 120%, rgba(0,224,198,.16), transparent 62%), #0A1220",
      border:"1px solid #22304d", boxShadow:"0 24px 60px -34px rgba(10,18,32,.6)" }}>
      <div style={{ position:"absolute", inset:0, pointerEvents:"none", backgroundImage:"linear-gradient(rgba(255,255,255,.028) 1px, transparent 1px)", backgroundSize:"100% 44px" }} />
      <div style={{ position:"absolute", top:0, left:0, height:2, width:"34%", background:"linear-gradient(90deg, transparent, #00E0C6, #4C9AFF, transparent)", animation:"fbSweep 5.5s linear infinite" }} />
      <div style={{ position:"relative", display:"grid", gridTemplateColumns:"320px minmax(0,1fr)", gap:30, padding:"34px 30px 24px" }}>

        <div style={{ display:"flex", flexDirection:"column", gap:20, borderRight:"1px solid rgba(255,255,255,.07)", paddingRight:26 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:9 }}>
              <span style={dot("#00E0C6")} />
              <span style={{ fontSize:12, fontWeight:700, letterSpacing:3.2, color:"#DCE6F5" }}>SPEND · VS · BUDGET</span>
            </div>
            <div style={{ fontSize:11.5, color:"#8296B8", letterSpacing:.4 }}>
              {fbTitle(MONTHS[0])} → {fbTitle(MONTHS[MONTHS.length-1])} {MONTHS[MONTHS.length-1].slice(0,4)} · net of VAT · CHF
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {prev && (
            <div style={{ borderRadius:12, padding:"14px 16px", background:"linear-gradient(135deg, rgba(76,154,255,.16), rgba(76,154,255,.04))", border:"1px solid rgba(76,154,255,.34)" }}>
              <div style={{ fontSize:10.5, fontWeight:700, letterSpacing:1.6, color:"#9FC0F5" }}>{prev.key} · CLOSED</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:9, marginTop:6 }}>
                <span style={{ fontFamily:FB_MONO, fontSize:27, fontWeight:700, color:"#fff" }}>{fbK(pSpend)}</span>
                <span style={{ fontFamily:FB_MONO, fontSize:12.5, color:"#8296B8", whiteSpace:"nowrap" }}>/ {fbK(pBud)} budget</span>
              </div>
              <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontFamily:FB_MONO, fontSize:11.5, fontWeight:700, color:pDelta<=0?"#3DDC84":"#FF9F1C", background:"rgba(61,220,132,.12)", border:"1px solid rgba(61,220,132,.4)", borderRadius:999, padding:"2px 9px", whiteSpace:"nowrap" }}>
                  {(pDelta>0?"+":"−") + Math.abs(pDelta).toFixed(1) + " %"}
                </span>
                <span style={{ fontSize:11, color:"#54688C" }}>{Math.abs(pDelta)<2 ? "on budget" : pDelta<0 ? "under budget" : "over budget"}</span>
              </div>
            </div>
            )}

            <div style={{ borderRadius:12, padding:"14px 16px", background:"linear-gradient(135deg, rgba(255,159,28,.16), rgba(255,159,28,.03))", border:"1px solid rgba(255,159,28,.34)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                <span style={{ fontSize:10.5, fontWeight:700, letterSpacing:1.6, color:"#FFC486" }}>{cur.key} · LIVE</span>
                <span style={dot("#FF9F1C", 6)} />
              </div>
              <div style={{ display:"flex", alignItems:"baseline", gap:9, marginTop:6 }}>
                <span style={{ fontFamily:FB_MONO, fontSize:27, fontWeight:700, color:"#fff" }}>{fbK(cSpend)}</span>
                <span style={{ fontFamily:FB_MONO, fontSize:12.5, color:"#8296B8", whiteSpace:"nowrap" }}>/ {fbK(cBud)} budget</span>
              </div>
              <div style={{ marginTop:10, height:6, borderRadius:999, background:"rgba(255,255,255,.07)", overflow:"hidden" }}>
                <div style={{ height:"100%", width:Math.min(cPct,100)+"%", borderRadius:999, background:"linear-gradient(90deg,#FF9F1C,#F472B6)", boxShadow:"0 0 14px rgba(255,159,28,.7)" }} />
              </div>
              <div style={{ marginTop:7, fontFamily:FB_MONO, fontSize:11, color:"#8296B8" }}>
                {fbK(Math.max(cBud - cSpend, 0))} left{cur.uniform != null ? " · " + fbK(cur.uniform) + "/week" : cur.wb ? " · weekly budget set in Admin" : ""}
              </div>
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
            {legend(<span style={{ width:22, height:8, borderRadius:3, background:"linear-gradient(90deg,#00E0C6,#4C9AFF)", flex:"none" }} />, "Actual spend")}
            {legend(<span style={{ width:22, height:0, borderTop:"2px dashed #A78BFA", flex:"none" }} />, "Monthly budget")}
            {legend(<span style={{ width:22, height:8, borderRadius:3, background:"linear-gradient(90deg,#FF9F1C,#F472B6)", flex:"none" }} />, "Over budget")}
            {legend(<span style={{ width:22, height:9, borderRadius:3, background:FB_HATCH, border:"1px dashed rgba(61,220,132,.6)", flex:"none" }} />, "Saved vs budget")}
          </div>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:14, minWidth:0 }}>
          <div style={{ display:"flex", gap:14, minWidth:0, alignItems:"flex-end" }}>
            <div style={{ width:44, height:FB_PLOT+40, position:"relative", flex:"none" }}>
              <div style={{ position:"absolute", top:0, left:0, right:0, height:FB_PLOT }}>
                {[150000,100000,50000,0].map(v => (
                  <span key={v} style={{ position:"absolute", right:0, bottom:fbPx(v), transform:"translateY(50%)", fontFamily:FB_MONO, fontSize:10, color:"#54688C" }}>{v?fbK(v):"0"}</span>
                ))}
              </div>
            </div>

            <div style={{ flex:1, minWidth:0, position:"relative" }}>
              <div style={{ position:"absolute", left:0, right:0, bottom:40, height:FB_PLOT, pointerEvents:"none" }}>
                {[150000,100000,50000].map(v => <div key={v} style={{ position:"absolute", left:0, right:0, bottom:fbPx(v), borderTop:"1px solid rgba(255,255,255,.06)" }} />)}
                <div style={{ position:"absolute", left:0, right:0, bottom:0, borderTop:"1px solid rgba(255,255,255,.16)" }} />
              </div>

              <div style={{ position:"relative", display:"grid", gridTemplateColumns:`repeat(${MONTHS.length},1fr)`, gap:20, alignItems:"end" }}>
                {MONTHS.map((m,i) => {
                  const bud = budgetOf(m), act = spend[m], has = typeof act === "number";
                  const open = m === curKey, closed = has && m < curKey;
                  const over = has && act > bud;
                  const delta = has && bud ? ((act - bud) / bud) * 100 : 0;
                  const wk = weeksOf(m);
                  return (
                    <div key={m} style={{ display:"flex", flexDirection:"column" }}>
                      <div style={{ position:"relative", height:FB_PLOT, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
                        <div style={{ position:"absolute", left:0, right:0, bottom:0, height:fbPx(bud), borderRadius:"5px 5px 0 0",
                          borderTop:"1.5px dashed rgba(167,139,250," + (has?.7:.45) + ")",
                          background:"linear-gradient(180deg, rgba(167,139,250," + (has?.13:.07) + "), rgba(167,139,250,.01))" }} />
                        <div style={{ position:"absolute", right:2, bottom:fbPx(bud)+2, zIndex:3, fontFamily:FB_MONO, fontSize:9.5,
                          color:has?"#A78BFA":"rgba(167,139,250,.85)", background:"rgba(10,18,32,.72)", borderRadius:3, padding:"1px 3px", whiteSpace:"nowrap" }}>{fbK(bud)}</div>

                        {has && closed && !over && (
                          <div style={{ position:"absolute", left:"26%", right:"26%", bottom:fbPx(act), height:fbPx(bud-act), borderRadius:"6px 6px 0 0",
                            background:FB_HATCH, border:"1px dashed rgba(61,220,132,.55)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:2 }}>
                            <span style={{ fontSize:8.5, fontWeight:700, letterSpacing:1.2, color:"#3DDC84", whiteSpace:"nowrap" }}>SAVED</span>
                            <span style={{ fontFamily:FB_MONO, fontSize:12, fontWeight:700, color:"#7DF3AE", whiteSpace:"nowrap" }}>{fbK(bud-act)}</span>
                          </div>
                        )}

                        {has ? (
                          <div style={{ position:"relative", width:"48%", height:fbPx(act), borderRadius:"7px 7px 0 0",
                            background: over ? "linear-gradient(180deg,#FF9F1C,#F472B6)" : "linear-gradient(180deg,#00E0C6,#4C9AFF)",
                            boxShadow: over ? "0 0 30px rgba(255,159,28,.5), inset 0 1px 0 rgba(255,255,255,.5)" : "0 0 26px rgba(0,224,198,.42), inset 0 1px 0 rgba(255,255,255,.5)",
                            transformOrigin:"bottom", animation:"fbGrow .95s cubic-bezier(.16,.9,.2,1) " + (0.05 + i*0.08) + "s both" }} />
                        ) : (
                          <div style={{ position:"absolute", bottom:8, left:"50%", transform:"translateX(-50%)", width:"100%", textAlign:"center", fontSize:9, lineHeight:1.25, letterSpacing:1, color:"#54688C" }}>TO<br />COME</div>
                        )}

                        {has && (
                          <div style={{ position:"absolute", bottom:14, left:"50%", transform:"translateX(-50%)", zIndex:4, display:"flex", flexDirection:"column", alignItems:"center", gap:3, whiteSpace:"nowrap" }}>
                            <span style={{ fontFamily:FB_MONO, fontSize:14, fontWeight:700, color:"#fff", textShadow:"0 1px 3px rgba(10,18,32,.75)" }}>{fbK(act)}</span>
                            <span style={{ fontFamily:FB_MONO, fontSize:10, fontWeight:700, color: open?"#00E0C6":over?"#FFC486":"#3DDC84", background:"rgba(10,18,32,.8)", borderRadius:999, padding:"1px 6px" }}>
                              {open ? "partial" : (delta>0?"+":"−") + Math.abs(delta).toFixed(1) + " %"}
                            </span>
                          </div>
                        )}
                      </div>
                      <div style={{ height:40, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:1,
                        fontSize:11.5, letterSpacing:.6, lineHeight:1.15, fontWeight: open?700:600,
                        color: open ? "#FF9F1C" : closed ? "#3DDC84" : has ? "#8296B8" : "#54688C" }}>
                        <span>{fbLabel(m)}{closed ? " ✓" : ""}</span>
                        {wk && <span style={{ fontSize:9.5, fontFamily:FB_MONO, color:"#54688C" }}>{"W" + wk[0] + "–" + wk[wk.length-1]}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {cur.w0 && (
          <div style={{ borderTop:"1px solid rgba(255,255,255,.07)", paddingTop:14, display:"flex", flexDirection:"column", gap:9 }}>
            <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:14, flexWrap:"wrap" }}>
              <span style={{ fontSize:10.5, fontWeight:700, letterSpacing:2.2, color:"#8296B8" }}>{cur.key} WEEKLY GRID · {cur.uniform != null ? fbK(cur.uniform) + " / WEEK" : "BUDGET PER WEEK"}</span>
              <span style={{ fontFamily:FB_MONO, fontSize:10.5, color:"#54688C" }}>W0 → W{cur.nWeeks-1} · {fbK(cBud)}</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:`repeat(${cur.nWeeks},1fr)`, gap:5, alignItems:"end" }}>
              {Array.from({ length: cur.nWeeks }, (_, w) => {
                const amount = weekSpend[w];
                const has = typeof amount === "number" && amount > 0;
                const wb = cur.wb[w];
                const over = has && amount > wb;
                const dlbl = fbWeekDate(cur, w);
                return (
                  <div key={w} style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    <span style={{ fontFamily:FB_MONO, fontSize:9.5, fontWeight:700, textAlign:"center", whiteSpace:"nowrap", color: has?(over?"#FFC486":"#7FF2E4"):"transparent" }}>{has?fbK(amount):"·"}</span>
                    <div title={"W"+w+" · "+dlbl+" · budget "+fbK(wb)} style={{ height:40, borderRadius:5, background: has?"rgba(0,224,198,.14)":"rgba(255,255,255,.04)",
                      border: has?"1px solid rgba(0,224,198,.45)":"1px dashed rgba(167,139,250,.4)", display:"flex", alignItems:"flex-end" }}>
                      {has && <div style={{ height: (wb > 0 ? Math.min(amount/wb,1) : 1)*100 + "%", width:"100%", borderRadius:4,
                        background: over ? "linear-gradient(180deg,#FF9F1C,#F472B6)" : "linear-gradient(180deg,#00E0C6,#4C9AFF)",
                        boxShadow:"0 0 10px rgba(0,224,198,.4)" }} />}
                    </div>
                    <span style={{ fontFamily:FB_MONO, fontSize:9, textAlign:"center", color: has?"#8296B8":"#54688C" }}>W{w}</span>
                    {cur.uniform == null && <span style={{ fontFamily:FB_MONO, fontSize:8.5, textAlign:"center", color:"rgba(167,139,250,.8)" }}>/{fbK(wb)}</span>}
                    <span style={{ fontFamily:FB_MONO, fontSize:8.5, textAlign:"center", color: has?"#54688C":"transparent" }}>{dlbl}</span>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"baseline", justifyContent:"space-between", gap:"6px 18px", borderTop:"1px solid rgba(255,255,255,.07)", paddingTop:13 }}>
            <div style={{ flex:"1 1 320px", minWidth:0, fontSize:11.5, color:"#8296B8" }}>
              {prev && <>Budget {prev.key} <b style={{ color:"#DCE6F5", fontFamily:FB_MONO }}>{fbK(pBud)}</b> · </>}
              Budget {cur.key} <b style={{ color:"#DCE6F5", fontFamily:FB_MONO }}>{fbK(cBud)}</b>
              {cur.w0 && <span style={{ color:"#54688C" }}> (W0→W{cur.nWeeks-1} · {cur.uniform != null ? fbK(cur.uniform) + " / week" : "weekly budget set in Admin"})</span>}
            </div>
            <div style={{ fontFamily:FB_MONO, fontSize:11.5, color:"#54688C" }}>source: prix_caa{updated ? " · updated " + updated : ""}</div>
          </div>
        </div>
      </div>
      <style>{"@keyframes fbGrow{from{transform:scaleY(0);opacity:.2}to{transform:scaleY(1);opacity:1}}@keyframes fbSweep{from{transform:translateX(-120%)}to{transform:translateX(320%)}}@keyframes fbPulse{0%,100%{opacity:.55}50%{opacity:1}}"}</style>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  FOOD INVOICES — Réconciliation PO Odoo ↔ Factures (15/10/2026, v2)
//  La facture scannée (prix_caa) est la vérité, la PO Odoo est contrôlée
//  contre elle. Chaque écart est décomposé : prix faux dans Odoo / unité /
//  quantité-pesée / livré hors PO / non livré. Seule la part PRIX est
//  « à corriger » — et c'est ce que le correctif Odoo (Admin) sait réparer.
//  API : GET  /dashboard/food_reconcile?since&until
//        GET  /dashboard/food_reconcile_po?po=…
//        GET  /dashboard/food_fix_preview?po=…      (Admin)
//        POST /dashboard/food_fix_apply {po, actions} (Admin)
//        GET  /dashboard/food_fix_journal?po=…      (Admin)
//        POST /dashboard/food_fix_undo {batch}      (Admin)
// ═══════════════════════════════════════════════════════════════════════════
const FR_L = {
  fr: {
    title: "Réconciliation PO Odoo ↔ Factures",
    sub: "Bon de commande Odoo vs facture scannée et réconciliée au centime (HT) — la facture fait foi",
    loading: "Rapprochement en cours…",
    inv: "Factures", odoo: "Odoo", gap: "Écart", conform: "conformes",
    kPos: "PO rapprochées", kOk: "Conformes", kIssues: "Prix faux dans Odoo", kIssuesSub: "à corriger",
    kCheck: "À vérifier", kCheckSub: "pesée, hors PO, non livré, unité",
    kMissing: "PO introuvable", kCover: "Couverture", kCoverSub: "du facturé porte un n° de PO",
    bySup: "Par fournisseur", supCol: "Fournisseur", posCol: "PO", priceCol: "dont prix", issuesCol: "Prix faux",
    showIssues: "À corriger", showCheck: "À vérifier", showAll: "Toutes",
    po: "PO Odoo", invoices: "Facture(s)", status: "Statut",
    st: { ok: "conforme", minor: "mineur", price: "prix faux", packqty: "qté en cartons", unit: "unité ≠", qty: "quantité / pesée",
          offpo: "livré hors PO", undelivered: "non livré", unpaired: "non apparié",
          under: "Odoo sous-évalué", over: "Odoo surévalué", missing: "PO introuvable", cancelled: "PO annulée" },
    dc: { price: "prix", unit: "unité", qty: "qté", weight: "kg↔pce", round: "arrondi pièce", packqty: "qté en cartons", inv_only: "hors PO", odoo_only: "non livré" },
    more: "Afficher plus", none: "Aucune PO rapprochée sur la période.",
    allGood: "Rien dans cette sélection.",
    noPo: "Factures sans n° de PO", noPoSub: "livraisons sans bon de commande ou référence illisible",
    poNoInv: "PO Odoo sans facture", poNoInvSub: "livraison prévue il y a plus de 7 jours, aucune facture scannée",
    poNoInvRecent: "PO récentes en attente de scan (non comptées)",
    dLoading: "Chargement du détail…", dPairs: "Lignes appariées (code article ou libellé)",
    convTip: "Commandé à la pièce, facturé au poids (ou l'inverse) : quantités non comparables, l'écart vient de la pesée et n'est jamais corrigé. Poids réel par pièce affiché.",
    convPer: "kg/pce", convOff: "poids/pce inhabituel", convRef: "annoncé",
    multiDel: "livraisons", multiTip: "Quantités et montants additionnés sur toutes les factures de cette PO",
    roundLbl: "arrondi", roundPer: "réel", roundTip: "Commande en poids, livraison en pièces entières (conditionnement Odoo) : écart mécanique, pas une anomalie. Le montant reste une vraie dépense.",
    sameBl: "même BL", sameBlTip: "PO livrée sur ce bon de livraison sans y être citée (même fournisseur, ±3 j, contenu retrouvé) : rattachée automatiquement",
    codeTip: "Code fournisseur dans Odoo (onglet Achats du produit)",
    dInvOnly: "Facturé, absent de la PO (ou code non apparié)", dOdooOnly: "Dans la PO, non facturé (ou code non apparié)",
    dCode: "Code", dArt: "Article", dQty: "Qté fact. / Odoo", dPrice: "Prix fact. / Odoo", dGap: "Écart",
    dDecomp: "L'écart se décompose en", dUnit: "unité ?",
    packTip: "Conditionnement : facturé à l'unité, compté au carton dans Odoo (ou l'inverse). Prix facturé ramené à l'unité Odoo. Jamais corrigé par défaut.",
    byName: "par nom", byNameTip: "Pas de code article commun : apparié par le libellé, dans cette PO uniquement. Jamais corrigé par défaut.",
    dNote: "Appariement par code article, puis par libellé (≈ par nom) pour les produits sans code dans Odoo — viande, poisson, fromage, frais. Ce qui reste non apparié est à trancher à l'œil.",
    open: "Ouvrir dans Odoo",
    fixBtn: "🛠 Corriger les prix dans Odoo", fixLoading: "Préparation des corrections…",
    fixNone: "Aucune correction automatique possible sur cette PO (pas de prix faux apparié par code).",
    fixApply: "Appliquer", fixConfirm: "Confirmer l'écriture dans Odoo", fixCancel: "Fermer",
    fixDone: "corrections écrites dans Odoo", fixSkipped: "non appliquées",
    fixIntro: "Rien n'est écrit tant que vous ne confirmez pas. Chaque correction est journalisée et annulable.",
    journal: "Corrections déjà faites sur cette PO", undo: "Annuler", undone: "annulé",
    undoDone: "valeurs restaurées",
    hint: "Écart = PO Odoo − facture. « Prix faux » = la ligne existe des deux côtés avec le même code mais un prix différent : c'est ce que le correctif répare. Pesées, livraisons hors PO et non-livrés sont à vérifier, pas à corriger en masse. Conforme < 0.51 CHF · mineur ≤ 2 CHF ou 2 %.",
  },
  en: {
    title: "Odoo PO ↔ Invoice reconciliation",
    sub: "Odoo purchase order vs scanned, cent-reconciled invoice (net of VAT) — the invoice is the reference",
    loading: "Reconciling…",
    inv: "Invoices", odoo: "Odoo", gap: "Gap", conform: "matched",
    kPos: "POs reconciled", kOk: "Matched", kIssues: "Wrong price in Odoo", kIssuesSub: "to fix",
    kCheck: "To check", kCheckSub: "weighing, off-PO, undelivered, unit",
    kMissing: "PO not found", kCover: "Coverage", kCoverSub: "of invoiced spend carries a PO number",
    bySup: "By supplier", supCol: "Supplier", posCol: "POs", priceCol: "of which price", issuesCol: "Wrong price",
    showIssues: "To fix", showCheck: "To check", showAll: "All",
    po: "Odoo PO", invoices: "Invoice(s)", status: "Status",
    st: { ok: "match", minor: "minor", price: "wrong price", packqty: "qty in cases", unit: "unit ≠", qty: "qty / weighing",
          offpo: "off-PO delivery", undelivered: "not delivered", unpaired: "unpaired",
          under: "Odoo under-stated", over: "Odoo over-stated", missing: "PO not found", cancelled: "PO cancelled" },
    dc: { price: "price", unit: "unit", qty: "qty", weight: "kg↔pc", round: "whole-piece", packqty: "qty in cases", inv_only: "off-PO", odoo_only: "undelivered" },
    more: "Show more", none: "No reconciled PO in this period.",
    allGood: "Nothing in this selection.",
    noPo: "Invoices without a PO number", noPoSub: "deliveries without an order or unreadable reference",
    poNoInv: "Odoo POs without an invoice", poNoInvSub: "planned more than 7 days ago, no scanned invoice",
    poNoInvRecent: "recent POs awaiting scan (not counted)",
    dLoading: "Loading detail…", dPairs: "Paired lines (item code or label)",
    convTip: "Ordered per piece, invoiced by weight (or the reverse): quantities are not comparable, the gap comes from weighing and is never fixed. Actual weight per piece shown.",
    convPer: "kg/pc", convOff: "unusual weight/pc", convRef: "stated",
    multiDel: "deliveries", multiTip: "Quantities and amounts summed over all invoices of this PO",
    roundLbl: "rounded", roundPer: "actual", roundTip: "Ordered by weight, delivered in whole pieces (Odoo packaging): mechanical gap, not an anomaly. The amount is still real spend.",
    sameBl: "same delivery note", sameBlTip: "PO delivered on this delivery note without being cited (same supplier, ±3 days, content found): attached automatically",
    codeTip: "Supplier code in Odoo (product Purchase tab)",
    dInvOnly: "Invoiced, not on the PO (or code not paired)", dOdooOnly: "On the PO, not invoiced (or code not paired)",
    dCode: "Code", dArt: "Item", dQty: "Qty inv. / Odoo", dPrice: "Price inv. / Odoo", dGap: "Gap",
    dDecomp: "The gap breaks down into", dUnit: "unit?",
    packTip: "Packaging: invoiced per unit, counted per case in Odoo (or the reverse). Invoiced price converted to the Odoo unit. Never fixed by default.",
    byName: "by name", byNameTip: "No shared item code: paired by label, within this PO only. Never fixed by default.",
    dNote: "Pairing by item code, then by label (≈ by name) for products without a code in Odoo — meat, fish, cheese, fresh. What remains unpaired is to be judged by eye.",
    open: "Open in Odoo",
    fixBtn: "🛠 Fix prices in Odoo", fixLoading: "Preparing corrections…",
    fixNone: "No automatic correction possible on this PO (no wrong price paired by code).",
    fixApply: "Apply", fixConfirm: "Confirm write to Odoo", fixCancel: "Close",
    fixDone: "corrections written to Odoo", fixSkipped: "not applied",
    fixIntro: "Nothing is written until you confirm. Every correction is logged and can be undone.",
    journal: "Corrections already made on this PO", undo: "Undo", undone: "undone",
    undoDone: "values restored",
    hint: "Gap = Odoo PO − invoice. “Wrong price” = the line exists on both sides with the same code but a different price: that is what the fix repairs. Weighing, off-PO and undelivered lines are to be checked, not bulk-fixed. Match < CHF 0.51 · minor ≤ CHF 2 or 2 %.",
  },
};
const FR_P = { soft: "#eef2f8", soft2: "#f7f9fc", card: "#ffffff", line: "#e3e8f0",
               ink: "#111827", sub: "#4b5563", hint: "#9aa4b2", ghost: "#c3cad6", acc: "#0ea5b7" };
const FR_FIX = new Set(["price", "missing", "cancelled"]);
const FR_CHECK = new Set(["packqty", "unit", "qty", "offpo", "undelivered", "unpaired", "under", "over"]);
const FR_ST_C = { ok: T.ok, minor: T.info, price: T.err, packqty: "#0369a1", unit: "#7c3aed", qty: T.info, offpo: T.warn,
                  undelivered: T.warn, unpaired: T.hint, under: T.err, over: T.warn,
                  missing: T.err, cancelled: T.hint };
const frPoLink = (id) => id ? `${WS_ODOO}/web#id=${id}&model=purchase.order&view_type=form` : null;
const fr2 = (n) => (n == null ? "—" : (+n).toFixed(2));
const frSigned = (n) => `${n > 0 ? "+" : ""}${(+n || 0).toFixed(2)}`;
const frMonthEnd = (mk) => { const [y, m] = (mk || "").split("-").map(Number);
  return (y && m) ? new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10) : ""; };
const frGapColor = (g) => Math.abs(g) < 0.51 ? T.ok : (g < 0 ? T.err : T.warn);
const frBtn = (bg, fg, edge) => ({ background: bg, color: fg, border: `1px solid ${edge || bg}`, borderRadius: 8,
  padding: "6px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" });

function FrDecompChips({ dc, L }) {
  if (!dc) return null;
  const parts = [["price", dc.price], ["unit", dc.unit], ["qty", dc.qty], ["weight", dc.weight], ["round", dc.round], ["packqty", dc.packqty],
                 ["inv_only", dc.inv_only], ["odoo_only", dc.odoo_only]].filter(([, v]) => Math.abs(v || 0) >= 0.5);
  if (!parts.length) return null;
  return (
    <span style={{ display: "inline-flex", gap: 5, flexWrap: "wrap", marginTop: 3 }}>
      {parts.map(([k, v]) => (
        <span key={k} style={{ ...WS_NUM, fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 999,
          color: k === "price" ? T.err : FR_P.sub, background: k === "price" ? "#fee2e2" : FR_P.soft,
          border: `1px solid ${k === "price" ? "#fca5a5" : FR_P.line}` }}>
          {L.dc[k]} {frSigned(v)}
        </span>))}
    </span>);
}

function FoodFixPanel({ po, L, onApplied }) {
  // Autorisé à corriger les prix : décidé par l'API au login (can_fix) —
  // Admin + acheteurs CAA (Sébastien, Sanford, Daniel). Contrôle refait côté API.
  const isAdmin = AUTH.role === "Admin" || AUTH.can_fix === true;
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState({});
  const [arm, setArm] = useState(false);
  const [res, setRes] = useState(null);
  const [jr, setJr] = useState([]);
  const loadJournal = useCallback(() => {
    if (!isAdmin) return;
    fetch(`${API}/dashboard/food_fix_journal?po=${encodeURIComponent(po)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { batches: [] }).then(j => setJr(j.batches || [])).catch(() => {});
  }, [po, isAdmin]);
  useEffect(() => { loadJournal(); }, [loadJournal]);
  if (!isAdmin) return null;

  const preview = async () => {
    setBusy(true); setErr(""); setRes(null); setArm(false);
    try {
      const r = await fetch(`${API}/dashboard/food_fix_preview?po=${encodeURIComponent(po)}`, { headers: authHeaders() });
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      setPlan(j);
      const s = {}; (j.actions || []).forEach(a => { s[a.id] = !!(a.enabled && a.default); }); setSel(s);
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  const apply = async () => {
    if (!arm) { setArm(true); return; }
    setBusy(true); setErr("");
    try {
      const ids = Object.keys(sel).filter(k => sel[k]);
      const r = await fetch(`${API}/dashboard/food_fix_apply`, { method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ po, actions: ids }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setRes(j); setPlan(null); setArm(false); loadJournal(); onApplied && onApplied();
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  const undo = async (batch) => {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API}/dashboard/food_fix_undo`, { method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ batch }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setRes({ undo: true, ...j }); loadJournal(); onApplied && onApplied();
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };

  const acts = (plan && plan.actions) || [];
  const byLine = {};
  acts.forEach(a => { (byLine[a.line] = byLine[a.line] || []).push(a); });
  const nSel = Object.values(sel).filter(Boolean).length;

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${FR_P.line}` }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {!plan && <button onClick={preview} disabled={busy} style={frBtn("#1f3b5b", "#fff")}>{busy ? "…" : L.fixBtn}</button>}
        {res && !res.undo && <span style={{ fontSize: 12, color: T.ok, fontWeight: 700 }}>
          ✓ {res.applied} {L.fixDone}{(res.skipped || []).length ? ` · ${res.skipped.length} ${L.fixSkipped}` : ""}</span>}
        {res && res.undo && <span style={{ fontSize: 12, color: T.ok, fontWeight: 700 }}>↺ {res.restored} {L.undoDone}
          {(res.skipped || []).length ? ` · ${res.skipped.length} ${L.fixSkipped}` : ""}</span>}
        {err && <span style={{ fontSize: 12, color: T.err }}>{err}</span>}
      </div>
      {res && (res.skipped || []).length > 0 && (
        <div style={{ fontSize: 11, color: FR_P.sub, marginTop: 4 }}>
          {res.skipped.map((s, i) => <div key={i}>• {s.label || s.id} — {s.why}</div>)}
        </div>)}

      {plan && (
        <div style={{ marginTop: 8, border: `1px solid #c7d2fe`, background: "#f5f7ff", borderRadius: 10, padding: "10px 12px" }}>
          <div style={{ fontSize: 11.5, color: FR_P.sub, marginBottom: 8 }}>{L.fixIntro}</div>
          {acts.length === 0 ? <div style={{ fontSize: 12, color: FR_P.hint }}>{L.fixNone}</div> :
            Object.entries(byLine).map(([line, list]) => (
              <div key={line} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: FR_P.ink }}>{line}
                  <span style={{ color: FR_P.hint, fontWeight: 500 }}> · {list[0].code}{list[0].po ? ` · ${list[0].po}` : ""}</span></div>
                {list.map(a => (
                  <label key={a.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, padding: "3px 0 3px 6px",
                    color: a.enabled ? FR_P.ink : FR_P.hint, cursor: a.enabled ? "pointer" : "not-allowed" }}>
                    <input type="checkbox" disabled={!a.enabled} checked={!!sel[a.id]}
                      onChange={e => { setSel(s => ({ ...s, [a.id]: e.target.checked })); setArm(false); }} />
                    <span style={{ flex: 1 }}>{a.label}
                      {a.impact != null && a.enabled && <b style={{ ...WS_NUM, color: frGapColor(-a.impact), marginLeft: 6 }}>
                        ({L.odoo} {frSigned(a.impact)})</b>}
                      {a.why && <span style={{ display: "block", fontSize: 11, color: "#b45309" }}>⚠ {a.why}</span>}
                    </span>
                  </label>))}
              </div>))}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {acts.length > 0 && <button onClick={apply} disabled={busy || nSel === 0}
              style={{ ...frBtn(arm ? T.err : "#1f3b5b", "#fff"), opacity: (busy || nSel === 0) ? .5 : 1 }}>
              {busy ? "…" : arm ? `${L.fixConfirm} (${nSel})` : `${L.fixApply} (${nSel})`}</button>}
            <button onClick={() => { setPlan(null); setArm(false); }} style={frBtn("#fff", FR_P.sub, FR_P.line)}>{L.fixCancel}</button>
          </div>
        </div>)}

      {jr.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: FR_P.hint }}>{L.journal}</div>
          {jr.map(b => (
            <div key={b.batch} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 11.5, padding: "4px 0",
              borderBottom: `1px solid ${FR_P.line}`, color: b.undone ? FR_P.hint : FR_P.sub }}>
              <span style={{ ...WS_NUM }}>{(b.ts || "").replace("T", " ").slice(0, 16)}</span>
              <span>{b.user}</span>
              <span style={{ flex: 1 }} title={(b.entries || []).map(e => `${e.line} — ${e.label}`).join("\n")}>
                {b.n} correction(s){b.undone ? ` · ${L.undone} (${b.undone_by})` : ""}</span>
              {!b.undone && <button onClick={() => undo(b.batch)} disabled={busy} style={frBtn("#fff", T.err, "#fca5a5")}>↺ {L.undo}</button>}
            </div>))}
        </div>)}
    </div>);
}

function FoodReconcileDetail({ po, L, onApplied }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setD(null);
    fetch(`${API}/dashboard/food_reconcile_po?po=${encodeURIComponent(po)}`)
      .then(r => r.json())
      .then(j => { if (!alive) return; if (j.error) setErr(j.error); else setD(j); })
      .catch(e => alive && setErr(String(e)));
    return () => { alive = false; };
  }, [po, tick]);
  if (err) return <div style={{ color: T.err, fontSize: 12, padding: "8px 0" }}>{err}</div>;
  if (!d) return <div style={{ color: FR_P.hint, fontSize: 12, padding: "8px 0" }}>{L.dLoading}</div>;

  const pairs = [...(d.pairs || [])].sort((a, b) =>
    (a.status === "ok") - (b.status === "ok") || Math.abs(b.gap) - Math.abs(a.gap));
  const cols = "92px 1fr 128px 150px 84px";
  const head = (t) => (
    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase",
      color: FR_P.hint, margin: "12px 0 5px" }}>{t}</div>);
  const simple = (rows, sign) => rows.length === 0 ? <div style={{ fontSize: 12, color: FR_P.ghost }}>—</div> :
    rows.map((l, i) => (
      <div key={i} style={{ display: "grid", gridTemplateColumns: "92px 1fr 120px 84px", gap: 8, fontSize: 12,
        padding: "5px 8px", background: i % 2 ? FR_P.soft2 : FR_P.card, borderTop: i ? `1px solid ${FR_P.line}` : "none" }}>
        <span style={{ ...WS_NUM, color: FR_P.ghost }}>{("code_real" in l ? l.code_real : (l.code || (l.codes || [])[0])) || "—"}</span>
        <span style={{ fontWeight: 600, color: FR_P.ink }}>{l.name}</span>
        <span style={{ ...WS_NUM, textAlign: "right", color: FR_P.sub }}>
          {l.qty} {l.uom} × {fr2(l.unit_price ?? l.price)}</span>
        <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 800, color: sign < 0 ? T.err : T.warn }}>
          {sign < 0 ? "−" : "+"}{fr2(l.total)}</span>
      </div>));

  return (
    <div style={{ background: FR_P.card, border: `1px solid ${FR_P.line}`, borderRadius: 10,
      padding: "6px 14px 12px", margin: "6px 0 4px", boxShadow: "0 1px 3px rgba(15,23,42,.08)" }}>
      {head(`${L.dPairs} (${pairs.length})`)}
      {pairs.length === 0 ? <div style={{ fontSize: 12, color: FR_P.ghost }}>—</div> : <>
        <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, fontSize: 9.5, fontWeight: 800,
          color: FR_P.hint, textTransform: "uppercase", letterSpacing: ".06em", padding: "4px 8px" }}>
          <span>{L.dCode}</span><span>{L.dArt}</span>
          <span style={{ textAlign: "right" }}>{L.dQty}</span>
          <span style={{ textAlign: "right" }}>{L.dPrice}</span>
          <span style={{ textAlign: "right" }}>{L.dGap}</span>
        </div>
        {pairs.map((p, i) => {
          const badQ = p.status === "qty" || p.status === "both";
          const badP = p.status === "price" || p.status === "both";
          return (
            <div key={i} style={{ display: "grid", gridTemplateColumns: cols, gap: 8, alignItems: "center",
              fontSize: 12, padding: "5px 8px", background: badP ? "#fff1f2" : (badQ ? "#f0f9ff" : "transparent"),
              borderTop: i ? `1px solid ${FR_P.line}` : "none" }}>
              <span title={`${L.codeTip}${p.code ? " · facture : " + p.code : ""}`}
                style={{ ...WS_NUM, color: p.odoo_code ? FR_P.sub : FR_P.ghost, overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.odoo_code || p.inv_code_real || (p.code && !/^synth/i.test(p.code) && !/^[A-Za-z]{2}$/.test(p.code) ? p.code : "—")}
                {p.match_by === "name" && <span title={L.byNameTip} style={{ display: "block", fontSize: 9.5, fontWeight: 800,
                  color: "#7c3aed" }}>≈ {L.byName}</span>}</span>
              <span style={{ fontWeight: 600, color: FR_P.ink }}>{p.inv_name}
                {p.n_lines > 1 && <span title={`${L.multiTip} : ${(p.invoices || []).join(" + ")}`}
                  style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#b45309", background: "#fef3c7",
                    border: "1px solid #fcd34d", borderRadius: 999, padding: "0 6px" }}>×{p.n_lines} {L.multiDel}</span>}
                {p.odoo_name && p.odoo_name !== p.inv_name &&
                  <span style={{ color: FR_P.hint, fontWeight: 400 }}> · {p.odoo_name}</span>}</span>
              <span style={{ ...WS_NUM, textAlign: "right", color: badQ ? T.info : FR_P.sub, fontWeight: badQ ? 800 : 400 }}>
                {p.uom_conv ? (
                  <span title={L.convTip}>
                    {p.inv_qty} {p.uom_conv.invoiced_in === "kg" ? "kg" : "pce"} / {p.odoo_qty} {p.uom_conv.ordered_in === "kg" ? "kg" : "pce"}
                    <span style={{ display: "block", fontSize: 10, fontWeight: 800,
                      color: p.uom_conv.weight_off ? T.warn : "#0369a1" }}>
                      ⚖ {fr2(p.uom_conv.kg_per_piece)} {L.convPer}
                      {p.uom_conv.ref_kg_per_piece ? ` (${L.convRef} ${p.uom_conv.ref_kg_per_piece})` : ""}
                      {p.uom_conv.weight_off ? ` · ${L.convOff}` : ""}</span>
                  </span>) : p.pack_round ? (
                  <span title={L.roundTip}>
                    {p.inv_qty} / {p.odoo_qty}
                    <span style={{ display: "block", fontSize: 10, fontWeight: 800, color: "#047857" }}>
                      ⚖ {L.roundLbl} : {p.pack_round.pieces} × {p.pack_round.name || p.pack_round.pack_qty} ≈ {p.pack_round.expected}
                      {" · "}{L.roundPer} {fr2(p.pack_round.kg_per_piece)}/pce</span>
                  </span>) : <>{p.inv_qty} / {p.odoo_qty}</>}</span>
              <span style={{ ...WS_NUM, textAlign: "right", color: badP ? T.err : FR_P.sub, fontWeight: badP ? 800 : 400 }}>
                {fr2(p.inv_price)} / {fr2(p.odoo_price)}
                {p.unit_suspect && <span style={{ color: "#7c3aed", fontWeight: 800 }}> · {L.dUnit}</span>}
                {p.pack && <span title={L.packTip} style={{ color: "#0369a1", fontWeight: 800 }}> · ×{p.pack}</span>}</span>
              <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 800, color: frGapColor(p.gap) }}>{frSigned(p.gap)}</span>
            </div>);
        })}
      </>}
      {head(`${L.dInvOnly} (${(d.inv_only || []).length})`)}
      {simple(d.inv_only || [], -1)}
      {head(`${L.dOdooOnly} (${(d.odoo_only || []).length})`)}
      {simple(d.odoo_only || [], +1)}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 12,
        paddingTop: 9, borderTop: `1px solid ${FR_P.line}`, fontSize: 11.5, color: FR_P.sub }}>
        <b style={{ color: FR_P.ink }}>{L.dDecomp}</b>
        <FrDecompChips dc={d.decomp} L={L} />
        <span>= <b style={{ ...WS_NUM, color: frGapColor(d.gap) }}>{frSigned(d.gap)}</b></span>
      </div>
      <div style={{ fontSize: 10.5, color: FR_P.hint, marginTop: 6, fontStyle: "italic" }}>{L.dNote}</div>
      <FoodFixPanel po={po} L={L} onApplied={() => { setTick(t => t + 1); onApplied && onApplied(); }} />
    </div>
  );
}

function FoodReconcile({ since, until, lang, supName, q }) {
  const L = FR_L[lang] || FR_L.fr;
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("issues");
  const [supSel, setSupSel] = useState(null);
  const [openPo, setOpenPo] = useState(null);
  const [limit, setLimit] = useState(25);
  const [reload, setReload] = useState(0);
  const [qq, setQq] = useState("");
  useEffect(() => { setD(null); setOpenPo(null); setLimit(25); }, [since, until]);
  useEffect(() => {
    let alive = true;
    setErr("");
    fetch(`${API}/dashboard/food_reconcile?since=${since}&until=${until || ""}`)
      .then(r => r.json())
      .then(j => { if (!alive) return; if (j.error) setErr(j.error); else setD(j); })
      .catch(e => alive && setErr(String(e)));
    return () => { alive = false; };
  }, [since, until, reload]);

  const sup = supSel || supName || null;
  const t = (d && d.totals) || {};
  const allRows = (d && d.rows) || [];
  const ql = (qq || q || "").trim().toLowerCase();
  const scoped = allRows.filter(r => (!sup || r.supplier === sup)
    && (!ql || r.po.toLowerCase().includes(ql)
        || (r.invoices || []).some(iv => (iv.number || "").toLowerCase().includes(ql))));
  const inMode = (r) => mode === "issues" ? FR_FIX.has(r.status) : mode === "check" ? FR_CHECK.has(r.status) : true;
  // une recherche (n° de PO / facture) montre la PO quel que soit son statut
  const rows = ql ? scoped : scoped.filter(inMode);
  const nFix = (t.issues || 0) + (t.missing || 0) + (t.cancelled || 0);
  const gapTot = t.gap || 0;
  const color = !d ? W.deep : (nFix ? W.red : W.green);
  const cols = "116px 1fr 104px 104px 96px 128px";
  const late = ((d && d.po_without_invoice) || []).filter(p => !p.recent);
  const recent = ((d && d.po_without_invoice) || []).filter(p => p.recent);
  const noPo = ((d && d.no_po) || []).filter(o => !sup || o.supplier === sup);

  return (
    <WSFold defaultOpen color={color} title={L.title} sub={L.sub}
      badges={d ? [
        { t: `${L.inv} ${fmtK(t.invoiced || 0)}`, c: W.deep },
        { t: `${L.odoo} ${fmtK(t.odoo || 0)}`, c: T.sub },
        { t: frSigned(gapTot), c: Math.abs(gapTot) < 1 ? T.ok : T.err },
        { t: `${t.issues || 0} ${L.st.price}`, c: t.issues ? T.err : T.ok },
      ] : []}>
      {err ? <div style={{ color: T.err, fontSize: 12.5 }}>{err}</div>
      : !d ? <div style={{ color: T.hint, fontSize: 12.5 }}>{L.loading}</div>
      : allRows.length === 0 ? <div style={{ color: T.hint, fontSize: 12.5 }}>{L.none}</div>
      : <>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 9, margin: "6px 0 14px" }}>
          {[
            [L.kPos, t.pos, W.deep],
            [L.kOk, (t.ok || 0) + (t.minor || 0), T.ok, `${t.ok || 0} + ${t.minor || 0} ${L.st.minor}`],
            [L.kIssues, t.issues, (t.issues ? T.err : T.ok), `${L.kIssuesSub} · CHF ${fr2(t.price_total || 0)}`],
            [L.kCheck, t.to_check, (t.to_check ? T.warn : T.ok), L.kCheckSub],
            [L.kMissing, (t.missing || 0) + (t.cancelled || 0), ((t.missing || t.cancelled) ? T.err : T.ok)],
            [L.kCover, `${t.coverage_pct || 0} %`, (t.coverage_pct >= 90 ? T.ok : T.warn), L.kCoverSub],
          ].map(([lab, v, c, s], i) => (
            <div key={i} style={{ border: `1px solid ${T.line}`, borderLeft: `3px solid ${c}`, borderRadius: 10, padding: "8px 11px", background: T.card }}>
              <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: T.hint }}>{lab}</div>
              <div style={{ ...WS_NUM, fontSize: 20, fontWeight: 800, color: c, marginTop: 2 }}>{v ?? 0}</div>
              {s && <div style={{ fontSize: 10.5, color: T.hint }}>{s}</div>}
            </div>
          ))}
        </div>

        <div style={{ fontSize: 10, fontWeight: 800, color: T.hint, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 5 }}>{L.bySup}</div>
        <div style={{ border: `1px solid ${T.line}`, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 52px 100px 100px 90px 90px 70px", gap: 8, padding: "7px 12px",
            background: FR_P.soft, fontSize: 9.5, fontWeight: 800, color: T.hint, textTransform: "uppercase", letterSpacing: ".06em" }}>
            <span>{L.supCol}</span><span style={{ textAlign: "right" }}>{L.posCol}</span>
            <span style={{ textAlign: "right" }}>{L.inv}</span><span style={{ textAlign: "right" }}>{L.odoo}</span>
            <span style={{ textAlign: "right" }}>{L.gap}</span><span style={{ textAlign: "right" }}>{L.priceCol}</span>
            <span style={{ textAlign: "right" }}>{L.issuesCol}</span>
          </div>
          {(d.suppliers || []).map((s, i) => {
            const on = sup === s.supplier;
            return (
              <div key={s.supplier} onClick={() => { setSupSel(on ? null : s.supplier); setOpenPo(null); }}
                style={{ display: "grid", gridTemplateColumns: "1fr 52px 100px 100px 90px 90px 70px", gap: 8, padding: "7px 12px",
                  fontSize: 12.5, cursor: "pointer", borderTop: `1px solid ${T.line}`,
                  background: on ? "rgba(14,165,183,.10)" : (i % 2 ? FR_P.soft2 : T.card),
                  borderLeft: `3px solid ${on ? FR_P.acc : "transparent"}` }}>
                <span style={{ fontWeight: 700, color: T.ink }}>{s.supplier}</span>
                <span style={{ ...WS_NUM, textAlign: "right", color: T.sub }}>{s.pos}</span>
                <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 700 }}>{fr2(s.invoiced)}</span>
                <span style={{ ...WS_NUM, textAlign: "right", color: T.sub }}>{fr2(s.odoo)}</span>
                <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 800, color: frGapColor(s.gap) }}>{frSigned(s.gap)}</span>
                <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 800, color: Math.abs(s.price) >= 0.5 ? T.err : FR_P.ghost }}>{frSigned(s.price)}</span>
                <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 800, color: s.issues ? T.err : T.ok }}>{s.issues}</span>
              </div>);
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: T.sub }}>
            {sup && <b style={{ color: T.ink }}>{sup} · </b>}{rows.length} PO
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input value={qq} onChange={e => { setQq(e.target.value); setLimit(25); }}
            placeholder={lang === "fr" ? "🔎 PO ou facture (ex. 5806)" : "🔎 PO or invoice (e.g. 5806)"}
            style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 10px", fontSize: 12.5,
              width: 200, background: T.card, color: T.ink }} />
          <WSSeg value={mode} onChange={v => { setMode(v); setLimit(25); }}
            opts={[{ v: "issues", l: `${L.showIssues} (${scoped.filter(r => FR_FIX.has(r.status)).length})` },
                   { v: "check", l: `${L.showCheck} (${scoped.filter(r => FR_CHECK.has(r.status)).length})` },
                   { v: "all", l: `${L.showAll} (${scoped.length})` }]} />
          </div>
        </div>
        {rows.length === 0 ? <div style={{ fontSize: 12.5, color: T.ok, padding: "8px 0" }}>✓ {L.allGood}</div> : <>
          <div style={{ display: "grid", gridTemplateColumns: cols, gap: 10, padding: "8px 4px", borderBottom: `2px solid ${T.line}`,
            fontSize: 10, fontWeight: 800, color: T.hint, textTransform: "uppercase", letterSpacing: ".06em" }}>
            <span>{L.po}</span><span>{L.invoices}</span>
            <span style={{ textAlign: "right" }}>{L.inv}</span><span style={{ textAlign: "right" }}>{L.odoo}</span>
            <span style={{ textAlign: "right" }}>{L.gap}</span><span style={{ textAlign: "right" }}>{L.status}</span>
          </div>
          {rows.slice(0, limit).map(r => {
            const c = FR_ST_C[r.status] || T.sub;
            const fill = r.invoiced ? Math.min((r.odoo / r.invoiced) * 100, 100) : 0;
            const isOpen = openPo === r.po;
            return (
              <div key={r.po} style={{ padding: "9px 4px", borderBottom: `1px solid ${T.line}` }}>
                <div onClick={() => setOpenPo(isOpen ? null : r.po)} style={{ display: "grid", gridTemplateColumns: cols, gap: 10,
                  alignItems: "center", fontSize: 12.5, cursor: "pointer" }}>
                  <span style={{ fontWeight: 800, color: r.po_id ? W.deep : T.err }}>
                    <span style={{ color: FR_P.acc, marginRight: 5 }}>{isOpen ? "▾" : "▸"}</span>{r.po}
                    {r.po_id && <a href={frPoLink(r.po_id)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      title={L.open} style={{ color: W.deep, textDecoration: "none", marginLeft: 4 }}>↗</a>}
                  </span>
                  <span style={{ color: T.sub, fontSize: 11.5, minWidth: 0 }}>
                    <b style={{ color: T.ink }}>{r.supplier}</b>
                    {(r.absorbed || []).length > 0 && <span title={L.sameBlTip} style={{ marginLeft: 6, fontSize: 10,
                      fontWeight: 800, color: "#0369a1", background: "#e0f2fe", border: "1px solid #7dd3fc",
                      borderRadius: 999, padding: "0 6px" }}>+ {r.absorbed.join(", ")} · {L.sameBl}</span>}
                    {r.inferred && <span title="Le BL ne porte pas de n° de PO : rattaché à la PO du même fournisseur, même date de livraison, mêmes articles"
                      style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#7c3aed", background: "#efedff",
                      border: "1px solid #c4b5fd", borderRadius: 999, padding: "0 6px" }}>PO déduite · date + contenu</span>}
                    {(r.chef || r.place) && <span style={{ color: T.hint }}> · {[r.chef, r.place].filter(Boolean).join(" · ")}</span>}
                    <br />
                    {(r.invoices || []).map((iv, k) => (
                      <span key={k} style={{ marginRight: 10, whiteSpace: "nowrap", opacity: iv.in_window ? 1 : .6 }}>
                        <b style={{ color: T.ink }}>{iv.number}</b> <span style={{ color: T.hint }}>{iv.date} · {fmtK(iv.total)}</span>
                      </span>))}
                    {!["ok", "minor"].includes(r.status) && <><br /><FrDecompChips dc={r.decomp} L={L} /></>}
                  </span>
                  <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 800, color: T.ink }}>{fr2(r.invoiced)}</span>
                  <span style={{ ...WS_NUM, textAlign: "right", color: T.sub }}>{r.po_id ? fr2(r.odoo) : "—"}</span>
                  <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 800, color: frGapColor(r.gap) }}>{frSigned(r.gap)}</span>
                  <span style={{ textAlign: "right" }}><Chip label={L.st[r.status] || r.status} color={c} /></span>
                </div>
                <div style={{ height: 4, background: T.grid || "#eee", borderRadius: 3, marginTop: 6, overflow: "hidden", display: "flex" }}>
                  <div style={{ width: fill + "%", background: c, borderRadius: 3 }} />
                  <div style={{ flex: 1, background: `repeating-linear-gradient(45deg,${T.err}22,${T.err}22 4px,transparent 4px,transparent 8px)` }} />
                </div>
                {isOpen && <FoodReconcileDetail po={r.po} L={L} onApplied={() => setReload(x => x + 1)} />}
              </div>);
          })}
          {rows.length > limit && (
            <button onClick={() => setLimit(l => l + 50)} style={{ marginTop: 8, border: `1px solid ${T.line}`, background: T.card,
              borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: T.sub, cursor: "pointer" }}>
              {L.more} ({rows.length - limit})</button>)}
        </>}

        {noPo.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 800, color: T.hint,
              textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 5 }}>
              <span>{L.noPo} ({noPo.length}) <span style={{ textTransform: "none", fontWeight: 500, letterSpacing: 0 }}>· {L.noPoSub}</span></span>
              <span style={{ ...WS_NUM, color: T.ink }}>{fr2(noPo.reduce((a, o) => a + o.total, 0))}</span>
            </div>
            {noPo.slice(0, 30).map((o, i) => (
              <div key={o.id} style={{ display: "grid", gridTemplateColumns: "150px 1fr 96px 96px", gap: 8, fontSize: 12, padding: "4px 6px",
                background: i % 2 ? FR_P.soft2 : "transparent" }}>
                <b style={{ color: FR_P.acc }}>{o.number}</b>
                <span style={{ color: T.sub }}>{o.supplier}{o.po_reference && <span style={{ color: T.hint }}> · réf. « {o.po_reference} »</span>}</span>
                <span style={{ ...WS_NUM, textAlign: "right", color: T.hint }}>{o.date}</span>
                <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 700 }}>{fr2(o.total)}</span>
              </div>))}
          </div>)}

        {(late.length > 0 || recent.length > 0) && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 800, color: T.hint,
              textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 5 }}>
              <span>{L.poNoInv} ({late.length}) <span style={{ textTransform: "none", fontWeight: 500, letterSpacing: 0 }}>· {L.poNoInvSub}</span></span>
              <span style={{ ...WS_NUM, color: T.ink }}>{fr2(d.po_without_invoice_total)}</span>
            </div>
            {late.slice(0, 40).map((p, i) => (
              <div key={p.po} style={{ display: "grid", gridTemplateColumns: "110px 1fr 96px 96px", gap: 8, fontSize: 12, padding: "4px 6px",
                background: i % 2 ? FR_P.soft2 : "transparent" }}>
                <a href={frPoLink(p.po_id)} target="_blank" rel="noreferrer" style={{ fontWeight: 800, color: W.deep, textDecoration: "none" }}>{p.po} ↗</a>
                <span style={{ color: T.sub }}>{p.partner}{(p.chef || p.place) && <span style={{ color: T.hint }}> · {[p.chef, p.place].filter(Boolean).join(" · ")}</span>}</span>
                <span style={{ ...WS_NUM, textAlign: "right", color: T.hint }}>{p.planned}</span>
                <span style={{ ...WS_NUM, textAlign: "right", fontWeight: 700 }}>{fr2(p.odoo)}</span>
              </div>))}
            {recent.length > 0 && <div style={{ fontSize: 11, color: T.hint, marginTop: 5 }}>
              + {recent.length} {L.poNoInvRecent} · {fr2(recent.reduce((a, p) => a + p.odoo, 0))}</div>}
          </div>)}
        {(d.po_unsent || []).length > 0 && <div style={{ fontSize: 11, color: T.hint, marginTop: 6 }}
          title={(d.po_unsent || []).map(p => `${p.po} · ${p.partner} · ${p.chef || ""} · ${p.planned} · ${fr2(p.odoo)}`).join("\n")}>
          + {d.po_unsent.length} {lang === "fr" ? "PO jamais envoyées au fournisseur (demandes regroupées dans une commande globale — non comptées)"
                                                 : "POs never sent to the supplier (requests grouped into a bulk order — not counted)"} · {fr2(d.po_unsent_total || 0)}
        </div>}
      </>}
      <div style={{ fontSize: 11, color: T.hint, marginTop: 12, fontStyle: "italic" }}>{L.hint}</div>
    </WSFold>
  );
}


function FoodTab() {
  const [data, setData]  = useState(null);
  const [loading, setL]  = useState(true);
  const [err, setErr]    = useState("");
  const [lang, setLang]  = useState("en");
  const [tf, setTf]      = useState("month");
  const [sup, setSup]    = useState("all");
  const [openInv, setOpen] = useState(null);
  const [openSup, setOpenSup] = useState(null);
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("");   // "" = mois le plus récent ; "__all__" = année
  const [repBusy, setRepBusy] = useState(false);
  // Portée du rapport direction, indépendante du filtre de la page :
  // "all" = mai → aujourd'hui ; sinon une clé de mois "2026-07".
  const [repScope, setRepScope] = useState("all");
  // Palette froide et claire pour le drill-down (au lieu du beige) + accents.
  const F = { soft: "#eef2f8", soft2: "#f7f9fc", card: "#ffffff", line: "#e3e8f0",
              ink: "#111827", sub: "#4b5563", hint: "#9aa4b2", ghost: "#c3cad6",
              acc: "#0ea5b7", accSoft: "rgba(14,165,183,.10)", accEdge: "#0ea5b7",
              warn: "#b45309", warnSoft: "#fef3c7", warnEdge: "#fcd34d" };
  // Facture « fourre-tout » : une seule ligne = récap Walker (SAFE total bulletin),
  // pas de détail article. On la signale à l'utilisateur.
  const isSummary = (o) => (o.lines || []).length === 1
    && /total bulletin|safe total|safe tot/i.test(((o.lines[0] || {}).name) || "");
  const badgeSum = { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 800,
    color: F.warn, background: F.warnSoft, border: `1px solid ${F.warnEdge}`, borderRadius: 999, padding: "1px 7px", marginLeft: 6, whiteSpace: "nowrap" };

  // ── Rapport achats direction (demande Nelson / David du 01.09.2026) ──────
  //  Une ligne par article : unité, quantité totale, prix unitaire actuel,
  //  dépense, fréquence, lieux — puis une ligne par fournisseur quand
  //  l'article vient de plusieurs sources, avec le prix payé à chacun.
  //  Source : prix_caa (factures réellement livrées), pas les PO Odoo.
  const exportDirection = async (topN) => {
    setRepBusy(true);
    try {
      // Le rapport couvre TOUTE la période chargée, indépendamment du
      // sélecteur de mois de la page : Nelson demande l'historique complet,
      // pas le mois affiché. Les scans démarrent en mai 2026.
      // Départ au 1er mai 2026 : avant cette date les scans étaient faits par
      // Sanford et leur fiabilité n'est pas établie. Le pipeline automatisé
      // couvre mai → aujourd'hui.
      let since = "2026-05-01", until = "2030-12-31";
      if (repScope !== "all") {
        const [yy, mm] = repScope.split("-").map(Number);
        since = `${repScope}-01`;
        until = new Date(yy, mm, 0).toISOString().slice(0, 10);
      }
      const r = await fetch(`${API}/dashboard/purchase_report`
                            + `?since=${since}&until=${until}&top=${topN || 0}`);
      const j = await r.json();
      if (j.error) { alert(j.error); return; }
      if (!Array.isArray(j.items)) {
        // 404 : l'endpoint /dashboard/purchase_report n'est pas encore
        // déployé sur le 87 (api.py à copier + systemctl restart foodops-api).
        alert(lang === "fr"
          ? "Le rapport n'est pas disponible : l'API du serveur n'a pas encore "
            + "l'endpoint /dashboard/purchase_report. Déployer api.py puis "
            + "redémarrer foodops-api."
          : "Report unavailable: the server API does not yet expose "
            + "/dashboard/purchase_report. Deploy api.py and restart foodops-api.");
        return;
      }
      if (!j.items.length) {
        alert(lang === "fr" ? "Aucun achat sur la période sélectionnée."
                            : "No purchases in the selected period.");
        return;
      }

      const heads = ["Rang","Article","Unité","Quantité totale","Prix unitaire actuel",
                     "Prix moyen payé","Dépense CHF","Achats","Achats/semaine",
                     "Premier achat","Dernier achat","Fournisseurs",
                     "Fournisseur (détail)","Code","Qté fournisseur",
                     "Prix payé (dernier)","Prix min","Prix max","Dépense fournisseur",
                     "Lieux de livraison"];
      const rowsX = [];
      j.items.forEach((it, idx) => {
        const locs = (it.locations || []).map(l => `${l[0]} (${l[1]})`).join(", ");
        // ligne consolidée
        rowsX.push([idx + 1, it.name, it.uom, it.qty, it.current_price ?? "",
                    it.avg_price ?? "", it.spend, it.n, it.per_week,
                    it.first, it.last, it.n_suppliers,
                    it.n_suppliers === 1 ? (it.suppliers[0] || {}).name || "" : "— voir ci-dessous —",
                    it.n_suppliers === 1 ? (it.suppliers[0] || {}).code || "" : "",
                    it.n_suppliers === 1 ? (it.suppliers[0] || {}).qty ?? "" : "",
                    it.n_suppliers === 1 ? (it.suppliers[0] || {}).last_price ?? "" : "",
                    it.n_suppliers === 1 ? (it.suppliers[0] || {}).min ?? "" : "",
                    it.n_suppliers === 1 ? (it.suppliers[0] || {}).max ?? "" : "",
                    it.n_suppliers === 1 ? (it.suppliers[0] || {}).spend ?? "" : "",
                    locs]);
        // une ligne par fournisseur si l'article est multi-sources
        if (it.n_suppliers > 1) it.suppliers.forEach(sp => {
          rowsX.push(["", `   ↳ ${it.name}`, it.uom, "", "", "", "", "", "", "", "", "",
                      sp.name, sp.code, sp.qty, sp.last_price ?? "",
                      sp.min ?? "", sp.max ?? "", sp.spend, ""]);
        });
      });

      const today = new Date().toISOString().slice(0,10);
      const tag = repScope === "all" ? "mai-a-ce-jour" : repScope;
      dlXls(`rapport_achats_direction_${tag}_${today}`,
            heads, rowsX,
            [{label:"Période couverte",
              val: repScope === "all"
                 ? `${j.since} → ${today}  (toute la période fiable)`
                 : `${monthLabel(repScope)}  (${j.since} → ${j.until})`},
             {label:"Références", val:`${j.n_items} articles · dépense totale CHF ${j.total_spend.toLocaleString("fr-CH")}`},
             {label:topN ? `Sélection` : "Périmètre",
              val:topN ? `Top ${topN} par dépense — ${j.top_share} % du total`
                       : "Toutes les références (100 %)"},
             {label:"Source des données",
              val:"Factures et bulletins de livraison fournisseurs, scannés et réconciliés au centime"},
             {label:"Méthode",
              val:"Prix réellement payé, remises et corrections comprises. Les bons de commande ne sont pas utilisés."},
             {label:"Consolidation",
              val:"Regroupement par libellé produit ; chaque fournisseur utilisant son propre système de codes"},
             {label:"Lecture",
              val:"Une ligne par article. Les articles fournis par plusieurs sources sont détaillés en dessous, en italique, avec le prix payé à chacun."}],
            3,
            {title: "Culinary Arts Academy Switzerland — Rapport achats",
             subtitle: `Établi le ${today} · FX1 Labs · source : factures fournisseurs réconciliées`,
             accent: "#1f3b5b", zebra: true, groupCol: 0});
    } catch (e) {
      alert(String(e));
    } finally {
      setRepBusy(false);
    }
  };

  useEffect(() => {
    setL(true); setErr("");
    fetch(`${API}/dashboard/food_invoices?since=2026-01-01`)
      .then(r => r.json())
      .then(d => { if (d.error && !d.totals) setErr(d.error); else setData(d); })
      .catch(e => setErr(String(e)))
      .finally(() => setL(false));
  }, []);

  const L = FOOD_L[lang];
  if (loading) return <Card><div style={{ color: T.hint, fontSize: 13, padding: "8px 0" }}>Chargement / Loading…</div></Card>;
  if (err)     return <Card><div style={{ color: T.err, fontSize: 13 }}>Erreur : {err}</div></Card>;
  if (!data || !data.totals || !data.totals.invoices)
    return <Card><div style={{ color: T.hint, fontSize: 13 }}>{L.none}</div></Card>;

  const STUD      = data.students || 96;
  const supName   = sup === "all" ? null : sup;
  const hasQ      = q.trim() !== "";
  const monthly   = data.monthly || [];
  const weekly    = data.weekly || [];

  // ── périmètre MOIS ────────────────────────────────────────────────────────
  const MLBL = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  const monthLabel = (mk) => { const p = (mk || "").split("-"); return (p.length === 2) ? `${MLBL[(+p[1]) - 1]} ${p[0]}` : mk; };
  const monthKeys = monthly.map(m => m.m);                              // ex. ["2026-03", … "2026-07"]
  const curMonth = month || (monthKeys.length ? monthKeys[monthKeys.length - 1] : "");  // défaut = le plus récent
  const allYear = curMonth === "__all__";
  // en recherche, on ignore le filtre mois (on cherche sur toute l'année)
  const inScope = (dateStr) => hasQ || allYear || !curMonth || (dateStr || "").slice(0, 7) === curMonth;

  // factures du périmètre (mois + fournisseur si filtré + recherche n° facture / PO)
  const qlInv = q.trim().toLowerCase();
  const invoices = (data.invoices || []).filter(o => inScope(o.date)
    && (!supName || o.supplier === supName)
    && (!hasQ || (o.number || "").toLowerCase().includes(qlInv)
              || (o.po_reference || "").toLowerCase().includes(qlInv)));

  // agrégat fournisseurs sur le mois (dérivé des factures)
  const supAgg = {};
  (data.invoices || []).filter(o => inScope(o.date)).forEach(o => {
    const a = supAgg[o.supplier] || (supAgg[o.supplier] = { name: o.supplier, spend: 0, invoices: 0, lines: 0 });
    a.spend += o.total || 0; a.invoices += 1; a.lines += o.n_lines || 0;
  });
  const allSup = Object.values(supAgg).map(s => ({ ...s, avg_invoice: s.invoices ? s.spend / s.invoices : 0 })).sort((a, b) => b.spend - a.spend);
  const totalScope = allSup.reduce((a, s) => a + s.spend, 0);
  allSup.forEach(s => s.share = totalScope ? Math.round(s.spend / totalScope * 1000) / 10 : 0);

  // Dérive de prix recalculée sur les lignes du périmètre — UNIQUEMENT si prix payé
  // et prix catalogue sont du MÊME ordre de grandeur (ratio 0.5–2). Sinon c'est une
  // incohérence d'unité (ex. prix/kg 1.74 vs prix/sac 43.50), pas une vraie dérive.
  const DRIFT_PCT = 0.05, RATIO_LO = 0.5, RATIO_HI = 2;
  const cmpUnit = (up, cat) => (cat != null && up != null && cat > 0 && (up / cat) >= RATIO_LO && (up / cat) <= RATIO_HI);
  const drift = [];
  invoices.forEach(o => (o.lines || []).forEach(ln => {
    if (!cmpUnit(ln.unit_price, ln.catalog_price)) return;
    const rel = (ln.unit_price - ln.catalog_price) / ln.catalog_price;
    if (Math.abs(rel) >= DRIFT_PCT) {
      drift.push({ supplier: o.supplier, name: ln.name, date: o.date, code: ln.code,
        unit_price: ln.unit_price, catalog_price: ln.catalog_price,
        delta: Math.round((ln.unit_price - ln.catalog_price) * 100) / 100,
        delta_pct: Math.round(rel * 1000) / 10, line_total: ln.line_total });
    }
  }));
  drift.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  // produits agrégés depuis les lignes des factures du périmètre
  const prodAgg = {};
  invoices.forEach(o => (o.lines || []).forEach(ln => {
    const key = (ln.code || "") + "|" + (ln.name || "");
    const p = prodAgg[key] || (prodAgg[key] = { code: ln.code || "", name: ln.name || "", supplier: o.supplier, unit: ln.uom || "", qty: 0, spend: 0, invoices: 0, prices: [] });
    p.qty += ln.qty || 0; p.spend += ln.line_total || 0; p.invoices += 1;
    if (ln.unit_price > 0) p.prices.push(ln.unit_price);
  }));
  const products = Object.values(prodAgg).map(p => {
    const mn = p.prices.length ? Math.min(...p.prices) : 0, mx = p.prices.length ? Math.max(...p.prices) : 0;
    return { ...p, min: mn, max: mx, avg: p.prices.length ? p.prices.reduce((a, b) => a + b, 0) / p.prices.length : 0, variance: mn ? Math.round((mx - mn) / mn * 1000) / 1000 : 0 };
  }).sort((a, b) => b.spend - a.spend);

  // KPI du périmètre
  let scopeSpend = 0, totLines = 0, totUnits = 0;
  invoices.forEach(o => { scopeSpend += o.total || 0; totLines += o.n_lines || 0; (o.lines || []).forEach(ln => totUnits += ln.qty || 0); });
  const view = {
    spend: scopeSpend, invoices: invoices.length, lines: totLines, units: totUnits,
    suppliers: supName ? 1 : allSup.length, avg_invoice: invoices.length ? scopeSpend / invoices.length : 0,
    drift_alerts: drift.length,
  };

  const FACC = "#22b8cf";
  // semaines RECALCULÉES sur le périmètre (mois + fournisseur), lundi comme clé
  const weekAgg = {};
  invoices.forEach(o => {
    if (!o.date) return;
    const d = new Date(o.date + "T00:00:00");
    if (isNaN(d)) return;
    const dow = (d.getDay() + 6) % 7;                 // 0 = lundi
    const monday = new Date(d); monday.setDate(d.getDate() - dow);
    const key = monday.getFullYear() + "-" + ("0" + (monday.getMonth() + 1)).slice(-2) + "-" + ("0" + monday.getDate()).slice(-2);
    const lbl = ("0" + monday.getDate()).slice(-2) + "." + ("0" + (monday.getMonth() + 1)).slice(-2);
    (weekAgg[key] = weekAgg[key] || { key, label: lbl, spend: 0 }).spend += o.total || 0;
  });
  const weekSeries = Object.values(weekAgg).sort((a, b) => (a.key < b.key ? -1 : 1)).map(w => ({ label: w.label, value: w.spend, color: FACC }));
  // graphe MOIS = vue année (contexte, barre du mois courant en ambre) ; SEMAINE = périmètre
  const series = (tf === "month"
    ? monthly.map(m => ({ label: m.label, value: supName ? (m.by_supplier?.[supName] || 0) : m.spend, color: (m.m === curMonth && !allYear && !hasQ) ? "#f59e0b" : FACC }))
    : weekSeries);

  const supMax = Math.max(1, ...allSup.map(s => s.spend));
  const supRows = allSup.slice(0, 10);

  const Q = data.quarters || {};
  const curQ = Q.current, prevQ = Q.previous;
  const curSpendQ = curQ ? (Q[curQ]?.spend || 0) : 0;
  const prevSpendQ = prevQ ? (Q[prevQ]?.spend || 0) : 0;
  const delta = prevSpendQ ? ((curSpendQ - prevSpendQ) / prevSpendQ) * 100 : 0;

  const csvEsc = (v) => {
    const s = (v == null ? "" : String(v));
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csvDownload = (filename, rows) => {
    const body = rows.map(r => r.map(csvEsc).join(";")).join("\r\n");
    const blob = new Blob(["﻿" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  const stamp = new Date().toISOString().slice(0, 10);
  const exportProducts = () => {
    const rows = [["code", "article", "fournisseur", "unité", "qté", "dépense_HT", "prix_moy", "min", "max", "variance"]];
    products.forEach(p => rows.push([p.code, p.name, p.supplier, p.unit, p.qty, p.spend, p.avg, p.min, p.max, p.variance]));
    csvDownload(`food_products_${stamp}.csv`, rows);
  };
  const exportInvoices = () => {
    const rows = [["facture", "fournisseur", "date", "po", "total_HT", "n_lignes", "statut", "fichier"]];
    invoices.forEach(o => rows.push([o.number, o.supplier, o.date, o.po_reference, o.total, o.n_lines, o.status, o.source_file]));
    csvDownload(`food_invoices_${stamp}.csv`, rows);
  };

  const supSelStyle = {
    background: W.panel, color: W.txt, border: `1px solid ${W.edge}`,
    borderRadius: 9, padding: "6px 10px", fontSize: 12, fontWeight: 700,
  };

  return (
    <div>
      <FoodBudgetBanner monthly={monthly} invoices={data.invoices || []} updated={data.updated || ""} />
      <div style={{
        background: "linear-gradient(150deg, #1b2740 0%, #22314f 55%, #283a5c 100%)",
        border: `1px solid ${W.edge}`, borderRadius: 18, padding: "18px 20px 20px",
        marginBottom: 16, boxShadow: "0 18px 40px -24px rgba(0,0,0,.55)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, flexWrap: "wrap" }}>
            <span style={{ width: 9, height: 9, borderRadius: 9, background: W.amber, boxShadow: `0 0 12px ${W.amber}` }} />
            <span style={{ fontSize: 12.5, fontWeight: 900, letterSpacing: ".16em", textTransform: "uppercase", color: W.txt }}>Food · Invoices</span>
            <span style={{ fontSize: 11.5, color: W.ghost }}>{allYear ? `${L.tagline} ${data.since}` : monthLabel(curMonth)} · {L.htnote}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <select value={curMonth} onChange={e => setMonth(e.target.value)} className="no-print" style={supSelStyle}>
              <option value="__all__">Année entière</option>
              {monthKeys.slice().reverse().map(mk => <option key={mk} value={mk}>{monthLabel(mk)}</option>)}
            </select>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔎 N° facture / PO" className="no-print"
              style={{ background: W.panel, color: W.txt, border: `1px solid ${W.edge}`, borderRadius: 9, padding: "6px 10px", fontSize: 12, fontWeight: 600, width: 168 }} />
            <select value={sup} onChange={e => setSup(e.target.value)} className="no-print" style={supSelStyle}>
              <option value="all">{L.all}</option>
              {allSup.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <button onClick={exportProducts} className="no-print" style={WS_CSV_BTN}>⬇ {L.csvProd}</button>
            <button onClick={exportInvoices} className="no-print" style={WS_CSV_BTN}>⬇ {L.csvInv}</button>
            <WSSeg dark value={lang} onChange={setLang} opts={[{ v: "fr", l: "FR" }, { v: "en", l: "EN" }]} />
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 9,
          background: "rgba(14,165,183,.10)", border: `1px solid rgba(14,165,183,.35)`,
          borderRadius: 11, padding: "9px 13px", marginBottom: 16 }}>
          <span style={{ fontSize: 13, lineHeight: "16px", color: W.teal }}>◆</span>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: W.txt, lineHeight: 1.45 }}>{L.truth}</span>
        </div>

        {/* Rapport direction — encart clair, volontairement contrasté avec le
            reste de la section pour que Nelson le trouve sans explication. */}
        <div className="no-print" style={{
          background: "#eafff5", border: "1px solid #7fe3bd", borderRadius: 12,
          padding: "14px 16px", marginBottom: 16,
          display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 340px", minWidth: 280 }}>
            <div style={{ fontSize: 13.5, fontWeight: 900, color: "#046b48",
              letterSpacing: "-.01em", marginBottom: 4 }}>
              ⬇ {L.repTitle}
            </div>
            <div style={{ fontSize: 11.8, color: "#1f6b52", lineHeight: 1.5 }}>
              {L.repDesc}
            </div>
            <div style={{ fontSize: 10.8, color: "#4f8a75", marginTop: 5 }}>
              {L.repNote}
              <b style={{ color:"#046b48" }}>
                {"  "}— {repScope === "all"
                  ? (lang === "fr" ? "mai 2026" : "May 2026")
                    + (monthKeys.length ? ` → ${monthLabel(monthKeys[monthKeys.length-1])}` : "")
                  : monthLabel(repScope)}
              </b>
            </div>
          </div>
          <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
            <select value={repScope} onChange={e => setRepScope(e.target.value)}
              style={{ background:"#fff", color:"#046b48", border:"1px solid #9fdcc4",
                borderRadius:9, padding:"10px 12px", fontSize:12.5, fontWeight:700,
                cursor:"pointer" }}>
              <option value="all">{L.repScopeAll}</option>
              {monthKeys.filter(mk => mk >= "2026-05").slice().reverse()
                .map(mk => <option key={mk} value={mk}>{monthLabel(mk)}</option>)}
            </select>
            <button onClick={() => exportDirection(15)} disabled={repBusy}
              style={{ background: "#0e9f6e", color: "#fff", border: "none",
                borderRadius: 9, padding: "10px 16px", fontSize: 12.5,
                fontWeight: 800, cursor: repBusy ? "wait" : "pointer",
                opacity: repBusy ? .55 : 1, whiteSpace: "nowrap" }}>
              {repBusy ? "…" : L.repTop}
            </button>
            <button onClick={() => exportDirection(0)} disabled={repBusy}
              style={{ background: "#fff", color: "#046b48",
                border: "1px solid #9fdcc4", borderRadius: 9,
                padding: "10px 16px", fontSize: 12.5, fontWeight: 800,
                cursor: repBusy ? "wait" : "pointer",
                opacity: repBusy ? .55 : 1, whiteSpace: "nowrap" }}>
              {repBusy ? "…" : L.repAll}
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gap: 11, gridTemplateColumns: "repeat(auto-fit,minmax(158px,1fr))", marginBottom: 16 }}>
          <WSTile label={L.spend}   value={fmtK(view.spend)}            sub={`${view.invoices} ${L.orders}`}         color={W.teal} />
          <WSTile label={L.invoices} value={fmtN(view.invoices)}        sub={`${view.suppliers} ${L.suppliers}`}     color={W.blue} />
          <WSTile label={L.units}   value={fmtN(view.units)}            sub={`${view.lines} ${L.lines}`}             color={W.viol} />
          <WSTile label={L.avgInv}  value={fmtK(view.avg_invoice)}      sub={L.perInv}                               color={W.pink} />
          {prevSpendQ > 0
            ? <WSTile label={`${curQ?.toUpperCase()} vs ${prevQ?.toUpperCase()}`} value={`${delta >= 0 ? "+" : ""}${delta.toFixed(0)} %`}
                      sub={`${fmtK(prevSpendQ)} → ${fmtK(curSpendQ)}`} color={delta >= 0 ? W.amber : W.green} />
            : <WSTile label={L.perStud} value={fmtK(view.spend / STUD)} sub={`${L.cohort} ${STUD}`} color={W.amber} />}
          <WSTile label={L.drift}   value={fmtN(view.drift_alerts)}     sub={L.driftSub}
                  color={view.drift_alerts > 0 ? W.red : W.green} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.1fr)", gap: 12 }}>
          <div style={{ background: W.panel, border: `1px solid ${W.edge}`, borderRadius: 13, padding: "14px 16px" }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".13em", textTransform: "uppercase", color: W.dim, marginBottom: 10 }}>{L.bySup}</div>
            {supRows.map((s, i) => (
              <div key={s.name} style={{ marginBottom: 9, cursor: "pointer" }} onClick={() => setSup(sup === s.name ? "all" : s.name)}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: sup === s.name ? W.txt : W.dim, marginBottom: 3 }}>
                  <span style={{ fontWeight: 700 }}>{s.name}</span>
                  <span style={{ ...WS_NUM }}>{fmtK(s.spend)} · {s.share}%</span>
                </div>
                <div style={{ height: 7, background: "#0b1526", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ width: `${(s.spend / supMax) * 100}%`, height: "100%", background: WS_PALETTE[i % WS_PALETTE.length] }} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ background: W.panel, border: `1px solid ${W.edge}`, borderRadius: 13, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".13em", textTransform: "uppercase", color: W.dim }}>
                {tf === "month" ? L.evo : L.evoWeek}{supName ? ` · ${supName}` : ""}
              </div>
              <WSSeg dark value={tf} onChange={setTf} opts={[{ v: "month", l: L.month }, { v: "week", l: L.week }]} />
            </div>
            <WSBars data={series} height={146} />
          </div>
        </div>
      </div>

      <FoodReconcile lang={lang} supName={supName} q={q}
        since={(allYear || !curMonth) ? "2026-05-01" : `${curMonth}-01`}
        until={(allYear || !curMonth) ? "" : frMonthEnd(curMonth)} />

      <WSFold defaultOpen color={W.teal} title={L.supTitle} sub={L.supSub}
        badges={[{ t: `${allSup.length} ${L.suppliers}`, c: W.deep }, { t: fmtK(totalScope), c: T.sub }]}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 90px 90px 110px", fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: W.dim, padding: "6px 0", borderBottom: `1px solid ${W.edge}` }}>
          <div>{L.suppliers}</div><div style={{ textAlign: "right" }}>{L.spend}</div>
          <div style={{ textAlign: "right" }}>{L.orders}</div><div style={{ textAlign: "right" }}>{L.lines}</div>
          <div style={{ textAlign: "right" }}>{L.avgInv}</div>
        </div>
        {allSup.map(s => {
          const hasQ = q.trim() !== "";
          const ql = q.trim().toLowerCase();
          const matchQ = (o) => !hasQ || (o.number || "").toLowerCase().includes(ql) || (o.po_reference || "").toLowerCase().includes(ql);
          const supInvoices = (data.invoices || []).filter(o => o.supplier === s.name && inScope(o.date) && matchQ(o));
          if (hasQ && supInvoices.length === 0) return null;
          const isOpenSup = hasQ ? true : (openSup === s.name);
          const pill = { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: F.sub, background: F.soft2, border: `1px solid ${F.line}`, borderRadius: 999, padding: "3px 9px", whiteSpace: "nowrap" };
          return (
          <div key={s.name}>
            <div onClick={() => { setOpenSup(isOpenSup ? null : s.name); setOpen(null); }}
              style={{ display: "grid", gridTemplateColumns: "1fr 120px 90px 90px 110px", alignItems: "center", fontSize: 13, padding: "10px 12px", borderBottom: `1px solid ${F.line}`, cursor: "pointer",
                color: F.ink, background: isOpenSup ? F.accSoft : "transparent",
                borderLeft: `3px solid ${isOpenSup ? F.accEdge : "transparent"}` }}>
              <div style={{ fontWeight: 700 }}>
                <span style={{ color: F.acc, fontWeight: 800, marginRight: 6 }}>{isOpenSup ? "▾" : "▸"}</span>
                {s.name} <span style={{ color: F.hint, fontWeight: 500 }}>· {s.share}%</span>
              </div>
              <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 800, color: F.ink }}>{fmtK(s.spend)}</div>
              <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{s.invoices}</div>
              <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{s.lines}</div>
              <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{fmtK(s.avg_invoice)}</div>
            </div>
            {isOpenSup && (
              <div style={{ background: F.soft, borderBottom: `1px solid ${F.line}`, padding: "10px 12px 12px 24px" }}>
                {supInvoices.length === 0
                  ? <div style={{ color: F.hint, fontSize: 12.5, padding: "6px 0" }}>—</div>
                  : supInvoices.map(o => {
                  const meta = [
                    o.chef && ["👨‍🍳", o.chef],
                    o.kitchen && ["📍", o.kitchen],
                    o.planned_date && ["🗓 prévu", o.planned_date],
                    o.week && ["Sem.", o.week],
                    o.term && ["🎓", o.term],
                    o.group && ["grp", o.group],
                    o.po_name && ["PO", o.po_name],
                    o.scanned_at && ["scan", o.scanned_at],
                  ].filter(Boolean);
                  return (
                  <div key={o.id} style={{ marginBottom: 6 }}>
                    <div onClick={() => setOpen(openInv === o.id ? null : o.id)}
                      style={{ display: "grid", gridTemplateColumns: "20px 128px 1fr 110px 100px 66px", alignItems: "center", gap: 4, fontSize: 12.5, padding: "9px 12px", borderRadius: 10, cursor: "pointer", color: F.ink,
                        background: openInv === o.id ? F.card : "transparent",
                        border: `1px solid ${openInv === o.id ? F.line : "transparent"}`,
                        boxShadow: openInv === o.id ? "0 1px 3px rgba(15,23,42,.08)" : "none" }}>
                      <div style={{ color: F.acc, fontWeight: 800 }}>{openInv === o.id ? "▾" : "▸"}</div>
                      <div style={{ fontWeight: 800, color: F.acc }}>{o.number}</div>
                      <div style={{ color: F.hint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.po_reference || ""}{isSummary(o) && <span style={badgeSum}>⚠ non détaillé</span>}</div>
                      <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700 }}>{fmtK(o.total)}</div>
                      <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{o.date}</div>
                      <div style={{ textAlign: "right", ...WS_NUM, color: F.hint }}>{o.n_lines} lig.</div>
                    </div>
                    {openInv === o.id && (
                      <div style={{ background: F.card, border: `1px solid ${F.line}`, borderRadius: 10, margin: "5px 0 9px", overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,.08)" }}>
                        {meta.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 14px", background: F.soft2, borderBottom: `1px solid ${F.line}` }}>
                            {meta.map((m, k) => (
                              <span key={k} style={pill}>
                                <span style={{ color: F.hint, fontWeight: 700 }}>{m[0]}</span>{m[1]}
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ display: "grid", gridTemplateColumns: "84px 1fr 70px 66px 82px 78px 74px", fontSize: 9.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: F.hint, padding: "9px 14px", background: F.soft, borderBottom: `1px solid ${F.line}` }}>
                          <div>Code</div><div>Article</div><div style={{ textAlign: "right" }}>Qté</div>
                          <div style={{ textAlign: "right" }}>PU</div><div style={{ textAlign: "right" }}>Montant</div>
                          <div style={{ textAlign: "right" }}>Cat.</div><div style={{ textAlign: "right" }}>Δ cat.</div>
                        </div>
                        {(o.lines || []).map((ln, j) => {
                          const cat = ln.catalog_price;
                          const d = cmpUnit(ln.unit_price, cat)
                            ? Math.round((ln.unit_price - cat) * 100) / 100
                            : null;   // unités non comparables (kg vs sac) → pas de Δ
                          return (
                          <div key={j} style={{ display: "grid", gridTemplateColumns: "84px 1fr 70px 66px 82px 78px 74px", alignItems: "center", fontSize: 12.5, padding: "8px 14px", color: F.ink,
                            background: j % 2 ? F.soft2 : F.card, borderTop: j ? `1px solid ${F.line}` : "none" }}>
                            <div style={{ color: F.ghost, ...WS_NUM }}>{ln.code}</div>
                            <div style={{ fontWeight: 600 }}>{ln.name}</div>
                            <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{ln.qty} {ln.uom}</div>
                            <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{ln.unit_price}</div>
                            <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700 }}>{ln.line_total}</div>
                            <div style={{ textAlign: "right", ...WS_NUM, color: cat == null ? F.ghost : F.sub }}>{cat == null ? "—" : cat}</div>
                            <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700, color: d == null ? F.ghost : (d > 0 ? T.err : T.ok) }}>
                              {d == null ? "—" : (d > 0 ? "+" : "") + d}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        );})}
      </WSFold>

      <WSFold color={drift.length ? W.red : W.green} title={L.driftTitle} sub={L.driftSub2}
        badges={[{ t: `${drift.length}`, c: drift.length ? T.err : T.ok }]}>
        {drift.length === 0
          ? <div style={{ color: F.hint, fontSize: 12.5, padding: "6px 0" }}>—</div>
          : (<>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 96px 96px 96px 84px 90px", fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: F.hint, padding: "8px 12px", borderBottom: `1px solid ${F.line}` }}>
              <div>Article · {L.suppliers}</div><div style={{ textAlign: "right" }}>Payé</div>
              <div style={{ textAlign: "right" }}>Catalogue</div><div style={{ textAlign: "right" }}>Δ</div>
              <div style={{ textAlign: "right" }}>Δ%</div><div style={{ textAlign: "right" }}>Ligne</div>
            </div>
            {drift.slice(0, 60).map((d, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 96px 96px 96px 84px 90px", alignItems: "center", fontSize: 12, padding: "8px 12px", color: F.ink, background: i % 2 ? F.soft2 : F.card, borderTop: i ? `1px solid ${F.line}` : "none" }}>
                <div><span style={{ fontWeight: 700 }}>{d.name}</span> <span style={{ color: F.hint }}>· {d.supplier} · {d.date}</span></div>
                <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700 }}>{d.unit_price}</div>
                <div style={{ textAlign: "right", ...WS_NUM, color: F.hint }}>{d.catalog_price}</div>
                <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700, color: d.delta > 0 ? T.err : T.ok }}>{d.delta > 0 ? "+" : ""}{d.delta}</div>
                <div style={{ textAlign: "right", ...WS_NUM, color: d.delta > 0 ? T.err : T.ok }}>{d.delta_pct > 0 ? "+" : ""}{d.delta_pct}%</div>
                <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{d.line_total}</div>
              </div>
            ))}
          </>)}
      </WSFold>

      <WSFold color={W.blue} title={L.invTitle} sub={L.invSub}
        badges={[{ t: `${invoices.length}`, c: W.deep }]}>
        <div style={{ display: "grid", gridTemplateColumns: "150px 1fr 100px 96px 70px", fontSize: 9.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: F.hint, padding: "8px 12px", borderBottom: `1px solid ${F.line}` }}>
          <div>Facture</div><div>{L.suppliers}</div><div style={{ textAlign: "right" }}>Total HT</div>
          <div style={{ textAlign: "right" }}>Date</div><div style={{ textAlign: "right" }}>Lignes</div>
        </div>
        {[...invoices].sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))).slice(0, 120).map((o, oi) => (
          <div key={o.id}>
            <div onClick={() => setOpen(openInv === o.id ? null : o.id)}
              style={{ display: "grid", gridTemplateColumns: "150px 1fr 100px 96px 70px", alignItems: "center", fontSize: 12.5, padding: "8px 12px", borderTop: oi ? `1px solid ${F.line}` : "none", cursor: "pointer", color: F.ink, background: openInv === o.id ? F.soft2 : "transparent" }}>
              <div style={{ fontWeight: 800, color: F.acc }}>{openInv === o.id ? "▾ " : "▸ "}{o.number}</div>
              <div>{o.supplier}{isSummary(o) && <span style={badgeSum}>⚠ non détaillé</span>}{o.po_reference ? <span style={{ color: F.hint }}> · {o.po_reference}</span> : null}</div>
              <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700 }}>{fmtK(o.total)}</div>
              <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{o.date}</div>
              <div style={{ textAlign: "right", ...WS_NUM, color: F.hint }}>{o.n_lines}</div>
            </div>
            {openInv === o.id && (
              <div style={{ background: F.soft, borderBottom: `1px solid ${F.line}`, padding: "6px 12px 10px" }}>
                {(o.lines || []).map((ln, j) => (
                  <div key={j} style={{ display: "grid", gridTemplateColumns: "84px 1fr 70px 70px 82px 78px", fontSize: 11.5, padding: "5px 0", color: F.ink, borderTop: j ? `1px solid ${F.line}` : "none" }}>
                    <div style={{ color: F.ghost, ...WS_NUM }}>{ln.code}</div>
                    <div style={{ fontWeight: 600 }}>{ln.name}</div>
                    <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{ln.qty} {ln.uom}</div>
                    <div style={{ textAlign: "right", ...WS_NUM, color: F.sub }}>{ln.unit_price}</div>
                    <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700 }}>{ln.line_total}</div>
                    <div style={{ textAlign: "right", ...WS_NUM, fontWeight: 700, color: ln.price_delta == null ? F.ghost : (ln.price_delta > 0 ? T.err : T.ok) }}>
                      {ln.price_delta == null ? "—" : (ln.price_delta > 0 ? "+" : "") + ln.price_delta}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </WSFold>

      {(data.warnings || []).length > 0 && (
        <div style={{ fontSize: 11.5, color: T.hint, marginTop: 12, fontStyle: "italic" }}>
          {(data.warnings || []).join(" · ")}
        </div>
      )}
      <div style={{ fontSize: 11, color: T.hint, marginTop: 8, fontStyle: "italic" }}>{L.hint}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  À COLLER DANS App.js (118) — onglet PROCUREMENT.
//  Réponse système au mail de Nelson : « per-item category · volume · unit price
//  · quality · supplier ». Deux étages :
//    1) FOOD global  → GET /dashboard/food_orders  (tout sauf webstar)
//    2) PROTEINS     → GET /dashboard/proteins      (seule catégorie qualifiée)
//  Même grammaire que WebstarTab (Card/Label/Bar/Chip/Stat/Seg).
//  Place ce bloc juste avant `const NAV = [`.
// ═══════════════════════════════════════════════════════════════════════════

const PROC_ACCENT = "#c2722e";               // ocre marque (accent page)
const PROC_SINCE  = "2026-04-01";            // début Q2 = période du dashboard (aligne les 2 endpoints)

// Périodes de la page Procurement. Trimestres académiques CAA :
// Q2 = avril→juin (clos), Q3 = juillet→septembre (clos), Q4 = octobre→décembre (en cours).
// Le trimestre en cours est « Live » (sans borne haute), les précédents « Closed ».
const PROC_PERIODS = [
  { id: "q4",  since: "2026-10-01", until: "",           fr: "Q4 Live · oct → déc",     en: "Q4 Live · Oct → Dec",     live: true  },
  { id: "q3",  since: "2026-07-01", until: "2026-09-30", fr: "Q3 Closed · juil → sept", en: "Q3 Closed · Jul → Sep",   live: false },
  { id: "q2",  since: "2026-04-01", until: "2026-06-30", fr: "Q2 Closed · avr → juin",  en: "Q2 Closed · Apr → Jun",   live: false },
  { id: "all", since: "2026-04-01", until: "",           fr: "Cumul · dès avril",       en: "Cumulative · from April", live: true  },
];
const PR_ACCENT   = "#a23b4e";               // garnet (marqueur section Proteins)
const PR_ODOO     = "https://caa2.fx1.tech";
const PR_SUB = { "Viande":"#a23b4e", "Poisson":"#2f6f9f", "Crustacé/Coquillage":"#c67b3e", "Végétale":"#5a8f4a" };
const PR_QUAL = { "Standard":T.info, "Premium":"#9a7b1f" };
const PR_QORDER = ["Standard","Premium"];
const PR_SORDER = ["Viande","Poisson","Crustacé/Coquillage","Végétale"];

const PROC_L = {
  fr: {
    title:"Food Procurement & Quality",
    sub:"Par article · catégorie · volume · prix unitaire · qualité · fournisseur — depuis le",
    g_title:"Approvisionnement food — global", g_sub:"Toutes catégories sauf non-food (Webstar) · PO envoyées",
    paTitle: "Analyse achats — direction", paSub: "top articles, prix comparés, fréquence de livraison",
    paPeriod: "Période", paAll: "Toute la période (dès avril)", paTopN: "Articles",
    paXls: "Export Excel", paPdf: "Imprimer / PDF", paLoading: "Calcul en cours…",
    paKpiShare: "Part du top", paKpiShareSub: "de la dépense totale",
    paKpiMulti: "Multi-fournisseurs", paKpiMultiSub: "articles achetés chez plusieurs",
    paKpiSaving: "Économie potentielle", paKpiSavingSub: "au meilleur prix constaté",
    paKpiFreq: "Intervalle moyen", paKpiFreqSub: "jours entre livraisons",
    paTopTitle: "Top articles par dépense", paTopSub: "consolidé tous fournisseurs · livré-facturé",
    paCmpTitle: "Comparaison fournisseurs", paCmpSub: "prix réellement payé à chacun",
    paDelTitle: "Fréquence et lieux de livraison", paDelSub: "par article et par cuisine",
    paProduct: "Article", paQty: "Qté", paSpend: "Dépense", paAvg: "Prix moyen",
    paLast: "Dernier", paDeliv: "Livr.", paInterval: "Interv.", paSup: "Fourn.",
    paBest: "Meilleur prix", paWorst: "Plus cher", paGap: "Écart", paSaving: "Gain",
    paPlaces: "Lieux", paNoMulti: "Aucun article acheté chez plusieurs fournisseurs sur la période.",
    scopeTitle:"Périmètre", scopeGross:"Achats bruts", scopeExcl:"dont non-food exclu",
    scopeFood:"Périmètre food retenu", scopeSent:"PO envoyées · période = date de livraison prévue",
    scopeAllPo:"tous les PO confirmés",
    lensTitle:"Périmètre achats — différent de Q4 Live.",
    lensBody:"Cette page mesure tout ce qui a été acheté chez un fournisseur (lentille achat) — achats stock inclus, ex. les réassorts de l'economat (Saviva, Pistor…). Les pages Live, elles, excluent ces achats stock et les comptent en DRY à la consommation, pour suivre ce qu'on consomme réellement chaque jour. Deux lentilles, deux totaux : c'est voulu, pas une incohérence.",
    kSpend:"Dépense PO HT", kSpendSub:"PO envoyées · hors Webstar · hors DRY",
    kItems:"Articles", kItemsSub:"commandés", kPos:"POs",
    kSup:"Fournisseurs", kSupSub:"actifs",
    kConc:"Concentration top-1", kConcSub:"du spend chez",
    kDrift:"Alertes dérive prix", kDriftSub:"articles > 10% (min↔max)",
    tblG:"Rapport achats global — par article", tblGsub:"Tous les articles food commandés",
    cArt:"Article", cVol:"Volume", cUnit:"Prix moyen", cSup:"Fournisseur", cAmt:"Montant HT", cQual:"Qualité",
    search:"Rechercher…", all:"Tous", showing:"affichés", topN:"top",
    p_band:"Proteins · qualité",
    p_sub:"Seule catégorie taggée Qualité + Sous-catégorie — base exploitable achats & coût",
    kCover:"Couverture qualité", kCoverSub:"SKU · 0 vide",
    kPremInt:"Intensité Premium", kPremIntSub:"du budget ·",
    kPremIntSku:"% des SKU",
    qmix:"Mix dépense par qualité", qmixSub:"où va le budget protéines",
    smix:"Mix dépense par sous-catégorie", smixSub:"Viande / Poisson / Crustacé / Végétale",
    ppt:"Prix moyen CHF/Kg par tier", pptSub:"valide le tagging qualité",
    matrix:"Répartition des SKU actifs", matrixSub:"Sous-catégorie × qualité (nb produits)",
    seg:"Dépense HT par segment", segSub:"sous-cat × qualité, sur la période",
    tblP:"Proteins — par article (avec qualité)", tblPsub:"le différenciateur du rapport COO",
    cBrand:"Marque & qualité",
    roadmap:"", soon1:"", soon1d:"", soon2:"", soon2d:"", soonChip:"",
    source:"Sources : food_orders (PO envoyées HT, hors Webstar) · proteins (product.template Studio : Sub-Category + Qualité). Périmètre = achats PO uniquement (= la part « PO Orders » du brut Live, hors DRY stock et hors Webstar non-food). Prix bruts HT, hors rabais.",
    empty:"Aucun article ne correspond.",
  },
  en: {
    title:"Food Procurement & Quality",
    sub:"Per item · category · volume · unit price · quality · supplier — since",
    g_title:"Food procurement — overall", g_sub:"All categories except non-food (Webstar) · sent POs",
    paTitle: "Procurement analysis — management", paSub: "top items, price comparison, delivery frequency",
    paPeriod: "Period", paAll: "Full period (from April)", paTopN: "Items",
    paXls: "Excel export", paPdf: "Print / PDF", paLoading: "Computing…",
    paKpiShare: "Top share", paKpiShareSub: "of total spend",
    paKpiMulti: "Multi-supplier", paKpiMultiSub: "items bought from several",
    paKpiSaving: "Potential saving", paKpiSavingSub: "at best observed price",
    paKpiFreq: "Avg interval", paKpiFreqSub: "days between deliveries",
    paTopTitle: "Top items by spend", paTopSub: "consolidated across suppliers · delivered & invoiced",
    paCmpTitle: "Supplier comparison", paCmpSub: "price actually paid to each",
    paDelTitle: "Delivery frequency and locations", paDelSub: "per item and per kitchen",
    paProduct: "Item", paQty: "Qty", paSpend: "Spend", paAvg: "Avg price",
    paLast: "Last", paDeliv: "Deliv.", paInterval: "Interval", paSup: "Sup.",
    paBest: "Best price", paWorst: "Highest", paGap: "Gap", paSaving: "Saving",
    paPlaces: "Locations", paNoMulti: "No item purchased from several suppliers in this period.",
    scopeTitle:"Scope", scopeGross:"Gross purchases", scopeExcl:"of which non-food excluded",
    scopeFood:"Food scope retained", scopeSent:"emailed POs · period = planned delivery date",
    scopeAllPo:"all confirmed POs",
    lensTitle:"Procurement scope — different from Q4 Live.",
    lensBody:"This page measures everything bought from a supplier (procurement lens) — stock buys included, e.g. the economat restocks (Saviva, Pistor…). The Live pages instead exclude those stock buys and count them as DRY at consumption, to track what's actually consumed each day. Two lenses, two totals — by design, not an inconsistency.",
    kSpend:"PO spend excl. VAT", kSpendSub:"sent POs · excl. Webstar · excl. DRY",
    kItems:"Items", kItemsSub:"ordered", kPos:"POs",
    kSup:"Suppliers", kSupSub:"active",
    kConc:"Top-1 concentration", kConcSub:"of spend at",
    kDrift:"Price-drift alerts", kDriftSub:"items > 10% (min↔max)",
    tblG:"Global procurement report — by item", tblGsub:"All ordered food items",
    cArt:"Item", cVol:"Volume", cUnit:"Avg price", cSup:"Supplier", cAmt:"Amount excl. VAT", cQual:"Quality",
    search:"Search…", all:"All", showing:"shown", topN:"top",
    p_band:"Proteins · quality",
    p_sub:"Only category tagged with Quality + Sub-category — actionable base for sourcing & cost",
    kCover:"Quality coverage", kCoverSub:"SKU · 0 empty",
    kPremInt:"Premium intensity", kPremIntSub:"of budget ·",
    kPremIntSku:"% of SKU",
    qmix:"Spend mix by quality", qmixSub:"where the protein budget goes",
    smix:"Spend mix by sub-category", smixSub:"Meat / Fish / Shellfish / Plant",
    ppt:"Avg CHF/Kg by tier", pptSub:"validates quality tagging",
    matrix:"Active SKU breakdown", matrixSub:"Sub-category × quality (product count)",
    seg:"Spend by segment", segSub:"sub-cat × quality, over the period",
    tblP:"Proteins — by item (with quality)", tblPsub:"the COO report differentiator",
    cBrand:"Brand & quality",
    roadmap:"", soon1:"", soon1d:"", soon2:"", soon2d:"", soonChip:"",
    source:"Sources: food_orders (sent POs excl. VAT, excl. Webstar) · proteins (product.template Studio: Sub-Category + Quality). Scope = PO purchases only (= the 'PO Orders' share of the Live gross, excl. DRY stock and excl. non-food Webstar). Gross prices excl. VAT, before rebates.",
    empty:"No item matches.",
  },
};

// Vue direction d'un trimestre (Q3, Q4…) — même écran, paramétré par `quarter`.
// L'API (/dashboard/q2_tracker?quarter=N) fournit la fenêtre, le nombre de jours
// et le statut clos/live ; les couverts et budgets restos viennent du planning F&B.
function Q3LiveTab({quarter=3}) {
  const [d, setD] = useState(null);
  const [cats, setCats] = useState(null);
  const [loading, setLoad] = useState(true);
  const [err, setErr] = useState("");
  const [lang, setLang] = useState("fr");
  useEffect(() => {
    setLoad(true); setErr("");
    Promise.all([
      fetch(`${API}/dashboard/q2_tracker?quarter=${quarter}`).then(r=>r.json()),
      fetch(`${API}/dashboard/q2_categories?quarter=${quarter}`).then(r=>r.json()),
    ]).then(([j, jc]) => {
      if (j.error) throw new Error(j.error);
      setD(j);
      if (jc && !jc.error) setCats(jc.by_entity||{});
    }).catch(e => setErr(String(e))).finally(()=>setLoad(false));
  }, [quarter]);
  const fr = lang==="fr"; const L=(f,e)=>fr?f:e;
  const QK = "Q" + quarter;
  if (loading) return <Card><div style={{color:T.hint,fontSize:13,padding:"8px 0"}}>Chargement / Loading…</div></Card>;
  if (err)     return <Card><div style={{color:T.err,fontSize:13}}>Erreur : {err}</div></Card>;

  const REF   = {lv:5, fd:15, fi:20, sk:12};
  const NAME  = {lv:"Lake View", fd:"Fine Dining", fi:"Food Inspired", sk:"Soul Kitchen"};
  const COLOR = {lv:"#2f6f9f", fd:"#c0392b", fi:"#7d3cc0", sk:"#d68910"};
  const be   = d.by_entity||{};
  const ctd  = d.covers_to_date||{};
  const ctot = d.covers_total||{};
  const tc   = d.teaching_catering||{};
  const spent   = d.april_committed||0;
  const proj    = d.q2_pace_projected||0;
  const budAll  = d.q2_budget_all||295000;
  const budResto= d.q2_budget_a||0;
  const within  = proj <= budAll;
  const closedQ = !!d.closed;
  const qDays   = d.quarter_days || 77;
  const MOIS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
  const MON_EN  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const ts      = d.track_start ? new Date(d.track_start + "T00:00:00") : null;
  const sinceFr = ts ? (ts.getDate()===1 ? "1er" : ts.getDate()) + " " + MOIS_FR[ts.getMonth()] : "";
  const sinceEn = ts ? ts.getDate() + " " + MON_EN[ts.getMonth()] : "";
  const dot = (c)=><span style={{display:"inline-block",width:9,height:9,borderRadius:9,background:c,marginRight:7}}/>;

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap",marginBottom:18}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.ink}}>{closedQ ? L(`${QK} Closed — bilan direction`,`${QK} Closed — management review`) : L(`${QK} Live — vue direction`,`${QK} Live — management view`)}</div>
          <div style={{fontSize:12.5,color:T.hint,marginTop:3}}>{L(`Culinary Arts Academy Switzerland · données Odoo live · ${QK} depuis le ${sinceFr}`,`Culinary Arts Academy Switzerland · live Odoo data · ${QK} since ${sinceEn}`)}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <div style={{display:"inline-flex",border:`1px solid ${T.line}`,borderRadius:8,overflow:"hidden"}}>
            {["fr","en"].map(lg=>(<button key={lg} onClick={()=>setLang(lg)} className="no-print" style={{border:"none",cursor:"pointer",padding:"6px 12px",fontSize:12,fontWeight:700,background:lang===lg?T.ink:T.card,color:lang===lg?"#fff":T.sub}}>{lg.toUpperCase()}</button>))}
          </div>
          <button onClick={()=>window.print()} className="no-print" style={{border:`1px solid ${T.line}`,background:T.card,color:T.sub,borderRadius:8,padding:"6px 13px",fontSize:12.5,fontWeight:700,cursor:"pointer"}}>🖶 Print / PDF</button>
        </div>
      </div>

      {/* 0 · Semaines pas encore saisies dans le planning → référence Q2 */}
      {!closedQ && (d.fallback_weeks||[]).length > 0 && (
        <Card style={{marginBottom:14,padding:"12px 18px",background:"#fff7e8",border:"1px solid #f3cf8a"}}>
          <div style={{fontSize:12.5,color:"#8a5a00"}}>
            <b>{L("Référence Q2 appliquée","Q2 reference applied")}</b> · {(d.fallback_weeks||[]).join(", ")} — {L(
              `semaines pas encore saisies dans « Restaurants Planning » : budget = semaine-type Q2 (LV 2 585 · SK 480 · FI 176 · FD 60 couverts). Dès qu'une semaine est saisie, le planning ${QK} fait foi.`,
              `weeks not yet entered in “Restaurants Planning”: budget = Q2 standard week (LV 2,585 · SK 480 · FI 176 · FD 60 covers). Once a week is entered, the ${QK} planning prevails.`)}
          </div>
        </Card>
      )}

      {/* 1 · RÉSUMÉ (le pitch en 3 secondes) */}
      <Card style={{marginBottom:18,padding:"26px 30px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:24,flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:11,letterSpacing:".08em",textTransform:"uppercase",color:T.hint,fontWeight:700}}>{closedQ ? L(`${QK} · clos · ${qDays} jours · dépensé`,`${QK} · closed · ${qDays} days · spent`) : L(`${QK} · jour ${d.days_elapsed}/${qDays} · dépensé à date`,`${QK} · day ${d.days_elapsed}/${qDays} · spent to date`)}</div>
            <div style={{fontSize:52,fontWeight:800,color:T.ink,lineHeight:1.05,letterSpacing:"-0.02em"}}>{fmtK(spent)}</div>
            <div style={{marginTop:8,fontSize:13,color:T.sub}}>
              <span style={{color:T.info,fontWeight:700}}>{L("PO frais","Fresh PO")}</span> {fmtK(d.april_po||0)}
              <span style={{margin:"0 8px",color:T.ghost}}>·</span>
              <span style={{color:"#c78a00",fontWeight:700}}>{L("Stock DRY","DRY stock")}</span> {fmtK(d.april_dry||0)}
            </div>
          </div>
          <div style={{display:"flex",gap:14,flexWrap:"wrap",alignItems:"stretch"}}>
            {[
              {l:closedQ?L(`Total ${QK}`,`${QK} total`):L(`Projeté fin ${QK}`,`Projected ${QK}`), v:fmtK(proj), c:T.ink},
              {l:L("Budget toutes places","All-places budget"), v:fmtK(budAll), c:T.sub},
              {l:L("Budget restaurants","Restaurants budget"), v:fmtK(budResto), c:T.sub},
            ].map((s,i)=>(
              <div key={i} style={{border:`1px solid ${T.line}`,borderRadius:12,padding:"12px 18px",minWidth:130}}>
                <div style={{fontSize:10.5,letterSpacing:".05em",textTransform:"uppercase",color:T.hint,fontWeight:700}}>{s.l}</div>
                <div style={{fontSize:22,fontWeight:800,color:s.c,marginTop:4}}>{s.v}</div>
              </div>
            ))}
            <div style={{display:"flex",alignItems:"center"}}>
              <span style={{fontSize:13,fontWeight:800,padding:"10px 16px",borderRadius:10,
                background:within?"#e7f6ec":"#fdeaea",color:within?"#1f8a4c":"#c0392b"}}>
                {within?L("✓ dans le budget","✓ within budget"):L("⚠ dépassement projeté","⚠ projected over")}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* 2 · ENSEIGNEMENT vs RESTAURATION */}
      {tc.gross!=null && (
      <Card style={{marginBottom:18,padding:"22px 26px"}}>
        <div style={{fontSize:15,fontWeight:800,color:T.ink,marginBottom:2}}>{L("Où va l'argent · Enseignement vs Restauration","Where the money goes · Teaching vs Catering")}</div>
        <div style={{fontSize:12,color:T.hint,marginBottom:14}}>{L(`Brut food ${QK} ${fmtK(tc.gross)}`,`${QK} gross food ${fmtK(tc.gross)}`)}</div>
        {(()=>{ const te=tc.teaching||0, ca=tc.catering||0, t=(te+ca)||1; return (<>
          <div style={{display:"flex",height:28,borderRadius:8,overflow:"hidden",border:`1px solid ${T.line}`,marginBottom:10}}>
            <div style={{width:`${100*te/t}%`,background:T.info,minWidth:te>0?36:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700}}>{tc.teaching_pct}%</div>
            <div style={{width:`${100*ca/t}%`,background:"#2f6f9f",minWidth:ca>0?36:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700}}>{tc.catering_pct}%</div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:12}}>
            <div>{dot(T.info)}<b style={{color:T.ink}}>{L("Enseignement","Teaching")}</b> <span style={{color:T.hint}}>· {fmtK(te)} · {tc.teaching_pct}%</span></div>
            <div>{dot("#2f6f9f")}<b style={{color:T.ink}}>{L("Restauration","Catering")}</b> <span style={{color:T.hint}}>· {fmtK(ca)} · {tc.catering_pct}%</span></div>
          </div></>);})()}
        <div style={{fontSize:11.5,color:T.hint,lineHeight:1.55}}>{fr?tc.note_fr:tc.note_en}</div>
      </Card>
      )}

      {/* 3 · LES 4 RESTAURANTS */}
      <div style={{fontSize:15,fontWeight:800,color:T.ink,margin:"4px 0 12px"}}>{L("Performance par restaurant","Performance by restaurant")}</div>
      <div style={{display:"grid",gap:14,gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",marginBottom:18}}>
        {["lv","fi","fd","sk"].map(k=>{
          const e = be[k]||{};
          const sp = e.committed||0;
          const cov = ctd[k]||0;
          const covT = ctot[k]||0;
          const chfCov = cov>0 ? sp/cov : 0;
          const ref = REF[k];
          const okCov = chfCov>0 && chfCov<=ref;
          const bud = e.q2_budget_a||0;
          const pr  = e.q2_pace||0;
          const overBud = pr > bud;
          const pctBud = bud>0 ? Math.min(sp/bud*100,100) : 0;
          return (
            <Card key={k} style={{padding:"18px 20px",borderTop:`3px solid ${COLOR[k]}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <div style={{fontSize:15,fontWeight:800,color:T.ink}}>{NAME[k]}</div>
                <div style={{fontSize:11,color:T.hint}}>{fmtN(covT)} {L(`couv./${QK}`,`cov./${QK}`)}</div>
              </div>
              <div style={{fontSize:28,fontWeight:800,color:COLOR[k],marginTop:6,lineHeight:1}}>{fmtK(sp)}</div>
              <div style={{fontSize:11.5,color:T.hint,marginTop:3,marginBottom:12}}>{closedQ?L("dépensé","spent"):L("dépensé à date","spent to date")} · {fmtN(cov)} {L("couverts","covers")}</div>

              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderTop:`1px solid ${T.grid}`}}>
                <span style={{fontSize:12,color:T.sub}}>{L("CHF / couvert","CHF / cover")}</span>
                <span style={{fontSize:13,fontWeight:800}}>
                  <span style={{color:okCov?"#1f8a4c":"#c0392b"}}>{chfCov>0?chfCov.toFixed(2):"—"}</span>
                  <span style={{color:T.ghost,fontWeight:600}}> / {ref}</span>
                  <span style={{marginLeft:6}}>{chfCov>0?(okCov?"🟢":"🔴"):""}</span>
                </span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderTop:`1px solid ${T.grid}`}}>
                <span style={{fontSize:12,color:T.sub}}>{closedQ?L(`Total / budget ${QK}`,`Total / ${QK} budget`):L(`Projeté / budget ${QK}`,`Projected / ${QK} budget`)}</span>
                <span style={{fontSize:13,fontWeight:800}}>
                  <span style={{color:overBud?"#c0392b":"#1f8a4c"}}>{fmtK(pr)}</span>
                  <span style={{color:T.ghost,fontWeight:600}}> / {fmtK(bud)}</span>
                </span>
              </div>
              <div style={{height:7,borderRadius:6,background:T.grid,overflow:"hidden",marginTop:8}}>
                <div style={{width:`${pctBud}%`,height:"100%",background:overBud?"#c0392b":COLOR[k]}}/>
              </div>
              <div style={{fontSize:10.5,color:T.hint,marginTop:5}}>{Math.round(pctBud)}% {L("du budget consommé","of budget used")}</div>

              {/* PO / DRY */}
              <div style={{marginTop:14,paddingTop:10,borderTop:`1px solid ${T.grid}`}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:5}}>
                  <span><b style={{color:T.info}}>PO</b> <span style={{color:T.sub}}>{fmtK(e.po||0)}</span></span>
                  <span><b style={{color:"#c78a00"}}>DRY</b> <span style={{color:T.sub}}>{fmtK(e.dry||0)}</span></span>
                </div>
                <div style={{display:"flex",height:8,borderRadius:5,overflow:"hidden",background:T.grid}}>
                  {(()=>{const p=e.po||0,dr=e.dry||0,t=(p+dr)||1;return(<>
                    <div style={{width:`${100*p/t}%`,background:T.info}}/>
                    <div style={{width:`${100*dr/t}%`,background:"#d6a72e"}}/>
                  </>);})()}
                </div>
              </div>

              {/* Catégories */}
              {cats && cats[k] && cats[k].categories && cats[k].categories.length>0 && (
                <div style={{marginTop:11}}>
                  <div style={{fontSize:10,letterSpacing:".05em",textTransform:"uppercase",color:T.hint,fontWeight:700,marginBottom:5}}>{L("Par catégorie","By category")}</div>
                  {cats[k].categories.map((c,i)=>(
                    <div key={i} style={{padding:"3px 0"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11.5}}>
                        <span style={{color:T.sub}}>{c.label}</span>
                        <span style={{color:T.ink,fontWeight:600}}>{fmtK(c.chf)} <span style={{color:T.ghost,fontWeight:400}}>· {c.pct}%</span></span>
                      </div>
                      <div style={{height:4,borderRadius:3,background:T.grid,overflow:"hidden",marginTop:2}}>
                        <div style={{width:`${Math.min(c.pct,100)}%`,height:"100%",background:COLOR[k],opacity:.55}}/>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* 3b · HORS RESTAURANTS */}
      {d.hors_resto && (d.hors_resto.total||0)>0 && (
      <Card style={{marginBottom:18,padding:"22px 26px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",flexWrap:"wrap",gap:8,marginBottom:3}}>
          <div style={{fontSize:15,fontWeight:800,color:T.ink}}>{L("Hors restaurants · Enseignement & Events","Off-restaurant · Teaching & Events")}</div>
          <div style={{fontSize:20,fontWeight:800,color:T.info}}>{fmtK(d.hors_resto.total)}</div>
        </div>
        <div style={{fontSize:12,color:T.hint,marginBottom:14}}>{L(`PO ${fmtK(d.hors_resto.po)} · DRY ${fmtK(d.hors_resto.dry)} · dépenses des places académiques (cours, démos, events — hors LV/SK/FD/FI)`,`PO ${fmtK(d.hors_resto.po)} · DRY ${fmtK(d.hors_resto.dry)} · academic-place spend (classes, demos, events — excl. LV/SK/FD/FI)`)}</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {(d.hors_resto.places||[]).map((p,i)=>(
            <span key={i} style={{fontSize:12,padding:"4px 10px",borderRadius:8,background:T.info+"12",color:T.ink,border:`1px solid ${T.info+"30"}`}}>{p.place} · {fmtK(p.total)}</span>
          ))}
        </div>
      </Card>
      )}

      {/* 4 · MÉTHODE */}
      <Card style={{padding:"14px 20px",background:T.bg}}>
        <div style={{fontSize:11.5,color:T.hint,lineHeight:1.6}}>
          <b style={{color:T.sub}}>{L("Méthode","Method")}:</b> {L(
            `Budget = couverts du planning ${QK} × référence CHF/couvert (LV 5 · FD 15 · FI 20 · SK 12), ` +
              (quarter===3 ? "sur 11 semaines actives (W1 = 6 juil, W0 = vacances). " : `du ${sinceFr} (jeudi de W0) à la fin de W11, soit ${qDays} jours. `) +
              "Application Kitchen & Production Kitchen comptées à 100 % dans Lake View. Dépense = PO confirmés + stock DRY à la consommation, achats de stock économat et Webstar exclus.",
            `Budget = ${QK} planning covers × CHF/cover reference (LV 5 · FD 15 · FI 20 · SK 12), ` +
              (quarter===3 ? "over 11 active weeks (W1 = 6 Jul, W0 = holidays). " : `from ${sinceEn} (Thursday of W0) to the end of W11, i.e. ${qDays} days. `) +
              "Application Kitchen & Production Kitchen fully counted in Lake View. Spend = confirmed PO + DRY stock at consumption, economat stock buys and Webstar excluded.")}
        </div>
      </Card>
    </div>
  );
}



// =====================================================================
// RECONCILIATION — « Totaux & Périmètres » · design cockpit sombre
// Reprend la palette W de FOOD INVOICES. Source unique :
// /dashboard/reconciliation, qui rappelle lui-même les endpoints des
// pages concernées — les chiffres ne peuvent donc pas diverger.
// =====================================================================
// ── helpers partagés (étaient locaux à ProcurementTab, donc invisibles
//    depuis les autres composants — extraits ici le 04/09/2026)
const hexAlpha = (hex, a) => {
  const h = String(hex || "#000000").replace("#", "");
  return `rgba(${parseInt(h.slice(0,2),16)},${parseInt(h.slice(2,4),16)},${parseInt(h.slice(4,6),16)},${a})`;
};

function SegBar({ value, opts, onChange, accent = "#c2722e" }) {
  return (
    <div style={{display:"inline-flex",border:`1px solid ${T.line}`,
                 borderRadius:9,overflow:"hidden"}}>
      {opts.map(o=>(
        <button key={o.v} onClick={()=>onChange(o.v)}
          style={{border:"none",cursor:"pointer",padding:"6px 13px",fontSize:12,
                  fontWeight:700,background:value===o.v?accent:T.card,
                  color:value===o.v?"#fff":T.sub}}>{o.l}</button>
      ))}
    </div>
  );
}

const RECO_L = {
  fr: {
    title:"Totaux & Périmètres", sub:"trois questions, trois totaux — et comment ils se rejoignent",
    principle:"Le principe", rules:"Les règles du dashboard",
    lensesTitle:"Les lentilles", bridgeTitle:"Réconciliation",
    waterfall:"Chemin d'un total à l'autre",
    page:"Page", source:"Source", basis:"Base de date",
    incl:"Compris", excl:"Exclu",
    detail:"Décomposition", why:"Ce qui explique l'écart", reading:"Comment le lire",
    gap:"Écart", closed:"clos", live:"en cours",
    xls:"Export XLS", pdf:"Imprimer / PDF", loading:"Calcul en cours…",
    compare:"Comparaison des lentilles", caveat:"Réserve de lecture", proof:"Source de preuve", intent:"Intention",
    frame:"Le cadre", before:"Avant", now:"Maintenant", split:"Répartition",
    guarantee:"La garantie de lecture", mRules:"Les règles de mesure",
  },
  en: {
    title:"Totals & Scopes", sub:"three questions, three totals — and how they reconcile",
    principle:"The principle", rules:"Dashboard rules",
    lensesTitle:"The lenses", bridgeTitle:"Reconciliation",
    waterfall:"From one total to the other",
    page:"Page", source:"Source", basis:"Date basis",
    incl:"Included", excl:"Excluded",
    detail:"Breakdown", why:"What explains the gap", reading:"How to read it",
    gap:"Gap", closed:"closed", live:"live",
    xls:"Excel export", pdf:"Print / PDF", loading:"Computing…",
    compare:"Lens comparison", caveat:"Caveat", proof:"Source of evidence", intent:"Intention",
    frame:"The framework", before:"Before", now:"Now", split:"Split",
    guarantee:"The reading guarantee", mRules:"Measurement rules",
  },
};

function RecoSimple({ d, lang, kf2, SHELL, LBL }) {
  const fr = lang === "fr";
  const lens = id => (d.lenses || []).find(l => l.id === id) || {};
  const part = (id, key) => (((lens(id).flow || {}).parts) || []).find(p => p.key === key) || {};
  const ordered  = lens("procurement").total || 0;
  const invoiced = lens("invoiced").total || 0;
  const consumed = lens("consumption").total || 0;
  const stockIn  = part("procurement", "stock").value || 0;
  const stockOut = part("consumption", "dry").value || 0;
  const diff = invoiced - ordered;
  const Box = ({ tone, title, value, what, star }) => (
    <div style={{flex:"1 1 220px",background:"rgba(255,255,255,.05)",border:`1px solid ${star?tone:W.edge}`,
      borderRadius:14,padding:"14px 16px"}}>
      <div style={{...LBL,color:tone}}>{title}{star ? (fr?"  ✓ chiffre de référence":"  ✓ reference figure"):""}</div>
      <div style={{fontSize:28,fontWeight:800,color:W.txt,margin:"4px 0 6px"}}>{kf2(value)}</div>
      <div style={{fontSize:12.5,color:W.dim,lineHeight:1.5}}>{what}</div>
    </div>);
  const Row = ({ label, value, text }) => (
    <div style={{display:"grid",gridTemplateColumns:"minmax(150px,220px) 90px 1fr",gap:12,alignItems:"baseline",
      padding:"9px 0",borderTop:`1px solid ${W.edge}`,fontSize:13}}>
      <span style={{color:W.txt,fontWeight:700}}>{label}</span>
      <span style={{...WS_NUM,color:W.amber,fontWeight:800,textAlign:"right"}}>{value}</span>
      <span style={{color:W.dim,lineHeight:1.5}}>{text}</span>
    </div>);
  const sign = n => (n >= 0 ? "+ " : "− ") + kf2(Math.abs(n));
  return (
    <div style={SHELL}>
      <div style={{...LBL,marginBottom:6}}>{fr ? "En bref" : "In short"}</div>
      <div style={{fontSize:14,color:W.txt,marginBottom:14}}>
        {fr ? `Depuis le ${d.since} jusqu'à ${d.closed ? "la fin du trimestre" : "aujourd'hui"}, trois chiffres :`
            : `From ${d.since} to ${d.closed ? "the end of the quarter" : "today"}, three figures:`}
      </div>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16}}>
        <Box tone={W.amber} title={fr?"① Commandé":"① Ordered"} value={ordered}
          what={fr?"Ce que les chefs ont commandé dans Odoo (bons de commande envoyés).":"What the chefs ordered in Odoo (purchase orders sent)."}/>
        <Box tone={W.teal} title={fr?"② Facturé":"② Invoiced"} value={invoiced} star
          what={fr?"Ce que les fournisseurs ont réellement livré et que CAA va payer (bons de livraison scannés).":"What suppliers actually delivered and CAA will pay (scanned delivery notes)."}/>
        <Box tone={W.viol} title={fr?"③ Consommé":"③ Consumed"} value={consumed}
          what={fr?"Ce qui est parti dans les cuisines : livraisons directes + sorties du stock de l'économat.":"What went into the kitchens: direct deliveries + issues from the economat store."}/>
      </div>
      <div style={{...LBL,marginBottom:2}}>{fr ? "Pourquoi les chiffres ne sont pas identiques" : "Why the figures differ"}</div>
      <Row label={fr?"Facturé − Commandé":"Invoiced − Ordered"} value={sign(diff)}
        text={fr?"Livré en plus ou en moins de la commande : ajouts du chef à la livraison, pesées (fromage, viande, légumes), produits remplacés, prix qui ont changé. Les scans des derniers jours arrivent avec 2 à 7 jours de retard.":
                 "Delivered more or less than ordered: chef add-ons at delivery, weighing (cheese, meat, vegetables), substitutions, price changes. The last few days' scans arrive 2–7 days late."}/>
      <Row label={fr?"Stock de l'économat":"Economat store"} value={`${kf2(stockIn)} / ${kf2(stockOut)}`}
        text={fr?`Acheté pour le stock ${kf2(stockIn)}, sorti du stock ${kf2(stockOut)}. Le stock est compté quand il sort vers une cuisine, pas quand il arrive : c'est pourquoi « Consommé » diffère de « Commandé ».`:
                 `Bought for stock ${kf2(stockIn)}, issued from stock ${kf2(stockOut)}. Stock counts when it leaves for a kitchen, not when it arrives — that is why "Consumed" differs from "Ordered".`}/>
      <div style={{...LBL,margin:"16px 0 6px"}}>{fr ? "Ce qu'on attend de chacun" : "What we expect from each of us"}</div>
      <ul style={{margin:0,paddingLeft:18,fontSize:13,color:W.txt,lineHeight:1.75}}>
        {(fr ? [
          "À la réception : comparer le bon de livraison avec la commande, et valider la quantité réellement livrée.",
          "Si la commande change par téléphone ou e-mail : corriger la commande dans Odoo.",
          "Dans FOOD INVOICES : corriger les « prix faux » avec le bouton Fix prices.",
        ] : [
          "At delivery: compare the delivery note with the order, and validate the quantity actually delivered.",
          "If an order changes by phone or e-mail: update it in Odoo.",
          "In FOOD INVOICES: fix the “wrong prices” with the Fix prices button.",
        ]).map((t,i)=><li key={i}>{t}</li>)}
      </ul>
    </div>
  );
}

function ReconciliationTab() {
  const [q, setQ]       = useState(CUR_Q >= 2 && CUR_Q <= 4 ? CUR_Q : 4);   // trimestre en cours : là où on peut agir
  const [d, setD]       = useState(null);
  const [busy, setBusy] = useState(true);
  const [lang, setLang] = useState("fr");   // lecteurs = acheteurs CAA (Sébastien, Sanford, Daniel)
  const [more, setMore] = useState(false);  // détails & méthode repliés par défaut

  useEffect(() => {
    let alive = true; setBusy(true);
    fetch(`${API}/dashboard/reconciliation?quarter=${q}`)
      .then(r => r.json())
      .then(j => { if (alive) { setD(j); setBusy(false); } })
      .catch(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [q]);

  const L = RECO_L[lang];
  const TONE = { consumption: W.viol, procurement: W.amber, invoiced: W.teal };
  const SPLIT_TONE = { restaurants: W.blue, teaching: W.pink };
  const FLOW_TONE  = { direct: W.green, dry: W.amber, stock: W.amber };
  const money = n => (n < 0 ? "− " : "") + Math.abs(Math.round(n)).toLocaleString("fr-CH");
  const kf2   = n => "CHF " + (Math.abs(n) >= 1000
                    ? (Math.round(n/100)/10).toLocaleString("fr-CH") + "k"
                    : Math.round(n).toLocaleString("fr-CH"));

  const SHELL = {
    background: "linear-gradient(150deg, #0d1728 0%, #131E33 55%, #18243c 100%)",
    border: `1px solid ${W.edge}`, borderRadius: 18, padding: "20px 22px",
    marginBottom: 16, boxShadow: "0 18px 44px -26px rgba(0,0,0,.65)",
  };
  const LBL = { fontSize: 9.5, fontWeight: 800, letterSpacing: ".1em",
                textTransform: "uppercase", color: W.ghost };

  const exportXls = () => {
    if (!d) return;
    const H = ["Lentille","Page","Total CHF","Source","Base de date","Question"];
    const rows = (d.lenses||[]).map(l => [l.title,l.page,l.total,l.source,l.date_basis,l.question]);
    (d.bridges||[]).forEach(b => rows.push([`${b.from} → ${b.to}`,"",b.delta,"écart","",b.why.join(" | ")]));
    dlXls(`totaux_perimetres_Q${d.quarter}`, H, rows);
  };

  const lenses  = (d && d.lenses)  || [];
  const bridges = (d && d.bridges) || [];
  const maxTot  = Math.max(1, ...lenses.map(l => Math.abs(l.total || 0)));

  return (
    <div>
      {/* ── en-tête ── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                   gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:9}}>
            <span style={{width:10,height:10,borderRadius:10,background:W.viol,
                          boxShadow:`0 0 12px ${W.viol}`}}/>
            <span style={{fontSize:18,fontWeight:800,color:T.ink,letterSpacing:"-.01em"}}>{L.title}</span>
          </div>
          <div style={{fontSize:12,color:T.hint,marginTop:3,marginLeft:19}}>
            {L.sub}
            {d && <> · {d.label} · {d.since} → {d.until}
              <span style={{color:d.closed?T.hint:W.amber,fontWeight:700}}>
                {" "}· {d.closed ? L.closed : L.live}</span></>}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <SegBar value={q} onChange={setQ}
               opts={[{v:4,l:"Q4 · oct → déc"},{v:3,l:"Q3 · juil → sept"},{v:2,l:"Q2 · avr → juin"}]}/>
          <SegBar value={lang} onChange={setLang} opts={[{v:"fr",l:"FR"},{v:"en",l:"EN"}]}/>
          <button onClick={exportXls} style={{fontSize:12,fontWeight:700,color:T.ink,
            cursor:"pointer",border:`1px solid ${T.line}`,borderRadius:8,
            padding:"6px 11px",background:T.card}}>⬇ {L.xls}</button>
          <button onClick={()=>window.print()} style={{fontSize:12,fontWeight:700,color:T.ink,
            cursor:"pointer",border:`1px solid ${T.line}`,borderRadius:8,
            padding:"6px 11px",background:T.card}}>🖨 {L.pdf}</button>
        </div>
      </div>

      {busy && <div style={{...SHELL,color:W.dim,fontSize:13}}>{L.loading}</div>}

      {!busy && d && d.error &&
        <div style={{...SHELL,color:W.red,fontSize:13}}>{String(d.error)}</div>}

      {!busy && d && !d.error && (<>

        <RecoSimple d={d} lang={lang} kf2={kf2} SHELL={SHELL} LBL={LBL} />

        <button onClick={()=>setMore(m=>!m)} style={{width:"100%",textAlign:"left",cursor:"pointer",
          background:"transparent",border:`1px dashed ${T.line}`,borderRadius:12,padding:"10px 14px",
          marginBottom:16,fontSize:12.5,fontWeight:700,color:T.sub}}>
          {more ? "▾" : "▸"} {lang==="fr" ? "Détails et méthode de calcul (pour la direction)"
                                        : "Details and calculation method (for management)"}
        </button>

        {more && (<>
        {/* ── principe ── */}
        <div style={{...SHELL,display:"flex",gap:13,alignItems:"flex-start"}}>
          <span style={{color:W.teal,fontWeight:800,fontSize:15,lineHeight:1.1,
                        textShadow:`0 0 14px ${W.teal}`}}>◈</span>
          <div>
            <div style={{...LBL,marginBottom:5}}>{L.principle}</div>
            <div style={{fontSize:13.5,color:W.txt,lineHeight:1.65}}>{d.principle[lang]}</div>
          </div>
        </div>

        {/* ── le cadre : CAPTURE / VERIFY / DECIDE ── */}
        {d.framework && (
          <div style={SHELL}>
            <div style={{...LBL,marginBottom:6}}>{L.frame}</div>
            <div style={{fontSize:15,fontWeight:700,color:W.txt,lineHeight:1.5,
                         marginBottom:16}}>{d.framework.headline[lang]}</div>
            <div style={{display:"grid",gap:12,
                         gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",
                         marginBottom:16}}>
              {d.framework.steps.map(st=>(
                <div key={st.n} style={{background:"rgba(255,255,255,.04)",
                     border:`1px solid ${W.edge}`,borderRadius:11,padding:"13px 15px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:7}}>
                    <span style={{width:22,height:22,borderRadius:11,background:W.teal,
                      color:W.bg,fontSize:11,fontWeight:800,display:"flex",
                      alignItems:"center",justifyContent:"center",
                      boxShadow:`0 0 14px ${hexAlpha(W.teal,.55)}`}}>{st.n}</span>
                    <span style={{fontSize:11,fontWeight:800,letterSpacing:".14em",
                                  color:W.teal}}>{st.key}</span>
                  </div>
                  <div style={{fontSize:12.5,color:W.txt,fontWeight:600,
                               lineHeight:1.5,marginBottom:5}}>
                    {lang==="fr"?st.problem_fr:st.problem_en}</div>
                  <div style={{fontSize:11.5,color:W.dim,lineHeight:1.55,marginBottom:7}}>
                    {lang==="fr"?st.action_fr:st.action_en}</div>
                  <div style={{fontSize:10.5,color:W.ghost,borderTop:`1px solid ${W.edge}`,
                               paddingTop:6}}>{lang==="fr"?st.page_fr:st.page_en}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:16,flexWrap:"wrap",alignItems:"center",
                         padding:"12px 14px",borderRadius:10,
                         background:"rgba(255,255,255,.04)",border:`1px solid ${W.edge}`}}>
              <div>
                <div style={{...LBL,color:W.ghost}}>{L.before}</div>
                <div style={{fontSize:13,color:W.ghost,fontStyle:"italic"}}>
                  {lang==="fr"?d.framework.shift.before_fr:d.framework.shift.before_en}</div>
              </div>
              <span style={{fontSize:18,color:W.teal}}>→</span>
              <div>
                <div style={{...LBL,color:W.teal}}>{L.now}</div>
                <div style={{fontSize:13,color:W.txt,fontWeight:700}}>
                  {lang==="fr"?d.framework.shift.now_fr:d.framework.shift.now_en}</div>
              </div>
              <div style={{flexBasis:"100%",fontSize:11.5,color:W.dim,fontStyle:"italic"}}>
                {lang==="fr"?d.framework.shift.note_fr:d.framework.shift.note_en}</div>
            </div>
          </div>
        )}

        {/* ── comparaison visuelle des lentilles ── */}
        <div style={SHELL}>
          <div style={{...LBL,marginBottom:14}}>{L.compare}</div>
          {lenses.map(l => {
            const pct = Math.round(100 * Math.abs(l.total||0) / maxTot);
            return (
              <div key={l.id} style={{marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",
                             alignItems:"baseline",marginBottom:5,flexWrap:"wrap",gap:8}}>
                  <span style={{fontSize:13,fontWeight:800,color:TONE[l.id]}}>
                    {l.title}
                    {!l.proof && l.caveat &&
                      <span title={l.caveat[lang]}
                            style={{color:W.amber,marginLeft:5}}>≈</span>}
                    <span style={{color:W.ghost,fontWeight:500}}> · {l.page}</span>
                  </span>
                  <span style={{fontSize:17,fontWeight:800,color:W.txt,...WS_NUM}}>
                    {kf2(l.total)}
                  </span>
                </div>
                <div style={{height:16,borderRadius:9,background:"rgba(255,255,255,.05)",
                             overflow:"hidden",position:"relative"}}>
                  {/* segments de décomposition si presents */}
                  {(l.components||[]).filter(c=>c.value>0).length>1 ? (
                    <div style={{display:"flex",height:"100%",width:`${pct}%`}}>
                      {(l.components||[]).filter(c=>c.value>0).map((c,ci)=>(
                        <div key={ci} title={`${c.label} · ${kf2(c.value)}`}
                          style={{width:`${100*c.value/Math.abs(l.total||1)}%`,height:"100%",
                                  background: (l.id==="consumption"
                                              ? (ci===0 ? W.blue : W.pink)
                                              : (ci===0 ? TONE[l.id] : hexAlpha(TONE[l.id],.45))),
                                  borderRight: ci===0 ? `1px solid ${W.bg}` : "none"}}/>
                      ))}
                    </div>
                  ) : (
                    <div style={{width:`${pct}%`,height:"100%",background:TONE[l.id],
                                 boxShadow:`0 0 16px ${TONE[l.id]}`}}/>
                  )}
                </div>
                {(l.components||[]).length>1 && (
                  <div style={{display:"flex",gap:14,marginTop:5,flexWrap:"wrap"}}>
                    {(l.components||[]).map((c,ci)=>(
                      <span key={ci} style={{fontSize:11,color:W.dim}}>
                        <span style={{display:"inline-block",width:8,height:8,borderRadius:2,
                          marginRight:5,background: (l.id==="consumption"
                            ? (ci===0 ? W.blue : W.pink)
                            : (ci===0 ? TONE[l.id] : hexAlpha(TONE[l.id],.45)))}}/>
                        {c.label} <b style={{color:W.txt,...WS_NUM}}>{money(c.value)}</b>

                      </span>
                    ))}
                  </div>
                )}
                <div style={{fontSize:12,color:W.dim,fontStyle:"italic",marginTop:6}}>
                  {l.question}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── couverts & budgets : restaurants (couverts × réf) ≠ budget alloué par mois ── */}
        {d.budget && (() => {
          const B = d.budget, fr = lang === "fr";
          const RN = {lv:"Lake View", fi:"Food Inspired", fd:"Fine Dining", sk:"Soul Kitchen"};
          const RC = {lv:W.blue, fi:W.viol, fd:W.red, sk:W.amber};
          const cov = B.covers || {}, rs = B.restaurants || {};
          const maxM = Math.max(1, ...(B.monthly||[]).map(m => Math.max(m.budget||0, m.invoiced||0)));
          const bar = (v, max, c) => (
            <div style={{height:6,borderRadius:6,background:"rgba(255,255,255,.07)",overflow:"hidden",marginTop:5}}>
              <div style={{width:`${Math.min(100, 100*(v||0)/(max||1))}%`,height:"100%",background:c,borderRadius:6}}/>
            </div>);
          const pct = (a, b) => b ? Math.round(100*a/b) + " %" : "—";
          const COL = {background:W.panel,border:`1px solid ${W.edge}`,borderRadius:12,padding:"14px 16px"};
          return (
            <div style={SHELL}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",flexWrap:"wrap",gap:8,marginBottom:4}}>
                <div style={LBL}>{fr ? "Couverts & budgets" : "Covers & budgets"} · Q{d.quarter}</div>
                <div style={{fontSize:11,color:W.dim}}>
                  {fr ? "Budget toutes places" : "All-places budget"} <b style={{color:W.txt}}>{kf2(B.all_places)}</b>
                  {" = "}{fr ? "restaurants" : "restaurants"} <b style={{color:W.blue}}>{kf2(rs.total)}</b>
                  {" + "}{fr ? "enveloppe enseignement" : "teaching envelope"} <b style={{color:W.pink}}>{kf2(B.teaching_envelope)}</b>
                </div>
              </div>
              <div style={{fontSize:13,color:W.txt,lineHeight:1.55,marginBottom:14}}>
                {fr ? "Deux budgets distincts : celui des restaurants découle des couverts planifiés × CHF/couvert ; le budget alloué par mois couvre toutes les places (restaurants + enseignement). La différence est l'enveloppe des places d'enseignement."
                    : "Two distinct budgets: the restaurants' one comes from planned covers × CHF/cover; the monthly allocation covers all places (restaurants + teaching). The difference is the teaching-places envelope."}
              </div>
              {(cov.fallback_weeks||[]).length > 0 && (
                <div style={{fontSize:11.5,color:W.amber,marginBottom:12}}>
                  ⚠ {fr ? "Semaines pas encore saisies dans Restaurants Planning — référence Q2 appliquée : " : "Weeks not yet entered in Restaurants Planning — Q2 reference applied: "}{cov.fallback_weeks.join(", ")}
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:12}}>
                {/* 1 · couverts */}
                <div style={COL}>
                  <div style={{...LBL,marginBottom:2}}>{fr ? "① Couverts planifiés" : "① Planned covers"}</div>
                  <div style={{fontSize:24,fontWeight:800,color:W.txt}}>{money(cov.total||0)}</div>
                  <div style={{fontSize:11,color:W.dim,marginBottom:8}}>
                    {cov.source === "planning" ? (fr ? "source : Restaurants Planning" : "source: Restaurants Planning") : (fr ? "source : semaine-type Q2" : "source: Q2 standard week")}
                  </div>
                  {["lv","fi","fd","sk"].map(k => (
                    <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0",color:W.dim}}>
                      <span><span style={{display:"inline-block",width:7,height:7,borderRadius:7,background:RC[k],marginRight:7}}/>{RN[k]}</span>
                      <span style={{color:W.txt,fontWeight:700}}>{money((cov.by_resto||{})[k]||0)}
                        {cov.to_date && !d.closed && <span style={{color:W.ghost,fontWeight:400}}> · {money(cov.to_date[k]||0)} {fr?"à date":"to date"}</span>}</span>
                    </div>
                  ))}
                </div>
                {/* 2 · budget restaurants */}
                <div style={COL}>
                  <div style={{...LBL,marginBottom:2}}>{fr ? "② Budget restaurants = couverts × réf" : "② Restaurants budget = covers × ref"}</div>
                  <div style={{fontSize:24,fontWeight:800,color:W.blue}}>{kf2(rs.total||0)}</div>
                  <div style={{fontSize:11,color:W.dim,marginBottom:8}}>
                    {fr ? "consommé" : "consumed"} {kf2(rs.spent_total||0)} · {pct(rs.spent_total, rs.total)}
                  </div>
                  {["lv","fi","fd","sk"].map(k => (
                    <div key={k} style={{padding:"3px 0"}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:W.dim}}>
                        <span>{RN[k]} <span style={{color:W.ghost}}>· CHF {(B.refs||{})[k]}/{fr?"couv.":"cov."}</span></span>
                        <span><b style={{color:W.txt}}>{kf2((rs.spent||{})[k]||0)}</b> / {kf2((rs.by_resto||{})[k]||0)}</span>
                      </div>
                      {bar((rs.spent||{})[k], (rs.by_resto||{})[k], RC[k])}
                    </div>
                  ))}
                </div>
                {/* 3 · budget alloué par mois */}
                <div style={COL}>
                  <div style={{...LBL,marginBottom:2}}>{fr ? "③ Budget alloué par mois · toutes places" : "③ Monthly allocated budget · all places"}</div>
                  <div style={{fontSize:24,fontWeight:800,color:W.teal}}>{kf2(B.all_places||0)}</div>
                  <div style={{fontSize:11,color:W.dim,marginBottom:8}}>
                    {fr ? "facturé" : "invoiced"} {kf2(B.invoiced_total||0)} · {pct(B.invoiced_total, B.all_places)} <span style={{color:W.ghost}}>(FOOD INVOICES)</span>
                  </div>
                  {(B.monthly||[]).map(m => (
                    <div key={m.m} style={{padding:"3px 0"}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:W.dim}}>
                        <span>{m.label} <span style={{color:m.status==="live"?W.amber:W.ghost,fontSize:10.5}}>
                          {m.status==="closed" ? "✓" : m.status==="live" ? (fr?"● en cours":"● live") : (fr?"à venir":"to come")}</span></span>
                        <span><b style={{color:W.txt}}>{kf2(m.invoiced||0)}</b> / {kf2(m.budget||0)}</span>
                      </div>
                      {bar(m.invoiced, m.budget, (m.invoiced||0) > (m.budget||0) ? W.red : W.teal)}
                    </div>
                  ))}
                </div>
              </div>
              {/* 4 · dissociation */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:12,marginTop:12}}>
                {[
                  {k:"r", c:W.blue, t:fr?"Restaurants":"Restaurants", s:rs.spent_total, b:rs.total, n:fr?"budget issu des couverts":"budget from covers"},
                  {k:"t", c:W.pink, t:fr?"Places d'enseignement":"Teaching places", s:B.teaching_spent, b:B.teaching_envelope, n:fr?"enveloppe = toutes places − restaurants":"envelope = all places − restaurants"},
                ].map(x => (
                  <div key={x.k} style={{...COL,borderLeft:`3px solid ${x.c}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                      <span style={{fontSize:13,fontWeight:800,color:W.txt}}>{x.t}</span>
                      <span style={{fontSize:12,color:W.dim}}><b style={{color:W.txt}}>{kf2(x.s||0)}</b> / {kf2(x.b||0)} · {pct(x.s, x.b)}</span>
                    </div>
                    {bar(x.s, x.b, (x.s||0) > (x.b||0) ? W.red : x.c)}
                    <div style={{fontSize:10.5,color:W.ghost,marginTop:5}}>{fr?"consommé (Odoo PO + DRY)":"consumed (Odoo PO + DRY)"} · {x.n}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── fiches détaillées ── */}
        <div style={{display:"grid",gap:14,gridTemplateColumns:"repeat(auto-fit,minmax(290px,1fr))",
                     marginBottom:16}}>
          {lenses.map(l => (
            <div key={l.id} style={{...SHELL,marginBottom:0,
                                    borderTop:`3px solid ${TONE[l.id]}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{...LBL,color:TONE[l.id]}}>{l.title}</span>
                {l.proof && (
                  <span style={{fontSize:8.5,fontWeight:800,letterSpacing:".09em",
                    textTransform:"uppercase",color:W.bg,background:W.teal,
                    padding:"2px 7px",borderRadius:20,
                    boxShadow:`0 0 14px ${hexAlpha(W.teal,.6)}`}}>✓ {L.proof}</span>
                )}
                {!l.proof && l.caveat && (
                  <span style={{fontSize:8.5,fontWeight:800,letterSpacing:".09em",
                    textTransform:"uppercase",color:W.amber,
                    border:`1px solid ${hexAlpha(W.amber,.5)}`,
                    padding:"2px 7px",borderRadius:20}}>≈ {L.intent}</span>
                )}
              </div>
              <div style={{fontSize:27,fontWeight:800,color:W.txt,letterSpacing:"-.025em",
                           margin:"3px 0 1px",...WS_NUM,
                           textShadow:`0 0 22px ${hexAlpha(TONE[l.id],.45)}`}}>{kf2(l.total)}</div>
              <div style={{fontSize:11.5,color:W.ghost,marginBottom:13}}>{l.page}</div>

              <div style={{fontSize:11.5,color:W.dim,lineHeight:1.75,marginBottom:12}}>
                <div><span style={{color:W.ghost}}>{L.source} · </span>{l.source}</div>
                <div><span style={{color:W.ghost}}>{L.basis} · </span>{l.date_basis}</div>
              </div>

              {l.split && (
                <div style={{marginBottom:12,padding:"12px 13px",borderRadius:10,
                             background:"rgba(255,255,255,.05)",
                             border:`1px solid ${W.edge}`}}>
                  <div style={{...LBL,marginBottom:8}}>
                    {lang==="fr"?l.split.title_fr:l.split.title_en}</div>
                  <div style={{display:"flex",height:20,borderRadius:10,
                               overflow:"hidden",marginBottom:10}}>
                    {l.split.parts.map(pt=>(
                      <div key={pt.key} title={`${pt[lang]} · ${pt.pct}%`}
                        style={{width:`${pt.pct}%`,background:SPLIT_TONE[pt.key]||W.blue,
                                display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <span style={{fontSize:10,fontWeight:800,color:W.bg,
                                      ...WS_NUM}}>{pt.pct}%</span>
                      </div>
                    ))}
                  </div>
                  {l.split.parts.map(pt=>(
                    <div key={pt.key} style={{marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"baseline",gap:7}}>
                        <span style={{width:9,height:9,borderRadius:2,flexShrink:0,
                          background:SPLIT_TONE[pt.key]||W.blue,display:"inline-block"}}/>
                        <span style={{fontSize:12.5,fontWeight:700,color:W.txt}}>
                          {pt[lang]}</span>
                        <span style={{fontSize:13,fontWeight:800,color:W.txt,
                                      ...WS_NUM,marginLeft:"auto"}}>{kf2(pt.value)}</span>
                      </div>
                      <div style={{fontSize:10.5,color:W.ghost,marginLeft:16,
                                   lineHeight:1.5}}>
                        {lang==="fr"?pt.detail_fr:pt.detail_en}</div>
                      <div style={{fontSize:10.5,color:W.dim,marginLeft:16,
                                   fontStyle:"italic"}}>
                        {lang==="fr"?pt.page_fr:pt.page_en}</div>
                    </div>
                  ))}
                  <div style={{fontSize:11,color:W.dim,lineHeight:1.6,marginTop:9,
                               paddingTop:9,borderTop:`1px solid ${W.edge}`}}>
                    {lang==="fr"?l.split.note_fr:l.split.note_en}
                  </div>
                </div>
              )}

              {l.flow && (
                <div style={{marginBottom:12,padding:"12px 13px",borderRadius:10,
                             background:"rgba(255,255,255,.05)",
                             border:`1px solid ${W.edge}`}}>
                  <div style={{...LBL,marginBottom:8}}>
                    {lang==="fr"?l.flow.title_fr:l.flow.title_en}</div>
                  <div style={{display:"flex",height:20,borderRadius:10,
                               overflow:"hidden",marginBottom:10}}>
                    {l.flow.parts.map(pt=>(
                      <div key={pt.key} title={`${pt[lang]} · ${pt.pct}%`}
                        style={{width:`${pt.pct}%`,background:FLOW_TONE[pt.key]||W.green,
                                display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <span style={{fontSize:10,fontWeight:800,color:W.bg,
                                      ...WS_NUM}}>{pt.pct}%</span>
                      </div>
                    ))}
                  </div>
                  {l.flow.parts.map(pt=>(
                    <div key={pt.key} style={{marginBottom:7}}>
                      <div style={{display:"flex",alignItems:"baseline",gap:7}}>
                        <span style={{width:9,height:9,borderRadius:2,flexShrink:0,
                          background:FLOW_TONE[pt.key]||W.green,display:"inline-block"}}/>
                        <span style={{fontSize:12.5,fontWeight:700,color:W.txt}}>
                          {pt[lang]}
                          {pt.approx && <span style={{color:W.amber,marginLeft:4}}>≈</span>}
                        </span>
                        <span style={{fontSize:13,fontWeight:800,color:W.txt,
                                      ...WS_NUM,marginLeft:"auto"}}>{kf2(pt.value)}</span>
                      </div>
                      <div style={{fontSize:10.5,color:W.ghost,marginLeft:16,
                                   lineHeight:1.5}}>
                        {lang==="fr"?pt.detail_fr:pt.detail_en}</div>
                    </div>
                  ))}
                </div>
              )}

              {l.caveat && (
                <div style={{display:"flex",gap:9,marginBottom:12,padding:"9px 11px",
                     borderRadius:9,
                     background: hexAlpha(l.proof ? W.teal : W.amber, .09),
                     border:`1px solid ${hexAlpha(l.proof ? W.teal : W.amber, .35)}`}}>
                  <span style={{color: l.proof ? W.teal : W.amber, fontWeight:800,
                                fontSize:12,lineHeight:1.3}}>{l.proof ? "✓" : "⚠"}</span>
                  <div>
                    <div style={{...LBL,color: l.proof ? W.teal : W.amber, marginBottom:2}}>
                      {l.proof ? L.proof : L.caveat}</div>
                    <div style={{fontSize:11.5,color:W.dim,lineHeight:1.6}}>{l.caveat[lang]}</div>
                  </div>
                </div>
              )}

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <div style={{...LBL,color:W.green,marginBottom:3}}>✓ {L.incl}</div>
                  {l.includes.map((x,k)=><div key={k} style={{fontSize:11,color:W.dim,
                    lineHeight:1.55}}>· {x}</div>)}
                </div>
                <div>
                  <div style={{...LBL,color:W.ghost,marginBottom:3}}>✕ {L.excl}</div>
                  {l.excludes.map((x,k)=><div key={k} style={{fontSize:11,color:W.ghost,
                    lineHeight:1.55}}>· {x}</div>)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── réconciliation : cascade ── */}
        <div style={{...LBL,color:T.hint,margin:"2px 0 10px"}}>{L.bridgeTitle}</div>
        {bridges.map((b,bi) => {
          const from = lenses.find(x=>x.id===b.from) || {};
          const to   = lenses.find(x=>x.id===b.to)   || {};
          const steps = [
            {label: from.title || b.from, value: b.from_total, kind: "base", tone: TONE[b.from]},
            ...(b.steps || []).map(st => ({label: st.label, value: st.value, kind: "step"})),
            {label: to.title || b.to, value: b.to_total, kind: "base", tone: TONE[b.to]},
          ];
          const mx = Math.max(...steps.map(x=>Math.abs(x.value||0)), 1);
          return (
            <div key={bi} style={SHELL}>
              <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",
                           marginBottom:16}}>
                <div>
                  <div style={{...LBL,color:TONE[b.from]}}>{from.title}</div>
                  <div style={{fontSize:20,fontWeight:800,color:W.txt,...WS_NUM}}>{kf2(b.from_total)}</div>
                </div>
                <div style={{fontSize:20,color:W.ghost}}>→</div>
                <div>
                  <div style={{...LBL,color:TONE[b.to]}}>{to.title}</div>
                  <div style={{fontSize:20,fontWeight:800,color:W.txt,...WS_NUM}}>{kf2(b.to_total)}</div>
                </div>
                <div style={{marginLeft:"auto",textAlign:"right"}}>
                  <div style={{...LBL}}>{L.gap}</div>
                  <div style={{fontSize:20,fontWeight:800,...WS_NUM,
                        color: b.delta>=0 ? W.amber : W.blue,
                        textShadow:`0 0 20px ${hexAlpha(b.delta>=0?W.amber:W.blue,.5)}`}}>
                    {b.delta>=0?"+":"−"}{kf2(Math.abs(b.delta)).replace("CHF ","")}
                  </div>
                </div>
              </div>

              {b.headline && (
                <div style={{fontSize:15,fontWeight:700,color:W.teal,lineHeight:1.5,
                             marginBottom:14,paddingLeft:11,
                             borderLeft:`3px solid ${W.teal}`}}>
                  {b.headline[lang]}
                </div>
              )}

              {/* cascade */}
              <div style={{...LBL,marginBottom:8}}>{L.waterfall}</div>
              <div style={{marginBottom:14}}>
                {steps.map((st,si)=>{
                  const w = Math.round(100*Math.abs(st.value||0)/mx);
                  const neg = (st.value||0) < 0;
                  const col = st.kind==="base" ? st.tone : (neg ? W.red : W.green);
                  return (
                    <div key={si} style={{display:"grid",
                        gridTemplateColumns:"minmax(150px,1fr) 3fr 96px",
                        alignItems:"center",gap:10,padding:"3px 0"}}>
                      <div style={{fontSize:11.5,
                                   color: st.kind==="base" ? W.txt : W.dim,
                                   fontWeight: st.kind==="base" ? 700 : 400}}>{st.label}</div>
                      <div style={{height:11,borderRadius:6,
                                   background:"rgba(255,255,255,.05)",overflow:"hidden"}}>
                        <div style={{width:`${w}%`,height:"100%",background:col,
                                     opacity: st.kind==="base" ? 1 : .8,
                                     boxShadow: st.kind==="base" ? `0 0 14px ${col}` : "none"}}/>
                      </div>
                      <div style={{fontSize:12,textAlign:"right",...WS_NUM,
                                   fontWeight: st.kind==="base" ? 800 : 600,
                                   color: st.kind==="base" ? W.txt : col}}>
                        {st.kind!=="base" && (st.value>=0?"+":"")}{money(st.value)}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{display:"grid",gap:14,
                           gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))"}}>
                <div>
                  <div style={{...LBL,marginBottom:4}}>{L.why}</div>
                  {b.why.map((x,k)=><div key={k} style={{fontSize:12,color:W.dim,
                    lineHeight:1.65}}>· {x}</div>)}
                </div>
                <div style={{background:"rgba(255,255,255,.04)",borderRadius:10,
                             padding:"11px 13px",border:`1px solid ${W.edge}`}}>
                  <div style={{...LBL,marginBottom:4}}>{L.reading}</div>
                  <div style={{fontSize:12,color:W.dim,lineHeight:1.65}}>{b.reading}</div>
                  {b.caveat && (
                    <div style={{marginTop:10,paddingTop:10,
                                 borderTop:`1px solid ${hexAlpha(W.amber,.3)}`}}>
                      <div style={{...LBL,color:W.amber,marginBottom:3}}>⚠ {L.caveat}</div>
                      <div style={{fontSize:11.5,color:W.dim,lineHeight:1.6}}>{b.caveat[lang]}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* ── garantie de lecture ── */}
        {d.guarantee && (
          <div style={{...SHELL,borderLeft:`3px solid ${W.teal}`}}>
            <div style={{...LBL,color:W.teal,marginBottom:9}}>{L.guarantee}</div>
            <div style={{fontSize:17,fontWeight:800,color:W.txt,...WS_NUM,
                         marginBottom:6,textShadow:`0 0 20px ${hexAlpha(W.teal,.4)}`}}>
              {lang==="fr"?d.guarantee.rule_fr:d.guarantee.rule_en}
            </div>
            <div style={{fontSize:12.5,color:W.dim,lineHeight:1.65,marginBottom:10}}>
              {lang==="fr"?d.guarantee.else_fr:d.guarantee.else_en}
            </div>
            <div style={{fontSize:12,color:W.amber,lineHeight:1.6,paddingTop:9,
                         borderTop:`1px solid ${W.edge}`}}>
              {lang==="fr"?d.guarantee.ai_fr:d.guarantee.ai_en}
            </div>
          </div>
        )}

        {/* ── règles de mesure ── */}
        {(d.measurement_rules||[]).length>0 && (
          <div style={SHELL}>
            <div style={{...LBL,marginBottom:11}}>{L.mRules}</div>
            <div style={{display:"grid",gap:12,
                         gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))"}}>
              {d.measurement_rules.map((r,k)=>(
                <div key={k} style={{background:"rgba(255,255,255,.04)",
                     border:`1px solid ${W.edge}`,borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:W.txt,
                               lineHeight:1.45,marginBottom:4}}>{r[lang]}</div>
                  <div style={{fontSize:11.5,color:W.dim,lineHeight:1.55}}>
                    {lang==="fr"?r.detail_fr:r.detail_en}</div>
                </div>
              ))}
            </div>
            {d.rules_note && (
              <div style={{fontSize:12,color:W.dim,fontStyle:"italic",marginTop:12,
                           paddingTop:10,borderTop:`1px solid ${W.edge}`,lineHeight:1.6}}>
                {d.rules_note[lang]}
              </div>
            )}
          </div>
        )}

        {/* ── règles ── */}
        <div style={SHELL}>
          <div style={{...LBL,marginBottom:9}}>{L.rules}</div>
          {(d.rules||[]).map((r,k)=>(
            <div key={k} style={{display:"flex",gap:11,fontSize:12.5,color:W.dim,
                                 padding:"5px 0",lineHeight:1.65}}>
              <span style={{color:W.teal,fontWeight:800,...WS_NUM}}>{k+1}</span>
              <span>{r[lang]}</span>
            </div>
          ))}
        </div>

        {(d.warnings||[]).length>0 && (
          <div style={{fontSize:11.5,color:T.hint,marginTop:4}}>
            {d.warnings.map((w,k)=><div key={k}>⚠ {w}</div>)}
          </div>
        )}
        </>)}
      </>)}
    </div>
  );
}

// PROCUREMENT — reste en thème CLAIR. Une tentative de bascule sombre par
// surcharge locale de `T` a échoué le 04/09/2026 : les helpers partagés
// (Card, Label, Pager…) lisent le `T` du module, pas celui du composant —
// résultat, fonds sombres et textes clairs, illisible. La bascule devra
// passer par un contexte React, pas par une variable locale.
function ProcurementTab() {
  const [food, setFood]   = useState(null);
  const [pr, setPr]       = useState(null);
  const [loading, setLoad]= useState(true);
  const [err, setErr]     = useState("");
  const [lang, setLang]   = useState("fr");
  const [pQ, setPQ]       = useState("");
  const [pfSub, setPfSub] = useState("Tous");
  const [pfQual, setPfQual]= useState("Tous");
  const [pPage, setPPage] = useState(1);
  const [period, setPeriod] = useState("q4");
  const PER = 10;

  const PP = PROC_PERIODS.find(x => x.id === period) || PROC_PERIODS[0];

  useEffect(() => {
    setLoad(true); setErr("");
    const qs = `since=${PP.since}${PP.until ? `&until=${PP.until}` : ""}`;
    Promise.all([
      fetch(`${API}/dashboard/food_orders?${qs}`).then(r=>r.json()),
      fetch(`${API}/dashboard/proteins?${qs}`).then(r=>r.json()),
    ]).then(([f,p]) => {
      if (f.error) setErr("food_orders: "+f.error);
      else if (p.error) setErr("proteins: "+p.error);
      else { setFood(f); setPr(p); setPPage(1); }
    }).catch(e=>setErr(String(e))).finally(()=>setLoad(false));
  }, [period]);

  const L = PROC_L[lang];
  if (loading) return <Card><div style={{color:T.hint,fontSize:13,padding:"8px 0"}}>Chargement / Loading…</div></Card>;
  if (err)     return <Card><div style={{color:T.err,fontSize:13}}>Erreur : {err}</div></Card>;
  if (!food || !food.totals || !pr || !pr.kpi)
    return <Card><div style={{color:T.hint,fontSize:13}}>Aucune donnée / No data.</div></Card>;

  // ── helpers locaux (mêmes signatures que WebstarTab) ──
  const Stat = ({label, value, sub, tone}) => (
    <Card style={{padding:"16px 18px"}}>
      <Label>{label}</Label>
      <div style={{fontSize:24,fontWeight:800,color:tone||T.ink,letterSpacing:"-.01em"}}>{value}</div>
      {sub && <div style={{fontSize:11.5,color:T.hint,marginTop:2}}>{sub}</div>}
    </Card>
  );
  const Seg = ({value, opts, onChange, accent=PROC_ACCENT}) => (
    <div style={{display:"inline-flex",border:`1px solid ${T.line}`,borderRadius:9,overflow:"hidden"}}>
      {opts.map(o=>(
        <button key={o.v} onClick={()=>onChange(o.v)} style={{border:"none",cursor:"pointer",
          padding:"6px 13px",fontSize:12,fontWeight:700,
          background:value===o.v?accent:T.card,color:value===o.v?"#fff":T.sub}}>{o.l}</button>
      ))}
    </div>
  );
  const Sel = ({value,onChange,opts}) => (
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{border:`1px solid ${T.line}`,borderRadius:8,padding:"7px 10px",fontSize:12.5,background:T.card,color:T.sub,fontWeight:600}}>
      {opts.map(o=> <option key={o} value={o}>{o==="Tous"?L.all:o}</option>)}
    </select>
  );
  const PBtn = ({onClick,disabled,children,accent}) => (
    <button onClick={onClick} disabled={disabled} style={{border:`1px solid ${T.line}`,background:T.card,
      color:disabled?T.ghost:accent,borderRadius:8,padding:"4px 10px",fontSize:13,fontWeight:700,
      cursor:disabled?"default":"pointer",minWidth:30}}>{children}</button>
  );
  const Pager = ({page,total,onPage,accent=PROC_ACCENT}) => {
    if (total<=1) return null;
    const go=(p)=>onPage(Math.max(1,Math.min(total,p)));
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:16}}>
        <PBtn onClick={()=>go(1)} disabled={page===1} accent={accent}>«</PBtn>
        <PBtn onClick={()=>go(page-1)} disabled={page===1} accent={accent}>‹</PBtn>
        <span style={{fontSize:12.5,color:T.sub,fontWeight:600,padding:"0 10px",whiteSpace:"nowrap"}}>Page {page} / {total}</span>
        <PBtn onClick={()=>go(page+1)} disabled={page===total} accent={accent}>›</PBtn>
        <PBtn onClick={()=>go(total)} disabled={page===total} accent={accent}>»</PBtn>
      </div>
    );
  };
  const Dot = ({c}) => <span style={{display:"inline-block",width:8,height:8,borderRadius:8,background:c,marginRight:6,verticalAlign:"middle"}}/>;
  const QChip = ({q}) => q ? <Chip label={q} color={PR_QUAL[q]||T.info}/> : <span style={{color:T.ghost}}>—</span>;
  const nameOf = (it) => lang==="en" ? (it.name_en||it.name_fr||it.name||"?") : (it.name_fr||it.name||"?");
  const hexA = (hex,a) => { const h=hex.replace("#",""); return `rgba(${parseInt(h.slice(0,2),16)},${parseInt(h.slice(2,4),16)},${parseInt(h.slice(4,6),16)},${a})`; };

  // ===================== ÉTAGE 1 — FOOD GLOBAL =====================
  const ft = food.totals;
  const top1 = (food.suppliers||[])[0];
  const top1pct = top1 && ft.spend ? Math.round(top1.spend/ft.spend*100) : 0;
  const driftN = (food.products||[]).filter(p=>p.variance>0.10).length;
  // ===================== ÉTAGE 2 — PROTEINS =====================
  const k = pr.kpi;
  const qmix = pr.quality_mix||[], smix = pr.sub_mix||[], ppt = pr.price_per_tier||{};
  const prem = qmix.find(x=>x.quality==="Premium") || {pct:0,sku_pct:0};
  const prSpend = qmix.reduce((s,x)=>s+x.spend,0);
  let maxCell=1; PR_SORDER.forEach(s=>PR_QORDER.forEach(q=>{maxCell=Math.max(maxCell,(pr.matrix?.[s]?.[q])||0);}));
  const maxSeg = Math.max(...(pr.segments||[]).map(s=>s.amount),1);
  const maxMix = Math.max(...qmix.map(x=>x.spend), ...smix.map(x=>x.spend), 1);
  const pItems = (pr.items||[])
    .filter(it=> pfSub==="Tous"  || it.sub===pfSub)
    .filter(it=> pfQual==="Tous" || it.quality===pfQual)
    .filter(it=> !pQ || nameOf(it).toLowerCase().includes(pQ.toLowerCase()) || (it.supplier||"").toLowerCase().includes(pQ.toLowerCase()))
    .sort((a,b)=>b.amount-a.amount);
  const pTotal = pItems.reduce((s,it)=>s+it.amount,0);
  const pPages = Math.max(1, Math.ceil(pItems.length / PER));
  const pPageC = Math.min(pPage, pPages);
  const pRows  = pItems.slice((pPageC-1)*PER, pPageC*PER);
  const poLink = (it)=> it.po_id ? `${PR_ODOO}/web#id=${it.po_id}&model=purchase.order&view_type=form` : null;

  const MixRow = ({label, color, spend, pct, skuPct}) => (
    <div style={{marginBottom:11}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
        <span style={{fontSize:12.5,fontWeight:700,color:T.ink}}><Dot c={color}/>{label}</span>
        <span style={{fontSize:12,color:T.hint}}><b style={{color:T.ink}}>{pct}%</b> · {fmtK(spend)}{skuPct!=null && <span style={{color:T.ghost}}> · {skuPct}% SKU</span>}</span>
      </div>
      <Bar value={spend} max={maxMix} color={color} height={9}/>
    </div>
  );

  // ── SEG Preferred (First) vs Non-preferred (Second) ──
  const pf = food.preferred || {};
  const prefSuppliers = (food.suppliers||[]).slice().sort((a,b)=>(b.spend||0)-(a.spend||0));
  const segLabel = (s) => s==="first" ? "Preferred (First)" : "Non-preferred (Second)";
  const exportPrefXls = () => {
    const H = lang==="fr"
      ? ["Fournisseur","Classement SEG","Dépense HT (CHF)","Nb PO"]
      : ["Supplier","SEG tier","Spend excl. VAT (CHF)","PO count"];
    const rows = prefSuppliers.map(s => [s.name, segLabel(s.seg), Math.round(s.spend||0), s.pos||0]);
    const pre = [
      {label:(lang==="fr"?"Résumé — depuis":"Summary — since")+" "+(food.since||""), val:null},
      {label:"Preferred (First)",     val:`${Math.round(pf.preferred_spend||0)} (${pf.preferred_pct||0}%)`},
      {label:"Non-preferred (Second)",val:`${Math.round(pf.non_preferred_spend||0)} (${pf.non_preferred_pct||0}%)`},
    ];
    dlXls(`preferred_suppliers_${(food.since||"").replace(/-/g,"")}`, H, rows, pre);
  };
  const exportProtXls = () => {
    const H = lang==="fr"
      ? ["Article","Sous-catégorie","Qualité","Volume","Unité","Prix moyen (CHF)","Fournisseur","Nom fournisseur","Montant HT (CHF)","PO"]
      : ["Item","Sub-category","Quality","Volume","Unit","Avg price (CHF)","Supplier","Supplier product","Amount excl. VAT (CHF)","PO"];
    const rows = pItems.map(it => [nameOf(it), it.sub||"", it.quality||"", Math.round(it.qty||0), it.uom||"",
      it.unit_price||0, it.supplier||"", it.supplier_product||"", Math.round(it.amount||0), (it.pos||[]).map(p=>p.ref).join(", ")]);
    dlXls(`proteins_by_item_${(food.since||"").replace(/-/g,"")}`, H, rows);
  };
  const exportProtSummaryXls = () => {
    const esc = v => String(v==null?"":v).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const th = t => `<tr><td colspan="4" style="background:#1f3b5b;color:#fff"><b>${esc(t)}</b></td></tr>`;
    const tr = (...c)=>`<tr>${c.map(x=>typeof x==="number"?`<td style="text-align:right">${Math.round(x)}</td>`:`<td>${esc(x)}</td>`).join("")}</tr>`;
    const bl = `<tr><td colspan="4"></td></tr>`;
    const fr = lang==="fr";
    let b = th((fr?"Proteins — synthèse · depuis ":"Proteins — summary · since ")+(food.since||""));
    b += tr(fr?"Couverture qualité (%)":"Quality coverage (%)", k.quality_coverage_pct||0);
    b += tr(fr?"SKU actifs":"Active SKU", k.active_products||0);
    b += tr(fr?"Dépense PO HT (CHF)":"PO spend excl. VAT (CHF)", k.po_amount_ht||0);
    b += tr(fr?"Intensité Premium (%)":"Premium intensity (%)", prem.pct||0);
    b += tr(fr?"Prix moyen /kg — Premium":"Avg price /kg — Premium", ppt.Premium||0);
    b += tr(fr?"Prix moyen /kg — Standard":"Avg price /kg — Standard", ppt.Standard||0);
    b += bl + th(fr?"Mix dépense par qualité":"Spend mix by quality");
    b += tr(fr?"Qualité":"Quality","%","CHF","% SKU");
    qmix.forEach(x=> b+= tr(x.quality, x.pct, Math.round(x.spend), x.sku_pct));
    b += bl + th(fr?"Mix dépense par sous-catégorie":"Spend mix by sub-category");
    b += tr(fr?"Sous-catégorie":"Sub-category","%","CHF","% SKU");
    smix.forEach(x=> b+= tr(x.sub, x.pct, Math.round(x.spend), x.sku_pct));
    b += bl + th(fr?"Répartition SKU (sous-cat × qualité)":"SKU breakdown (sub-cat × quality)");
    b += tr(fr?"Sous-catégorie":"Sub-category","Standard","Premium","Total");
    PR_SORDER.forEach(s=>{ const row=(pr.matrix&&pr.matrix[s])||{}; const st=row["Standard"]||0, pm=row["Premium"]||0; b+= tr(s, st, pm, st+pm); });
    b += bl + th(fr?"Dépense par segment (CHF)":"Spend by segment (CHF)");
    b += tr("Segment","CHF","","");
    (pr.segments||[]).forEach(s=> b+= tr(`${s.sub} / ${s.quality}`, Math.round(s.amount), "", ""));
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1">${b}</table></body></html>`;
    const blob = new Blob(["\ufeff", html], {type:"application/vnd.ms-excel;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `proteins_summary_${(food.since||"").replace(/-/g,"")}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* ===== en-tête page ===== */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:9}}>
            <span style={{width:10,height:10,borderRadius:10,background:PROC_ACCENT}}/>
            <span style={{fontSize:18,fontWeight:800,color:T.ink,letterSpacing:"-.01em"}}>{L.title}</span>
          </div>
          <div style={{fontSize:12,color:T.hint,marginTop:3,marginLeft:19}}>
            {L.sub} {PP.since}{PP.until ? ` → ${PP.until}` : ""}
            {PP.live && <span style={{color:PROC_ACCENT,fontWeight:700}}> · live</span>}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <Seg value={period} onChange={setPeriod}
               opts={PROC_PERIODS.map(x=>({v:x.id,l:lang==="fr"?x.fr:x.en}))}/>
          <Seg value={lang} onChange={setLang} opts={[{v:"fr",l:"FR"},{v:"en",l:"EN"}]}/>
        </div>
      </div>

      {/* ===== ÉTAGE 1 — bandeau global ===== */}
      <div style={{display:"flex",alignItems:"center",gap:9,margin:"4px 0 12px"}}>
        <span style={{fontSize:12,fontWeight:800,letterSpacing:".06em",textTransform:"uppercase",color:PROC_ACCENT}}>{L.g_title}</span>
        <span style={{fontSize:11.5,color:T.hint}}>· {L.g_sub}</span>
      </div>
      <div style={{display:"flex",gap:11,padding:"12px 14px",background:hexA(T.info,0.06),border:`1px solid ${hexA(T.info,0.28)}`,borderRadius:10,marginBottom:10,lineHeight:1.55}}>
        <span style={{color:T.info,fontWeight:800,fontSize:14,lineHeight:1.2}}>ⓘ</span>
        <div style={{fontSize:12.5,color:T.sub}}><b style={{color:T.ink}}>{L.lensTitle}</b> {L.lensBody}</div>
      </div>

      {/* ── périmètre chiffré : food + non-food exclu = brut ── */}
      {food.totals && food.totals.gross_spend != null && (
        <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:14,
                     padding:"10px 14px",marginBottom:16,borderRadius:10,
                     background:hexA(T.ink,0.03),border:`1px solid ${T.line}`}}>
          <span style={{fontSize:10,fontWeight:800,letterSpacing:".08em",
                        textTransform:"uppercase",color:T.hint}}>{L.scopeTitle}</span>
          <span style={{fontSize:12.5,color:T.sub}}>
            {L.scopeGross} <b style={{color:T.ink}}>{fmtK(food.totals.gross_spend)}</b>
          </span>
          <span style={{color:T.hint}}>−</span>
          <span style={{fontSize:12.5,color:T.sub}}>
            {L.scopeExcl} <b style={{color:PROC_ACCENT}}>{fmtK(food.totals.excluded_spend)}</b>
            <span style={{color:T.hint}}> ({food.totals.excluded_pct}%)</span>
            {(food.scope?.excluded_suppliers||[]).length>0 &&
              <span style={{color:T.hint}}> · {food.scope.excluded_suppliers.map(x=>x.name).join(", ")}</span>}
          </span>
          <span style={{color:T.hint}}>=</span>
          <span style={{fontSize:12.5,color:T.sub}}>
            {L.scopeFood} <b style={{color:T.ok}}>{fmtK(food.totals.spend)}</b>
          </span>
          <div style={{flex:1}}/>
          <span style={{fontSize:11.5,color:T.hint}}>
            {food.sent_only ? L.scopeSent : L.scopeAllPo}
          </span>
        </div>
      )}
      <div style={{display:"grid",gap:14,gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",marginBottom:16}}>
        <Stat label={L.kSpend} value={fmtK(ft.spend)} sub={L.kSpendSub} tone={PROC_ACCENT}/>
        <Stat label={L.kItems} value={fmtN(ft.distinct)} sub={`${ft.pos} ${L.kPos} · ${L.kItemsSub}`}/>
        <Stat label={L.kSup} value={fmtN(ft.suppliers)} sub={L.kSupSub}/>
        <Stat label={L.kConc} value={`${top1pct} %`} sub={top1?`${L.kConcSub} ${top1.name}`:"—"} tone={top1pct>=40?T.warn:T.ink}/>
        <Stat label={L.kDrift} value={fmtN(driftN)} sub={L.kDriftSub} tone={driftN>0?T.warn:T.ok}/>
      </div>

      {/* ── SEG Preferred vs Non-preferred ── */}
      {pf.preferred_spend!=null && (
      <Card style={{marginBottom:24,padding:"20px 24px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap",marginBottom:14}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:T.ink}}>{lang==="fr"?"Fournisseurs préférés SEG · Preferred vs Non-preferred":"SEG suppliers · Preferred vs Non-preferred"}</div>
            <div style={{fontSize:12,color:T.hint,marginTop:2}}>{lang==="fr"?"Dépense food HT par classement SEG (liste First 2026) · hors Webstar":"Food spend excl. VAT by SEG tier (2026 First list) · excl. Webstar"}</div>
          </div>
          <button onClick={exportPrefXls} className="no-print"
            style={{border:`1px solid ${T.line}`,background:T.card,color:T.sub,borderRadius:8,
              padding:"7px 13px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
            ⬇ Export XLS
          </button>
        </div>
        {(()=>{ const a=pf.preferred_spend||0,b=pf.non_preferred_spend||0,t=(a+b)||1; return (
          <>
          <div style={{display:"flex",height:26,borderRadius:7,overflow:"hidden",border:`1px solid ${T.line}`,marginBottom:10}}>
            <div style={{width:`${100*a/t}%`,background:T.ok,minWidth:a>0?28:0}}/>
            <div style={{width:`${100*b/t}%`,background:T.warn,minWidth:b>0?28:0}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:16}}>
            <div><Dot c={T.ok}/><b style={{color:T.ink}}>Preferred</b> <span style={{color:T.hint}}>· {fmtK(a)} · {pf.preferred_pct}%</span></div>
            <div><Dot c={T.warn}/><b style={{color:T.ink}}>Non-preferred</b> <span style={{color:T.hint}}>· {fmtK(b)} · {pf.non_preferred_pct}%</span></div>
          </div>
          </>
        );})()}
        <div style={{display:"grid",gap:16,gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))"}}>
          <div>
            <div style={{fontSize:10.5,letterSpacing:".05em",textTransform:"uppercase",color:T.ok,fontWeight:800,marginBottom:8}}>Preferred (First) · {(pf.first_suppliers||[]).length}</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {(pf.first_suppliers||[]).map((n,i)=>(
                <span key={i} style={{fontSize:12,padding:"3px 9px",borderRadius:7,background:hexA(T.ok,0.12),color:T.ink,border:`1px solid ${hexA(T.ok,0.3)}`}}>{n}</span>
              ))}
              {(pf.first_suppliers||[]).length===0 && <span style={{fontSize:12,color:T.hint}}>—</span>}
            </div>
          </div>
          <div>
            <div style={{fontSize:10.5,letterSpacing:".05em",textTransform:"uppercase",color:T.warn,fontWeight:800,marginBottom:8}}>Non-preferred (Second) · {(pf.second_suppliers||[]).length}</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {(pf.second_suppliers||[]).map((n,i)=>(
                <span key={i} style={{fontSize:12,padding:"3px 9px",borderRadius:7,background:hexA(T.warn,0.12),color:T.ink,border:`1px solid ${hexA(T.warn,0.3)}`}}>{n}</span>
              ))}
              {(pf.second_suppliers||[]).length===0 && <span style={{fontSize:12,color:T.hint}}>—</span>}
            </div>
          </div>
        </div>
        <div style={{fontSize:11,color:T.hint,marginTop:14,lineHeight:1.5}}>{lang==="fr"?"Amstein, Hiestand & Transgourmet sont fournisseurs First SEG mais commandés hors système sur la période (donc CHF 0 ici).":"Amstein, Hiestand & Transgourmet are SEG First suppliers but ordered off-system in this period (hence CHF 0 here)."}</div>
      </Card>
      )}

      {/* Rapport achats global par article — RETIRÉ le 04/09/2026 : il faisait
          doublon avec le classement de FOOD INVOICES, en moins fiable (PO =
          intention, facture = preuve). Procurement traite désormais la qualité
          et la conformité, pas les montants par article. */}

      {/* ===== ÉTAGE 2 — section Proteins ===== */}
      <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0 14px",paddingTop:8,borderTop:`2px solid ${hexA(PR_ACCENT,0.18)}`}}>
        <span style={{width:9,height:9,borderRadius:9,background:PR_ACCENT}}/>
        <span style={{fontSize:12,fontWeight:800,letterSpacing:".06em",textTransform:"uppercase",color:PR_ACCENT}}>{L.p_band}</span>
        <span style={{fontSize:11.5,color:T.hint}}>· {L.p_sub}</span>
        <button onClick={exportProtSummaryXls} className="no-print"
          style={{marginLeft:"auto",border:`1px solid ${T.line}`,background:T.card,color:T.sub,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>⬇ Export XLS</button>
      </div>

      {/* KPIs proteins */}
      <div style={{display:"grid",gap:14,gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",marginBottom:16}}>
        <Stat label={L.kCover} value={`${k.quality_coverage_pct} %`} sub={`${fmtN(k.active_products)} ${L.kCoverSub}`} tone={T.ok}/>
        <Stat label={L.kSpend} value={fmtK(k.po_amount_ht)} sub={`${fmtN(k.po_products)} ${L.kItemsSub}`} tone={PR_ACCENT}/>
        <Stat label={L.kPremInt} value={`${prem.pct}%`} sub={`${L.kPremIntSub} ${prem.sku_pct}${L.kPremIntSku}`} tone={prem.pct>prem.sku_pct?T.warn:T.ink}/>
        <Stat label={L.ppt} value={`${fmtN(ppt.Premium||0)}`} sub={`Premium · Std ${fmtN(ppt.Standard||0)}`}/>
      </div>

      {/* mix qualité + mix sous-cat */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
        <Card>
          <div style={{fontSize:14,fontWeight:800,color:T.ink}}>{L.qmix}</div>
          <div style={{fontSize:12,color:T.hint,marginTop:2,marginBottom:14}}>{L.qmixSub}</div>
          {PR_QORDER.slice().reverse().map(q=>{ const r=qmix.find(x=>x.quality===q); return r?
            <MixRow key={q} label={q} color={PR_QUAL[q]} spend={r.spend} pct={r.pct} skuPct={r.sku_pct}/> : null; })}
        </Card>
        <Card>
          <div style={{fontSize:14,fontWeight:800,color:T.ink}}>{L.smix}</div>
          <div style={{fontSize:12,color:T.hint,marginTop:2,marginBottom:14}}>{L.smixSub}</div>
          {PR_SORDER.map(s=>{ const r=smix.find(x=>x.sub===s); return r?
            <MixRow key={s} label={s} color={PR_SUB[s]} spend={r.spend} pct={r.pct} skuPct={r.sku_pct}/> : null; })}
        </Card>
      </div>

      {/* matrice + segments */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
        <Card>
          <div style={{fontSize:14,fontWeight:800,color:T.ink}}>{L.matrix}</div>
          <div style={{fontSize:12,color:T.hint,marginTop:2}}>{L.matrixSub}</div>
          <table style={{width:"100%",borderCollapse:"separate",borderSpacing:"4px",marginTop:14}}>
            <thead><tr>
              <th style={{width:92}}/>
              {PR_QORDER.map(q=> <th key={q} style={{textAlign:"center",paddingBottom:4}}><QChip q={q}/></th>)}
              <th style={{textAlign:"right",fontSize:11,fontWeight:700,color:T.hint,paddingBottom:4}}>Total</th>
            </tr></thead>
            <tbody>
              {PR_SORDER.map(s=>{ const row=pr.matrix?.[s]||{}; const tot=PR_QORDER.reduce((a,q)=>a+(row[q]||0),0);
                return (<tr key={s}>
                  <td style={{fontSize:13,fontWeight:700,color:T.ink,whiteSpace:"nowrap"}}><Dot c={PR_SUB[s]}/>{s}</td>
                  {PR_QORDER.map(q=>{ const v=row[q]||0; const it=0.10+0.55*(v/maxCell);
                    return <td key={q} style={{padding:0}}><div style={{borderRadius:8,padding:"12px 0",textAlign:"center",fontSize:15,fontWeight:800,color:T.ink,background:hexA(PR_SUB[s],it)}}>{v}</div></td>; })}
                  <td style={{textAlign:"right",fontWeight:800,fontSize:14,color:T.sub,paddingLeft:8}}>{tot}</td>
                </tr>); })}
              <tr>
                <td style={{fontSize:12,fontWeight:700,color:T.hint,paddingTop:6}}>Total</td>
                {PR_QORDER.map(q=> <td key={q} style={{textAlign:"center",fontWeight:700,fontSize:13,color:T.hint,paddingTop:6}}>{PR_SORDER.reduce((a,s)=>a+((pr.matrix?.[s]?.[q])||0),0)}</td>)}
                <td style={{textAlign:"right",fontWeight:800,fontSize:14,color:T.ink,paddingTop:6}}>{fmtN(k.active_products)}</td>
              </tr>
            </tbody>
          </table>
        </Card>
        <Card>
          <div style={{fontSize:14,fontWeight:800,color:T.ink}}>{L.seg}</div>
          <div style={{fontSize:12,color:T.hint,marginTop:2}}>{L.segSub}</div>
          <div style={{marginTop:14}}>
            {(pr.segments||[]).map((s,i)=>(
              <div key={i} style={{display:"grid",gridTemplateColumns:"165px 1fr 92px",alignItems:"center",gap:10,marginBottom:11}}>
                <div style={{fontSize:12.5,fontWeight:600,color:T.ink,whiteSpace:"nowrap"}}>
                  <Dot c={PR_SUB[s.sub]||T.hint}/>{s.sub} <span style={{color:T.ghost}}>/</span> <span style={{color:PR_QUAL[s.quality]||T.info,fontWeight:700}}>{s.quality}</span>
                </div>
                <Bar value={s.amount} max={maxSeg} color={PR_SUB[s.sub]||PR_ACCENT} height={10}/>
                <div style={{fontSize:13,fontWeight:800,textAlign:"right",color:T.ink}}>{fmtK(s.amount)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* table proteins (avec qualité) */}
      <Card style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",gap:12,flexWrap:"wrap",marginBottom:12}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:T.ink}}>{L.tblP}</div>
            <div style={{fontSize:12,color:T.hint,marginTop:2}}>{L.tblPsub} · {pItems.length} · {fmtK(pTotal)}</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <input value={pQ} onChange={e=>{setPQ(e.target.value); setPPage(1);}} placeholder={L.search}
              style={{border:`1px solid ${T.line}`,borderRadius:8,padding:"7px 12px",fontSize:12.5,width:200,color:T.ink}}/>
            <Sel value={pfSub} onChange={v=>{setPfSub(v); setPPage(1);}} opts={["Tous",...PR_SORDER]}/>
            <Sel value={pfQual} onChange={v=>{setPfQual(v); setPPage(1);}} opts={["Tous",...PR_QORDER]}/>
            <button onClick={exportProtXls} className="no-print"
              style={{border:`1px solid ${T.line}`,background:T.card,color:T.sub,borderRadius:8,padding:"7px 13px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>⬇ Export XLS</button>
          </div>
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead><tr>
              {[L.cArt,L.cVol,L.cUnit,L.cBrand,L.cSup,L.cAmt,"PO"].map((h,i)=>(
                <th key={i} style={{textAlign:(i===1||i===2||i===5)?"right":"left",fontSize:10.5,letterSpacing:".05em",
                  textTransform:"uppercase",color:T.hint,fontWeight:700,padding:"0 12px 10px",borderBottom:`1px solid ${T.line}`}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {pRows.map((it,i)=>(
                <tr key={i} style={{borderBottom:`1px solid ${T.grid}`}}>
                  <td style={{padding:"12px",verticalAlign:"top"}}>
                    <div style={{fontWeight:700,color:T.ink}}>{nameOf(it)}</div>
                    <div style={{fontSize:11,color:T.hint,marginTop:2}}><Dot c={PR_SUB[it.sub]||T.hint}/>PROTEINS · {it.sub}</div>
                  </td>
                  <td style={{padding:"12px",textAlign:"right",whiteSpace:"nowrap",color:T.ink,verticalAlign:"top"}}>{fmtN(it.qty)} <span style={{color:T.hint,fontSize:11}}>{it.uom}</span></td>
                  <td style={{padding:"12px",textAlign:"right",whiteSpace:"nowrap",color:T.ink,verticalAlign:"top"}}>{fmtK(it.unit_price)}</td>
                  <td style={{padding:"12px",verticalAlign:"top"}}>
                    <QChip q={it.quality}/>
                    {it.supplier_product && <div style={{fontSize:11,color:T.hint,marginTop:3,maxWidth:190}}>{it.supplier_product}</div>}
                  </td>
                  <td style={{padding:"12px",color:T.sub,verticalAlign:"top"}}>{it.supplier}</td>
                  <td style={{padding:"12px",textAlign:"right",fontWeight:800,whiteSpace:"nowrap",color:T.ink,verticalAlign:"top"}}>{fmtK(it.amount)}</td>
                  <td style={{padding:"12px",verticalAlign:"top",minWidth:90}}>
                    {(it.pos&&it.pos.length)
                      ? <div style={{display:"flex",flexWrap:"wrap",gap:"4px 8px",maxWidth:150}}>
                          {it.pos.map((p,j)=>(
                            <a key={j} href={`${PR_ODOO}/web#id=${p.id}&model=purchase.order&view_type=form`} target="_blank" rel="noreferrer"
                               title={fmtK(p.amount)} style={{fontWeight:700,color:PR_ACCENT,textDecoration:"none",fontSize:12,whiteSpace:"nowrap"}}>{p.ref} ↗</a>
                          ))}
                        </div>
                      : <span style={{color:T.hint}}>—</span>}
                  </td>
                </tr>
              ))}
              {pItems.length===0 && <tr><td colSpan={7} style={{padding:24,textAlign:"center",color:T.hint}}>{L.empty}</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={pPageC} total={pPages} onPage={setPPage} accent={PR_ACCENT}/>
      </Card>

      {/* Analyse achats — repliée par défaut, ne charge qu'à l'ouverture */}
      <ProcurementAnalysis L={L} />

      <div style={{fontSize:11,color:T.hint,lineHeight:1.6}}>{L.source}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  INTÉGRATION :
//   1) dans NAV :   {id:"procurement", icon:"⊙", label:"Procurement"},
//   2) dans le switch de App() :   {tab==="procurement" && <ProcurementTab/>}
// ═══════════════════════════════════════════════════════════════════════════

// Statut Live / Closed déterminé par la date du jour.
// Trimestre calendaire : Q1 Jan-Mar · Q2 Avr-Juin · Q3 Juil-Sep · Q4 Oct-Déc
// Le trimestre courant s'affiche « Live », les précédents « Closed » — et ça
// bascule tout seul au changement de trimestre.
const CUR_Q = Math.floor(new Date().getMonth() / 3) + 1;
const qStat = (q) => (q === CUR_Q ? "Live" : "Closed");


// ═══════════════════════════════════════════════════════════════════════════
//  PRICE HISTORY — évolution du prix payé, produit par produit
//  Source : GET /dashboard/price_history (api.py 87 → table product_price_history
//  de prix_caa sur le 242). Un produit acheté 10 fois = 10 points datés.
//  Courbes en SVG, aucune dépendance ajoutée.
// ═══════════════════════════════════════════════════════════════════════════
const PH_MONO = "ui-monospace,SFMono-Regular,Menlo,monospace";
const PH_C = {
  up:   "#b3261e",   // le prix monte : mauvais côté achats
  down: "#0b7a5a",   // le prix baisse
  flat: "#7e7974",
};
const PH_L = {
  fr: { title:"Évolution des prix", sub:"Prix unitaire réellement facturé, achat par achat",
        rbBtn:"Recalculer", rbRun:"Recalcul…", rbBuilt:"Recalculé le", rbBuy:"achats jusqu'au",
        rbInv:"factures en base jusqu'au", rbStale:"des factures plus récentes attendent un recalcul",
        rbDone:(a,b,s)=>`Recalcul terminé : ${a} → ${b} lignes en ${s} s`, rbErr:"Recalcul impossible :",
        search:"Rechercher un produit…", allSup:"Tous les fournisseurs",
        spend:"Dépense", delta:"Variation", spread:"Amplitude", nbuy:"Nb d'achats",
        buys:"achats", last:"Dernier", range:"Min / Max", spent:"Dépensé",
        loading:"Chargement des prix…", empty:"Aucun produit acheté au moins deux fois.",
        susp:"écartés", suspT:"achats écartés de la courbe : la ligne de facture est incohérente et le doute subsiste",
        jump:"var. >10%", jumpT:"achats dont le prix bouge de plus de 10 % par rapport au précédent — ni corrigés ni écartés, à vérifier",
        onlyJump:"Sauts >10% seulement",
        xlsSummary:"Export XLS — résumé", xlsDetail:"Export XLS — détail",
        maxJump:"Saut max %",
        per:"Période", pAll:"Tout l'historique", pYtd:"Année en cours",
        p90:"90 derniers jours", p30:"30 derniers jours",
        pMonth:"Un mois…", pWeek:"Une semaine…", pRange:"Plage libre…",
        from:"du", to:"au", perInfo:"achats du",
        fixed:"corrigés", fixedT:"achats dont le prix a été recalé : qty × PU ≠ montant sur la facture, le prix retenu est celui qui colle au prix de référence du produit",
        err:"Historique indisponible", date:"Date", price:"Prix", qty:"Qté",
        total:"Total", inv:"Facture", minPts:"Achats min.", prods:"produits",
        avg:"moyenne", src:"source : prix_caa · table product_price_history" },
  en: { title:"Price history", sub:"Unit price actually invoiced, purchase by purchase",
        rbBtn:"Recompute", rbRun:"Recomputing…", rbBuilt:"Recomputed", rbBuy:"purchases up to",
        rbInv:"invoices in database up to", rbStale:"newer invoices are waiting for a recompute",
        rbDone:(a,b,s)=>`Recompute done: ${a} → ${b} rows in ${s} s`, rbErr:"Recompute failed:",
        search:"Search a product…", allSup:"All suppliers",
        spend:"Spend", delta:"Change", spread:"Spread", nbuy:"Purchases",
        buys:"purchases", last:"Latest", range:"Min / Max", spent:"Spent",
        loading:"Loading prices…", empty:"No product purchased at least twice.",
        susp:"dropped", suspT:"purchases dropped from the curve: the invoice line is inconsistent and the doubt remains",
        jump:"var. >10%", jumpT:"purchases whose price moves more than 10 % from the previous one — neither fixed nor dropped, worth a look",
        onlyJump:"Jumps >10% only",
        xlsSummary:"Export XLS — summary", xlsDetail:"Export XLS — detail",
        maxJump:"Max jump %",
        per:"Period", pAll:"All history", pYtd:"Year to date",
        p90:"Last 90 days", p30:"Last 30 days",
        pMonth:"A month…", pWeek:"A week…", pRange:"Custom range…",
        from:"from", to:"to", perInfo:"purchases from",
        fixed:"fixed", fixedT:"purchases whose price was realigned: qty × unit price ≠ line total, the retained price is the one matching the product reference price",
        err:"History unavailable", date:"Date", price:"Price", qty:"Qty",
        total:"Total", inv:"Invoice", minPts:"Min. purchases", prods:"products",
        avg:"average", src:"source: prix_caa · table product_price_history" },
};
const phFmt = (n, d = 2) =>
  (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toFixed(d);
const phDay = (s) => (s ? s.slice(8,10) + "." + s.slice(5,7) : "");
const phCol = (d) => (d > 1.5 ? PH_C.up : d < -1.5 ? PH_C.down : PH_C.flat);

// Échelle verticale avec plancher : si l'amplitude réelle des prix est
// inférieure à 2 % du niveau, on force une fenêtre de 2 % centrée sur la
// moyenne. Sans ça, un prix stable à 17.3081 / 17.3122 (quatre millièmes de
// centime, de simples arrondis de facture) dessine des montagnes russes.
const PH_FLOOR = 0.02;
function phScale(lo, hi, withPad) {
  const mid = (lo + hi) / 2;
  const floor = Math.max(Math.abs(mid) * PH_FLOOR, 0.02);
  if (hi - lo < floor) return [mid - floor / 2, mid + floor / 2];
  const pad = withPad ? (hi - lo) * 0.15 : 0;
  return [Math.max(0, lo - pad), hi + pad];
}

function PHSpark({ series, w = 128, h = 32 }) {
  const pts = series.map(s => s.p);
  const raw = [Math.min(...pts), Math.max(...pts)];
  // plancher d'échelle : sous 2 % d'amplitude, on n'agrandit pas le bruit
  // d'arrondi. Un prix stable doit se voir comme une droite.
  const [lo, hi] = phScale(raw[0], raw[1]);
  const span = (hi - lo) || 1;
  const stepX = pts.length > 1 ? w / (pts.length - 1) : 0;
  const Y = (p) => h - 3 - ((p - lo) / span) * (h - 6);
  const d = pts.map((p,i) => `${i?"L":"M"}${(i*stepX).toFixed(1)},${Y(p).toFixed(1)}`).join(" ");
  const col = phCol(((pts[pts.length-1] - pts[0]) / (pts[0] || 1)) * 100);
  return (
    <svg width={w} height={h} style={{display:"block"}}>
      <path d={`${d} L${w},${h} L0,${h} Z`} fill={col} opacity=".08"/>
      <path d={d} fill="none" stroke={col} strokeWidth="1.6" strokeLinejoin="round"/>
      <circle cx={(pts.length-1)*stepX} cy={Y(pts[pts.length-1])} r="2.6" fill={col}/>
    </svg>
  );
}

function PHChart({ series, uom, L }) {
  const [hov, setHov] = useState(null);
  const W = 760, H = 250, PL = 54, PB = 28, PT = 14, PR = 14;
  const pts = series.map(s => s.p);
  const [yLo, yHi] = phScale(Math.min(...pts), Math.max(...pts), true);
  const iw = W - PL - PR, ih = H - PT - PB;
  const X = (i) => PL + (series.length > 1 ? (i * iw) / (series.length - 1) : iw / 2);
  const Y = (p) => PT + ih - ((p - yLo) / ((yHi - yLo) || 1)) * ih;
  const line = series.map((s,i) => `${i?"L":"M"}${X(i).toFixed(1)},${Y(s.p).toFixed(1)}`).join(" ");
  const col = phCol(((pts[pts.length-1] - pts[0]) / (pts[0] || 1)) * 100);
  const avg = pts.reduce((a,b) => a+b, 0) / pts.length;
  const every = Math.max(1, Math.ceil(series.length / 12));
  return (
    <div style={{position:"relative"}}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{display:"block"}}
           onMouseLeave={() => setHov(null)}>
        {[yLo,(yLo+yHi)/2,yHi].map((v,i) => (
          <g key={i}>
            <line x1={PL} x2={W-PR} y1={Y(v)} y2={Y(v)} stroke={T.line}/>
            <text x={PL-8} y={Y(v)+4} textAnchor="end" fontSize="10"
                  fontFamily={PH_MONO} fill={T.hint}>
              {phFmt(v, (yHi - yLo) < 0.5 ? 3 : 2)}
            </text>
          </g>
        ))}
        <line x1={PL} x2={W-PR} y1={Y(avg)} y2={Y(avg)} stroke={T.ghost}
              strokeDasharray="4 4" strokeWidth="1"/>
        <text x={W-PR} y={Y(avg)-5} textAnchor="end" fontSize="9.5"
              fontFamily={PH_MONO} fill={T.ghost}>{L.avg} {phFmt(avg)}</text>
        <path d={`${line} L${X(series.length-1)},${PT+ih} L${PL},${PT+ih} Z`}
              fill={col} opacity=".07"/>
        <path d={line} fill="none" stroke={col} strokeWidth="2" strokeLinejoin="round"/>
        {series.map((s,i) => (
          <g key={i}>
            <circle cx={X(i)} cy={Y(s.p)} r={hov===i?5:3}
                    fill={hov===i?"#fff":col} stroke={col} strokeWidth="1.6"/>
            <rect x={X(i)-15} y={PT} width="30" height={ih} fill="transparent"
                  onMouseEnter={() => setHov(i)}/>
          </g>
        ))}
        {series.map((s,i) => (i % every === 0 || i === series.length-1) ? (
          <text key={"x"+i} x={X(i)} y={H-9} textAnchor="middle" fontSize="9.5"
                fontFamily={PH_MONO} fill={T.hint}>{phDay(s.d)}</text>
        ) : null)}
      </svg>
      {hov !== null && (
        <div style={{position:"absolute",top:6,right:6,background:T.card,
          border:`1px solid ${T.line}`,borderRadius:8,padding:"8px 11px",
          fontSize:11.5,color:T.ink,lineHeight:1.7,boxShadow:T.sh2,pointerEvents:"none"}}>
          <div style={{color:T.hint,fontFamily:PH_MONO}}>{series[hov].d}</div>
          <div style={{fontSize:14,fontWeight:800,fontFamily:PH_MONO}}>
            {phFmt(series[hov].p)} <span style={{fontSize:10,fontWeight:600,color:T.hint}}>CHF{uom?" / "+uom:""}</span>
          </div>
          <div style={{color:T.sub}}>{L.qty} {phFmt(series[hov].q,3)} · {L.total} {phFmt(series[hov].t)}</div>
          {series[hov].inv && <div style={{color:T.hint,fontFamily:PH_MONO}}>{L.inv} {series[hov].inv}</div>}
        </div>
      )}
    </div>
  );
}

// ── périodes ────────────────────────────────────────────────────────────────
//  L'API accepte since / until. Les préréglages calculent ces deux bornes ;
//  « mois », « semaine » et « plage » les laissent piloter par l'utilisateur.
const phToday = () => new Date().toISOString().slice(0, 10);
const phMinus = (days) => {
  const d = new Date(); d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};
const phMonthBounds = (mk) => {           // "2026-07" -> ["2026-07-01","2026-07-31"]
  const [y, m] = mk.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return [`${mk}-01`, `${mk}-${String(last).padStart(2, "0")}`];
};
const phWeekBounds = (ws) => {            // lundi -> [lundi, dimanche]
  const d = new Date(ws + "T00:00:00");
  const end = new Date(d); end.setDate(end.getDate() + 6);
  return [ws, end.toISOString().slice(0, 10)];
};
const phMonthList = (from = "2025-05") => {
  const out = []; const now = new Date();
  let [y, m] = from.split("-").map(Number);
  while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth() + 1)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return out.reverse();
};
const PH_MLBL = ["janv.","févr.","mars","avr.","mai","juin",
                 "juil.","août","sept.","oct.","nov.","déc."];
const phMonthLabel = (mk) => {
  const [y, m] = mk.split("-"); return `${PH_MLBL[(+m) - 1]} ${y}`;
};

function PriceHistoryTab() {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState("");
  const [lang, setLang] = useState("fr");
  const [q, setQ]       = useState("");
  const [sup, setSup]   = useState(0);
  const [minPts, setMinPts] = useState(2);
  const [sort, setSort] = useState("spend");
  const [dir, setDir]   = useState("desc");            // tri asc / desc par colonne
  const [fVar, setFVar] = useState("all");             // filtre colonne Variation
  const [fMinP, setFMinP] = useState("");              // filtre Dernier prix min
  const [fMaxP, setFMaxP] = useState("");              // filtre Dernier prix max
  const [fMinS, setFMinS] = useState("");              // filtre Dépensé min
  const sortBy = k => { if (sort === k) setDir(dir === "desc" ? "asc" : "desc");
                        else { setSort(k); setDir(k === "name" || k === "supplier" ? "asc" : "desc"); } };
  const [period, setPeriod] = useState("ytd");
  const [since, setSince]   = useState("2026-01-01");
  const [until, setUntil]   = useState("2026-12-31");
  const [monthSel, setMonthSel] = useState(phMonthList()[0]);
  const [weekSel, setWeekSel]   = useState("");
  const [onlyJump, setOnlyJump] = useState(false);
  const [open, setOpen] = useState(null);
  const [reload, setReload] = useState(0);             // relit la table après un recalcul
  const [rb, setRb] = useState(null);                  // état du bouton Recalculer
  const L = PH_L[lang];

  // Recalcul de product_price_history (rebuild_product_price_history sur le 242)
  const rebuild = () => {
    setRb({ busy: true });
    fetch(`${API}/dashboard/price_history/rebuild`, { method: "POST" })
      .then(r => r.json())
      .then(j => { setRb(j.ok ? { done: j } : { error: j.error || "?" });
                   if (j.ok) setReload(n => n + 1); })
      .catch(e => setRb({ error: String(e) }));
  };
  const fmtDT = v => { if (!v) return "—"; const d = new Date(v);
    return isNaN(d) ? v : d.toLocaleString("fr-CH", { day:"2-digit", month:"2-digit", year:"numeric",
      hour:"2-digit", minute:"2-digit" }); };
  const fmtD = v => (v ? v.split("-").reverse().join(".") : "—");

  const applyPeriod = (p, mk, wk) => {
    setPeriod(p);
    if (p === "all")  { setSince("2025-01-01"); setUntil("2030-12-31"); }
    if (p === "ytd")  { setSince(`${new Date().getFullYear()}-01-01`); setUntil("2030-12-31"); }
    if (p === "d90")  { setSince(phMinus(90)); setUntil(phToday()); }
    if (p === "d30")  { setSince(phMinus(30)); setUntil(phToday()); }
    if (p === "month"){ const [a, b] = phMonthBounds(mk || monthSel); setSince(a); setUntil(b); }
    if (p === "week") {
      const w = wk || weekSel;
      if (w) { const [a, b] = phWeekBounds(w); setSince(a); setUntil(b); }
    }
    // "range" : les deux champs de date pilotent directement since/until
  };

  useEffect(() => {
    let dead = false;
    setData(null); setErr("");
    fetch(`${API}/dashboard/price_history?since=${since}&until=${until}&min_points=${minPts}&limit=20000`)
      .then(r => r.json())
      .then(j => { if (dead) return; if (j.error) setErr(j.error); else setData(j); })
      .catch(e => { if (!dead) setErr(String(e)); });
    return () => { dead = true; };
  }, [since, until, minPts, reload]);

  const rows = (() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    // un « saut » = plus de 10 % d'écart avec l'achat précédent du même
    // produit. Calculé ici, la série est déjà complète côté front.
    (data.products || []).forEach(p => {
      if (p.n_jump === undefined) {
        let n = 0, mx = 0;
        for (let i = 1; i < p.series.length; i++) {
          const a = p.series[i - 1].p, b = p.series[i].p;
          if (a > 0) {
            const v = (b - a) / a * 100;
            if (Math.abs(v) > Math.abs(mx)) mx = v;
            if (Math.abs(v) > 10) { n++; p.series[i].jp = true; }
          }
        }
        p.n_jump = n;
        p.max_jump = Number(mx.toFixed(2));
      }
    });
    const r = (data.products || []).filter(p =>
      (!sup || p.supplier_id === sup) &&
      (!onlyJump || p.n_jump > 0) &&
      (!needle || p.name.toLowerCase().includes(needle) ||
        (p.code || "").toLowerCase().includes(needle)));
    const num = v => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));
    const minP = num(fMinP), maxP = num(fMaxP), minS = num(fMinS);
    const r2 = r.filter(p => {
      const d = p.delta_pct || 0;
      if (fVar === "up"   && !(d > 0)) return false;
      if (fVar === "down" && !(d < 0)) return false;
      if (fVar === "flat" && d !== 0) return false;
      if (fVar === "j10"  && !(Math.abs(d) > 10)) return false;
      if (fVar === "j25"  && !(Math.abs(d) > 25)) return false;
      if (minP != null && !(p.last >= minP)) return false;
      if (maxP != null && !(p.last <= maxP)) return false;
      if (minS != null && !(p.spend >= minS)) return false;
      return true;
    });
    const val = p => ({ name: (p.name || "").toLowerCase(), supplier: (p.supplier || "").toLowerCase(),
      n: p.n, last: p.last, min: p.min, max: p.max, delta: p.delta_pct, absdelta: Math.abs(p.delta_pct || 0),
      jump: Math.abs(p.max_jump || 0), spread: p.spread_pct, spend: p.spend }[sort]);
    const sg = dir === "asc" ? 1 : -1;
    return [...r2].sort((a, b) => { const x = val(a), y = val(b);
      return (x > y ? 1 : x < y ? -1 : 0) * sg; });
  })();

  // Export XLS — résumé : une ligne par produit, tel que la liste l'affiche
  const exportSummary = () => {
    // « Variation % » = écart premier → dernier prix.
    // « Saut max % »  = plus gros écart entre DEUX achats consécutifs — c'est
    // sur celui-ci que porte le filtre, les deux peuvent donc diverger : un
    // produit qui monte de 15 % puis redescend de 15 % a un saut max de 15 %
    // et une variation totale de 0 %.
    const heads = ["Produit","Fournisseur","Code","Unité",
                   "Achats","Corrigés","Écartés","Sauts >10%",
                   "Prix min","Prix max","Amplitude",
                   "Premier prix","Dernier prix","Variation %", L.maxJump,
                   "Dépense CHF"];
    const data = rows.map(p => [
      p.name, p.supplier, p.code || "", p.uom || "",
      p.n, p.n_fixed || 0, p.n_suspect || 0, p.n_jump || 0,
      p.min, p.max, p.min > 0 ? Number((p.max / p.min).toFixed(2)) : 0,
      p.first, p.last, p.delta_pct, p.max_jump || 0, p.spend,
    ]);
    dlXls(`price_history_resume_${new Date().toISOString().slice(0,10)}`,
          heads, data,
          [{label:"Période", val:`${since} → ${until > "2029" ? phToday() : until}`},
           {label:"Références", val:`${rows.length} produits`},
           {label:"Variation %", val:"écart entre le premier et le dernier prix de la période"},
           {label:"Saut max %", val:"plus gros écart entre deux achats consécutifs — c'est lui que filtre « Sauts >10% »"},
           {label:"Source", val:"factures fournisseurs réconciliées (prix_caa)"}], 2,
          {title:"Culinary Arts Academy Switzerland — Évolution des prix",
           subtitle:`Établi le ${phToday()} · FX1 Labs · prix unitaire réellement facturé, achat par achat`,
           accent:"#1f3b5b", zebra:true});
  };

  // Export XLS — détail : une ligne par ACHAT, c'est l'historique complet
  const exportDetail = () => {
    const heads = ["Produit","Fournisseur","Code","Unité","Date","Facture",
                   "Prix","Variation %","Quantité","Montant CHF","Statut"];
    const data = [];
    rows.forEach(p => p.series.forEach(s => data.push([
      p.name, p.supplier, p.code || "", p.uom || "", s.d, s.inv || "",
      s.p, s.dp == null ? "" : s.dp, s.q, s.t, s.cf || "ok",
    ])));
    dlXls(`price_history_detail_${new Date().toISOString().slice(0,10)}`,
          heads, data,
          [{label:"Période", val:`${since} → ${until > "2029" ? phToday() : until}`},
           {label:"Volume", val:`${data.length} achats`},
           {label:"Statut", val:"ok = ligne de facture arithmétiquement vérifiée · corrigé = quantité ou prix recalé"},
           {label:"Source", val:"factures fournisseurs réconciliées (prix_caa)"}], 3,
          {title:"Culinary Arts Academy Switzerland — Prix payés, achat par achat",
           subtitle:`Établi le ${phToday()} · FX1 Labs`,
           accent:"#1f3b5b", zebra:true});
  };

  const inp = {background:T.card, border:`1px solid ${T.line}`, borderRadius:8,
    color:T.ink, padding:"8px 11px", fontSize:12.5, outline:"none"};
  const btnX = {...inp, cursor:"pointer", fontWeight:700, color:"#0b6ea8",
    borderColor:"rgba(11,110,168,.30)", background:"rgba(11,110,168,.06)",
    whiteSpace:"nowrap"};

  if (err)   return <Card><div style={{color:T.err,fontSize:13}}>{L.err} — {err}</div></Card>;
  if (!data) return <Card><div style={{color:T.hint,fontSize:13}}>{L.loading}</div></Card>;

  return (
    <div>
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",
          gap:14,flexWrap:"wrap",marginBottom:14}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:T.ink,letterSpacing:"-.02em"}}>{L.title}</div>
            <div style={{fontSize:12,color:T.hint,marginTop:3}}>
              {L.sub} · {data.count} {L.prods} · {L.perInfo} {since} → {until > "2029" ? phToday() : until}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginLeft:"auto"}}>
            {(() => {
              const f = (rb && rb.done && rb.done.fresh) || data.fresh || {};
              return (
                <div style={{fontSize:11.5,color:T.hint,textAlign:"right",lineHeight:1.5}}>
                  <div>{L.rbBuilt} <b style={{color:T.ink}}>{fmtDT(f.built_at)}</b>
                    {" · "}{L.rbBuy} <b style={{color:T.ink}}>{fmtD(f.last_purchase)}</b></div>
                  <div>{L.rbInv} {fmtD(f.last_invoice)}
                    {f.stale && <span style={{color:"#B45309",fontWeight:700}}> · {L.rbStale}</span>}</div>
                  {rb && rb.done && <div style={{color:"#15803D",fontWeight:700}}>
                    {L.rbDone((rb.done.rows_before ?? "—").toLocaleString("fr-CH"),
                              (rb.done.rows ?? "—").toLocaleString("fr-CH"), rb.done.seconds)}</div>}
                  {rb && rb.error && <div style={{color:"#B91C1C",fontWeight:700}}>{L.rbErr} {rb.error}</div>}
                </div>
              );
            })()}
            <button onClick={rebuild} disabled={rb && rb.busy}
              style={{fontSize:12,fontWeight:700,borderRadius:8,padding:"7px 14px",cursor:"pointer",
                border:`1px solid ${(data.fresh && data.fresh.stale) ? "#B45309" : T.line}`,
                background:(data.fresh && data.fresh.stale) ? "#FEF3C7" : "#fff",
                color:T.ink, opacity:(rb && rb.busy) ? .6 : 1}}>
              ↻ {(rb && rb.busy) ? L.rbRun : L.rbBtn}
            </button>
            <div onClick={() => setLang(lang==="fr"?"en":"fr")}
              style={{fontSize:11,fontWeight:700,color:T.hint,border:`1px solid ${T.line}`,
                borderRadius:999,padding:"3px 10px",cursor:"pointer"}}>
              {lang==="fr" ? "EN" : "FR"}
            </div>
          </div>
        </div>

        <div style={{display:"flex",gap:9,flexWrap:"wrap",marginBottom:14}}>
          <input style={{...inp,minWidth:220,flex:"1 1 220px"}} placeholder={L.search}
                 value={q} onChange={e => setQ(e.target.value)}/>
          <select style={inp} value={period} onChange={e => applyPeriod(e.target.value)}>
            <option value="all">{L.pAll}</option>
            <option value="ytd">{L.pYtd}</option>
            <option value="d90">{L.p90}</option>
            <option value="d30">{L.p30}</option>
            <option value="month">{L.pMonth}</option>
            <option value="week">{L.pWeek}</option>
            <option value="range">{L.pRange}</option>
          </select>

          {period === "month" && (
            <select style={inp} value={monthSel}
                    onChange={e => { setMonthSel(e.target.value); applyPeriod("month", e.target.value); }}>
              {phMonthList().map(mk => (
                <option key={mk} value={mk}>{phMonthLabel(mk)}</option>
              ))}
            </select>
          )}

          {period === "week" && (
            <input type="week" style={inp}
                   onChange={e => {
                     // "2026-W32" -> lundi de cette semaine
                     const [y, w] = e.target.value.split("-W").map(Number);
                     if (!y || !w) return;
                     const d = new Date(y, 0, 1 + (w - 1) * 7);
                     d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
                     const ws = d.toISOString().slice(0, 10);
                     setWeekSel(ws); applyPeriod("week", null, ws);
                   }}/>
          )}

          {period === "range" && (
            <>
              <label style={{...inp, display:"flex", alignItems:"center", gap:6}}>
                <span style={{color:T.hint, fontSize:11}}>{L.from}</span>
                <input type="date" value={since} onChange={e => setSince(e.target.value)}
                       style={{border:"none", outline:"none", background:"transparent",
                               color:T.ink, fontSize:12.5}}/>
              </label>
              <label style={{...inp, display:"flex", alignItems:"center", gap:6}}>
                <span style={{color:T.hint, fontSize:11}}>{L.to}</span>
                <input type="date" value={until} onChange={e => setUntil(e.target.value)}
                       style={{border:"none", outline:"none", background:"transparent",
                               color:T.ink, fontSize:12.5}}/>
              </label>
            </>
          )}

          <select style={inp} value={sup} onChange={e => setSup(Number(e.target.value))}>
            <option value={0}>{L.allSup}</option>
            {(data.suppliers||[]).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select style={inp} value={minPts} onChange={e => setMinPts(Number(e.target.value))}>
            {[1,2,3,5,10].map(n => <option key={n} value={n}>{n === 1 ? (lang==="fr"?"Tous les produits (≥ 1 achat)":"All products (≥ 1 purchase)") : `${L.minPts} : ${n}`}</option>)}
          </select>
          <label style={{...inp, display:"flex", alignItems:"center", gap:7,
            cursor:"pointer", background: onlyJump ? "rgba(163,50,7,.08)" : T.card,
            borderColor: onlyJump ? "rgba(163,50,7,.32)" : T.line,
            color: onlyJump ? "#a33207" : T.sub, fontWeight: onlyJump ? 700 : 500}}>
            <input type="checkbox" checked={onlyJump}
                   onChange={e => setOnlyJump(e.target.checked)}
                   style={{margin:0, accentColor:"#a33207"}}/>
            {L.onlyJump}
          </label>
          <button style={btnX} onClick={exportSummary}>{L.xlsSummary}</button>
          <button style={btnX} onClick={exportDetail}>{L.xlsDetail}</button>
        </div>

        {rows.length === 0 && <div style={{color:T.hint,fontSize:13,padding:"10px 0"}}>{L.empty}</div>}

        {(() => {
          const fr = lang === "fr";
          const COLS = "minmax(160px,1fr) 128px 96px 116px 84px 92px";
          const H = ({ k, label, right, title }) => (
            <span onClick={() => k && sortBy(k)} title={title || (fr ? "Cliquer pour trier" : "Click to sort")}
              style={{cursor: k ? "pointer" : "default", textAlign: right ? "right" : "left", userSelect: "none",
                color: sort === k ? T.ink : T.hint, whiteSpace: "nowrap"}}>
              {label}{sort === k ? (dir === "desc" ? " ▼" : " ▲") : (k ? " ↕" : "")}
            </span>);
          const sm = {...inp, padding: "4px 7px", fontSize: 11.5, width: "100%", boxSizing: "border-box"};
          return (
            <div style={{position: "sticky", top: 0, zIndex: 2, background: T.card, marginBottom: 6,
              borderBottom: `2px solid ${T.line}`, paddingBottom: 6}}>
              <div style={{display: "grid", gridTemplateColumns: COLS, gap: 12, padding: "4px 13px",
                fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase"}}>
                <span style={{display: "flex", gap: 14}}>
                  <H k="name" label={fr ? "Produit" : "Product"}/>
                  <H k="supplier" label={fr ? "Fournisseur" : "Supplier"}/>
                  <H k="n" label={fr ? "Nb achats" : "Purchases"}/>
                </span>
                <H k="jump" label={fr ? "Tendance" : "Trend"} title={fr ? "Trier par plus gros saut entre deux achats" : "Sort by largest jump between two purchases"}/>
                <H k="last" label={fr ? "Dernier prix" : "Last price"} right/>
                <H k="spread" label={fr ? "Min / Max" : "Min / Max"} right title={fr ? "Trier par amplitude (max ÷ min)" : "Sort by spread (max ÷ min)"}/>
                <H k="delta" label={fr ? "Variation" : "Change"} right title={fr ? "Premier → dernier prix de la période" : "First → last price of the period"}/>
                <H k="spend" label={fr ? "Dépensé" : "Spent"} right/>
              </div>
              <div style={{display: "grid", gridTemplateColumns: COLS, gap: 12, padding: "2px 13px"}}>
                <span style={{fontSize: 11, color: T.hint, alignSelf: "center"}}>
                  {rows.length} {fr ? "produits affichés" : "products shown"}</span>
                <span/>
                <span style={{display: "flex", gap: 4}}>
                  <input style={sm} placeholder="min" value={fMinP} onChange={e => setFMinP(e.target.value)}/>
                  <input style={sm} placeholder="max" value={fMaxP} onChange={e => setFMaxP(e.target.value)}/>
                </span>
                <span/>
                <select style={sm} value={fVar} onChange={e => setFVar(e.target.value)}>
                  <option value="all">{fr ? "Toutes" : "All"}</option>
                  <option value="up">{fr ? "Hausse" : "Up"}</option>
                  <option value="down">{fr ? "Baisse" : "Down"}</option>
                  <option value="flat">{fr ? "Stable" : "Flat"}</option>
                  <option value="j10">{fr ? "± > 10 %" : "± > 10 %"}</option>
                  <option value="j25">{fr ? "± > 25 %" : "± > 25 %"}</option>
                </select>
                <input style={sm} placeholder={fr ? "min CHF" : "min CHF"} value={fMinS} onChange={e => setFMinS(e.target.value)}/>
              </div>
            </div>);
        })()}

        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {rows.map(p => {
            const isOpen = open === p.key;
            const col = phCol(p.delta_pct);
            return (
              <div key={p.key} style={{border:`1px solid ${isOpen?col:T.line}`,
                borderRadius:10,background:isOpen?T.bg:T.card,overflow:"hidden"}}>
                <div onClick={() => setOpen(isOpen ? null : p.key)}
                  style={{display:"grid",
                    gridTemplateColumns:"minmax(160px,1fr) 128px 96px 116px 84px 92px",
                    alignItems:"center",gap:12,padding:"9px 13px",cursor:"pointer"}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:T.ink,whiteSpace:"nowrap",
                      overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</div>
                    <div style={{fontSize:11,color:T.hint,marginTop:2,whiteSpace:"nowrap",
                      overflow:"hidden",textOverflow:"ellipsis"}}>
                      {p.supplier} · {p.n} {L.buys}{p.uom?" · "+p.uom:""}{p.code?" · "+p.code:""}
                      {p.n_fixed>0 && (
                        <span title={L.fixedT} style={{marginLeft:6,fontSize:9.5,fontWeight:800,
                          color:"#0b6ea8",background:"rgba(11,110,168,.09)",
                          border:"1px solid rgba(11,110,168,.26)",borderRadius:999,padding:"1px 6px"}}>
                          {p.n_fixed} {L.fixed}
                        </span>
                      )}
                      {p.n_jump>0 && (
                        <span title={L.jumpT} style={{marginLeft:6,fontSize:9.5,fontWeight:800,
                          color:"#a33207",background:"rgba(163,50,7,.09)",
                          border:"1px solid rgba(163,50,7,.26)",borderRadius:999,padding:"1px 6px"}}>
                          {p.n_jump} {L.jump}
                        </span>
                      )}
                      {p.n_suspect>0 && (
                        <span title={L.suspT} style={{marginLeft:6,fontSize:9.5,fontWeight:800,
                          color:T.warn||"#b45309",background:"rgba(180,83,9,.10)",
                          border:"1px solid rgba(180,83,9,.28)",borderRadius:999,padding:"1px 6px"}}>
                          {p.n_suspect} {L.susp}
                        </span>
                      )}
                    </div>
                  </div>
                  <PHSpark series={p.series}/>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:14.5,fontWeight:800,color:T.ink,fontFamily:PH_MONO}}>{phFmt(p.last)}</div>
                    <div style={{fontSize:10,color:T.hint}}>{L.last}</div>
                  </div>
                  <div style={{textAlign:"right",fontSize:11.5,color:T.sub,fontFamily:PH_MONO}}>
                    {phFmt(p.min)} / {phFmt(p.max)}
                    <div style={{fontSize:10,color:T.hint,fontFamily:"inherit"}}>{L.range}</div>
                  </div>
                  <div style={{textAlign:"right",color:col,fontWeight:800,fontSize:13.5,fontFamily:PH_MONO}}>
                    {p.delta_pct>0?"+":""}{phFmt(p.delta_pct,1)}%
                  </div>
                  <div style={{textAlign:"right",fontSize:12,color:T.sub,fontFamily:PH_MONO}}>
                    {phFmt(p.spend,0)}
                    <div style={{fontSize:10,color:T.hint,fontFamily:"inherit"}}>{L.spent}</div>
                  </div>
                </div>

                {isOpen && (
                  <div style={{borderTop:`1px solid ${T.line}`,padding:"12px 13px 8px",background:T.card}}>
                    <PHChart series={p.series} uom={p.uom} L={L}/>
                    <div style={{maxHeight:200,overflowY:"auto",marginTop:8}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                        <thead>
                          <tr style={{color:T.hint,textAlign:"right"}}>
                            <th style={{textAlign:"left",padding:"4px 6px",fontWeight:600}}>{L.date}</th>
                            <th style={{padding:"4px 6px",fontWeight:600}}>{L.price}</th>
                            <th style={{padding:"4px 6px",fontWeight:600}}>{L.delta}</th>
                            <th style={{padding:"4px 6px",fontWeight:600}}>{L.qty}</th>
                            <th style={{padding:"4px 6px",fontWeight:600}}>{L.total}</th>
                            <th style={{padding:"4px 6px",fontWeight:600}}>{L.inv}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...p.series].reverse().map((s,i) => (
                            <tr key={i} style={{borderTop:`1px solid ${T.line}`,textAlign:"right"}}>
                              <td style={{textAlign:"left",padding:"4px 6px",fontFamily:PH_MONO,color:T.sub}}>{s.d}</td>
                              <td style={{padding:"4px 6px",fontWeight:800,fontFamily:PH_MONO,color:T.ink}}>
                                {phFmt(s.p)}
                                {s.cf==="corrigé" && <span title={L.fixedT}
                                  style={{marginLeft:4,fontSize:9,color:"#0b6ea8"}}>◆</span>}
                              </td>
                              <td style={{padding:"4px 6px",fontFamily:PH_MONO,
                                fontWeight:s.jp?800:400,
                                color:s.dp==null?T.ghost:phCol(s.dp),
                                background:s.jp?"rgba(163,50,7,.07)":"transparent"}}>
                                {s.dp==null?"—":(s.dp>0?"+":"")+phFmt(s.dp,1)+"%"}</td>
                              <td style={{padding:"4px 6px",color:T.sub,fontFamily:PH_MONO}}>{phFmt(s.q,3)}</td>
                              <td style={{padding:"4px 6px",color:T.sub,fontFamily:PH_MONO}}>{phFmt(s.t)}</td>
                              <td style={{padding:"4px 6px",color:T.hint,fontFamily:PH_MONO}}>{s.inv}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{marginTop:12,fontSize:10.5,color:T.ghost,fontFamily:PH_MONO}}>{L.src}</div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  USERS ACTIVITY — qui se connecte au dashboard (réservé au rôle Admin)
//  Source : GET /seg/audit (journal logs/dashboard_audit.jsonl sur le 87).
// ═══════════════════════════════════════════════════════════════════════════
const UA_EVT = {
  login_ok:   { l:"Connexion",   e:"Login",   c:"#16a34a" },
  login_fail: { l:"Échec",       e:"Failed",  c:"#dc2626" },
  blocked:    { l:"Bloqué",      e:"Blocked", c:"#b45309" },
  logout:     { l:"Déconnexion", e:"Logout",  c:"#7e7974" },
};
// Le journal du 87 est écrit en UTC sans fuseau : on le relit comme UTC,
// puis on l'affiche à l'heure locale du navigateur (Zurich).
const uaDate = (iso) => !iso ? null
  : new Date(/[zZ]$|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z");
const uaAgo = (iso, lang = "fr") => {
  const en = lang === "en";
  if (!iso) return en ? "never" : "jamais";
  const s = (Date.now() - uaDate(iso).getTime()) / 1000;
  if (s < 60)    return en ? "just now" : "à l'instant";
  if (s < 3600)  return en ? `${Math.round(s/60)} min ago` : `il y a ${Math.round(s/60)} min`;
  if (s < 86400) return en ? `${Math.round(s/3600)} h ago` : `il y a ${Math.round(s/3600)} h`;
  return en ? `${Math.round(s/86400)} d ago` : `il y a ${Math.round(s/86400)} j`;
};
const uaDur = (m) => !m ? "—" : m < 60 ? `${m} min` : `${Math.floor(m/60)} h ${String(m%60).padStart(2,"0")}`;
const uaDevice = (ua) => {
  if (!ua) return "—";
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android"
           : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : "Autre";
  const br = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome"
           : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "?";
  return `${br} · ${os}`;
};
const uaTs = (iso) => {
  const d = uaDate(iso);
  if (!d || isNaN(d)) return "—";
  const p = (n) => String(n).padStart(2,"0");
  return `${p(d.getDate())}.${p(d.getMonth()+1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// ═══════════════════════════════════════════════════════════════════════════
//  ADMIN — budget Food hebdomadaire W0→W11 (30/10/2026)
//  Stocké sur le 87 (food_budgets.json via POST /seg/food_budgets), relu par le
//  bandeau SPEND vs BUDGET de FOOD INVOICES. Total mois = Σ semaines du mois.
// ═══════════════════════════════════════════════════════════════════════════
const FB_FR_MONTH = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const FB_EN_MONTH = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const fbChf = (v) => "CHF " + Math.round(v || 0).toLocaleString("de-CH");
const fbNum = (x) => { const n = parseFloat(String(x).replace(/['’\s]/g, "").replace(",", ".")); return isFinite(n) && n > 0 ? n : 0; };

function FoodBudgetEditor({ lang = "fr" }) {
  const L = (f, e) => lang === "fr" ? f : e;
  const MN = lang === "fr" ? FB_FR_MONTH : FB_EN_MONTH;
  const nowMk = fbMonthKey(new Date());
  const editable = FB_QUARTERS.filter(q => q.weeks && q.months[q.months.length - 1] >= nowMk);
  const [qid, setQid]     = useState(editable[0] ? editable[0].id : null);
  const [saved, setSaved] = useState(null);
  const [vals, setVals]   = useState({});
  const [msg, setMsg]     = useState("");
  const [busy, setBusy]   = useState(false);
  useEffect(() => { fbLoad().then(setSaved); }, []);

  if (!qid) return null;
  if (!saved) return <Card style={{marginBottom:18}}><div style={{color:T.hint,fontSize:13}}>{L("Chargement des budgets…","Loading budgets…")}</div></Card>;

  const q    = fbApply(saved).find(x => x.id === qid);
  const raw  = vals[qid] || q.wb.map(String);
  const nums = raw.map(fbNum);
  const dirty = nums.some((v, i) => v !== q.wb[i]);
  const total = nums.reduce((a, b) => a + b, 0);
  const meta  = saved[qid];
  const setW = (i, x) => setVals(p => { const a = [...(p[qid] || q.wb.map(String))]; a[i] = x; return { ...p, [qid]: a }; });
  const fillAll = () => {
    const x = window.prompt(L("Montant identique pour les " + q.nWeeks + " semaines (CHF)", "Same amount for all " + q.nWeeks + " weeks (CHF)"), String(nums[0] || q.weekBudget));
    if (x == null) return;
    setVals(p => ({ ...p, [qid]: Array(q.nWeeks).fill(String(fbNum(x))) }));
  };
  const cancel = () => setVals(p => { const c = { ...p }; delete c[qid]; return c; });
  const save = () => {
    setBusy(true); setMsg("");
    fetch(`${API}/seg/food_budgets`, {
      method:"POST", headers:{ "Content-Type":"application/json", ...authHeaders() },
      body: JSON.stringify({ quarter: qid, weeks: nums }),
    })
      .then(r => {
        if (r.status === 401) throw new Error(L("Session expirée — se reconnecter","Session expired — please log in again"));
        if (r.status === 403) throw new Error(L("Réservé à l'administrateur","Administrator only"));
        if (!r.ok) throw new Error(`HTTP ${r.status} /seg/food_budgets`);
        return r.json();
      })
      .then(j => {
        setSaved(p => ({ ...p, [qid]: { weeks: j.weeks, updated: j.updated, by: j.by } }));
        cancel(); setMsg(L("✓ Enregistré — visible dans FOOD INVOICES","✓ Saved — now live in FOOD INVOICES"));
      })
      .catch(e => setMsg("⚠ " + (e.message || e)))
      .finally(() => setBusy(false));
  };
  const btn = (primary, disabled) => ({
    border: primary ? "none" : `1px solid ${T.line}`, borderRadius:8, padding:"7px 14px",
    fontSize:12, fontWeight:700, cursor: disabled ? "default" : "pointer", opacity: disabled ? .45 : 1,
    background: primary ? T.ink : T.card, color: primary ? "#fff" : T.sub,
  });
  const mono = "ui-monospace,Menlo,monospace";

  return (
    <Card style={{marginBottom:22}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14,flexWrap:"wrap",marginBottom:16}}>
        <div>
          <div style={{fontSize:15,fontWeight:800,color:T.ink}}>{L("Budget Food hebdomadaire","Weekly Food budget")}</div>
          <div style={{fontSize:12,color:T.hint,marginTop:3}}>
            {L("Grille","Grid")} W0 → W{q.nWeeks - 1} · {L("CHF HT · alimente le bandeau SPEND vs BUDGET de FOOD INVOICES","CHF net of VAT · feeds the SPEND vs BUDGET banner in FOOD INVOICES")}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div style={{display:"inline-flex",border:`1px solid ${T.line}`,borderRadius:8,overflow:"hidden"}}>
            {editable.map(e => (
              <button key={e.id} onClick={() => { setQid(e.id); setMsg(""); }} style={{border:"none",cursor:"pointer",
                padding:"6px 12px",fontSize:12,fontWeight:700,
                background:qid===e.id?T.ink:T.card,color:qid===e.id?"#fff":T.sub}}>
                {e.key} {e.months[0].slice(0,4)}
              </button>
            ))}
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:9.5,letterSpacing:".08em",textTransform:"uppercase",color:T.hint,fontWeight:700}}>Total {q.key}</div>
            <div style={{fontSize:22,fontWeight:800,color:T.ink,fontFamily:mono}}>{fbChf(total)}</div>
          </div>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:12}}>
        {q.months.map(m => {
          const mt = q.weeks[m].reduce((a, w) => a + nums[w], 0);
          return (
            <div key={m} style={{border:`1px solid ${T.line}`,borderRadius:12,padding:"12px 14px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                <span style={{fontSize:13,fontWeight:800,color:T.ink,textTransform:"capitalize"}}>
                  {MN[+m.slice(5,7) - 1]} {m.slice(0,4)}
                </span>
                <span style={{fontFamily:mono,fontSize:10.5,color:T.hint}}>
                  W{q.weeks[m][0]}–W{q.weeks[m][q.weeks[m].length - 1]}
                </span>
              </div>
              {q.weeks[m].map(w => {
                const changed = nums[w] !== q.wb[w];
                return (
                  <div key={w} style={{display:"grid",gridTemplateColumns:"34px 44px 1fr",gap:8,alignItems:"center",padding:"3px 0"}}>
                    <span style={{fontFamily:mono,fontSize:12,fontWeight:700,color:T.sub}}>W{w}</span>
                    <span style={{fontFamily:mono,fontSize:11,color:T.hint}}>{fbWeekDate(q, w)}</span>
                    <input value={raw[w]} inputMode="decimal" onChange={e => setW(w, e.target.value)}
                      onFocus={e => e.target.select()}
                      style={{width:"100%",boxSizing:"border-box",textAlign:"right",fontFamily:mono,fontSize:13,
                        padding:"6px 9px",borderRadius:7,color:T.ink,background:changed?"#fff7e6":T.card,
                        border:`1px solid ${changed?"#f59e0b":T.line}`}}/>
                  </div>
                );
              })}
              <div style={{display:"flex",justifyContent:"space-between",borderTop:`1px solid ${T.line}`,
                marginTop:8,paddingTop:8,fontSize:12.5}}>
                <span style={{color:T.sub,fontWeight:700}}>Total {MN[+m.slice(5,7) - 1]}</span>
                <span style={{fontFamily:mono,fontWeight:800,color:T.ink}}>{fbChf(mt)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginTop:14}}>
        <div style={{fontSize:11.5,color:T.hint}}>
          {meta && meta.updated
            ? <>{L("Dernière modification :","Last change:")} <b style={{color:T.sub}}>{uaTs(meta.updated)}</b>{meta.by ? <> {L("par","by")} <b style={{color:T.sub}}>{meta.by}</b></> : null}</>
            : <>{L("Jamais enregistré — valeurs par défaut","Never saved — default values")} ({fbChf(q.weekBudget)} / {L("semaine","week")})</>}
          {msg && <span style={{marginLeft:10,fontWeight:700,color:msg[0]==="✓"?T.ok:T.err}}>{msg}</span>}
        </div>
        <div className="no-print" style={{display:"flex",gap:8}}>
          <button onClick={fillAll} style={btn(false, false)}>{L("Même montant partout…","Same amount everywhere…")}</button>
          <button onClick={cancel} disabled={!dirty} style={btn(false, !dirty)}>{L("Annuler","Cancel")}</button>
          <button onClick={save} disabled={!dirty || busy} style={btn(true, !dirty || busy)}>
            {busy ? L("Enregistrement…","Saving…") : L("Enregistrer","Save")}
          </button>
        </div>
      </div>
    </Card>
  );
}

function AdminTab() {
  // langue de la page, mémorisée par navigateur (défaut EN, comme Webstar)
  const [lang, setLangS] = useState(() => { try { return localStorage.getItem("caa_admin_lang") || "en"; } catch (e) { return "en"; } });
  const setLang = (lg) => { setLangS(lg); try { localStorage.setItem("caa_admin_lang", lg); } catch (e) {} };
  return (
    <div>
      <div className="no-print" style={{display:"flex",justifyContent:"flex-end",marginBottom:10}}>
        <div style={{display:"inline-flex",border:`1px solid ${T.line}`,borderRadius:8,overflow:"hidden"}}>
          {["fr","en"].map(lg => (
            <button key={lg} onClick={() => setLang(lg)} style={{border:"none",cursor:"pointer",padding:"5px 11px",
              fontSize:11.5,fontWeight:700,background:lang===lg?T.ink:T.card,color:lang===lg?"#fff":T.sub}}>{lg.toUpperCase()}</button>
          ))}
        </div>
      </div>
      <FoodBudgetEditor lang={lang}/>
      <div style={{fontSize:15,fontWeight:800,color:T.ink,margin:"4px 0 8px"}}>{lang==="fr" ? "Activité des utilisateurs" : "User activity"}</div>
      <UsersActivityTab lang={lang}/>
    </div>
  );
}

function UsersActivityTab({ lang = "fr" }) {
  const L = (f, e) => lang === "fr" ? f : e;
  const [days, setDays] = useState(30);
  const [d, setD]       = useState(null);
  const [err, setErr]   = useState("");

  const load = useCallback(() => {
    fetch(`${API}/seg/audit?days=${days}`, { headers:authHeaders(), cache:"no-store" })
      .then(r => {
        if (r.status===401) throw new Error(L("Session expirée — se déconnecter puis se reconnecter","Session expired — log out and back in"));
        if (r.status===403) throw new Error(L("Réservé à l'administrateur","Administrator only"));
        if (!r.ok) throw new Error(`HTTP ${r.status} /seg/audit`);
        return r.json();
      })
      .then(j => { setD(j); setErr(""); })
      .catch(e => setErr(e.message || String(e)));
  }, [days, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  if (err && !d) return <Card><div style={{color:T.err,fontSize:13}}>⚠ {err}</div></Card>;
  if (!d)        return <Card><div style={{color:T.hint,fontSize:13}}>{L("Chargement…","Loading…")}</div></Card>;

  const k = d.kpi || {};
  const exportXls = () => dlXls(
    `users_activity_${new Date().toISOString().slice(0,10)}`,
    lang==="fr"
      ? ["Utilisateur","Rôle","En ligne","Dernière activité","Dernière connexion","Connexions",
         "Échecs","Sessions","Temps cumulé (min)","Onglets les plus vus","Dernière IP","Appareil"]
      : ["User","Role","Online","Last activity","Last login","Logins",
         "Failures","Sessions","Total time (min)","Most viewed tabs","Last IP","Device"],
    d.users.map(u => [u.user, u.role, u.online?L("oui","yes"):"", uaTs(u.last_seen), uaTs(u.last_login),
      u.logins, u.fails, u.sessions, u.minutes,
      (u.top_tabs||[]).map(([t,n]) => `${NAV_LABEL(t)||t} (${n})`).join(", "),
      u.last_ip||"", uaDevice(u.last_ua)]),
    [{label:L("Période","Period"), val:L(`${days} derniers jours`,`last ${days} days`)},
     {label:L("Généré le","Generated"), val:uaTs(d.generated)}], 0,
    {title:L("CAA Executive Dashboard — Activité des utilisateurs","CAA Executive Dashboard — User activity"), zebra:true});

  const tiles = [
    {l:L("En ligne maintenant","Online now"), v:k.online||0, s:L("activité < 7 min","active < 7 min"), c:T.ok},
    {l:L("Utilisateurs actifs","Active users"), v:`${k.active||0} / ${k.users||0}`, s:L(`sur ${days} jours`,`over ${days} days`), c:T.info},
    {l:L("Connexions","Logins"),          v:k.logins||0, s:L(`sur ${days} jours`,`over ${days} days`), c:T.ink},
    {l:L("Échecs de login","Failed logins"), v:k.fails||0,  s:L("mot de passe ou nom inconnu","wrong password or unknown name"), c:(k.fails||0)>0?T.err:T.hint},
  ];
  const th = {padding:"9px 12px",textAlign:"left",fontSize:9.5,letterSpacing:".08em",
              textTransform:"uppercase",color:"#fff",fontWeight:700,whiteSpace:"nowrap"};
  const td = {padding:"9px 12px",borderBottom:`1px solid ${T.line}`,fontSize:12.5,verticalAlign:"top"};

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,
        flexWrap:"wrap",marginBottom:14}}>
        <div style={{fontSize:12,color:T.hint}}>
          {L("Journal des connexions au dashboard · rafraîchi toutes les 60 s","Dashboard login log · refreshed every 60 s")}
          {err && <span style={{color:T.err,marginLeft:8}}>· ⚠ {err}</span>}
        </div>
        <div className="no-print" style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{display:"inline-flex",border:`1px solid ${T.line}`,borderRadius:8,overflow:"hidden"}}>
            {[7,30,90].map(n => (
              <button key={n} onClick={()=>setDays(n)} style={{border:"none",cursor:"pointer",
                padding:"6px 12px",fontSize:12,fontWeight:700,
                background:days===n?T.ink:T.card,color:days===n?"#fff":T.sub}}>{n} {L("j","d")}</button>
            ))}
          </div>
          <button onClick={exportXls} style={{border:`1px solid ${T.line}`,background:T.card,
            color:T.sub,borderRadius:8,padding:"6px 13px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            ⬇ Export XLS
          </button>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginBottom:16}}>
        {tiles.map((t,i) => (
          <Card key={i} style={{padding:"16px 18px",borderLeft:`3px solid ${t.c}`}}>
            <Label>{t.l}</Label>
            <div style={{fontSize:26,fontWeight:800,color:t.c,letterSpacing:"-.02em"}}>{t.v}</div>
            <div style={{fontSize:11,color:T.hint,marginTop:2}}>{t.s}</div>
          </Card>
        ))}
      </div>

      <Card style={{padding:0,overflow:"hidden",marginBottom:16}}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:T.graphite}}>
              {(lang==="fr"
                ? ["Utilisateur","Dernière activité","Connexions","Échecs","Sessions","Temps cumulé","Onglets les plus vus","Dernière IP · appareil"]
                : ["User","Last activity","Logins","Failures","Sessions","Total time","Most viewed tabs","Last IP · device"]
              ).map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {d.users.map(u => (
                <tr key={u.user} style={{background:u.online?"rgba(22,163,74,0.05)":T.card}}>
                  <td style={td}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span title={u.online?L("en ligne","online"):L("hors ligne","offline")} style={{width:8,height:8,borderRadius:8,
                        flexShrink:0,background:u.online?T.ok:T.ghost,
                        boxShadow:u.online?"0 0 0 3px rgba(22,163,74,0.18)":"none"}}/>
                      <div>
                        <div style={{fontWeight:700,color:T.ink}}>{u.user}</div>
                        <div style={{fontSize:10.5,color:T.hint}}>{u.role}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{color:u.last_seen?T.ink:T.ghost,fontWeight:600}}>{uaAgo(u.last_seen, lang)}</div>
                    <div style={{fontSize:10.5,color:T.hint}}>{uaTs(u.last_seen)}</div>
                  </td>
                  <td style={{...td,fontWeight:700,fontVariantNumeric:"tabular-nums"}}>{u.logins}</td>
                  <td style={{...td,fontWeight:700,color:u.fails?T.err:T.ghost,
                    fontVariantNumeric:"tabular-nums"}}>{u.fails||"—"}</td>
                  <td style={{...td,fontVariantNumeric:"tabular-nums"}}>{u.sessions||"—"}</td>
                  <td style={{...td,fontVariantNumeric:"tabular-nums"}}>{uaDur(u.minutes)}</td>
                  <td style={td}>
                    {(u.top_tabs||[]).length ? (u.top_tabs||[]).map(([t,n]) => (
                      <span key={t} style={{display:"inline-block",fontSize:11,padding:"2px 8px",margin:"0 4px 4px 0",
                        borderRadius:6,background:T.bg2,color:T.sub,border:`1px solid ${T.line}`}}>
                        {NAV_LABEL(t)||t} <b style={{color:T.ink}}>{n}</b>
                      </span>)) : <span style={{color:T.ghost}}>—</span>}
                  </td>
                  <td style={td}>
                    <div style={{fontFamily:"ui-monospace,Menlo,monospace",fontSize:11.5,color:T.sub}}>{u.last_ip||"—"}</div>
                    <div style={{fontSize:10.5,color:T.hint}}>{uaDevice(u.last_ua)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {(k.never||[]).length>0 && (
        <div style={{fontSize:12,color:T.hint,marginBottom:16}}>
          {L(`Aucune connexion sur ${days} jours :`,`No login over ${days} days:`)} <b style={{color:T.sub}}>{k.never.join(", ")}</b>
        </div>
      )}

      <Card>
        <div style={{fontSize:14,fontWeight:800,color:T.ink,marginBottom:10}}>{L("Derniers événements","Recent events")}</div>
        <div style={{maxHeight:380,overflowY:"auto"}}>
          {(d.recent||[]).length===0 && <div style={{fontSize:12.5,color:T.hint}}>{L("Aucun événement sur la période.","No events in this period.")}</div>}
          {(d.recent||[]).map((e,i) => {
            const m = UA_EVT[e.event] || {l:e.event,c:T.hint};
            return (
              <div key={i} style={{display:"grid",gridTemplateColumns:"128px 104px 1fr 130px 130px",gap:10,
                alignItems:"center",padding:"7px 4px",borderBottom:`1px solid ${T.line}`,fontSize:12.5}}>
                <span style={{fontFamily:"ui-monospace,Menlo,monospace",fontSize:11.5,color:T.hint}}>{uaTs(e.ts)}</span>
                <span><Chip label={lang==="fr" ? m.l : (m.e || m.l)} color={m.c}/></span>
                <span style={{fontWeight:600,color:T.ink}}>
                  {e.user}
                  {e.reason==="unknown_user" && <span style={{color:T.err,fontWeight:500}}> · {L("nom inconnu","unknown name")}</span>}
                  {e.reason==="bad_password" && <span style={{color:T.hint,fontWeight:500}}> · {L("mauvais mot de passe","wrong password")}</span>}
                  {e.reason==="close" && <span style={{color:T.hint,fontWeight:500}}> · {L("onglet fermé","tab closed")}</span>}
                </span>
                <span style={{fontFamily:"ui-monospace,Menlo,monospace",fontSize:11.5,color:T.sub}}>{e.ip||"—"}</span>
                <span style={{fontSize:11.5,color:T.hint}}>{uaDevice(e.ua)}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

const NAV = [
  // Page d'entrée : elle pose le cadre de lecture avant tout chiffre.
  // `accent` = fond rouge distinctif, elle n'est pas une page comme les autres.
  {id:"reconciliation",icon:"⚖", label:"Totaux & Périmètres", accent:"#d1372e"},
  {group:"live",   icon:"▲", label:"Live",   items:[
    {id:"q4live", label:`Q4 ${qStat(4)}`},
    {id:"q3live", label:`Q3 ${qStat(3)}`},
    {id:"q2live", label:`Q2 ${qStat(2)}`},
  ]},
  {group:"weekly", icon:"◉", label:"Weekly", items:[
    {id:"weeklyq4", label:`Weekly Q4 · ${qStat(4)}`},
    {id:"weeklyq3", label:`Weekly Q3 · ${qStat(3)}`},
    {id:"weekly",   label:`Weekly Q2 · ${qStat(2)}`},
  ]},
  {id:"procurement", icon:"⊙", label:"PROCUREMENT"},
  {id:"food",     icon:"⊛", label:"FOOD INVOICES"},
  {id:"prices",   icon:"↗", label:"PRICE HISTORY"},
  {id:"webstar",  icon:"⊡", label:"NON FOOD INVOICES"},
  {id:"premium",  icon:"★", label:"Premium Restaurants"},
  {id:"lakeview", icon:"◈", label:"Lake View"},
  {id:"terms",    icon:"≡", label:"Terms/Kitchen Costs"},
  {id:"economat", icon:"⊟", label:"Economat"},
  {id:"inventory",icon:"⊞", label:"Inventory Valuation"},
  {id:"simulator",icon:"⊕", label:"Budget vs Costs Simulator"},
  {id:"planning", icon:"▦", label:"Restaurants Planning"},
];
// Libellé plat (pour le titre de page), inclut les items groupés
const NAV_LABEL = (id) => {
  if (id==="users") return "Admin";
  for (const n of NAV) {
    if (n.id===id) return n.label;
    if (n.items) { const it=n.items.find(x=>x.id===id); if (it) return it.label; }
  }
  return "";
};

export default function App() {
  const [user,setUser]=useState(null);
  const [name,setName]=useState("");
  const [pwd, setPwd] =useState("");
  const [err, setErr] =useState("");
  // Page d'accueil = Totaux & Périmètres : elle pose le cadre de lecture
  // avant que l'utilisateur ouvre un chiffre en particulier.
  const [tab, setTab] =useState("reconciliation");
  const [openMenu, setOpenMenu] = useState(null);
  const [userNames, setUserNames] = useState(SEG_USERS_FALLBACK);
  const [busy, setBusy] = useState(false);

  // Liste des utilisateurs = seg_passwords.json du 87 (noms seulement).
  useEffect(() => {
    fetch(`${API}/seg/users`, {cache:"no-store"})
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && Array.isArray(d.users) && d.users.length) setUserNames(d.users); })
      .catch(() => {});
  }, []);

  // Audit : onglet consulté
  useEffect(() => { if (user) auditEvent("tab", {tab}); }, [tab, user]);

  // Audit : heartbeat toutes les 5 min tant que la page est visible,
  // et fin de session quand l'onglet du navigateur est fermé.
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => { if (!document.hidden) auditEvent("heartbeat"); }, 5*60*1000);
    const onHide = () => auditEvent("logout", {reason:"close"});
    window.addEventListener("pagehide", onHide);
    return () => { clearInterval(id); window.removeEventListener("pagehide", onHide); };
  }, [user]);

  const login = async () => {
    if (!name) { setErr("Sélectionner un utilisateur"); return; }
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API}/seg/login`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({user:name, password:pwd}),
      });
      if (r.status===401) { setErr("Mot de passe incorrect"); return; }
      if (r.status===429) { setErr("Trop de tentatives — réessayer dans 10 minutes"); return; }
      if (!r.ok)          { setErr(`Serveur indisponible (HTTP ${r.status})`); return; }
      const d = await r.json();
      AUTH = { token:d.token, session:d.session, role:d.role, can_fix:!!d.can_fix };
      setPwd("");
      setUser({name:d.user, role:d.role});
    } catch (e) {
      setErr("API injoignable — vérifier le serveur 87");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    auditEvent("logout");
    AUTH = { token:null, session:null };
    setTab("reconciliation");
    setUser(null);
  };

  if(!user) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",
      justifyContent:"center",fontFamily:"DM Sans,Helvetica Neue,sans-serif",
      background:"linear-gradient(135deg,#1d1916 0%,#2b2824 60%,#1d1916 100%)"}}>
      <div style={{width:420}}>
        <div style={{textAlign:"center",marginBottom:36}}>
          <div style={{width:64,height:64,borderRadius:18,margin:"0 auto 18px",
            background:"linear-gradient(135deg,#2b2824,#af642a)",
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:28,fontWeight:900,color:"#fff",
            boxShadow:"0 8px 32px rgba(175,100,42,0.35)"}}>C</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",
            letterSpacing:".25em",marginBottom:6}}>Culinary Arts Academy</div>
          <div style={{fontSize:26,fontWeight:800,color:"#fff",marginBottom:4}}>Executive Dashboard</div>
          <div style={{fontSize:12.5,color:"rgba(255,255,255,0.4)"}}>Food Cost Intelligence · Q1 & Q2 2026</div>
        </div>
        <div style={{background:"rgba(255,255,255,0.05)",backdropFilter:"blur(16px)",
          border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,padding:"32px"}}>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",
              letterSpacing:".1em",marginBottom:8}}>Nom</div>
            <select value={name} onChange={e=>{setName(e.target.value);setErr("")}}
              style={{width:"100%",padding:"12px 14px",background:"rgba(255,255,255,0.07)",
                border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,color:"#fff",
                fontSize:14,boxSizing:"border-box",outline:"none"}}>
              <option value="" style={{background:"#2b2824"}}>Sélectionner…</option>
              {userNames.map(n=>(
                <option key={n} style={{background:"#2b2824"}}>{n}</option>
              ))}
            </select>
          </div>
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",
              letterSpacing:".1em",marginBottom:8}}>Mot de passe</div>
            <input type="password" value={pwd}
              onChange={e=>{setPwd(e.target.value);setErr("")}}
              onKeyDown={e=>e.key==="Enter"&&login()}
              placeholder="••••••••"
              style={{width:"100%",padding:"12px 14px",background:"rgba(255,255,255,0.07)",
                border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,color:"#fff",
                fontSize:14,boxSizing:"border-box",outline:"none"}}/>
          </div>
          {err&&<div style={{background:"rgba(220,38,38,0.15)",border:"1px solid rgba(220,38,38,0.3)",
            borderRadius:9,padding:"9px 12px",fontSize:12,color:"#e8a09a",marginBottom:14}}>{err}</div>}
          <button onClick={login} disabled={busy}
            style={{width:"100%",padding:14,opacity:busy?0.6:1,background:"linear-gradient(135deg,#2b2824,#af642a)",
              border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",
              boxShadow:"0 4px 20px rgba(21,128,61,0.45)"}}>
            {busy ? "Connexion…" : "Connexion"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",
      fontFamily:"DM Sans,Helvetica Neue,sans-serif",
      background:T.bg,color:T.ink}}>

      {/* TOP BAR */}
      <header style={{background:T.sidebar,position:"sticky",top:0,zIndex:100,
        boxShadow:"0 2px 8px rgba(0,0,0,0.15)"}}>
        {/* Logo row */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"10px 20px"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:32,height:32,borderRadius:8,
              background:"linear-gradient(135deg,#2b2824,#af642a)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:15,fontWeight:900,color:"#edeae6",flexShrink:0}}>C</div>
            <div>
              <div style={{fontSize:13,fontWeight:800,color:"#fff"}}>CAA Food Cost</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>Culinary Arts Academy · 2026</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:"#37804b",
                boxShadow:"0 0 0 2px rgba(55,128,75,0.2)"}}/>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontWeight:500}}>
                {new Date().toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
              </span>
            </div>
            <span style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>
              {user.name} <span style={{padding:"1px 6px",borderRadius:8,
                background:"rgba(175,100,42,0.15)",color:"#af642a",fontSize:9,fontWeight:600,marginLeft:4}}>
                {user.role}</span>
            </span>
            <button onClick={logout}
              style={{padding:"4px 10px",borderRadius:6,
                background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
                color:"rgba(255,255,255,0.4)",fontSize:10,cursor:"pointer"}}>
              Logout
            </button>
          </div>
        </div>
        {/* Nav tabs - wrap on narrow screens so nothing disappears */}
        <nav style={{display:"flex",flexWrap:"wrap",gap:4,padding:"0 16px 8px",
          WebkitOverflowScrolling:"touch"}}>
          {(user.role==="Admin"
              ? [...NAV, {id:"users", icon:"⚙", label:"Admin"}]
              : NAV).map(n=>{
            const _acc = n.accent || "#0ea5e9";
            const btnStyle=(a)=>({display:"flex",alignItems:"center",gap:6,
              padding:"7px 12px",borderRadius:9,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,
              background: a ? _acc : (n.accent ? hexAlpha(n.accent,.18) : "transparent"),
              border: a ? `1px solid ${_acc}`
                        : (n.accent ? `1px solid ${hexAlpha(n.accent,.55)}` : "1px solid transparent"),
              color: a ? "#ffffff" : (n.accent ? "#ffffff" : "rgba(255,255,255,0.78)"),
              fontSize:12.5,fontWeight: a || n.accent ? 800 : 600,
              letterSpacing:".005em",
              boxShadow: a ? `0 2px 12px ${hexAlpha(_acc,.5)}` : "none",
              transition:"background .15s,color .15s"});
            if (n.items) {
              const activeItem = n.items.find(it=>it.id===tab);
              const a = !!activeItem;
              const isOpen = openMenu===n.group;
              return (
                <div key={n.group} style={{position:"relative"}}>
                  <button onClick={()=>setOpenMenu(isOpen?null:n.group)} style={btnStyle(a)}
                    onMouseEnter={e=>{if(!a){e.currentTarget.style.background="rgba(255,255,255,0.10)";e.currentTarget.style.color="#fff";}}}
                    onMouseLeave={e=>{if(!a){e.currentTarget.style.background="transparent";e.currentTarget.style.color="rgba(255,255,255,0.78)";}}}>
                    <span style={{fontSize:12,opacity:a?1:.75}}>{n.icon}</span>
                    <span>{activeItem?activeItem.label:n.label}</span>
                    <span style={{fontSize:9,opacity:.8}}>▾</span>
                  </button>
                  {isOpen && (
                    <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,zIndex:50,
                      background:"#1d1916",border:"1px solid rgba(255,255,255,0.14)",borderRadius:10,
                      padding:4,minWidth:180,boxShadow:"0 8px 24px rgba(0,0,0,0.4)"}}>
                      {n.items.map(it=>{
                        const ia=tab===it.id;
                        return (
                          <button key={it.id} onClick={()=>{setTab(it.id);setOpenMenu(null);}}
                            style={{display:"block",width:"100%",textAlign:"left",padding:"8px 12px",
                              borderRadius:7,border:"none",cursor:"pointer",whiteSpace:"nowrap",
                              background:ia?"#0ea5e9":"transparent",color:ia?"#fff":"rgba(255,255,255,0.82)",
                              fontSize:12.5,fontWeight:ia?800:600}}
                            onMouseEnter={e=>{if(!ia)e.currentTarget.style.background="rgba(255,255,255,0.10)";}}
                            onMouseLeave={e=>{if(!ia)e.currentTarget.style.background="transparent";}}>
                            {it.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const a=tab===n.id;
            return (
              <button key={n.id} onClick={()=>{setTab(n.id);setOpenMenu(null);}} style={btnStyle(a)}
                onMouseEnter={e=>{if(!a){e.currentTarget.style.background="rgba(255,255,255,0.10)";e.currentTarget.style.color="#fff";}}}
                onMouseLeave={e=>{if(!a){e.currentTarget.style.background="transparent";e.currentTarget.style.color="rgba(255,255,255,0.78)";}}}>
                <span style={{fontSize:12,opacity:a?1:.75}}>{n.icon}</span>
                <span>{n.label}</span>
              </button>
            );
          })}
        </nav>
      </header>

      {/* MAIN */}
      <style>{`
        @media print {
          header { display: none !important; }
          footer { display: none !important; }
          .no-print { display: none !important; }
          main { padding: 0 !important; }
          main > div { padding: 10px !important; max-width: 100% !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
      <main style={{flex:1,minWidth:0}}>
        <div style={{padding:"20px 24px",maxWidth:1440,margin:"0 auto",width:"100%",boxSizing:"border-box"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
            <div style={{fontSize:17,fontWeight:800,color:T.ink,letterSpacing:"-.02em"}}>
              {NAV_LABEL(tab)}
            </div>
            <button className="no-print" onClick={()=>window.print()}
              style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",
                borderRadius:8,border:`1px solid ${T.line}`,background:T.card,
                color:T.ink,fontSize:11.5,fontWeight:600,cursor:"pointer",
                boxShadow:"0 1px 2px rgba(0,0,0,0.05)"}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9V2h12v7"/>
                <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print / PDF
            </button>
          </div>
          <div style={{fontSize:11.5,color:T.hint,marginBottom:20}}>
            {tab==="prices"
              ? "Culinary Arts Academy Switzerland · prix payés par produit — table product_price_history (prix_caa)"
              : tab==="food"
              ? "Culinary Arts Academy Switzerland · factures live — publiées chaque jour à 16h (pipeline OCR → prix_caa)"
              : tab==="webstar"
              ? "Culinary Arts Academy Switzerland · Webstar + réconciliation Odoo"
              : "Culinary Arts Academy Switzerland · Live Odoo data"}
          </div>
          {tab==="q4live"   && <Q3LiveTab key="q4" quarter={4}/>}
          {tab==="q3live"   && <Q3LiveTab key="q3" quarter={3}/>}
          {tab==="q2live"   && <Q2Tab quarter={2}/>}
          {tab==="weeklyq4" && <WeeklyReportsTab key="wq4" quarter={4}/>}
          {tab==="weeklyq3" && <WeeklyReportsTab key="wq3" quarter={3}/>}
          {tab==="weekly"   && <WeeklyReportsTab quarter={2}/>}
          {tab==="premium"  &&<PremiumTab/>}
          {tab==="terms"    &&<TermsTab/>}
          {tab==="lakeview" &&<LakeViewTab/>}
          {tab==="economat" &&<EconomatTab/>}
          {tab==="food" &&<FoodTab/>}
          {tab==="prices" &&<PriceHistoryTab/>}
          {tab==="webstar" &&<WebstarTab/>}
          {tab==="reconciliation" && <ReconciliationTab/>}
          {tab==="procurement" && <ProcurementTab/>}
          {tab==="inventory"&&<InventoryValuationTab/>}
          {tab==="simulator"&&<SimulatorTab/>}
          {tab==="planning" &&<PlanningTab/>}
          {tab==="users" && user.role==="Admin" && <AdminTab/>}
        </div>
        <footer style={{padding:"14px 28px",borderTop:`1px solid ${T.line}`,
          fontSize:11,color:T.ghost,textAlign:"center"}}>
          Designed by APE · Powered by FX1 Labs · All rights reserved · 04/2026
        </footer>
      </main>
    </div>
  );
}
